/**
 * Mock Data Service
 * 
 * Provides realistic mock data for standalone development
 */

import type * as API from '../types/api';
import { calculateHealthStatus } from '../utils/healthCalculator';

// Demo trace literals carry the frontend-alias fields (prompt/completion/
// createdAt) but omit the canonical backend names the Trace contract also
// requires. Derive the canonical aliases from the existing fields so the mock
// follows the declared Trace type without restating every field.
type MockTraceSeed = Omit<
  API.Trace,
  'traceId' | 'inputContext' | 'outputDecision' | 'humanOverride' | 'timestamp'
>;
const fillTraceFields = (t: MockTraceSeed): API.Trace => ({
  ...t,
  traceId: t.id,
  inputContext: t.prompt,
  outputDecision: t.completion,
  humanOverride: false,
  timestamp: t.createdAt,
});

// Mock mutation methods receive a status as `string` (api.ts forwards an
// untyped value from the hook layer). Narrow it to the contract union with a
// validated fallback so demo objects follow the declared type — no casts.
const oneOf = <T extends string>(allowed: readonly T[], value: string, fallback: T): T =>
  (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

// Simulated delay to mimic network latency
const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Relative date helpers — used across multiple mock functions
const H = 3_600_000;
const ago = (h: number) => new Date(Date.now() - h * H).toISOString();
const fromNow = (h: number) => new Date(Date.now() + h * H).toISOString();

// ============================================================================
// MASTER DATA CONSTANTS - Single Source of Truth
// ============================================================================
// All mock data MUST derive from these constants to ensure consistency
// Health status is now calculated dynamically using calculateHealthStatus()

// Demo-Persona „Nordlicht Versicherung AG" (SSOT: docs/DEMO_PERSONA.md) — 6 KI-
// Systeme, gewachsen über ~9 Monate. `traffic` = 30-Tage-Entscheidungsvolumen
// (Summe ≈ 35.000, leicht wachsend; fraud+uw hoch, doc/pricing/chat mittel,
// churn klein/neu). blockRate in % je System (plausibel je Sparte).
const MASTER_AGENTS = [
  { id: "agent_fraud",    name: "Fraud Detector",                 traffic: 9200, blockRate: 2.1 },
  { id: "agent_uw_leben", name: "Underwriting Assistent Leben",   traffic: 8400, blockRate: 0.9 },
  { id: "agent_doc",      name: "Dokument-Klassifizierer",        traffic: 6800, blockRate: 0.4 },
  { id: "agent_pricing",  name: "Pricing Engine Komposit",        traffic: 5400, blockRate: 1.6 },
  { id: "agent_chat",     name: "Kunden-Chat-Assistent",          traffic: 4600, blockRate: 0.6 },
] as const;
// (agent_churn ist idle — 0 Verkehr — und wird separat als 6. System geführt.)

// Aggregat über 30 Tage: ~35.000 Entscheidungen (≈ 1.100–1.300/Tag, wachsend).
// Verteilung ~92 % approved / ~7 % flagged / ~1 % blocked.
const MASTER_STATS = {
  totalDecisions: 35000,
  avgConfidence: 93,
  blocked: 350,           // ~1.0 % of total
  inReview: 8,            // offene Review-Queue (siehe getReviewQueue: 8 Items)
  // Konfidenz-Verteilung als 24-h-Snapshot (windowHours: 24, ~1.200 heute):
  high: 1104,             // ~92 % (>90 % Konfidenz)
  medium: 84,             // ~7 %  (70–90 % Konfidenz)
  low: 12,                // ~1 %  (<70 % Konfidenz)
} as const;

// ============================================================================
// AGENT STATE — module-level store so mutations persist across calls in demo mode
// ============================================================================

const DEFAULT_POLICIES: API.AgentPolicies = {
  strictMode: false,
  logPayloads: true,
  piiMasking: true,
  autoBlockAnomalies: false,
  confidenceThreshold: 80,
  maxRequestsPerMinute: 600,
  timeoutMs: 5000,
};

// Per-agent version history. Newest first in each array. Keyed by the 6
// Nordlicht-Systeme; ältere Systeme haben mehr Versionen (länger im Betrieb).
const _AGENT_VERSIONS: Record<string, string[]> = {
  "agent_fraud":    ["v3.1.0", "v3.0.0", "v2.2.0", "v2.0.0", "v1.0.0"],
  "agent_uw_leben": ["v2.2.0", "v2.1.0", "v2.0.0", "v1.0.0"],
  "agent_pricing":  ["v2.0.0", "v1.4.0", "v1.2.0", "v1.0.0"],
  "agent_doc":      ["v1.3.0", "v1.1.0", "v1.0.0"],
  "agent_chat":     ["v1.1.0", "v1.0.0"],
};

// Per-agent block-reason templates. Each entry has domain-appropriate reasons.
const _AGENT_BLOCK_REASONS: Record<string, Array<{ reason: string; weight: number }>> = {
  "agent_fraud": [
    { reason: "Auffälliges Schaden-Muster (Häufung)", weight: 31 },
    { reason: "Beleg-Inkonsistenz (Datum/Betrag)", weight: 26 },
    { reason: "Mehrfachmeldung gleicher Schaden", weight: 18 },
    { reason: "Werkstatt auf Prüf-Liste", weight: 14 },
    { reason: "Geo-Abweichung Schadensort/Police", weight: 11 },
  ],
  "agent_uw_leben": [
    { reason: "Antragsdaten unvollständig", weight: 38 },
    { reason: "Gesundheitsangaben prüfbedürftig", weight: 27 },
    { reason: "Risikoklasse über Annahme-Schwelle", weight: 19 },
    { reason: "Nachversicherung außerhalb Rahmen", weight: 10 },
    { reason: "Konfidenz unter Schwellwert", weight: 6 },
  ],
  "agent_doc": [
    { reason: "Dokumenttyp nicht eindeutig", weight: 46 },
    { reason: "Schlechte Scan-Qualität (OCR)", weight: 22 },
    { reason: "Falscher Posteingangs-Kanal", weight: 16 },
    { reason: "Fehlende Vorgangs-Zuordnung", weight: 10 },
    { reason: "Dublette im Stapel", weight: 6 },
  ],
  "agent_pricing": [
    { reason: "Tarif-Drift über Toleranz", weight: 30 },
    { reason: "Eingangsdaten außerhalb Wertebereich", weight: 26 },
    { reason: "Rabatt-Stufe nicht regelkonform", weight: 18 },
    { reason: "Risikomerkmal fehlt für Sparte", weight: 15 },
    { reason: "Konfidenz unter Schwellwert", weight: 11 },
  ],
  "agent_chat": [
    { reason: "Anfrage außerhalb Wissensbasis", weight: 34 },
    { reason: "Verbindliche Auskunft angefragt", weight: 28 },
    { reason: "Beschwerde-Eskalation erkannt", weight: 20 },
    { reason: "Mögliche PII in Antwort-Entwurf", weight: 12 },
    { reason: "Konfidenz unter Schwellwert", weight: 6 },
  ],
};

// Per-agent recent-activity templates. Confidence ± ~6pp around agent mean.
// Jede „letzte Aktivität" trägt die ECHTE Trace-ID des passenden Decision-Log-
// Seeds (mockData.getTraces) → ein Klick öffnet im Entscheidungsprotokoll genau
// diese Entscheidung (statt einer synthetischen, nicht auflösbaren Aktivitäts-ID).
// Nur Einträge mit vorhandenem Seed werden geführt — so löst jeder Klick auf.
const _AGENT_ACTIVITY_TEMPLATES: Record<string, Array<{ action: string; outcome: 'APPROVED' | 'REVIEWED' | 'BLOCKED'; traceId: string }>> = {
  "agent_fraud": [
    { action: "Schaden-Scoring — VS-2026-104883 — KFZ-Kasko", outcome: 'APPROVED', traceId: 'trace_abc123' },
    { action: "Schaden-Scoring — VS-2026-105210 — Häufung erkannt", outcome: 'REVIEWED', traceId: 'trace_klm789' },
    { action: "Schaden-Scoring — VS-2026-104991 — Hausrat", outcome: 'APPROVED', traceId: 'trace_yza567' },
    { action: "Schaden-Scoring — VS-2026-105344 — Werkstatt geprüft", outcome: 'APPROVED', traceId: 'trace_hij456' },
  ],
  "agent_uw_leben": [
    { action: "Risikoprüfung — Antrag AN-2026-77120 — Risikoleben", outcome: 'APPROVED', traceId: 'trace_jkl012' },
    { action: "Risikoprüfung — Antrag AN-2026-77302 — Kapitallebens.", outcome: 'APPROVED', traceId: 'trace_stu901' },
    { action: "Risikoprüfung — Antrag AN-2026-77410 — Nachversicherung", outcome: 'APPROVED', traceId: 'trace_nop012' },
  ],
  "agent_doc": [
    { action: "Posteingang — Dok DC-2026-882140 — Schadenmeldung", outcome: 'APPROVED', traceId: 'trace_def456' },
    { action: "Posteingang — Dok DC-2026-882256 — Vertragsänderung", outcome: 'APPROVED', traceId: 'trace_pqr678' },
    { action: "Posteingang — Dok DC-2026-882309 — Kündigung", outcome: 'APPROVED', traceId: 'trace_bcd890' },
  ],
  "agent_pricing": [
    { action: "Tarifierung — Angebot PR-2026-44021 — Wohngebäude", outcome: 'APPROVED', traceId: 'trace_qrs345' },
    { action: "Tarifierung — Angebot PR-2026-44088 — Tarif-Drift", outcome: 'REVIEWED', traceId: 'trace_vwx234' },
    { action: "Tarifierung — Angebot PR-2026-44055 — Wert außerhalb Bereich", outcome: 'BLOCKED', traceId: 'trace_ghi789' },
  ],
  "agent_chat": [
    { action: "Service-FAQ — Sitzung CH-2026-90014 — Beitragsfrage", outcome: 'APPROVED', traceId: 'trace_mno345' },
    { action: "Service-FAQ — Sitzung CH-2026-90120 — Adressänderung", outcome: 'APPROVED', traceId: 'trace_efg123' },
  ],
};

// Deterministic pseudo-random so the same agent always gets the same analytics.
function _seededRandom(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function _hashAgentId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Lazy-initialized, mutable in-memory store. Populated on first access.
let _agentStore: API.Agent[] | null = null;
function _getAgentStore(): API.Agent[] {
  if (_agentStore === null) {
    // Synchronously evaluate the hard-coded agent array from _getAgentsRaw by
    // re-declaring it here — avoids await in init.
    _agentStore = _buildInitialAgents();
  }
  return _agentStore;
}

function _buildInitialAgents(): API.Agent[] {
  // Keep in sync with mockData._getAgentsRaw() — the method stays for parity
  // but the store is what mutations operate on.
  return _PERSONA_AGENTS();
}

// Single source of truth for the 6 Nordlicht-Systeme (SSOT: docs/DEMO_PERSONA.md).
// `_buildInitialAgents` (store) and `_getAgentsRaw` (parity) both return this so
// they never drift. Per-agent `todayDecisions` ≈ traffic-share × heutigem Tag
// (~1.203, leicht über Schnitt = wachsend). Pricing = degraded (1 System darf
// kränkeln), Churn = idle + riskClass null (unklassifiziert, im Mai 2026 neu).
function _PERSONA_AGENTS(): API.Agent[] {
  const TODAY = 1203; // heutiges Gesamtvolumen (≈ Schnitt 1.167 + Wachstum)
  return [
    {
      id: "agent_fraud", name: "Fraud Detector",
      description: "Betrugserkennung bei Schadenmeldungen (Schaden-Scoring).",
      modelMetadata: { provider: "Eigenbetrieb (Frankfurt)", model: "GBM-Gradient-Boosting v3", purpose: "Schaden-Betrugserkennung", outputType: "Risiko-Score + Entscheidung", riskClass: "high-risk", version: "v3.1.0" },
      apiKeyPrefix: "6c9af4d3", status: 'healthy', isActive: true,
      createdAt: "2025-09-18T00:00:00Z", lastActive: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: true, logPayloads: true, piiMasking: true, autoBlockAnomalies: true, confidenceThreshold: 90, maxRequestsPerMinute: 1200, timeoutMs: 3000 },
      metrics: { totalDecisions: MASTER_AGENTS[0].traffic, todayDecisions: Math.round(TODAY * 0.263), avgConfidence: 94.2, blockRate: MASTER_AGENTS[0].blockRate, reviewRate: 5.4, successRate: 98.2, avgLatency: 92 },
    },
    {
      id: "agent_uw_leben", name: "Underwriting Assistent Leben",
      description: "Automatisierte Risikoprüfung für Lebensversicherungs-Anträge.",
      modelMetadata: { provider: "Eigenbetrieb (Frankfurt)", model: "Tabular-Klassifikator v2", purpose: "Risikoprüfung Leben", outputType: "Annahme-Empfehlung", riskClass: "high-risk", version: "v2.2.0" },
      apiKeyPrefix: "a17c5b91", status: 'healthy', isActive: true,
      createdAt: "2025-09-25T00:00:00Z", lastActive: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: true, logPayloads: true, piiMasking: true, autoBlockAnomalies: false, confidenceThreshold: 88, maxRequestsPerMinute: 800, timeoutMs: 4000 },
      metrics: { totalDecisions: MASTER_AGENTS[1].traffic, todayDecisions: Math.round(TODAY * 0.240), avgConfidence: 92.8, blockRate: MASTER_AGENTS[1].blockRate, reviewRate: 6.8, successRate: 97.6, avgLatency: 168 },
    },
    {
      id: "agent_pricing", name: "Pricing Engine Komposit",
      description: "Tarifierung für Komposit-Sparten (Wohngebäude, Haftpflicht, KFZ).",
      modelMetadata: { provider: "Eigenbetrieb (Frankfurt)", model: "GLM-Tarifmodell v2", purpose: "Tarifierung Komposit", outputType: "Prämien-Vorschlag", riskClass: "high-risk", version: "v2.0.0" },
      apiKeyPrefix: "b820e4f7", status: 'degraded', isActive: true,
      createdAt: "2025-11-12T00:00:00Z", lastActive: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: true, logPayloads: true, piiMasking: true, autoBlockAnomalies: true, confidenceThreshold: 85, maxRequestsPerMinute: 700, timeoutMs: 3500 },
      metrics: { totalDecisions: MASTER_AGENTS[3].traffic, todayDecisions: Math.round(TODAY * 0.154), avgConfidence: 86.4, blockRate: MASTER_AGENTS[3].blockRate, reviewRate: 9.8, successRate: 95.8, avgLatency: 214 },
    },
    {
      id: "agent_doc", name: "Dokument-Klassifizierer",
      description: "Klassifiziert eingehende Dokumente im Posteingang.",
      modelMetadata: { provider: "Eigenbetrieb (Frankfurt)", model: "Text-/Layout-Klassifikator v1", purpose: "Posteingangs-Triage", outputType: "Dokument-Kategorie", riskClass: "minimal", version: "v1.3.0" },
      apiKeyPrefix: "c93a7d20", status: 'healthy', isActive: true,
      createdAt: "2026-01-14T00:00:00Z", lastActive: new Date(Date.now() - 1 * 60 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: false, logPayloads: true, piiMasking: true, autoBlockAnomalies: false, confidenceThreshold: 75, maxRequestsPerMinute: 1500, timeoutMs: 5000 },
      metrics: { totalDecisions: MASTER_AGENTS[2].traffic, todayDecisions: Math.round(TODAY * 0.194), avgConfidence: 95.6, blockRate: MASTER_AGENTS[2].blockRate, reviewRate: 3.2, successRate: 99.0, avgLatency: 121 },
    },
    {
      id: "agent_chat", name: "Kunden-Chat-Assistent",
      description: "Beantwortet Service-FAQ im Kunden-Self-Service.",
      modelMetadata: { provider: "Azure OpenAI (EU)", model: "GPT-4o-mini", purpose: "Service-FAQ", outputType: "Text-Antwort", riskClass: "limited", version: "v1.1.0" },
      apiKeyPrefix: "e44d1f08", status: 'healthy', isActive: true,
      createdAt: "2026-03-08T00:00:00Z", lastActive: new Date(Date.now() - 30 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: false, logPayloads: false, piiMasking: true, autoBlockAnomalies: false, confidenceThreshold: 70, maxRequestsPerMinute: 1000, timeoutMs: 6000 },
      metrics: { totalDecisions: MASTER_AGENTS[4].traffic, todayDecisions: Math.round(TODAY * 0.131), avgConfidence: 91.3, blockRate: MASTER_AGENTS[4].blockRate, reviewRate: 4.1, successRate: 98.6, avgLatency: 142 },
    },
    {
      // Der Neue (Mai 2026) — noch unklassifiziert + ohne Verkehr (idle).
      id: "agent_churn", name: "Churn-Prädiktor",
      description: "Bestandsanalyse zur Kündiger-Früherkennung — Pilotbetrieb.",
      modelMetadata: { provider: "Eigenbetrieb (Frankfurt)", model: "Logistische Regression v0.9", purpose: "Bestands-/Churn-Analyse", outputType: "Kündigungs-Wahrscheinlichkeit", riskClass: null, version: "v0.9.0" },
      apiKeyPrefix: "f1057ab2", status: 'idle', isActive: true,
      createdAt: "2026-05-20T00:00:00Z", lastActive: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      organizationId: "org-mock-001", workspaceId: "ws-mock-001",
      policies: { strictMode: false, logPayloads: true, piiMasking: true, autoBlockAnomalies: false, confidenceThreshold: 75, maxRequestsPerMinute: 200, timeoutMs: 5000 },
      metrics: { totalDecisions: 0, todayDecisions: 0, avgConfidence: 0, blockRate: 0, reviewRate: 0, successRate: 0, avgLatency: 0 },
    },
  ];
}

function _buildAgentAnalytics(agent: API.Agent): API.AgentAnalyticsResponse {
  const hasTraffic = (agent.metrics?.totalDecisions ?? 0) > 0;
  if (!hasTraffic) {
    return { confidenceTrend: [], outcomeDistribution: [], topBlockReasons: [], recentActivity: [] };
  }

  const rand = _seededRandom(_hashAgentId(agent.id));
  const meanAvg = agent.metrics!.avgConfidence;
  const blockRate = agent.metrics!.blockRate / 100;
  const reviewRate = agent.metrics!.reviewRate / 100;

  // 7-day confidence trend
  const confidenceTrend: API.AgentConfidenceTrendPoint[] = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(Date.now() - i * 24 * 3600 * 1000);
    const iso = date.toISOString().slice(0, 10);
    const noise = (rand() - 0.5) * 3.0; // ±1.5pp
    const avg = Math.max(60, Math.min(99, meanAvg + noise));
    const p50 = Math.max(55, Math.min(avg - 2.5 - rand() * 1.5, 99));
    const p95 = Math.max(avg, Math.min(99, avg + 3.0 + rand() * 2.0));
    confidenceTrend.push({
      date: iso,
      avg: +avg.toFixed(1),
      p50: +p50.toFixed(1),
      p95: +p95.toFixed(1),
    });
  }

  // 24-hour outcome distribution with business-hours curve
  const todayTotal = agent.metrics!.todayDecisions;
  const hourWeights: number[] = [];
  for (let h = 0; h < 24; h++) {
    // Gaussian-ish curve centered at 13:00, σ≈4
    const dist = Math.abs(h - 13);
    const weight = Math.exp(-(dist * dist) / 32) + 0.05;
    hourWeights.push(weight);
  }
  const weightSum = hourWeights.reduce((a, b) => a + b, 0);
  const outcomeDistribution: API.AgentOutcomePoint[] = hourWeights.map((w, h) => {
    const hourTotal = Math.round((w / weightSum) * todayTotal);
    const blocked = Math.round(hourTotal * blockRate);
    const reviewed = Math.round(hourTotal * reviewRate);
    const approved = Math.max(0, hourTotal - blocked - reviewed);
    return {
      hour: `${h.toString().padStart(2, '0')}:00`,
      approved,
      reviewed,
      blocked,
    };
  });

  // Top block reasons — scale weights so total count ≈ 30-day blocked volume
  const blockTemplate = _AGENT_BLOCK_REASONS[agent.id] ?? [
    { reason: "Low confidence threshold", weight: 50 },
    { reason: "Policy violation", weight: 30 },
    { reason: "Rate limit exceeded", weight: 20 },
  ];
  const totalBlockedApprox = Math.round(agent.metrics!.totalDecisions * (blockRate) * 4.3); // 7d → ~30d
  const weightTotal = blockTemplate.reduce((a, b) => a + b.weight, 0);
  const topBlockReasons: API.AgentBlockReason[] = blockTemplate.map(t => ({
    reason: t.reason,
    count: Math.max(1, Math.round((t.weight / weightTotal) * totalBlockedApprox)),
    percentage: +((t.weight / weightTotal) * 100).toFixed(1),
  }));

  // Recent activity — 5 entries over last ~2 hours
  const activityTemplate = _AGENT_ACTIVITY_TEMPLATES[agent.id] ?? [];
  const recentActivity: API.AgentRecentActivity[] = activityTemplate.map((a, i) => {
    const minutesAgo = Math.round(3 + i * 17 + rand() * 9);
    const outcomeConfidence = a.outcome === 'APPROVED'
      ? Math.min(99, meanAvg + 2 + rand() * 3)
      : a.outcome === 'REVIEWED'
      ? Math.max(65, meanAvg - 8 + rand() * 6)
      : Math.max(40, meanAvg - 18 + rand() * 8);
    return {
      // Echte Trace-ID → Deeplink ins Entscheidungsprotokoll löst auf.
      id: a.traceId,
      timestamp: new Date(Date.now() - minutesAgo * 60 * 1000).toISOString(),
      action: a.action,
      confidence: Math.round(outcomeConfidence),
      outcome: a.outcome,
    };
  });

  return { confidenceTrend, outcomeDistribution, topBlockReasons, recentActivity };
}

// 7-day breakdown (sums to 8420)
export const mockData = {
  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async getCurrentUser(): Promise<API.User> {
    await delay(300);
    return {
      id: 'usr_1234567890',
      email: 'admin@acmecorp.com',
      name: 'Jane Smith',
      role: 'admin',
      organizationId: 'org_acme',
      workspaceId: 'ws_main',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jane',
      emailVerified: true,
      onboardingCompleted: true,
      hasFirstTrace: true,
      twoFactorEnabled: false,
      createdAt: '2025-01-15T10:00:00Z',
    };
  },

  async login(_credentials: API.LoginRequest): Promise<API.LoginResponse> {
    await delay(800);
    return {
      token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
      user: await this.getCurrentUser(),
    };
  },

  async signup(data: { name: string; email: string; password: string; company: string }): Promise<API.LoginResponse> {
    await delay(1000);
    return {
      token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
      user: {
        id: 'usr_new',
        email: data.email,
        name: data.name,
        role: 'owner',
        organizationId: 'org_new',
        workspaceId: 'ws_new',
        emailVerified: false,
        onboardingCompleted: false,
        hasFirstTrace: false,
        twoFactorEnabled: false,
        createdAt: new Date().toISOString(),
      },
    };
  },

  async logout(): Promise<void> {
    await delay(300);
  },

  async forgotPassword(_email: string): Promise<{ success: boolean }> {
    await delay(800);
    return { success: true };
  },

  async resetPassword(_data: { token: string; password: string }): Promise<{ success: boolean }> {
    await delay(800);
    return { success: true };
  },

  async verify2FA(data: { tempToken: string; code: string }): Promise<API.LoginResponse> {
    await delay(800);
    if (!data.tempToken) {
      throw new Error('Missing tempToken');
    }
    if (!/^\d{6}$/.test(data.code) && !/^ADJD-[A-F0-9]{8}$/i.test(data.code)) {
      throw new Error('Invalid 2FA code');
    }
    return {
      token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
      user: await this.getCurrentUser(),
    };
  },

  async resend2FA(): Promise<{ success: boolean }> {
    await delay(500);
    return { success: true };
  },

  async verifyEmail(_data: { token: string }): Promise<{ success: boolean }> {
    await delay(800);
    return { success: true };
  },

  async resendVerificationEmail(): Promise<{ success: boolean }> {
    await delay(500);
    return { success: true };
  },

  // completeOnboarding mock removed 2026-05-02 — workspace-onboarding
  // wizard deleted. Per-agent onboarding mocks live near
  // getOnboardingDraft below.

  // ============================================================================
  // ENTERPRISE SECURITY
  // ============================================================================

  async getLoginActivity(_params?: { timeRange?: string }): Promise<API.LoginActivity[]> {
    await delay(300);
    
    const now = Date.now();
    return [
      {
        id: 'la_1',
        sessionJti: 'jti_mock_la1',
        timestamp: now - 5 * 60 * 1000,
        success: true,
        deviceType: 'desktop',
        deviceName: 'Chrome on Windows',
        browser: 'Chrome 120',
        os: 'Windows 11',
        ipAddress: '192.168.1.100',
        location: 'Munich',
        country: 'Germany',
        isCurrent: true,
        suspicious: false,
        sessionActive: true,
      },
      {
        id: 'la_2',
        sessionJti: 'jti_mock_la2',
        timestamp: now - 2 * 24 * 60 * 60 * 1000,
        success: true,
        deviceType: 'mobile',
        deviceName: 'Safari on iPhone',
        browser: 'Safari 17',
        os: 'iOS 17.2',
        ipAddress: '192.168.1.101',
        location: 'Munich',
        country: 'Germany',
        isCurrent: false,
        suspicious: false,
        sessionActive: true,
      },
      {
        id: 'la_3',
        sessionJti: null,
        timestamp: now - 3 * 24 * 60 * 60 * 1000,
        success: false,
        deviceType: 'desktop',
        deviceName: 'Firefox on Mac',
        browser: 'Firefox 121',
        os: 'macOS 14',
        ipAddress: '45.123.45.67',
        location: 'Unknown',
        country: 'Russia',
        isCurrent: false,
        suspicious: true,
        sessionActive: false,
      },
      {
        id: 'la_4',
        sessionJti: null,
        timestamp: now - 5 * 24 * 60 * 60 * 1000,
        success: true,
        deviceType: 'desktop',
        deviceName: 'Edge on Windows',
        browser: 'Edge 120',
        os: 'Windows 11',
        ipAddress: '192.168.1.100',
        location: 'Munich',
        country: 'Germany',
        isCurrent: false,
        suspicious: false,
        sessionActive: false,
      },
    ];
  },

  async getSecuritySettings(): Promise<API.SecuritySettings> {
    await delay(300);
    
    const now = Date.now();
    return {
      twoFactorEnabled: false,
      lastTwoFactorVerification: null,
      backupCodesRemaining: 0,
      trustedDevices: [
        {
          id: 'dev_1',
          name: 'Chrome on Windows',
          deviceType: 'desktop',
          browser: 'Chrome 120',
          os: 'Windows 11',
          lastUsed: now - 5 * 60 * 1000,
          addedAt: now - 30 * 24 * 60 * 60 * 1000,
          isCurrent: true,
        },
        {
          id: 'dev_2',
          name: 'Safari on iPhone',
          deviceType: 'mobile',
          browser: 'Safari 17',
          os: 'iOS 17.2',
          lastUsed: now - 2 * 24 * 60 * 60 * 1000,
          addedAt: now - 45 * 24 * 60 * 60 * 1000,
          isCurrent: false,
        },
      ],
    };
  },

  async generateBackupCodes(): Promise<API.BackupCodesResponse> {
    await delay(500);
    return {
      codes: [
        'ADJD-1234-5678',
        'ADJD-2345-6789',
        'ADJD-3456-7890',
        'ADJD-4567-8901',
        'ADJD-5678-9012',
        'ADJD-6789-0123',
        'ADJD-7890-1234',
        'ADJD-8901-2345',
        'ADJD-9012-3456',
        'ADJD-0123-4567',
      ],
    };
  },

  async getConsentHistory(): Promise<API.ConsentHistoryEntry[]> {
    await delay(300);
    
    const now = Date.now();
    return [
      {
        timestamp: now - 30 * 24 * 60 * 60 * 1000,
        version: '1.0',
        action: 'Accepted',
        preferences: {
          essential: true,
          functional: true,
          analytics: true,
          marketing: false,
          productUpdates: true,
        },
      },
      {
        timestamp: now - 60 * 24 * 60 * 60 * 1000,
        version: '1.0',
        action: 'Initial Accept',
        preferences: {
          essential: true,
          functional: true,
          analytics: false,
          marketing: false,
          productUpdates: false,
        },
      },
    ];
  },

  async verifyDomain(domain: string): Promise<API.DomainVerificationResponse> {
    await delay(800);
    
    // Simulate SSO enabled for certain domains
    if (domain.includes('company.com') || domain.includes('enterprise.com')) {
      return {
        ssoEnabled: true,
        providers: [
          {
            id: 'okta',
            name: 'Okta',
            logo: 'https://logo.clearbit.com/okta.com',
            description: 'Login with Okta',
            configured: true,
          },
          {
            id: 'azure',
            name: 'Microsoft Azure AD',
            logo: 'https://logo.clearbit.com/microsoft.com',
            description: 'Login with Azure Active Directory',
            configured: true,
          },
        ],
      };
    }
    
    return { ssoEnabled: false };
  },

  // ============================================================================
  // PASSKEYS / WEBAUTHN
  // ============================================================================

  async getPasskeyRegistrationOptions(): Promise<API.PasskeyRegistrationOptions> {
    await delay(300);
    return {
      challenge: btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))),
      rp: {
        name: 'ADJUDON',
        id: window.location.hostname,
      },
      user: {
        id: 'usr_1234567890',
        name: 'admin@acmecorp.com',
        displayName: 'Jane Smith',
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' },
        { alg: -257, type: 'public-key' },
      ],
      timeout: 60000,
      attestation: 'none',
    };
  },

  async getPasskeyAuthenticationOptions(): Promise<API.PasskeyAuthenticationOptions> {
    await delay(300);
    return {
      challenge: btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))),
      rpId: window.location.hostname,
      allowCredentials: [
        { id: 'cred_1', type: 'public-key' },
      ],
      timeout: 60000,
    };
  },

  async listPasskeys(): Promise<API.PasskeyCredential[]> {
    await delay(300);
    
    const now = Date.now();
    return [
      {
        id: 'cred_1',
        name: 'iPhone 15 Pro',
        createdAt: now - 30 * 24 * 60 * 60 * 1000,
        lastUsed: now - 2 * 24 * 60 * 60 * 1000,
        aaguid: '00000000-0000-0000-0000-000000000000',
      },
      {
        id: 'cred_2',
        name: 'MacBook Pro TouchID',
        createdAt: now - 60 * 24 * 60 * 60 * 1000,
        lastUsed: now - 5 * 60 * 1000,
        aaguid: '00000000-0000-0000-0000-000000000000',
      },
    ];
  },

  // ============================================================================
  // PASSWORD HISTORY
  // ============================================================================

  async checkPasswordHistory(password: string): Promise<{ isReused: boolean }> {
    await delay(300);
    // Demo-mode heuristic: only the most common passwords trip the reuse check.
    // Real backend hashes the candidate and compares against the stored history
    // (current + last-N) using bcrypt.compare.
    const commonPasswords = ['password', 'password123', '12345678', 'qwerty12345'];
    return { isReused: commonPasswords.includes(password.toLowerCase()) };
  },

  // ============================================================================
  // ANALYTICS
  // ============================================================================
  
  async getAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<API.AnalyticsResponse> {
    await delay(300);
    // Derive numDays from the requested window so the demo-mode sparkline
    // matches real-backend behavior (7d default, 30d when StatsBar asks for
    // it, up to 90d clamp). Without this the mock always returned 7 points
    // regardless of the caller's range, producing a visible density
    // mismatch against ReviewQueue's 30-day snapshot sparkline.
    const MAX_DAYS = 90;
    let numDays = 7;
    if (params?.dateFrom) {
      const from = new Date(params.dateFrom);
      const to = params.dateTo ? new Date(params.dateTo) : new Date();
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        const spanMs = Math.max(0, to.getTime() - from.getTime());
        numDays = Math.min(MAX_DAYS, Math.max(1, Math.ceil(spanMs / (24 * 60 * 60 * 1000)) + 1));
      }
    }
    // Deterministic wave for total/blocked — anchored to the MASTER base so
    // a 7-day slice still renders the familiar demo shape, but longer
    // windows fall back to a smooth sine + weekly dip. Keeps the demo
    // page-to-page visually coherent across every range.
    const decisionVolume = [];
    const dailyCounts = [];
    for (let i = 0; i < numDays; i++) {
      const day = new Date();
      day.setDate(day.getDate() - (numDays - 1 - i));
      const dow = day.getUTCDay();
      const weekendFactor = dow === 0 || dow === 6 ? 0.65 : 1;
      const base = 1300 + Math.round(Math.sin(i * 0.45) * 220 + Math.cos(i * 0.8) * 120);
      const total = Math.max(300, Math.round(base * weekendFactor));
      const blocked = Math.max(0, Math.round(total * 0.016 + Math.sin(i * 0.9) * 6));
      const avgConfidence = 88 + Math.round(Math.sin(i * 1.3) * 4 + (i % 2) * 2);
      const reviews = Math.max(1, Math.round(blocked * 0.8 + (i % 3) * 2));
      const iso = day.toISOString().slice(0, 10);
      const label = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      decisionVolume.push({
        // Match backend contract: ISO date + separate human label (see
        // analyticsRoutes.js and types/api.ts — Overview /analyse Runde 2 #1).
        date: iso,
        label,
        total,
        blocked,
        avgConfidence,
        reviews,
      });
      dailyCounts.push({ date: label, traces: total });
    }
    return {
      overview: {
        totalDecisions: MASTER_STATS.totalDecisions,
        blockedActions: MASTER_STATS.blocked,
        averageConfidence: MASTER_STATS.avgConfidence,
        reviewQueueSize: MASTER_STATS.inReview,
        uptime: 99.8,
      },
      confidenceDistribution: {
        high: MASTER_STATS.high,
        medium: MASTER_STATS.medium,
        low: MASTER_STATS.low,
        windowHours: 24,
        previousWindow: {
          high: Math.round(MASTER_STATS.high * 0.93),
          medium: Math.round(MASTER_STATS.medium * 1.08),
          low: Math.round(MASTER_STATS.low * 1.12),
        },
      },
      decisionVolume,
      dailyCounts,
      activeAgents: [
        ...MASTER_AGENTS.map((a, idx) => {
          // Spiegelt die Metriken aus _PERSONA_AGENTS(); Reihenfolge = MASTER_AGENTS
          // (fraud · uw_leben · doc · pricing · chat). Pricing ergibt 'warning'
          // (→ degraded), die übrigen 'healthy'.
          const metricsMap = [
            { avgConfidence: 94.2, successRate: 98.2, uptime: 99.7 }, // fraud
            { avgConfidence: 92.8, successRate: 97.6, uptime: 99.5 }, // uw_leben
            { avgConfidence: 95.6, successRate: 99.0, uptime: 99.8 }, // doc
            { avgConfidence: 86.4, successRate: 95.8, uptime: 98.4 }, // pricing → degraded
            { avgConfidence: 91.3, successRate: 98.6, uptime: 99.6 }, // chat
          ];
          return {
            id: a.id,
            name: a.name,
            status: calculateHealthStatus({
              blockRate: a.blockRate,
              ...metricsMap[idx]
            }) as API.AnalyticsResponse['activeAgents'][number]['status'],
            traffic: a.traffic,
            blockRate: `${a.blockRate}%`,
          };
        }),
        // Churn-Prädiktor — registriert, aber noch ohne Verkehr (idle, neu Mai 2026).
        {
          id: "agent_churn",
          name: "Churn-Prädiktor",
          status: 'idle' as const,
          traffic: 0,
          blockRate: '0%',
        },
      ],
      // Fleet-wide count mirrors the activeAgents array length because the
      // mock never exceeds the 50-cap. Real payloads from the backend may
      // differ (fleet of 300 → activeAgents capped at 50, totalActiveAgents = 300).
      totalActiveAgents: MASTER_AGENTS.length + 1,
      recentDecisions: (() => {
        const now = Date.now();
        const min = 60_000;
        // `status: "success"` mirrors the backend pipeline (DecisionTrace.status
        // = 'success' for the unmarked happy path, see analyticsRoutes.js
        // aggregation). Previously this mock emitted `"approved"` which the
        // ENFORCEMENT_BADGES map happened to resolve identically (badge-
        // confidence) but exposed a Demo↔Prod label divergence: demo users
        // saw "approved" in the Live Queue while production users saw
        // "success". One vocabulary, both modes.
        return [
          { timestamp: new Date(now - 1 * min).toISOString(),  agentId: "Dokument-Klassifizierer",      status: "success",   metadata: { reason: "Hohe Konfidenz", executionTimeMs: 8 } },
          { timestamp: new Date(now - 3 * min).toISOString(),  agentId: "Fraud Detector",               status: "blocked",   metadata: { reason: "Policy: Beleg-Inkonsistenz", executionTimeMs: 12 } },
          { timestamp: new Date(now - 6 * min).toISOString(),  agentId: "Underwriting Assistent Leben", status: "success",   metadata: { reason: "Innerhalb Annahme-Rahmen", executionTimeMs: 6 } },
          { timestamp: new Date(now - 9 * min).toISOString(),  agentId: "Pricing Engine Komposit",      status: "flagged",   metadata: { reason: "Tarif-Drift über Toleranz", executionTimeMs: 14 } },
          { timestamp: new Date(now - 13 * min).toISOString(), agentId: "Kunden-Chat-Assistent",        status: "success",   metadata: { reason: "Anfrage beantwortet", executionTimeMs: 9 } },
          { timestamp: new Date(now - 18 * min).toISOString(), agentId: "Fraud Detector",               status: "blocked",   metadata: { reason: "Policy: Schaden-Häufung", executionTimeMs: 11 } },
          { timestamp: new Date(now - 22 * min).toISOString(), agentId: "Dokument-Klassifizierer",      status: "success",   metadata: { reason: "Hohe Konfidenz", executionTimeMs: 7 } },
          { timestamp: new Date(now - 27 * min).toISOString(), agentId: "Underwriting Assistent Leben", status: "escalated", metadata: { reason: "Menschliche Prüfung nötig", executionTimeMs: 15 } },
          { timestamp: new Date(now - 33 * min).toISOString(), agentId: "Pricing Engine Komposit",      status: "success",   metadata: { reason: "Tarifierung freigegeben", executionTimeMs: 5 } },
          { timestamp: new Date(now - 41 * min).toISOString(), agentId: "Kunden-Chat-Assistent",        status: "flagged",   metadata: { reason: "Verbindliche Auskunft angefragt", executionTimeMs: 13 } },
        ];
      })(),
    };
  },

  async getAnalyticsComparison(period: '7d' | '30d' | '90d' = '30d') {
    await delay(200);
    // Backend /comparison liefert PERIODEN-Zähler: `current` = Entscheidungen im
    // gewählten Fenster (analyticsRoutes.js:653). MASTER_STATS sind auf ein
    // 30-Tage-Fenster kalibriert (decisionVolume ~1300/Tag). Count-Metriken daher
    // proportional zum Fenster skalieren, damit die Analytics-KPI ihrem Volumen-
    // Chart über alle Zeiträume entspricht. Raten (avgConfidence) und Änderungs-%
    // sind fenster-unabhängig. Bei 30d ist der Faktor 1 → Overview (StatsBar /
    // DecisionPulseCard nutzen '30d') bleibt bit-identisch.
    const days = parseInt(period, 10) || 30;
    const f = days / 30;
    const scale = (n: number) => Math.round(n * f);
    return {
      period,
      totalDecisions:  { current: scale(MASTER_STATS.totalDecisions), previous: scale(Math.round(MASTER_STATS.totalDecisions * 0.87)), change: 15.0 },
      avgConfidence:   { current: MASTER_STATS.avgConfidence,         previous: MASTER_STATS.avgConfidence - 2.3,                       change: 2.6  },
      blockedActions:  { current: scale(MASTER_STATS.blocked),        previous: scale(Math.round(MASTER_STATS.blocked * 1.12)),         change: -10.7 },
      humanReviews:    { current: scale(MASTER_STATS.inReview),       previous: scale(Math.round(MASTER_STATS.inReview * 0.9)),         change: 11.1 },
    };
  },

  // Demo dashboard does not surface compliance features (the demo persona is
  // a sandbox-tier prospect). Returning `null` mirrors what a real backend
  // would return for an org without unlocked compliance features and lets
  // the Overview render exactly the runtime-only layout demo viewers expect.
  async getCompliancePosture(): Promise<API.CompliancePosture | null> {
    await delay(50);
    // Demo-Persona „Nordlicht Versicherung AG" (~9 Monate Nutzung, SSOT: docs/
    // DEMO_PERSONA.md): weitgehend abgedeckt, aber NICHT perfekt. Offene Punkte:
    // 2 aktive Vorfälle (#6/#7) mit GENAU 1 überfälligen Frist (#7 gdpr-Abschluss),
    // 1 offene Deployer-Bestätigung, 1 unquittierter Kalibrierungs-Drift (Pricing),
    // 1 vorgeschlagene Fairness-Remediation (zu Vorfall #5), 1 fehlende Model-Card
    // (Churn-Prädiktor). overall 'warning', kein Rot. Alerts realistisch dosiert (2,
    // NICHT 7): die überfällige Vorfalls-Frist + die offene Deployer-Bestätigung.
    const now = Date.now();
    const iso = (msFromNow: number) => new Date(now + msFromNow).toISOString();
    return {
      overall: 'warning',
      plan: 'governance',
      generatedAt: new Date(now).toISOString(),
      sections: {
        hashChain: {
          enabled: true,
          status: 'healthy',
          metric: 178400,
          label: 'Audit-Kette',
          detail: 'Verankert',
          href: '/dashboard/audit-chain',
          lastSequence: 178400,
          lastEntryAt: iso(-2 * 60 * 1000),
          integrity: 'fresh',
        },
        incidents: {
          enabled: true,
          status: 'warning',
          metric: 2,
          label: 'Vorfälle',
          detail: '2 aktiv · 1 Frist überfällig',
          href: '/dashboard/incidents',
          nextDeadlineAt: iso(-1 * 24 * 60 * 60 * 1000),
          nextDeadlineRegulator: 'gdpr-art-33',
          nextDeadlineIncidentId: 'inc_007',
          criticalClocks: 1,
        },
        fria: {
          enabled: true,
          status: 'healthy',
          metric: 2,
          label: 'FRIA',
          detail: '2 freigegeben · 1 in Prüfung',
          href: '/dashboard/fria',
          incompleteCount: 1,
          approvedCount: 2,
        },
        provenance: {
          enabled: true,
          status: 'healthy',
          metric: 100,
          metricUnit: '%',
          label: 'Provenance',
          detail: 'C2PA aktiv',
          href: '/dashboard/provenance',
          revokedLast30d: 0,
        },
        deployerProfile: {
          enabled: true,
          status: 'warning',
          metric: 92,
          metricUnit: '%',
          label: 'Deployer-Pack',
          detail: '1 Bestätigung ausstehend',
          href: '/dashboard/deployer',
          pendingAcknowledgements: 1,
          completeness: 92,
        },
        privacyBudget: {
          enabled: true,
          status: 'healthy',
          metric: 78,
          metricUnit: '%',
          label: 'Privacy-Budget',
          detail: 'ε 0,78 / 1,00 übrig',
          href: '/dashboard/privacy-budget',
          epsilonRemaining: 0.22,
          epsilonCap: 1.0,
          budgetStatus: 'active',
        },
        dsar: {
          enabled: true,
          status: 'healthy',
          metric: 3,
          label: 'Subject requests',
          detail: '3 offen · 1 bald fällig · 0 überfällig',
          href: '/dashboard/gdpr',
          overdueCount: 0,
          dueSoonCount: 1,
          openCount: 3,
        },
        art22: {
          enabled: true,
          status: 'healthy',
          metric: 3,
          label: 'Automated decisions',
          detail: '3 registered · safeguarded',
          href: '/dashboard/gdpr',
          missingSafeguards: 0,
          unassessed: 0,
        },
        fairness: {
          enabled: true,
          status: 'warning',
          metric: 1,
          label: 'Fairness remediation',
          detail: '1 Remediation vorgeschlagen',
          href: '/dashboard/fairness-bias',
          openCount: 1,
          proposedCount: 1,
        },
        calibration: {
          enabled: true,
          status: 'warning',
          metric: 1,
          label: 'Calibration drift',
          detail: '1 drift alert unacknowledged',
          href: '/dashboard/calibration-confidence',
          unacknowledged: 1,
        },
        ropa: {
          enabled: true,
          status: 'healthy',
          metric: 7,
          label: 'Records of processing',
          detail: 'v7 · current',
          href: '/dashboard/gdpr',
          currentVersion: 7,
          stale: false,
        },
        modelCard: {
          enabled: true,
          status: 'warning',
          metric: 5,
          label: 'Model cards',
          detail: '5 von 6 Systemen dokumentiert',
          href: '/dashboard/eu-ai-act',
          documented: 5,
          total: 6,
          gap: 1,
        },
      },
      // Realistisch dosiert (2 Alerts, NICHT 7): die EINE überfällige Vorfalls-Frist
      // (#7 gdpr-Abschlussbericht) + die offene Deployer-Bestätigung. Der zweite
      // aktive Vorfall (#6) hat eine Frist > 0 → erzeugt hier keinen kritischen Alert.
      alerts: [
        { id: 'al-incident-007', severity: 'critical', kind: 'incident_clock', message: 'Abschlussbericht überfällig — Bestandsdaten-Export (BfDI)', href: '/dashboard/incidents/inc_007' },
        { id: 'al-deployer', severity: 'warning', kind: 'deployer_unacknowledged', message: '1 Provider-Anweisung zu bestätigen', href: '/dashboard/deployer' },
      ],
    };
  },

  // Demo readiness grid: the demo org runs traces + incidents + advisories,
  // so those frameworks read `active`; the rest are `not_configured` (no
  // fabricated "compliant" state — matches the honest backend derivation).
  async getFrameworkCoverage(): Promise<API.FrameworkCoverage> {
    await delay(50);
    const frameworks: API.FrameworkCoverageItem[] = [
      { key: 'eu-ai-act', name: 'EU AI Act', status: 'active' },
      { key: 'gdpr', name: 'GDPR', status: 'active' },
      { key: 'dora', name: 'DORA', status: 'active' },
      { key: 'solvency-ii', name: 'Solvency II', status: 'not_configured' },
      { key: 'vag-mago', name: 'VAG / MaGo', status: 'active' },
      { key: 'beratung', name: 'VVG / Beratung', status: 'active' },
      { key: 'eiopa', name: 'EIOPA AI', status: 'active' },
      { key: 'aml-gwg', name: 'AML / GwG', status: 'not_configured' },
      { key: 'data-act-fida', name: 'Data Act / FiDA', status: 'active' },
    ];
    return {
      frameworks,
      summary: { total: frameworks.length, active: frameworks.filter((f) => f.status === 'active').length },
    };
  },

  // ============================================================================
  // CPI
  // ============================================================================

  async getCPI(): Promise<API.CPIResponse> {
    await delay(300);
    // Risk distribution drives totalTraces + totalDecisions — keep them
    // consistent so demo-mode viewers don't spot arithmetic that doesn't add
    // up. LOW+MEDIUM+HIGH+CRITICAL must equal totalTraces; percentages must
    // sum to 100 across the four buckets. Order CRITICAL→LOW (highest-risk
    // first) to match backend cpiController.js RISK_ORDER — otherwise demo
    // shows a different sort order than production.
    // Counts→percentages: 27/1405=1.92, 55/1405=3.91, 303/1405=21.57,
    // 1020/1405=72.60 → rounded 2/4/22/72 = 100 ✓ (previous 74/22/4/2
    // summed to 102 due to a transcription bug + reverse order).
    const riskDistribution = [
      { level: "CRITICAL", count: 27,   percentage: 2  },
      { level: "HIGH",     count: 55,   percentage: 4  },
      { level: "MEDIUM",   count: 303,  percentage: 22 },
      { level: "LOW",      count: 1020, percentage: 72 },
    ];
    const totalTraces = riskDistribution.reduce((s, r) => s + r.count, 0);
    return {
      score: 96,
      totalTraces,
      underSampled: false,
      trendMetric: 'avgConfidence',
      dimensions: {
        transparency: 98,
        humanOversight: 95,
        dataGovernance: 97,
        accuracyRobustness: 94,
        technicalDocumentation: 96,
        riskManagement: 96,
      },
      articles: [
        { articleNumber: "Art. 9",  title: "Risk Management",        coverage: 91, status: "compliant" },
        { articleNumber: "Art. 10", title: "Data Governance",         coverage: 97, status: "compliant" },
        { articleNumber: "Art. 11", title: "Technical Documentation", coverage: 72, status: "partial"   },
        { articleNumber: "Art. 13", title: "Transparency",            coverage: 98, status: "compliant" },
        { articleNumber: "Art. 14", title: "Human Oversight",         coverage: 95, status: "compliant" },
        { articleNumber: "Art. 15", title: "Accuracy & Robustness",   coverage: 94, status: "compliant" },
      ],
      trend: Array.from({ length: 15 }).map((_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (28 - i * 2));
        return {
          date: d.toISOString().slice(0, 10),
          score: Math.round(78 + (18 * i) / 14),
        };
      }),
      riskDistribution,
      recentImprovements: [
        { action: "Menschliche Aufsicht für Hochrisiko-Entscheidungen aktiviert", impact: "+Menschliche Aufsicht", date: "vor 2 Tagen" },
        { action: "Transparenz-Regel für Art. 13 aktualisiert", impact: "+Datengovernance", date: "vor 4 Tagen" },
        { action: "Durchschnittliche Antwortlatenz um 12 ms reduziert", impact: "+Compliance-Abdeckung", date: "vor 6 Tagen" },
        { action: "Audit-Trail-Vollständigkeit auf 99,97 % erhöht", impact: "+Compliance-Abdeckung", date: "vor 8 Tagen" },
      ],
    };
  },

  // ============================================================================
  // AGENTS
  // ============================================================================

  async getAgents(): Promise<API.Agent[]> {
    await delay(300);
    // Return a deep copy so external consumers can't mutate the store directly.
    return _getAgentStore().map(a => ({ ...a, policies: a.policies ? { ...a.policies } : undefined, metrics: a.metrics ? { ...a.metrics } : undefined }));
  },

  async getAgent(id: string): Promise<API.Agent> {
    await delay(200);
    const agent = _getAgentStore().find(a => a.id === id);
    if (!agent) throw new Error(`Agent ${id} not found`);
    return { ...agent, policies: agent.policies ? { ...agent.policies } : undefined, metrics: agent.metrics ? { ...agent.metrics } : undefined };
  },

  async createAgent(input: API.AgentCreateRequest): Promise<API.AgentCreateResponse> {
    await delay(500);
    const id = `agent-demo-${Math.random().toString(36).slice(2, 8)}`;
    const rawKey = `adj_agent_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;
    const prefix = rawKey.slice(-8);
    // Match the backend createAgent semantics for the onboarding flag:
    // when set, the agent is born paused at Step 2 (Pick Integration).
    // Without this the AgentOnboardingPage would derive currentStep
    // from a missing onboardingProgress and stay stuck on Step 1.
    const onboardingProgress: API.AgentOnboardingProgress = input.onboarding
      ? {
          currentStep: 2,
          selectedMethodId: null,
          completedAt: null,
          firstTraceReceived: false,
          firstTraceReceivedAt: null,
          firstTracePresetId: null,
        }
      : {
          currentStep: 3,
          selectedMethodId: null,
          completedAt: new Date().toISOString(),
          firstTraceReceived: false,
          firstTraceReceivedAt: null,
          firstTracePresetId: null,
        };
    const agent: API.Agent = {
      id,
      name: input.name,
      description: input.description,
      apiKeyPrefix: prefix,
      status: 'idle',
      isActive: true,
      createdAt: new Date().toISOString(),
      lastActive: undefined,
      organizationId: 'org-mock-001',
      workspaceId: 'ws-mock-001',
      policies: { ...DEFAULT_POLICIES },
      metrics: { totalDecisions: 0, todayDecisions: 0, avgConfidence: 0, blockRate: 0, reviewRate: 0, successRate: 0, avgLatency: 0 },
      onboardingProgress,
    };
    _getAgentStore().push(agent);
    return { agent, apiKey: rawKey };
  },

  async updateAgent(id: string, updates: Partial<API.Agent>): Promise<API.Agent> {
    await delay(200);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    store[idx] = { ...store[idx], ...updates };
    return { ...store[idx] };
  },

  async deleteAgent(id: string): Promise<void> {
    await delay(200);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    store.splice(idx, 1);
  },

  // Store-backed (Demo-Session): die Klassifizierung muss den Refetch der
  // Agents-Liste überleben — ein synthetisches Merge-Objekt ohne Store-Write
  // verschwände nach invalidateQueries(['agents']) sofort wieder.
  async updateAgentModelMetadata(id: string, patch: Partial<API.AgentModelMetadata>): Promise<API.Agent> {
    await delay(250);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store[idx] = { ...store[idx], modelMetadata: { ...(store[idx].modelMetadata ?? {}), ...patch } };
    return { ...store[idx] };
  },

  async updateAgentPolicies(id: string, policies: API.AgentPolicies): Promise<API.Agent> {
    await delay(200);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    store[idx] = { ...store[idx], policies: { ...policies } };
    return { ...store[idx] };
  },

  async regenerateAgentKey(id: string): Promise<{ apiKey: string; apiKeyPrefix: string }> {
    await delay(400);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    const rawKey = `adj_agent_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;
    const prefix = rawKey.slice(-8);
    store[idx] = { ...store[idx], apiKeyPrefix: prefix };
    return { apiKey: rawKey, apiKeyPrefix: prefix };
  },

  // ── Agent Onboarding (AgentOnboardingPage) — mock implementations ────
  // Mirrors the backend behaviour: read-only draft lookup (never
  // auto-creates), Step-1-create via the regular createAgent flow, PATCH
  // progress, DELETE to discard, POST complete.
  async getOnboardingDraft(): Promise<API.AgentOnboardingDraftResponse> {
    await delay(150);
    const store = _getAgentStore();
    const existing = store.find(a => a.onboardingProgress && a.onboardingProgress.completedAt === null);
    return { agent: existing ? { ...existing } : null };
  },

  async discardOnboardingDraft(id: string): Promise<API.DiscardOnboardingDraftResponse> {
    await delay(150);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx >= 0 && !store[idx].onboardingProgress?.completedAt) {
      store.splice(idx, 1);
    }
    return { discarded: true };
  },

  async updateOnboardingProgress(id: string, payload: API.UpdateOnboardingProgressRequest): Promise<API.Agent> {
    await delay(150);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    const prev = store[idx].onboardingProgress ?? {
      currentStep: 1, selectedMethodId: null, completedAt: null, firstTraceReceived: false, firstTraceReceivedAt: null,
    };
    const next: API.AgentOnboardingProgress = {
      ...prev,
      currentStep: payload.currentStep ?? prev.currentStep,
      selectedMethodId: payload.selectedMethodId !== undefined ? payload.selectedMethodId : prev.selectedMethodId,
      firstTraceReceived: payload.firstTraceReceived ? true : prev.firstTraceReceived,
      firstTraceReceivedAt: payload.firstTraceReceived ? new Date().toISOString() : prev.firstTraceReceivedAt,
      // Persist the verify-path marker so the mock store mirrors the
      // backend semantics — undefined = no change, string|null = set.
      firstTracePresetId:
        payload.firstTracePresetId !== undefined
          ? payload.firstTracePresetId
          : prev.firstTracePresetId ?? null,
    };
    const patchedAgent: API.Agent = { ...store[idx], onboardingProgress: next };
    if (typeof payload.name === 'string') {
      const trimmed = payload.name.trim();
      if (trimmed.length > 0) patchedAgent.name = trimmed;
    }
    // Match backend semantics: a non-empty trimmed string sets the
    // description; null OR an all-whitespace string clears it. The
    // Agent type has `description?: string` so undefined and missing
    // are equivalent when serialised.
    if (typeof payload.description === 'string') {
      const trimmed = payload.description.trim();
      patchedAgent.description = trimmed.length > 0 ? trimmed : undefined;
    } else if (payload.description === null) {
      patchedAgent.description = undefined;
    }
    store[idx] = patchedAgent;
    return { ...patchedAgent };
  },

  async completeAgentOnboarding(id: string): Promise<API.Agent> {
    await delay(200);
    const store = _getAgentStore();
    const idx = store.findIndex(a => a.id === id);
    if (idx < 0) throw new Error(`Agent ${id} not found`);
    const prev = store[idx].onboardingProgress ?? {
      currentStep: 1, selectedMethodId: null, completedAt: null, firstTraceReceived: false, firstTraceReceivedAt: null,
    };
    store[idx] = {
      ...store[idx],
      onboardingProgress: { ...prev, currentStep: 3, completedAt: new Date().toISOString() },
    };
    return { ...store[idx] };
  },

  async getAgentAnalytics(id: string): Promise<API.AgentAnalyticsResponse> {
    await delay(300);
    const agent = _getAgentStore().find(a => a.id === id);
    if (!agent) {
      return { confidenceTrend: [], outcomeDistribution: [], topBlockReasons: [], recentActivity: [] };
    }
    return _buildAgentAnalytics(agent);
  },

  async getAgentVersions(id: string): Promise<string[]> {
    await delay(150);
    const agent = _getAgentStore().find(a => a.id === id);
    if (!agent || (agent.metrics?.totalDecisions ?? 0) === 0) return [];
    return _AGENT_VERSIONS[id] ?? ['v1.0.0'];
  },

  // Vertrag SPIEGELT das Backend (GET /api/analytics/agent-compare):
  // { agentId, v1:{version,count,avgConfidence,blocked,flagged,blockRate}, v2:{…} }.
  // avgConfidence als 0–100-Prozent (Persona-Skala) — die Detail-Seite
  // normalisiert defensiv (≤1 → ×100), weil das echte Backend 0–1 liefert.
  async compareAgentVersions(agentId: string, v1: string, v2: string): Promise<{ agentId: string; v1: any; v2: any }> {
    await delay(400);
    const agent = _getAgentStore().find(a => a.id === agentId);
    const base = agent?.metrics ?? { totalDecisions: 0, todayDecisions: 0, avgConfidence: 0, blockRate: 0, reviewRate: 0, successRate: 0, avgLatency: 0 };
    // ältere Version (v1) = mehr Traffic-Historie, etwas schwächer kalibriert.
    const c1 = Math.round(base.totalDecisions * 0.42);
    const c2 = Math.round(base.totalDecisions * 0.58);
    const br1 = +(base.blockRate + 0.4).toFixed(2);
    const br2 = +base.blockRate.toFixed(2);
    return {
      agentId,
      v1: {
        version: v1,
        count: c1,
        avgConfidence: Math.max(0, +(base.avgConfidence - 1.8).toFixed(2)),
        blocked: Math.round((br1 / 100) * c1),
        flagged: Math.round((base.reviewRate / 100) * c1),
        blockRate: br1,
      },
      v2: {
        version: v2,
        count: c2,
        avgConfidence: +base.avgConfidence.toFixed(2),
        blocked: Math.round((br2 / 100) * c2),
        flagged: Math.round((base.reviewRate / 100) * c2),
        blockRate: br2,
      },
    };
  },

  // ML-BOM (CycloneDX 1.7) — aus den modelMetadata des Persona-Systems
  // gebaut, damit die Demo eine echte Stückliste je System zeigt (Art. 11 /
  // Annex IV §1+§2). idle-System ohne Verkehr → noch keine BOM.
  async getAgentMlBom(id: string): Promise<API.AgentMlBomResult> {
    await delay(250);
    const agent = _getAgentStore().find(a => a.id === id);
    if (!agent) throw this._vvgErr('NOT_FOUND');
    if ((agent.metrics?.totalDecisions ?? 0) === 0) {
      return { bom: null, digest: '', urn: '' };
    }
    const m = agent.modelMetadata ?? {};
    const isLlm = (m.provider ?? '').toLowerCase().includes('openai') || (m.model ?? '').toLowerCase().includes('gpt');
    const bom: API.AgentMlBom = {
      bomFormat: 'CycloneDX',
      specVersion: '1.7',
      serialNumber: `urn:uuid:${_hashAgentId(id).toString(16).padStart(8, '0')}-mlbom`,
      version: 1,
      metadata: { timestamp: new Date(Date.now() - 36 * 3600 * 1000).toISOString(), component: { type: 'machine-learning-model', name: m.model ?? agent.name } },
      components: [
        {
          type: 'machine-learning-model',
          'bom-ref': `${id}-model`,
          name: m.model ?? agent.name,
          version: m.version ?? 'v1.0.0',
          publisher: m.provider ?? 'Eigenbetrieb (Frankfurt)',
          purl: `pkg:adjudon/${id}@${(m.version ?? 'v1.0.0').replace(/^v/, '')}`,
          licenses: [{ license: { id: isLlm ? 'LicenseRef-Azure-OpenAI-EU' : 'LicenseRef-proprietary-internal' } }],
          modelCard: {
            modelParameters: {
              approach: { type: isLlm ? 'supervised' : 'supervised' },
              datasets: isLlm
                ? [] // gekaufte LLM-Modelle: keine eigene Trainingsdaten-Zusammenfassung im Haus
                : [
                    { type: 'training', name: 'historische Vorgänge (anonymisiert)', contents: { url: 'internal://datalake/frankfurt', properties: [{ name: 'rows', value: '2,4 Mio' }, { name: 'zeitraum', value: '2019–2025' }, { name: 'residenz', value: 'Frankfurt (eu-central-1)' }] } },
                  ],
            },
            considerations: {
              users: ['Fachbereich', 'Compliance'],
              useCases: [m.purpose ?? 'Entscheidungsunterstützung'],
              ethicalConsiderations: (m.riskClass ?? '').includes('high')
                ? [{ name: 'Diskriminierungsrisiko', description: 'Schutzmerkmale ausgeschlossen; Bias-Monitoring aktiv (Art. 15(4)).' }]
                : [],
            },
          },
        },
      ],
    };
    return { bom, digest: `sha256:${_hashAgentId(id + 'bom').toString(16).padStart(16, '0')}${'a3f9c2d1e7b04c69'}`, urn: bom.serialNumber ?? '' };
  },

  async refreshAgentMlBom(id: string): Promise<{ digest: string; urn: string }> {
    await delay(350);
    const res = await this.getAgentMlBom(id);
    return { digest: res.digest, urn: res.urn };
  },

  async _getAgentsRaw(): Promise<API.Agent[]> {
    // Parity copy — delegiert auf die SSOT der 6 Nordlicht-Systeme, damit
    // _getAgentsRaw() und der mutierbare Store nie auseinanderlaufen.
    return _PERSONA_AGENTS();
  },

  // ============================================================================
  // TRACES
  // ============================================================================

  async getTraces(params?: API.ListParams): Promise<API.TracesListResponse> {
    await delay(300);
    const traceSeeds: MockTraceSeed[] = [
        {
          id: "trace_abc123",
          agentId: "agent_fraud",
          prompt: "Schaden-Scoring: KFZ-Kasko-Meldung VS-2026-104883 (Glasbruch).",
          completion: "Freigegeben — Schaden-Score 0,12, plausibel. Reguliert ohne Prüf-Hinweis.",
          confidenceScore: 94,
          status: "approved",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 38 },
            { step: "Policy prüfen", status: "success", latencyMs: 21 },
            { step: "Entscheidung", status: "success", latencyMs: 33 },
          ],
          metadata: { vorgang: "VS-2026-104883", sparte: "KFZ-Kasko", schadenScore: 0.12 },
          createdAt: "2026-06-12T10:30:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_def456",
          agentId: "agent_doc",
          prompt: "Posteingang klassifizieren: Dok DC-2026-882140 (gescanntes Schreiben).",
          completion: "Kategorie 'Schadenmeldung' — Vorgang VS-2026-104883 zugeordnet.",
          confidenceScore: 96,
          status: "approved",
          executionSteps: [
            { step: "OCR", status: "success", latencyMs: 142 },
            { step: "Klassifikation", status: "success", latencyMs: 56 },
            { step: "Zuordnung", status: "success", latencyMs: 28 },
          ],
          metadata: { dokument: "DC-2026-882140", kategorie: "Schadenmeldung" },
          createdAt: "2026-06-12T10:15:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_ghi789",
          agentId: "agent_pricing",
          prompt: "Tarifierung: Angebot PR-2026-44055 (Wohngebäude), Eingangswert außerhalb Bereich.",
          completion: "BLOCKIERT — Wohnfläche außerhalb plausiblem Wertebereich. Manuelle Prüfung.",
          confidenceScore: 62,
          status: "blocked",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 18 },
            { step: "Wertebereich prüfen", status: "failure", latencyMs: 31 },
            { step: "Blockieren", status: "success", latencyMs: 11 },
          ],
          metadata: { angebot: "PR-2026-44055", sparte: "Wohngebäude" },
          createdAt: ago(5),
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_jkl012",
          agentId: "agent_uw_leben",
          prompt: "Risikoprüfung: Antrag AN-2026-77120 (Risikoleben, Standard-Tarif).",
          completion: "Freigegeben — keine erhöhten Risikomerkmale, Annahme empfohlen.",
          confidenceScore: 95,
          status: "approved",
          executionSteps: [
            { step: "Antragsdaten prüfen", status: "success", latencyMs: 138 },
            { step: "Risikobewertung", status: "success", latencyMs: 84 },
            { step: "Empfehlung", status: "success", latencyMs: 41 },
          ],
          metadata: { antrag: "AN-2026-77120", produkt: "Risikoleben" },
          createdAt: "2026-06-12T09:30:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_mno345",
          agentId: "agent_chat",
          prompt: "Service-FAQ: Sitzung CH-2026-90014 — 'Wie ändere ich meinen Beitrags-Turnus?'",
          completion: "Auskunft erteilt — Schritt-für-Schritt-Anleitung im Kundenportal verlinkt.",
          confidenceScore: 92,
          status: "approved",
          executionSteps: [
            { step: "Intent erkennen", status: "success", latencyMs: 22 },
            { step: "Wissensbasis abfragen", status: "success", latencyMs: 48 },
            { step: "Antwort erzeugen", status: "success", latencyMs: 64 },
          ],
          metadata: { sitzung: "CH-2026-90014", thema: "Beitrags-Turnus" },
          createdAt: "2026-06-12T09:00:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_pqr678",
          agentId: "agent_doc",
          prompt: "Posteingang klassifizieren: Dok DC-2026-882256 (Vertragsänderung).",
          completion: "Kategorie 'Vertragsänderung' — an Bestandsführung weitergeleitet.",
          confidenceScore: 98,
          status: "approved",
          executionSteps: [
            { step: "OCR", status: "success", latencyMs: 156 },
            { step: "Klassifikation", status: "success", latencyMs: 62 },
            { step: "Weiterleitung", status: "success", latencyMs: 24 },
          ],
          metadata: { dokument: "DC-2026-882256", kategorie: "Vertragsänderung" },
          createdAt: "2026-06-12T08:45:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_stu901",
          agentId: "agent_uw_leben",
          prompt: "Risikoprüfung: Antrag AN-2026-77302 (Kapitallebensversicherung).",
          completion: "Freigegeben — Annahme im Standard-Rahmen, keine Rückfrage nötig.",
          confidenceScore: 91,
          status: "approved",
          executionSteps: [
            { step: "Antragsdaten prüfen", status: "success", latencyMs: 121 },
            { step: "Risikobewertung", status: "success", latencyMs: 77 },
            { step: "Empfehlung", status: "success", latencyMs: 39 },
          ],
          metadata: { antrag: "AN-2026-77302", produkt: "Kapitalleben" },
          createdAt: "2026-06-12T08:30:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_vwx234",
          agentId: "agent_pricing",
          prompt: "Tarifierung: Angebot PR-2026-44088 (Haftpflicht) — Tarif-Drift erkannt.",
          completion: "MARKIERT — Prämie weicht über Toleranz vom Vergleichswert ab. Zur Prüfung.",
          confidenceScore: 68,
          status: "flagged",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 26 },
            { step: "Drift-Abgleich", status: "success", latencyMs: 92 },
            { step: "Zur Prüfung", status: "success", latencyMs: 12 },
          ],
          metadata: { angebot: "PR-2026-44088", sparte: "Haftpflicht", driftPct: 14.2 },
          createdAt: ago(2),
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_yza567",
          agentId: "agent_fraud",
          prompt: "Schaden-Scoring: Hausrat-Meldung VS-2026-104991 (Leitungswasser).",
          completion: "Freigegeben — Schaden-Score 0,18, im Normalbereich. Reguliert.",
          confidenceScore: 93,
          status: "approved",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 35 },
            { step: "Policy prüfen", status: "success", latencyMs: 22 },
            { step: "Entscheidung", status: "success", latencyMs: 31 },
          ],
          metadata: { vorgang: "VS-2026-104991", sparte: "Hausrat", schadenScore: 0.18 },
          createdAt: "2026-06-12T08:00:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_bcd890",
          agentId: "agent_doc",
          prompt: "Posteingang klassifizieren: Dok DC-2026-882309 (Kündigungsschreiben).",
          completion: "Kategorie 'Kündigung' — an Bestandsmanagement weitergeleitet.",
          confidenceScore: 90,
          status: "approved",
          executionSteps: [
            { step: "OCR", status: "success", latencyMs: 148 },
            { step: "Klassifikation", status: "success", latencyMs: 71 },
            { step: "Weiterleitung", status: "success", latencyMs: 26 },
          ],
          metadata: { dokument: "DC-2026-882309", kategorie: "Kündigung" },
          createdAt: "2026-06-12T07:45:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_efg123",
          agentId: "agent_chat",
          prompt: "Service-FAQ: Sitzung CH-2026-90120 — 'Wo finde ich meine Beitragsbescheinigung?'",
          completion: "Auskunft erteilt — Dokument im Kundenportal verfügbar, Pfad genannt.",
          confidenceScore: 97,
          status: "approved",
          executionSteps: [
            { step: "Intent erkennen", status: "success", latencyMs: 20 },
            { step: "Wissensbasis abfragen", status: "success", latencyMs: 33 },
            { step: "Antwort erzeugen", status: "success", latencyMs: 58 },
          ],
          metadata: { sitzung: "CH-2026-90120", thema: "Beitragsbescheinigung" },
          createdAt: "2026-06-12T07:30:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_hij456",
          agentId: "agent_fraud",
          prompt: "Schaden-Scoring: KFZ-Haftpflicht VS-2026-105344 (Auffahrunfall).",
          completion: "Freigegeben — Werkstatt geprüft, Beleg konsistent. Reguliert.",
          confidenceScore: 93,
          status: "approved",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 34 },
            { step: "Werkstatt-Abgleich", status: "success", latencyMs: 67 },
            { step: "Entscheidung", status: "success", latencyMs: 29 },
          ],
          metadata: { vorgang: "VS-2026-105344", sparte: "KFZ-Haftpflicht", schadenScore: 0.21 },
          createdAt: "2026-06-12T07:15:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_klm789",
          agentId: "agent_fraud",
          prompt: "Schaden-Scoring: Mehrfachmeldung VS-2026-105210 — Häufung erkannt.",
          completion: "MARKIERT — gleicher Schaden in kurzer Frist mehrfach gemeldet. Zur Prüfung.",
          confidenceScore: 75,
          status: "flagged",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 41 },
            { step: "Häufungs-Analyse", status: "success", latencyMs: 88 },
            { step: "Fall anlegen", status: "success", latencyMs: 33 },
          ],
          metadata: { vorgang: "VS-2026-105210", sparte: "Hausrat", schadenScore: 0.61 },
          createdAt: ago(3),
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_nop012",
          agentId: "agent_uw_leben",
          prompt: "Risikoprüfung: Antrag AN-2026-77410 (Nachversicherung Risikoleben).",
          completion: "Freigegeben — Nachversicherung innerhalb des vertraglichen Rahmens.",
          confidenceScore: 89,
          status: "approved",
          executionSteps: [
            { step: "Antragsdaten prüfen", status: "success", latencyMs: 124 },
            { step: "Rahmen prüfen", status: "success", latencyMs: 58 },
            { step: "Empfehlung", status: "success", latencyMs: 36 },
          ],
          metadata: { antrag: "AN-2026-77410", produkt: "Risikoleben-Nachversicherung" },
          createdAt: "2026-06-12T06:45:00Z",
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
        {
          id: "trace_qrs345",
          agentId: "agent_pricing",
          prompt: "Tarifierung: Angebot PR-2026-44021 (Wohngebäude, Neugeschäft).",
          completion: "Freigegeben — Prämie regelkonform, kein Drift-Hinweis.",
          confidenceScore: 90,
          status: "approved",
          executionSteps: [
            { step: "Eingang parsen", status: "success", latencyMs: 27 },
            { step: "Tarif berechnen", status: "success", latencyMs: 96 },
            { step: "Drift-Abgleich", status: "success", latencyMs: 52 },
          ],
          metadata: { angebot: "PR-2026-44021", sparte: "Wohngebäude" },
          createdAt: ago(4),
          workspaceId: "ws_main",
          organizationId: "org_acme",
        },
    ];
    // agentName auflösen (Demo zeigt „Fraud Detector", nicht „agent_fraud").
    const nameById: Record<string, string> = {};
    MASTER_AGENTS.forEach((a) => { nameById[a.id] = a.name; });
    nameById['agent_churn'] = 'Churn-Prädiktor';
    const all = traceSeeds
      .map(fillTraceFields)
      .map((t) => ({ ...t, agentName: nameById[t.agentId] ?? t.agentId }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Filter honorieren — wie das Backend (search über prompt/traceId/agentId/
    // Name, status exakt, agentId exakt). Ohne Filter: großer Backdrop-Total
    // (~35.000) + hasMore (Real-Use-Gefühl). Mit Filter: ehrliche Treffer-Zahl.
    const q = (params?.search ?? '').trim().toLowerCase();
    const statusF = params?.status?.trim();
    const agentF = params?.agentId?.trim();
    const filterActive = !!q || !!statusF || !!agentF;
    let rows = all;
    if (q) {
      rows = rows.filter((t) =>
        [t.prompt, t.completion, t.traceId, t.agentId, t.agentName ?? '']
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    if (statusF) rows = rows.filter((t) => t.status === statusF);
    if (agentF) rows = rows.filter((t) => t.agentId === agentF);

    const limit = 20;
    const page = Math.max(1, params?.page ?? 1);
    const total = filterActive ? rows.length : MASTER_STATS.totalDecisions;
    const pages = Math.max(1, Math.ceil(total / limit));
    const pageRows = rows.slice((page - 1) * limit, (page - 1) * limit + limit);
    return {
      traces: pageRows,
      pagination: {
        page,
        limit,
        total,
        pages,
        hasMore: filterActive ? page < pages : true,
      },
    };
  },

  // ============================================================================
  // REVIEW QUEUE
  // ============================================================================

  // Session-Store: Entscheidungen/Eskalationen muessen den Refetch ueberleben,
  // sonst tauchen entschiedene Items im Demo wieder auf (etabliertes _xSeed-Muster).
  _reviewQueueStore: null as API.ReviewItem[] | null,
  _reviewQueueSeed(): API.ReviewItem[] {
    if (this._reviewQueueStore) return this._reviewQueueStore;
    // Demo-Persona „Nordlicht Versicherung AG": GENAU 8 offene Items (SSOT:
    // docs/DEMO_PERSONA.md). Mix aus markierten (flagged) + eskalierten Items
    // (Eskalation via escalatedAt/workflowStep — `status` bleibt 'pending').
    // Alter 1 h–3 Tage, Konfidenz 52–78, deutsche Entscheidungs-Kontexte,
    // keine PII (synthetische VS-/AN-/PR-Nummern). Keine Frist überfällig.
    const items: Omit<API.ReviewItem, 'status'>[] = [
      {
        id: "review_1",
        traceId: "trace_klm789",
        agentId: "agent_fraud",
        action: "Schaden-Auszahlung freigeben — Mehrfachmeldung VS-2026-105210",
        confidence: 61,
        priority: "high",
        context: {
          reason: "Schaden-Score über Schwelle (0,61) — Häufung gleicher Schaden",
          riskLevel: "HIGH",
          detectedIssue: "Gleicher Schaden in kurzer Frist mehrfach gemeldet",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht bei Hochrisiko-Entscheidungen"
        },
        payload: {
          vorgang: "VS-2026-105210",
          sparte: "Hausrat",
          schadenScore: 0.61,
          auszahlungEur: 2480,
          empfehlung: "MENSCHLICHE_PRUEFUNG"
        },
        createdAt: ago(2),
        slaDeadline: fromNow(22),
      },
      {
        id: "review_2",
        traceId: "trace_vwx234",
        agentId: "agent_pricing",
        action: "Tarif-Einstufung prüfen — Drift bei Angebot PR-2026-44088",
        confidence: 68,
        priority: "high",
        context: {
          reason: "Prämie weicht über Toleranz vom Vergleichswert ab (14,2 %)",
          riskLevel: "MEDIUM",
          detectedIssue: "Tarif-Drift über Toleranz — Pricing Engine beeinträchtigt",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht"
        },
        payload: {
          angebot: "PR-2026-44088",
          sparte: "Haftpflicht",
          driftPct: 14.2,
          empfehlung: "MANUELLE_TARIFIERUNG"
        },
        createdAt: ago(6),
        slaDeadline: fromNow(18),
      },
      {
        id: "review_3",
        traceId: "trace_ghi789",
        agentId: "agent_uw_leben",
        action: "Annahme-Empfehlung prüfen — Antrag AN-2026-77198 (Risikoleben)",
        confidence: 57,
        priority: "critical",
        context: {
          reason: "Risikoklasse über Annahme-Schwelle — Gesundheitsangaben prüfbedürftig",
          riskLevel: "HIGH",
          detectedIssue: "Konfidenz deutlich unter Schwellwert (57 % < 88 %)",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht + DSGVO Art. 9 (Gesundheitsdaten)"
        },
        payload: {
          antrag: "AN-2026-77198",
          produkt: "Risikoleben",
          versicherungssumme: 250000,
          empfehlung: "RISIKOPRUEFER_VORLAGE"
        },
        createdAt: ago(28),
        slaDeadline: fromNow(8),
        // Eskaliert an den Underwriting-Leiter (4-Augen, Art. 14).
        workflowStep: 2,
        escalatedAt: ago(20),
        escalationReason: "Hochrisiko-Antrag über Annahme-Schwelle — Zweitfreigabe nötig",
      },
      {
        id: "review_4",
        traceId: "trace_pqr678",
        agentId: "agent_fraud",
        action: "Schaden-Auszahlung freigeben — Beleg-Inkonsistenz VS-2026-105077",
        confidence: 64,
        priority: "high",
        context: {
          reason: "Beleg-Inkonsistenz (Datum/Betrag) — Schaden-Score 0,58",
          riskLevel: "HIGH",
          detectedIssue: "Rechnungsdatum vor Schadensdatum",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht"
        },
        payload: {
          vorgang: "VS-2026-105077",
          sparte: "KFZ-Kasko",
          schadenScore: 0.58,
          auszahlungEur: 1890,
          empfehlung: "BELEG_NACHFORDERN"
        },
        createdAt: ago(34),
        slaDeadline: fromNow(14),
      },
      {
        id: "review_5",
        traceId: "trace_def456",
        agentId: "agent_doc",
        action: "Dokument-Zuordnung bestätigen — Posteingang DC-2026-882201",
        confidence: 72,
        priority: "medium",
        context: {
          reason: "Dokumenttyp nicht eindeutig — zwischen 'Beschwerde' und 'Kündigung'",
          riskLevel: "LOW",
          detectedIssue: "Klassifikation knapp unter Auto-Schwelle",
          euAiActArticle: "Art. 15 — Genauigkeit"
        },
        payload: {
          dokument: "DC-2026-882201",
          kandidaten: ["Beschwerde", "Kündigung"],
          empfehlung: "MANUELLE_ZUORDNUNG"
        },
        createdAt: ago(10),
        slaDeadline: fromNow(30),
      },
      {
        id: "review_6",
        traceId: "trace_efg123",
        agentId: "agent_chat",
        action: "Chat-Antwort prüfen — verbindliche Auskunft angefragt (CH-2026-90087)",
        confidence: 55,
        priority: "medium",
        context: {
          reason: "Kunde fragt verbindliche Deckungs-Zusage — außerhalb Wissensbasis",
          riskLevel: "MEDIUM",
          detectedIssue: "Verbindliche Auskunft darf nicht automatisiert erteilt werden",
          euAiActArticle: "Art. 50 — Transparenz (KI-Interaktion)"
        },
        payload: {
          sitzung: "CH-2026-90087",
          thema: "Deckungs-Zusage Elementarschaden",
          empfehlung: "AN_FACHBERATER"
        },
        createdAt: ago(14),
        slaDeadline: fromNow(34),
      },
      {
        id: "review_7",
        traceId: "trace_qrs345",
        agentId: "agent_pricing",
        action: "Rabatt-Stufe prüfen — Angebot PR-2026-44190 nicht regelkonform",
        confidence: 66,
        priority: "high",
        context: {
          reason: "Rabatt-Stufe außerhalb regelkonformem Rahmen",
          riskLevel: "MEDIUM",
          detectedIssue: "Sondernachlass ohne hinterlegte Begründung",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht"
        },
        payload: {
          angebot: "PR-2026-44190",
          sparte: "Wohngebäude",
          rabattPct: 22,
          empfehlung: "FREIGABE_VERTRIEBSLEITUNG"
        },
        createdAt: ago(48),
        slaDeadline: fromNow(12),
        // Eskaliert an die Vertriebsleitung wegen Sondernachlass.
        workflowStep: 2,
        escalatedAt: ago(40),
        escalationReason: "Sondernachlass über Standard-Rahmen — Freigabe Vertriebsleitung",
      },
      {
        id: "review_8",
        traceId: "trace_jkl012",
        agentId: "agent_uw_leben",
        action: "Annahme-Empfehlung prüfen — Antrag AN-2026-77245 (Gesundheitsfragen)",
        confidence: 74,
        priority: "medium",
        context: {
          reason: "Gesundheitsangaben prüfbedürftig — Konfidenz unter Schwellwert",
          riskLevel: "MEDIUM",
          detectedIssue: "Vorerkrankung angegeben, Tarif-Zuschlag unklar",
          euAiActArticle: "Art. 14 — Menschliche Aufsicht + DSGVO Art. 9"
        },
        payload: {
          antrag: "AN-2026-77245",
          produkt: "Kapitalleben",
          empfehlung: "RISIKOPRUEFER_VORLAGE"
        },
        createdAt: ago(66),
        slaDeadline: fromNow(6),
      },
    ];
    // Mock items are all pending by default — matches what the backend returns
    // when filtered by ?status=pending. Mutations in mock-mode fabricate the new
    // status on the fly inside reviewDecision/bulkReviewDecision.
    this._reviewQueueStore = items.map(item => ({ ...item, status: 'pending' as const }));
    return this._reviewQueueStore;
  },

  async getReviewQueue(): Promise<API.ReviewItem[]> {
    await delay(300);
    // Nur offene Items — Entscheidungen entfernen sie aus der Queue (Store).
    return this._reviewQueueSeed().filter((i) => i.status === 'pending');
  },

  async reviewDecision(id: string, action: API.ReviewActionRequest): Promise<API.ReviewItem> {
    await delay(250);
    const item = this._reviewQueueSeed().find((i) => i.id === id);
    if (!item) throw this._vvgErr('NOT_FOUND');
    // Backend-Guard-Spiegel: bereits entschiedene Items nicht erneut entscheidbar.
    if (item.status !== 'pending') throw this._vvgErr('ALREADY_DECIDED');
    item.status = action.decision === 'approve' ? 'approved' : 'rejected';
    item.decision = action.decision;
    item.reason = action.reason;
    item.notes = action.notes;
    item.updatedAt = new Date().toISOString();
    return { ...item };
  },

  async bulkReviewDecision(ids: string[], decision: 'approve' | 'reject', reason?: string): Promise<{ processed: number; skipped: number; decision: string }> {
    await delay(350);
    let processed = 0;
    let skipped = 0;
    for (const id of ids) {
      const item = this._reviewQueueSeed().find((i) => i.id === id);
      if (!item || item.status !== 'pending') { skipped += 1; continue; }
      item.status = decision === 'approve' ? 'approved' : 'rejected';
      item.decision = decision;
      item.reason = reason;
      item.updatedAt = new Date().toISOString();
      processed += 1;
    }
    return { processed, skipped, decision };
  },

  async escalateReviewItem(id: string, reason: string): Promise<API.ReviewItem> {
    await delay(250);
    const item = this._reviewQueueSeed().find((i) => i.id === id);
    if (!item) throw this._vvgErr('NOT_FOUND');
    item.escalatedAt = new Date().toISOString();
    item.escalationReason = reason;
    item.workflowStep = (item.workflowStep ?? 1) + 1;
    return { ...item };
  },

  // ============================================================================
  // POLICIES
  // ============================================================================

  // Session-Store: Toggle/Anlegen/Löschen müssen den Refetch überleben
  // (etabliertes _xSeed-Muster wie Review-Queue). _buildPolicySeeds liefert die
  // SSOT der 6 Nordlicht-Regeln; der Store wird einmal lazy initialisiert.
  _policyStore: null as API.Policy[] | null,
  _policySeed(): API.Policy[] {
    if (!this._policyStore) this._policyStore = this._buildPolicySeeds();
    return this._policyStore;
  },
  _buildPolicySeeds(): API.Policy[] {
    // Nordlicht Versicherung AG — 6 Regeln, gewachsen über ~9 Monate Nutzung.
    // matchCount = Lebenszeit-Treffer seit createdAt; lastMatched relativ frisch.
    // Welt-Konsistenz: ~35.000 Entscheidungen/30 Tage, Blockrate gesamt ~1 %
    // (≈ 350 Blocks/Monat). Block-erzeugende Regeln (policy_1 + policy_4) summieren
    // sich plausibel zu dieser Größenordnung; Flag-Regeln treffen deutlich häufiger.
    // 1 Regel (policy_6) wurde ausprobiert und wieder deaktiviert — bewusst real.
    const recentISO = (hoursAgo: number) => new Date(Date.now() - hoursAgo * 3600 * 1000).toISOString();
    return [
      {
        id: "policy_1",
        name: "Niedrig-Konfidenz blockieren (< 70 %)",
        description: "Entscheidungen unter 70 % Konfidenz werden blockiert und an die Review-Queue übergeben (EU AI Act Art. 14).",
        enabled: true,
        priority: 1,
        conditions: [
          { field: "confidenceScore", operator: "less_than", value: 0.7, label: "Konfidenz < 70 %" },
        ],
        actions: [
          { type: "block", label: "BLOCKIEREN" },
          { type: "notify", config: { channel: "review_queue" }, label: "REVIEW-QUEUE" },
        ],
        matchCount: 2614,
        lastMatched: recentISO(3),
        createdAt: "2025-10-08T09:30:00Z",
        updatedAt: "2026-02-12T14:10:00Z",
      },
      {
        id: "policy_2",
        name: "Großschaden > 50.000 € in Review",
        description: "Schadenentscheidungen mit einer Schadensumme über 50.000 € werden zur fachlichen Prüfung markiert (MaRisk AT 4.3.1).",
        enabled: true,
        priority: 2,
        conditions: [
          { field: "inputContext.schadensumme", operator: "greater_than", value: 50000, label: "Schadensumme > 50.000 €" },
        ],
        actions: [
          { type: "flag_for_review", label: "ZUR PRÜFUNG" },
          { type: "notify", config: { channel: "schaden_review" }, label: "BENACHRICHTIGEN" },
        ],
        matchCount: 487,
        lastMatched: recentISO(11),
        createdAt: "2025-11-15T11:00:00Z",
        updatedAt: "2026-01-20T08:45:00Z",
      },
      {
        id: "policy_3",
        name: "Underwriting-Ablehnung immer in Review (Art. 22)",
        description: "Jede automatische Risiko-Ablehnung im Underwriting wird vor Kundenmitteilung von einem Menschen geprüft (DSGVO Art. 22).",
        enabled: true,
        priority: 3,
        conditions: [
          { field: "agentId", operator: "equals", value: "agent_uw_leben", logicalOperator: "AND", label: "System = Underwriting Leben" },
          { field: "outputDecision", operator: "equals", value: "rejected", label: "Entscheidung = abgelehnt" },
        ],
        actions: [
          { type: "flag_for_review", label: "ZUR PRÜFUNG" },
        ],
        matchCount: 1342,
        lastMatched: recentISO(6),
        createdAt: "2026-01-12T10:15:00Z",
        updatedAt: "2026-03-02T16:30:00Z",
      },
      {
        id: "policy_4",
        name: "PII-Anomalie blockieren",
        description: "Entscheidungen, deren Begründung ungescrubbte personenbezogene Daten enthält (E-Mail, IBAN-Fragmente), werden blockiert (DSGVO Art. 5(1)(c), Art. 32).",
        enabled: true,
        priority: 4,
        conditions: [
          { field: "inputContext.rationale", operator: "regex", value: "[\\w.+-]+@[\\w-]+\\.[\\w.-]+", label: "PII-Muster in Begründung" },
        ],
        actions: [
          { type: "block", config: { reason: "pii_leak_in_reasoning" }, label: "BLOCKIEREN" },
          { type: "notify", config: { channel: "datenschutz" }, label: "DATENSCHUTZ" },
        ],
        matchCount: 38,
        lastMatched: recentISO(52),
        createdAt: "2025-12-03T13:20:00Z",
        updatedAt: "2026-02-28T09:00:00Z",
      },
      {
        id: "policy_5",
        name: "Neue Systeme: alles in Review (Churn-Prädiktor)",
        description: "Solange ein neues KI-System nicht klassifiziert und freigegeben ist, geht jede Entscheidung in die Review-Queue. Aktuell aktiv für den Churn-Prädiktor.",
        enabled: true,
        priority: 5,
        conditions: [
          { field: "agentId", operator: "equals", value: "agent_churn", label: "System = Churn-Prädiktor" },
        ],
        actions: [
          { type: "flag_for_review", label: "ZUR PRÜFUNG" },
        ],
        matchCount: 0,
        lastMatched: undefined,
        createdAt: "2026-05-21T15:40:00Z",
        updatedAt: "2026-05-21T15:40:00Z",
      },
      {
        id: "policy_6",
        name: "Tarif-Drift automatisch eskalieren",
        description: "Versuch, Konfidenz-Einbrüche der Pricing Engine direkt zu blockieren. Zu viele Fehlalarme im Tagesgeschäft — deaktiviert, Drift läuft jetzt über die Kalibrierungs-Alarme.",
        enabled: false,
        priority: 6,
        conditions: [
          { field: "agentId", operator: "equals", value: "agent_pricing", logicalOperator: "AND", label: "System = Pricing Engine" },
          { field: "confidenceScore", operator: "less_than", value: 0.82, label: "Konfidenz < 82 %" },
        ],
        actions: [
          { type: "block", label: "BLOCKIEREN" },
        ],
        matchCount: 211,
        lastMatched: "2026-02-09T18:22:00Z",
        createdAt: "2026-02-04T10:00:00Z",
        updatedAt: "2026-02-10T11:15:00Z",
      },
    ];
  },

  async getPolicies(): Promise<API.Policy[]> {
    await delay(300);
    // Tiefe Kopie der mutierbaren Verschachtelungen (conditions/actions/config),
    // damit ein Consumer den Session-Store nicht versehentlich in-place verändert.
    return this._policySeed().map((p) => ({
      ...p,
      conditions: (p.conditions ?? []).map((c) => ({ ...c })),
      actions: (p.actions ?? []).map((a) => ({ ...a, config: a.config ? { ...a.config } : a.config })),
    }));
  },

  async updatePolicy(id: string, updates: Partial<API.Policy>): Promise<API.Policy> {
    await delay(220);
    const store = this._policySeed();
    const idx = store.findIndex((p) => p.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store[idx] = { ...store[idx], ...updates, updatedAt: new Date().toISOString() };
    return { ...store[idx] };
  },

  async deletePolicy(id: string): Promise<void> {
    await delay(220);
    const store = this._policySeed();
    const idx = store.findIndex((p) => p.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store.splice(idx, 1);
  },

  async createPolicy(policy: Omit<API.Policy, 'id' | 'createdAt' | 'matchCount'>): Promise<API.Policy> {
    await delay(260);
    const store = this._policySeed();
    const now = new Date().toISOString();
    const created: API.Policy = {
      ...policy,
      id: `policy_${_hashAgentId(policy.name + now).toString(16).slice(0, 8)}`,
      matchCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    store.push(created);
    return { ...created };
  },

  async createPolicyFromTemplate(templateId: string, name?: string): Promise<API.Policy> {
    await delay(260);
    const tpl = (await this.getPolicyTemplates()).find((t) => t.id === templateId);
    if (!tpl) throw this._vvgErr('NOT_FOUND');
    const store = this._policySeed();
    const now = new Date().toISOString();
    const created: API.Policy = {
      id: `policy_${_hashAgentId(templateId + now).toString(16).slice(0, 8)}`,
      name: name?.trim() || tpl.name,
      description: tpl.description ?? '',
      enabled: true,
      priority: store.length + 1,
      conditions: (tpl.conditions ?? []).map((c) => ({ ...c })),
      actions: (tpl.actions ?? []).map((a) => ({ ...a })),
      matchCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    store.unshift(created);
    return { ...created };
  },

  async getPolicyTemplates(): Promise<API.PolicyTemplate[]> {
    await delay(150);
    // NOTE: Several templates below reference fields that are NOT yet in the
    // backend canonical FIELD_TYPES registry (backend/services/policyCompiler.js:57):
    //   - agentType, reasoning, payload.amount, payload.country, payload.domain
    // Canonical FIELD_TYPES today: confidenceScore, score_calibrated,
    // scoreCalibrated, score_uncalibrated, scoreUncalibrated, status, agentId,
    // outputDecision, inputContext, humanOverride.
    // Templates with non-canonical fields display the FUTURE Policy Engine
    // surface — a customer copying these into the real backend will hit a
    // PolicyCompileError (cedarCompileErrors++) until FIELD_TYPES is extended
    // (planned Phase 2+ ADR). Demo-mode users see them; real-mode users
    // currently can only author with the 10-field canonical set. The 6
    // top-of-file mock policies (above) use ONLY canonical fields and are
    // safe to copy-paste into real backend.
    return [
      // ─── Compliance ────────────────────────────────────────────────
      {
        id: 'tpl_compliance_low_conf',
        name: 'Block low-confidence decisions',
        description: 'Block decisions below 70% confidence and route to the human review queue (EU AI Act Art. 14).',
        plainSummary: 'When the agent is less than 70% sure, a human decides instead.',
        example: 'An underwriting agent returns an approval with confidence 0.62 — the decision is blocked and a reviewer receives it in the queue.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight of high-risk AI systems' },
          { framework: 'GDPR', article: 'Art. 22', note: 'Safeguards against solely automated decisions' },
        ],
        severity: 'block',
        industries: ['financial_services', 'insurance', 'public_sector', 'general'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Typically 3–8% of decisions at 0.70 threshold on calibrated models.',
        fieldHelp: { confidenceScore: "The agent's self-reported certainty, 0–1. Requires the SDK to send it." },
        tunable: [
          'Raise threshold (e.g. 0.8) for regulated workflows, lower it (0.5) for internal tooling.',
          'Swap "block" for "flag_for_review" during pilot phase to observe without disruption.',
          'Add a notify action to page on-call compliance.',
        ],
        isSystem: true,
        category: 'compliance',
        conditions: [{ field: 'confidenceScore', operator: 'less_than', value: 0.7 }],
        actions: [{ type: 'block' }, { type: 'notify', config: { channel: 'review_queue' } }],
      },
      {
        id: 'tpl_compliance_gdpr22',
        name: 'GDPR Art. 22 — Explainable credit scoring',
        description: 'Flag automated credit or scoring decisions missing a reasoning field. Required for GDPR Art. 22 explainability.',
        plainSummary: "Credit-scoring decisions must carry a human-readable reason — otherwise they're queued.",
        example: 'A credit agent approves an application but returns an empty reasoning field — the decision is flagged for review before it reaches the applicant.',
        regulatoryRefs: [
          { framework: 'GDPR', article: 'Art. 22(3)', note: 'Human intervention + subject-contest safeguards for automated decisions' },
          { framework: 'EU AI Act', article: 'Art. 13', note: 'Transparency of AI systems' },
        ],
        severity: 'flag',
        industries: ['financial_services', 'insurance'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Usually <1% once agents are instrumented correctly — spikes indicate a bug.',
        fieldHelp: {
          agentType: 'The agent category declared on SDK init (e.g. "credit_scoring").',
          reasoning: 'A short human-readable justification the agent returns with each decision.',
        },
        tunable: [
          'Broaden agentType regex to cover "scoring", "underwriting", "pricing".',
          'Add a minimum reasoning length check by switching to a regex condition.',
          'Escalate to block once the explainability fix is deployed.',
        ],
        isSystem: true,
        category: 'compliance',
        conditions: [
          { field: 'agentType', operator: 'contains', value: 'credit', logicalOperator: 'AND' },
          { field: 'rationale', operator: 'equals', value: '' },
        ],
        actions: [{ type: 'flag_for_review' }, { type: 'notify', config: { channel: 'review_queue' } }],
      },
      {
        id: 'tpl_compliance_annex3',
        name: 'High-risk use case (Annex III)',
        description: 'Flag decisions from Annex III high-risk systems (recruitment, education, law enforcement, biometrics) for enhanced oversight.',
        plainSummary: 'Annex III domains always get a human in the loop — no exceptions.',
        example: 'A resume-screening agent rejects a candidate. The decision is routed to the recruitment reviewer before any rejection email is sent.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Annex III', note: 'List of high-risk AI use cases' },
          { framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight obligations' },
        ],
        severity: 'flag',
        industries: ['hr', 'public_sector', 'legal'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Depends on fleet mix — 100% of matching agents, 0% of others.',
        fieldHelp: { agentType: 'Declared agent domain. Must be set consistently by your SDK deployment.' },
        tunable: [
          'Narrow regex to only specific sub-domains (e.g. just "recruitment").',
          'Add domain-specific reviewers via the notify channel.',
          'Pair with a low-confidence block policy for layered defence.',
        ],
        isSystem: true,
        category: 'compliance',
        conditions: [{ field: 'agentType', operator: 'regex', value: '(recruitment|education|law_enforcement|biometric)' }],
        actions: [{ type: 'flag_for_review' }],
      },
      {
        id: 'tpl_compliance_audit_notify',
        name: 'Audit trail — notify on every blocked decision',
        description: 'Send a notification whenever a decision is blocked by any policy. Creates a tight audit feedback loop for compliance teams.',
        plainSummary: 'Every block creates an audit notification — compliance sees blocks in near real time.',
        example: 'A sanctions hit blocks a transfer. Within seconds compliance gets a Slack ping with the trace ID and can open the audit log.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 12', note: 'Record-keeping obligations' }],
        severity: 'notify',
        industries: ['financial_services', 'public_sector', 'general'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Equal to total block rate — typically 1–5% on production fleets.',
        fieldHelp: { status: 'Decision outcome after policy evaluation. "blocked" fires this rule.' },
        tunable: [
          'Route to a different channel for off-hours (e.g. compliance_oncall).',
          'Add a severity label in the notify config for downstream routing.',
          'Combine with a Slack webhook for engineering visibility.',
        ],
        isSystem: true,
        category: 'compliance',
        conditions: [{ field: 'status', operator: 'equals', value: 'blocked' }],
        actions: [{ type: 'notify', config: { channel: 'compliance_team' } }],
      },
      {
        id: 'tpl_compliance_pii_reasoning',
        name: 'PII in reasoning field',
        description: 'Block decisions whose reasoning field contains unscrubbed personal data patterns (email, IBAN fragments).',
        plainSummary: 'Agents must not echo raw PII in their reasoning — decisions that do are blocked.',
        example: 'An agent returns reasoning: "Approved because user@example.com is a returning customer" — blocked before storage.',
        regulatoryRefs: [
          { framework: 'GDPR', article: 'Art. 5(1)(c)', note: 'Data minimisation principle' },
          { framework: 'GDPR', article: 'Art. 32', note: 'Security of processing' },
        ],
        severity: 'block',
        industries: ['general', 'financial_services', 'healthcare', 'hr'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Should trend to zero — any match usually indicates a prompt-engineering regression.',
        fieldHelp: { rationale: 'Free-text justification returned by the agent — scanned here for PII patterns.' },
        tunable: [
          'Extend the regex with credit-card or SSN patterns.',
          'Start with flag_for_review during the discovery phase to size the problem.',
          'Add notify to alert the prompt-engineering team when rates spike.',
        ],
        isSystem: true,
        category: 'compliance',
        conditions: [{ field: 'rationale', operator: 'regex', value: '[\\w.+-]+@[\\w-]+\\.[\\w.-]+' }],
        actions: [{ type: 'block', config: { reason: 'pii_leak_in_reasoning' } }, { type: 'notify', config: { channel: 'compliance_team' } }],
      },

      // ─── Financial ─────────────────────────────────────────────────
      {
        id: 'tpl_financial_large_txn',
        name: 'Large transaction threshold',
        description: 'Flag any decision affecting a transaction above €10,000 for manual approval.',
        plainSummary: 'Transactions over €10k always see a human approver before execution.',
        example: 'An agent auto-approves a €15,400 vendor payment — the decision is routed to the finance reviewer instead of executed.',
        regulatoryRefs: [
          { framework: 'BaFin MaRisk', article: 'AT 4.3.1', note: 'Internal controls for significant transactions' },
          { framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight' },
        ],
        severity: 'flag',
        industries: ['financial_services', 'insurance'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Depends on transaction size distribution — typically 1–5% of payment-agent decisions.',
        fieldHelp: { 'payload.amount': 'Transaction amount in the base currency your SDK is configured for.' },
        tunable: [
          'Adjust threshold per business unit (retail vs. corporate).',
          'Add a currency guard with a second condition on payload.currency.',
          'Escalate to block for amounts above a second, higher threshold.',
        ],
        isSystem: true,
        category: 'financial',
        conditions: [{ field: 'payload.amount', operator: 'greater_than', value: 10000 }],
        actions: [{ type: 'flag_for_review' }],
      },
      {
        id: 'tpl_financial_sanctions',
        name: 'Sanctioned jurisdiction',
        description: 'Block any decision whose counterparty country matches an OFAC / EU sanctions list.',
        plainSummary: 'Never approve a decision touching a sanctioned jurisdiction — no human override at runtime.',
        example: 'A payment agent tries to route funds to a Belarus-based counterparty — the decision is blocked and compliance is paged.',
        regulatoryRefs: [
          { framework: 'EU Sanctions Regulation', article: '(EU) 269/2014', note: 'Restrictive measures against designated persons' },
          { framework: 'OFAC', article: '31 CFR 501', note: 'US sanctions enforcement' },
        ],
        severity: 'block',
        industries: ['financial_services', 'legal'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Very low on hygienic pipelines — any match is significant and should be investigated.',
        fieldHelp: { 'payload.country': 'ISO country code or country name attached by your SDK.' },
        tunable: [
          'Maintain the regex list from a single source of truth — update monthly.',
          'Add ISO-alpha-2 aliases (BY, IR, KP) alongside full names.',
          'Layer with a sanctions-check provider call upstream if available.',
        ],
        isSystem: true,
        category: 'financial',
        conditions: [{ field: 'payload.country', operator: 'regex', value: '(russia|belarus|iran|north_korea|syria|cuba)' }],
        actions: [{ type: 'block' }, { type: 'notify', config: { channel: 'compliance_team' } }],
      },
      {
        id: 'tpl_financial_structuring',
        name: 'Structured-transaction pattern',
        description: 'Flag amounts just under common reporting thresholds (e.g. €9,500–€10,000) as potential structuring.',
        plainSummary: 'Amounts suspiciously close to reporting thresholds get reviewed — a classic structuring signal.',
        example: 'An agent approves a €9,750 transfer. The amount sits in the structuring band and is flagged for AML review.',
        regulatoryRefs: [
          { framework: 'EU AMLD6', article: 'Art. 2', note: 'Money-laundering offences' },
          { framework: 'BaFin AML', article: '§ 10 GwG', note: 'Reporting obligations' },
        ],
        severity: 'flag',
        industries: ['financial_services'],
        falsePositiveRisk: 'high',
        expectedMatchRate: 'Tune to your volume — expect 0.5–2% initial flags, refine by business context.',
        fieldHelp: { 'payload.amount': 'Transaction amount — matched against the structuring band.' },
        tunable: [
          'Adjust the band to your local reporting threshold (€10k in EU, $10k in US).',
          'Combine with counterparty-country or frequency heuristics for precision.',
          'Escalate to notify AML team on repeated flags per agent.',
        ],
        isSystem: true,
        category: 'financial',
        conditions: [
          { field: 'payload.amount', operator: 'greater_than', value: 9500, logicalOperator: 'AND' },
          { field: 'payload.amount', operator: 'less_than', value: 10000 },
        ],
        actions: [{ type: 'flag_for_review', config: { reason: 'structuring_pattern' } }],
      },
      {
        id: 'tpl_financial_payment_low_conf',
        name: 'Low-confidence payment approval',
        description: 'Block payment-domain approvals below 90% confidence. Finance workflows demand higher certainty.',
        plainSummary: 'Payments need to be clearly right — anything under 90% sure is blocked.',
        example: 'A payment agent approves a refund with confidence 0.83 — blocked and queued for the finance reviewer.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight' }],
        severity: 'block',
        industries: ['financial_services'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Typically 2–6% on well-calibrated payment agents.',
        fieldHelp: {
          agentType: 'Agent domain — this rule scopes to anything containing "payment".',
          confidenceScore: 'Self-reported certainty. Requires calibration to be meaningful.',
        },
        tunable: [
          'Raise to 0.95 for wire transfers, lower to 0.85 for internal reconciliation.',
          'Add amount threshold so small-value decisions bypass this gate.',
          'Swap block for flag_for_review during rollout.',
        ],
        isSystem: true,
        category: 'financial',
        conditions: [
          { field: 'agentType', operator: 'contains', value: 'payment', logicalOperator: 'AND' },
          { field: 'confidenceScore', operator: 'less_than', value: 0.9 },
        ],
        actions: [{ type: 'block' }, { type: 'notify', config: { channel: 'finance_reviewers' } }],
      },

      // ─── Healthcare ────────────────────────────────────────────────
      {
        id: 'tpl_healthcare_phi',
        name: 'PHI low-confidence review',
        description: 'Flag healthcare-domain decisions below 85% confidence. PHI handling requires higher certainty.',
        plainSummary: 'Healthcare decisions need high certainty — anything under 85% sure goes to a clinical reviewer.',
        example: 'A triage agent categorises a symptom report at confidence 0.78 — flagged for a clinician to confirm.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Annex III (5)', note: 'Healthcare as high-risk domain' },
          { framework: 'GDPR', article: 'Art. 9', note: 'Special category data (health)' },
        ],
        severity: 'flag',
        industries: ['healthcare', 'insurance'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Highly model-dependent — expect 5–15% on triage-style agents.',
        fieldHelp: {
          'payload.domain': 'Your SDK should tag healthcare decisions with domain="healthcare".',
          confidenceScore: 'Self-reported certainty from the agent.',
        },
        tunable: [
          'Raise to 0.95 for diagnostic agents, keep at 0.85 for triage.',
          'Narrow scope with an additional condition on agent sub-type.',
          'Escalate to block once clinical review capacity is in place.',
        ],
        isSystem: true,
        category: 'healthcare',
        conditions: [
          { field: 'payload.domain', operator: 'equals', value: 'healthcare', logicalOperator: 'AND' },
          { field: 'confidenceScore', operator: 'less_than', value: 0.85 },
        ],
        actions: [{ type: 'flag_for_review' }],
      },
      {
        id: 'tpl_healthcare_pediatric',
        name: 'Pediatric case — mandatory review',
        description: 'Flag any decision whose payload indicates a patient age under 18 for clinical oversight.',
        plainSummary: 'All pediatric AI decisions get a human clinician in the loop, regardless of confidence.',
        example: 'An agent recommends a medication dosage for a 14-year-old — flagged before the recommendation reaches the prescriber.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Annex III (5)', note: 'Healthcare as high-risk' },
          { framework: 'GDPR', article: 'Art. 8', note: 'Child-data protections' },
        ],
        severity: 'flag',
        industries: ['healthcare'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Proportional to pediatric case volume — 100% of matching cases.',
        fieldHelp: { 'payload.patientAge': 'Patient age in years. SDK must populate for this rule to match.' },
        tunable: [
          'Adjust threshold (e.g. 21 for adolescent medicine protocols).',
          'Add notify channel for pediatric-specialist reviewers.',
          'Combine with PHI low-confidence rule for layered safety.',
        ],
        isSystem: true,
        category: 'healthcare',
        conditions: [{ field: 'payload.patientAge', operator: 'less_than', value: 18 }],
        actions: [{ type: 'flag_for_review', config: { reason: 'pediatric_case' } }],
      },
      {
        id: 'tpl_healthcare_controlled_substance',
        name: 'Controlled-substance prescription flag',
        description: 'Flag agent decisions whose payload medication name matches a controlled-substance pattern.',
        plainSummary: 'Any controlled-substance recommendation needs a human prescriber — AI cannot finalise it alone.',
        example: 'A prescribing agent suggests oxycodone — flagged for prescriber confirmation.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Annex III (5)', note: 'Healthcare as high-risk' },
          { framework: 'BtMG', article: '§ 1', note: 'Controlled substances list (DE)' },
        ],
        severity: 'flag',
        industries: ['healthcare'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Rare on general-practice agents — any match deserves scrutiny.',
        fieldHelp: { 'payload.medication': 'Medication name or DIN/PZN code returned by the agent.' },
        tunable: [
          'Maintain the regex list from your formulary team.',
          'Escalate to block once prescriber workflow can handle the queue.',
          'Pair with dosage-threshold checks for specific medications.',
        ],
        isSystem: true,
        category: 'healthcare',
        conditions: [{ field: 'payload.medication', operator: 'regex', value: '(oxycodone|fentanyl|morphine|methadone|diazepam)' }],
        actions: [{ type: 'flag_for_review', config: { reason: 'controlled_substance' } }, { type: 'notify', config: { channel: 'prescriber_queue' } }],
      },

      // ─── Technical ─────────────────────────────────────────────────
      {
        id: 'tpl_technical_medium_conf',
        name: 'Flag medium-confidence decisions',
        description: 'Flag decisions in the 50–80% confidence band for human review without blocking the agent.',
        plainSummary: 'The fuzzy middle — not confident enough to trust, not uncertain enough to block.',
        example: 'A categorisation agent returns confidence 0.67 — flagged for reviewer spot-checking without disrupting the flow.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight' }],
        severity: 'flag',
        industries: ['general'],
        falsePositiveRisk: 'high',
        expectedMatchRate: 'Expect 10–25% on general-purpose agents — tune the band to your volume.',
        fieldHelp: { confidenceScore: 'Self-reported agent certainty.' },
        tunable: [
          'Narrow the band to 0.6–0.75 for high-volume agents.',
          'Add agent-type scoping to avoid reviewer fatigue.',
          'Swap to notify-only during the calibration phase.',
        ],
        isSystem: true,
        category: 'technical',
        conditions: [
          { field: 'confidenceScore', operator: 'greater_than', value: 0.5, logicalOperator: 'AND' },
          { field: 'confidenceScore', operator: 'less_than', value: 0.8 },
        ],
        actions: [{ type: 'flag_for_review' }],
      },
      {
        id: 'tpl_technical_escalation',
        name: 'Notify on escalation',
        description: 'Send a notification to the configured channel when a decision status is escalated.',
        plainSummary: 'Every escalation pings the on-call channel — no escalation sits unnoticed.',
        example: 'A reviewer escalates a flagged trace — the on-call gets a Slack ping with the trace ID.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight workflow' }],
        severity: 'notify',
        industries: ['general'],
        falsePositiveRisk: 'low',
        expectedMatchRate: '100% of escalations — rate depends on reviewer behaviour.',
        fieldHelp: { status: '"escalated" is set when a reviewer promotes a trace beyond the standard queue.' },
        tunable: [
          'Route to a paging channel (PagerDuty, Opsgenie) for true off-hours coverage.',
          'Add a severity label for downstream routing.',
          'Combine with a webhook to forward to your ticketing system.',
        ],
        isSystem: true,
        category: 'technical',
        conditions: [{ field: 'status', operator: 'equals', value: 'escalated' }],
        actions: [{ type: 'notify' }],
      },
      {
        id: 'tpl_technical_unauth_agent',
        name: 'Unauthorized agent detection',
        description: 'Block decisions from unknown or unregistered agent IDs. Whitelist-style control for onboarding governance.',
        plainSummary: "If an agent isn't on the allow-list, its decisions don't count.",
        example: 'A staging SDK key is accidentally used in production — every decision is blocked and security is paged.',
        regulatoryRefs: [
          { framework: 'EU AI Act', article: 'Art. 12', note: 'Record-keeping — only approved agents' },
          { framework: 'ISO 27001', article: 'A.9.2', note: 'User access management' },
        ],
        severity: 'block',
        industries: ['general'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Should be 0% in steady state — any match indicates an onboarding gap.',
        fieldHelp: { agentId: 'Unique agent identifier. Naming convention should make unknowns obvious.' },
        tunable: [
          'Replace "unknown" with your real naming convention (e.g. "staging_", "test_").',
          'Swap regex for a stricter allow-list pattern.',
          'Downgrade to flag while migrating existing fleets.',
        ],
        isSystem: true,
        category: 'technical',
        conditions: [{ field: 'agentId', operator: 'contains', value: 'unknown' }],
        actions: [{ type: 'block' }, { type: 'notify', config: { channel: 'security_team' } }],
      },
      {
        id: 'tpl_technical_auto_approve',
        name: 'High-confidence auto-approve',
        description: 'Auto-approve decisions above 98% confidence to cut reviewer load on low-risk, high-certainty workflows.',
        plainSummary: 'Very-high-confidence decisions fast-track without review — reserve reviewers for the ambiguous cases.',
        example: 'A categorisation agent returns confidence 0.995 on a routine case — approved automatically with no queue impact.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 14(4)', note: 'Proportionate oversight' }],
        severity: 'notify',
        industries: ['general'],
        falsePositiveRisk: 'low',
        expectedMatchRate: 'Varies widely by model calibration — expect 30–60% on mature agents.',
        fieldHelp: {
          confidenceScore: 'Self-reported certainty. Only meaningful with calibration.',
          agentType: 'Agent domain — scope this rule away from regulated domains.',
        },
        tunable: [
          'Scope to specific low-risk agent types only — never apply to healthcare, credit, recruitment.',
          'Raise threshold to 0.99 for safer auto-approve.',
          'Pair with a sampled review rule (e.g. 1% of approvals go to queue anyway).',
        ],
        isSystem: true,
        category: 'technical',
        conditions: [{ field: 'confidenceScore', operator: 'greater_than', value: 0.98 }],
        actions: [{ type: 'approve', config: { reason: 'high_confidence_autoapprove' } }],
      },
      {
        id: 'tpl_technical_reasoning_length',
        name: 'Excessive reasoning length',
        description: 'Flag decisions whose reasoning field is suspiciously long — often a sign of prompt-injection or hallucination.',
        plainSummary: "Agents shouldn't ramble — very long reasoning is often a symptom of something going wrong.",
        example: 'An agent returns reasoning with 4,000 characters on a yes/no decision — flagged for investigation.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 15', note: 'Accuracy and robustness' }],
        severity: 'flag',
        industries: ['general'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Low single digits on healthy fleets — spikes deserve investigation.',
        fieldHelp: { rationale: 'Free-text justification returned by the agent. This rule uses a regex to catch long responses.' },
        tunable: [
          "Adjust the length threshold to your agent's normal response profile.",
          'Combine with confidence-score checks for stronger signal.',
          'Escalate to notify the ML-ops team when rates climb.',
        ],
        isSystem: true,
        category: 'technical',
        conditions: [{ field: 'rationale', operator: 'regex', value: '^.{2000,}$' }],
        actions: [{ type: 'flag_for_review', config: { reason: 'excessive_reasoning_length' } }],
      },

      // ─── Fraud ─────────────────────────────────────────────────────
      {
        id: 'tpl_fraud_rejected',
        name: 'Rejected decision pattern',
        description: 'Flag and notify on any decision whose status is rejected — surfaces potential fraud or policy misfires.',
        plainSummary: 'Every rejection gets eyes on it — catches both fraud and over-eager agents.',
        example: 'An agent rejects a refund as "suspicious" — flagged for the fraud team to confirm the call was correct.',
        regulatoryRefs: [{ framework: 'EU AI Act', article: 'Art. 14', note: 'Human oversight on adverse decisions' }],
        severity: 'flag',
        industries: ['financial_services', 'insurance', 'general'],
        falsePositiveRisk: 'medium',
        expectedMatchRate: 'Tracks overall rejection rate — typically 1–5% on transactional agents.',
        fieldHelp: { status: "\"rejected\" is the agent's own negative decision outcome." },
        tunable: [
          'Scope to specific agent types (e.g. only fraud or payment agents).',
          'Sample (review 10% of rejections) once volume becomes high.',
          'Escalate to block for specific rejection reasons.',
        ],
        isSystem: true,
        category: 'fraud',
        conditions: [{ field: 'status', operator: 'equals', value: 'rejected' }],
        actions: [{ type: 'flag_for_review' }, { type: 'notify', config: { channel: 'fraud_team' } }],
      },
    ];
  },

  // ============================================================================
  // ALERTS
  // ============================================================================

  async getAlerts(): Promise<API.Alert[]> {
    await delay(300);
    // GET /api/alerts returns rule-shaped DTOs (alertController.toAlertDTO):
    // every Alert carries the required rule fields (name/conditions/actions/
    // enabled/triggerCount/createdAt). These demo entries additionally exercise
    // the optional runtime-alert surface (title/type/status/metadata). The
    // shared rule-shell keeps each entry conformant without restating it.
    const ruleShell = (): Pick<
      API.Alert,
      'conditions' | 'actions' | 'enabled' | 'triggerCount' | 'createdAt'
    > => ({
      conditions: [],
      actions: [{ type: 'email', config: { recipients: ['ops@acmecorp.com'] } }],
      enabled: true,
      triggerCount: 1,
      createdAt: "2026-03-01T00:00:00Z",
    });
    return [
      {
        ...ruleShell(),
        id: "alert_1",
        name: "High Block Rate Detected",
        title: "High Block Rate Detected",
        description: "Block rate exceeded 5% threshold within the last hour across multiple agents",
        severity: "warning",
        status: "active",
        type: "enforcement",
        agentName: "Pricing Engine Komposit",
        timestamp: "2026-03-14T14:30:00Z",
        metadata: {
          threshold: 5,
          actual: 7.2,
          affectedAgents: 3,
          timeWindow: "1h"
        }
      },
      {
        ...ruleShell(),
        id: "alert_2",
        name: "Critical Confidence Drop",
        title: "Critical Confidence Drop",
        description: "Average decision confidence fell below 80% - immediate investigation required",
        severity: "critical",
        status: "active",
        type: "compliance",
        timestamp: "2026-03-13T09:15:00Z",
        metadata: {
          threshold: 80,
          actual: 76.3,
          duration: "30min"
        }
      },
      {
        ...ruleShell(),
        id: "alert_3",
        name: "Unusual Traffic Spike",
        title: "Unusual Traffic Spike",
        description: "Request volume exceeded 200% of normal baseline",
        severity: "warning",
        status: "active",
        type: "anomaly",
        agentName: "Fraud Detector",
        timestamp: "2026-03-14T11:45:00Z",
        metadata: {
          baseline: 1200,
          actual: 2840,
          percentIncrease: 237
        }
      },
      {
        ...ruleShell(),
        id: "alert_4",
        name: "Agent Downtime Detected",
        title: "Agent Downtime Detected",
        description: "Agent hasn't reported heartbeat in over 5 minutes - possible service disruption",
        severity: "critical",
        status: "resolved",
        type: "system",
        agentName: "Dokument-Klassifizierer",
        timestamp: "2026-03-12T22:30:00Z",
        resolvedAt: "2026-03-12T22:38:00Z",
        metadata: {
          downtimeDuration: "8min",
          lastHeartbeat: "2026-03-12T22:22:00Z"
        }
      },
      {
        ...ruleShell(),
        id: "alert_5",
        name: "Review Queue Backlog",
        title: "Review Queue Backlog",
        description: "Review queue exceeded 20 pending items - human reviewers may be overwhelmed",
        severity: "warning",
        status: "active",
        type: "enforcement",
        timestamp: "2026-03-14T08:00:00Z",
        metadata: {
          queueSize: 24,
          threshold: 20,
          avgWaitTime: "45min"
        }
      },
      {
        ...ruleShell(),
        id: "alert_6",
        name: "High Latency Warning",
        title: "High Latency Warning",
        description: "Average response time exceeded 500ms - user experience may be degraded",
        severity: "warning",
        status: "resolved",
        type: "system",
        agentName: "Kunden-Chat-Assistent",
        timestamp: "2026-03-13T16:20:00Z",
        resolvedAt: "2026-03-13T17:05:00Z",
        metadata: {
          threshold: 500,
          actual: 673,
          duration: "45min"
        }
      },
      {
        ...ruleShell(),
        id: "alert_7",
        name: "PII Detection Spike",
        title: "PII Detection Spike",
        description: "PII detections increased by 300% - potential data exposure risk",
        severity: "critical",
        status: "dismissed",
        type: "compliance",
        agentName: "Underwriting Assistent Leben",
        timestamp: "2026-03-10T13:45:00Z",
        dismissedAt: "2026-03-10T14:30:00Z",
        metadata: {
          baseline: 12,
          actual: 48,
          percentIncrease: 300
        }
      },
      {
        ...ruleShell(),
        id: "alert_8",
        name: "Failed Authentication Attempts",
        title: "Failed Authentication Attempts",
        description: "5+ failed authentication attempts detected from same IP address",
        severity: "critical",
        status: "active",
        type: "policy_violation",
        timestamp: "2026-03-11T19:30:00Z",
        metadata: {
          ipAddress: "203.0.113.42",
          attempts: 7,
          timeWindow: "5min"
        }
      },
      {
        ...ruleShell(),
        id: "alert_9",
        name: "Daily Compliance Report",
        title: "Daily Compliance Report",
        description: "Automated daily CPI score summary delivered",
        severity: "info",
        status: "resolved",
        type: "compliance",
        timestamp: "2026-03-14T09:00:00Z",
        resolvedAt: "2026-03-14T09:00:05Z",
        metadata: {
          cpiScore: 92.4,
          reportType: "daily_summary"
        }
      },
      {
        ...ruleShell(),
        id: "alert_10",
        name: "API Rate Limit Warning",
        title: "API Rate Limit Warning",
        description: "API usage approaching 80% of rate limit quota",
        severity: "warning",
        status: "dismissed",
        type: "system",
        timestamp: "2026-03-08T14:15:00Z",
        dismissedAt: "2026-03-08T15:00:00Z",
        metadata: {
          currentUsage: 82,
          limit: 100,
          timeWindow: "1h"
        }
      },
    ];
  },

  // ============================================================================
  // WEBHOOKS
  // ============================================================================

  // Session-Store (wie _policyStore) — CRUD im Demo-Mode persistiert über Refetches.
  _webhookStore: null as API.Webhook[] | null,
  _webhookSeed(): API.Webhook[] {
    if (!this._webhookStore) this._webhookStore = this._buildWebhookSeeds();
    return this._webhookStore;
  },
  _buildWebhookSeeds(): API.Webhook[] {
    // Nordlicht: SIEM-Anbindung (Splunk) + Slack-Alarme. Der Failover-SIEM-Endpunkt
    // fiel nach einer TLS-Zertifikatsrotation wiederholt aus und wurde vom Security-
    // Team deaktiviert (über isActive: false abgebildet — das Webhook-Schema liefert
    // keine Delivery-Zähler im API-Response).
    return [
      {
        id: "webhook_1",
        name: "Slack — Compliance-Alarme",
        description: "Slack — Compliance-Alarme",
        url: "https://hooks.slack.com/services/T0NORDLICHT/B07COMP/xxxxxxxxxxxx",
        events: ["alert.triggered"],
        type: "slack",
        isActive: true,
        enabled: true,
        secret: "whsec_...nl_slack",
        createdAt: "2025-11-04T09:00:00Z",
      },
      {
        id: "webhook_2",
        name: "SIEM — Splunk HEC (Trace-Stream)",
        description: "SIEM — Splunk HEC (Trace-Stream)",
        url: "https://siem.nordlicht.example/services/collector/event",
        events: ["trace.created", "alert.triggered", "setting.updated"],
        type: "custom",
        isActive: true,
        enabled: true,
        secret: "whsec_...nl_splunk",
        createdAt: "2025-10-21T14:30:00Z",
      },
      {
        id: "webhook_3",
        name: "SIEM — Failover-Endpunkt (deaktiviert)",
        description: "SIEM — Failover-Endpunkt (deaktiviert nach TLS-Rotation 02/2026)",
        url: "https://siem-dr.nordlicht.example/collector/event",
        events: ["trace.created", "alert.triggered"],
        type: "custom",
        isActive: false,
        enabled: false,
        secret: "whsec_...nl_failover",
        createdAt: "2026-01-09T11:20:00Z",
      },
    ];
  },

  async getWebhooks(): Promise<API.Webhook[]> {
    await delay(300);
    // Tiefe Kopie der events-Arrays, damit ein Consumer den Store nicht mutiert.
    return this._webhookSeed().map((w) => ({ ...w, events: [...w.events] }));
  },

  async createWebhook(req: API.WebhookCreateRequest): Promise<API.Webhook> {
    await delay(260);
    const store = this._webhookSeed();
    const now = new Date().toISOString();
    const created: API.Webhook = {
      id: `webhook_${_hashAgentId((req.description ?? req.url) + now).toString(16).slice(0, 8)}`,
      description: req.description,
      name: req.description,
      url: req.url,
      events: [...(req.events ?? [])],
      isActive: true,
      enabled: true,
      type: req.url.includes('hooks.slack.com') ? 'slack' : 'custom',
      // Demo-Secret; das echte HMAC-Secret erzeugt das Backend serverseitig.
      secret: `whsec_demo_${_hashAgentId(req.url + now).toString(16).slice(0, 12)}`,
      createdAt: now,
    };
    store.unshift(created);
    return { ...created, events: [...created.events] };
  },

  async updateWebhook(id: string, updates: Partial<API.Webhook>): Promise<API.Webhook> {
    await delay(200);
    const store = this._webhookSeed();
    const idx = store.findIndex((w) => w.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    const merged = { ...store[idx], ...updates };
    // isActive/enabled sind Schema-Aliase — synchron halten.
    if (updates.isActive !== undefined) merged.enabled = updates.isActive;
    if (updates.enabled !== undefined) merged.isActive = updates.enabled;
    if (updates.events) merged.events = [...updates.events];
    store[idx] = merged;
    return { ...merged, events: [...merged.events] };
  },

  async deleteWebhook(id: string): Promise<void> {
    await delay(200);
    const store = this._webhookSeed();
    const idx = store.findIndex((w) => w.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store.splice(idx, 1);
  },

  async testWebhook(id: string): Promise<{ ok: boolean; statusCode: number }> {
    await delay(400);
    const wh = this._webhookSeed().find((w) => w.id === id);
    if (!wh) throw this._vvgErr('NOT_FOUND');
    // Live-Shape spiegeln (webhookController liefert { statusCode, ok }). Demo-
    // Realismus: ein deaktivierter Endpunkt antwortet nicht (Test schlägt fehl).
    return wh.isActive ? { ok: true, statusCode: 200 } : { ok: false, statusCode: 0 };
  },

  // Session-Store (wie _policyStore) — Org-API-Schlüssel (adj_live_). Schlüssel
  // maskiert (Kardinalregel #7); der Voll-Token wird NUR bei createAPIKey einmalig
  // zurückgegeben (one-time reveal), nie in der Liste.
  _apiKeyStore: null as API.APIKey[] | null,
  _apiKeySeed(): API.APIKey[] {
    if (!this._apiKeyStore) this._apiKeyStore = this._buildApiKeySeeds();
    return this._apiKeyStore;
  },
  _buildApiKeySeeds(): API.APIKey[] {
    const usedISO = (hoursAgo: number) => new Date(Date.now() - hoursAgo * 3600 * 1000).toISOString();
    return [
      { id: "key_1", name: "Produktion — SDK-Ingestion",       key: "adj_live_pk_nl_prod…••••", permissions: ["read", "write"], status: "active", createdAt: "2025-09-18T00:00:00Z", lastUsed: usedISO(1),  expiresAt: undefined },
      { id: "key_2", name: "Staging — Integrationstests",      key: "adj_test_pk_nl_stg…••••", permissions: ["read", "write"], status: "active", createdAt: "2025-10-02T00:00:00Z", lastUsed: usedISO(20), expiresAt: "2026-12-31T23:59:59Z" },
      { id: "key_3", name: "Monitoring — Read-Only (Grafana)", key: "adj_live_pk_nl_mon…••••", permissions: ["read"],          status: "active", createdAt: "2025-11-26T00:00:00Z", lastUsed: usedISO(5),  expiresAt: undefined },
    ];
  },

  async getApiKeys(): Promise<API.APIKey[]> {
    await delay(300);
    return this._apiKeySeed().map((k) => ({ ...k, permissions: k.permissions ? [...k.permissions] : undefined }));
  },

  async createAPIKey(name: string, permissions: string[]): Promise<API.APIKey> {
    await delay(300);
    const store = this._apiKeySeed();
    const now = new Date().toISOString();
    const rnd = _hashAgentId(name + now).toString(16).padStart(8, '0') + _hashAgentId(now + name).toString(16).padStart(8, '0');
    const fullKey = `adj_live_pk_${rnd}${rnd}`.slice(0, 40); // Voll-Token (one-time)
    const id = `key_${_hashAgentId(name + now).toString(16).slice(0, 8)}`;
    const stored: API.APIKey = {
      id, name, key: `adj_live_pk_${rnd.slice(0, 6)}…••••`, permissions: [...permissions], status: 'active', createdAt: now, lastUsed: undefined, expiresAt: undefined,
    };
    store.unshift(stored);
    return { ...stored, key: fullKey }; // einmalig der volle Schlüssel
  },

  async revokeAPIKey(keyId: string): Promise<void> {
    await delay(200);
    const store = this._apiKeySeed();
    const idx = store.findIndex((k) => k.id === keyId);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store[idx] = { ...store[idx], status: 'revoked' };
  },

  // ============================================================================
  // TEAM
  // ============================================================================

  // Session-Store (wie _policyStore) — Team-Mitglieder + ausstehende Einladungen.
  // Persona: Nordlicht-Leute @nordlicht.example (Org bleibt technisch „Acme", SSOT
  // DEMO_PERSONA.md). Rollen-Spektrum vollständig (owner/admin/member/viewer/auditor),
  // 1 suspendiert. lastActive als ISO (Page formatiert locale-aware).
  _teamStore: null as API.TeamMember[] | null,
  _teamSeed(): API.TeamMember[] {
    if (!this._teamStore) this._teamStore = this._buildTeamSeeds();
    return this._teamStore;
  },
  _buildTeamSeeds(): API.TeamMember[] {
    const min = 60_000, hr = 3_600_000, day = 86_400_000;
    const ago = (ms: number) => new Date(Date.now() - ms).toISOString();
    return [
      { id: "user_owner",    email: "birgit.sauer@nordlicht.example",     name: "Birgit Sauer",    role: "owner",   isActive: true,  status: "active",    lastActive: ago(4 * min),  joinedAt: "2025-09-02T00:00:00Z", permissions: ["*"] },
      { id: "user_admin",    email: "compliance@nordlicht.example",       name: "Markus Hoffmann", role: "admin",   isActive: true,  status: "active",    lastActive: ago(2 * hr),   joinedAt: "2025-09-12T00:00:00Z", permissions: ["read", "write", "manage_team"] },
      { id: "user_aktuar",   email: "aktuariat@nordlicht.example",        name: "Dr. Anja Vogt",   role: "member",  isActive: true,  status: "active",    lastActive: ago(35 * min), joinedAt: "2025-10-08T00:00:00Z", permissions: ["read", "write"] },
      { id: "user_uw",       email: "underwriting@nordlicht.example",     name: "Tobias Krenz",    role: "viewer",  isActive: true,  status: "active",    lastActive: ago(3 * hr),   joinedAt: "2026-01-20T00:00:00Z", permissions: ["read"] },
      { id: "user_revision", email: "interne-revision@nordlicht.example", name: "Petra Lange",     role: "auditor", isActive: true,  status: "active",    lastActive: ago(1 * day),  joinedAt: "2025-11-15T00:00:00Z", permissions: ["read", "audit"] },
      { id: "user_susp",     email: "j.reuter@nordlicht.example",         name: "Jonas Reuter",    role: "member",  isActive: false, status: "suspended", lastActive: ago(9 * day),  joinedAt: "2025-10-30T00:00:00Z", permissions: ["read"] },
    ];
  },

  _teamInviteStore: null as Array<{ id: string; email: string; role: string; createdAt: string; expiresAt: string }> | null,
  _teamInviteSeed() {
    if (!this._teamInviteStore) {
      const day = 86_400_000;
      this._teamInviteStore = [
        { id: "inv_1", email: "lena.fischer@nordlicht.example",    role: "member",  createdAt: new Date(Date.now() - 2 * day).toISOString(), expiresAt: new Date(Date.now() + 5 * day).toISOString() },
        { id: "inv_2", email: "revision-extern@nordlicht.example", role: "auditor", createdAt: new Date(Date.now() - 1 * day).toISOString(), expiresAt: new Date(Date.now() + 6 * day).toISOString() },
      ];
    }
    return this._teamInviteStore;
  },

  async getTeamMembers(): Promise<API.TeamMember[]> {
    await delay(300);
    return this._teamSeed().map((m) => ({ ...m, permissions: m.permissions ? [...m.permissions] : undefined }));
  },

  async getTeamInvites() {
    await delay(200);
    return this._teamInviteSeed().map((i) => ({ ...i }));
  },

  async inviteTeamMember(invite: API.TeamInviteRequest): Promise<API.TeamMember> {
    await delay(300);
    const store = this._teamInviteSeed();
    const now = new Date().toISOString();
    const id = `inv_${_hashAgentId(invite.email + now).toString(16).slice(0, 8)}`;
    store.unshift({ id, email: invite.email, role: invite.role, createdAt: now, expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString() });
    return { id: `pending_${id}`, email: invite.email, name: invite.email.split('@')[0], role: invite.role, isActive: false, status: 'invited', permissions: [] };
  },

  async updateTeamMember(id: string, updates: Partial<API.TeamMember>): Promise<API.TeamMember> {
    await delay(220);
    const store = this._teamSeed();
    const idx = store.findIndex((m) => m.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    const merged = { ...store[idx], ...updates };
    if (updates.status === 'suspended') merged.isActive = false;
    if (updates.status === 'active') merged.isActive = true;
    store[idx] = merged;
    return { ...merged };
  },

  async removeTeamMember(id: string): Promise<void> {
    await delay(220);
    const store = this._teamSeed();
    const idx = store.findIndex((m) => m.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store.splice(idx, 1);
  },

  async revokeTeamInvite(id: string): Promise<void> {
    await delay(200);
    const store = this._teamInviteSeed();
    const idx = store.findIndex((i) => i.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    store.splice(idx, 1);
  },

  // ============================================================================
  // BILLING
  // ============================================================================

  async getBilling(): Promise<API.BillingResponse> {
    await delay(300);
    return {
      subscription: {
        plan: "custom",
        status: "active",
        currentPeriodEnd: "2026-04-06T00:00:00Z",
        cancelAtPeriodEnd: false,
        stripeCustomerId: null,
      },
      usage: {
        traces: MASTER_STATS.totalDecisions,
        tracesLimit: -1,
      },
      invoices: [
        {
          id: "inv_1",
          amount: 1000,
          currency: "EUR",
          status: "paid",
          date: "2026-03-01T00:00:00Z",
          downloadUrl: "#",
        },
        {
          id: "inv_2",
          amount: 1000,
          currency: "EUR",
          status: "paid",
          date: "2026-02-01T00:00:00Z",
          downloadUrl: "#",
        },
        {
          id: "inv_3",
          amount: 950,
          currency: "EUR",
          status: "paid",
          date: "2026-01-01T00:00:00Z",
          downloadUrl: "#",
        },
        {
          id: "inv_4",
          amount: 950,
          currency: "EUR",
          status: "paid",
          date: "2025-12-01T00:00:00Z",
          downloadUrl: "#",
        },
        {
          id: "inv_5",
          amount: 950,
          currency: "EUR",
          status: "paid",
          date: "2025-11-01T00:00:00Z",
          downloadUrl: "#",
        },
      ],
    };
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  async getNotifications(): Promise<API.Notification[]> {
    await delay(300);
    return [
      {
        id: "notif_1",
        type: "security",
        title: "New login from Munich",
        body: "A new device logged into your account",
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        isRead: false,
        link: "/dashboard/settings/security",
      },
      {
        id: "notif_2",
        type: "success",
        title: "CPI Score Improved",
        body: "Your compliance score increased to 96%",
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        isRead: true,
      },
    ];
  },

  // ============================================================================
  // WORKSPACES
  // ============================================================================

  async getWorkspaces(): Promise<API.Workspace[]> {
    await delay(300);
    return [
      {
        id: "ws_main",
        name: "Production Workspace",
        organizationId: "org_acme",
        apiKeys: [
          {
            id: "key_1",
            name: "Production Backend",
            key: "adjudon_live_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6",
            permissions: ["read", "write"],
            createdAt: "2025-12-01T00:00:00Z",
            lastUsed: "2026-03-06T08:30:00Z",
          },
          {
            id: "key_2",
            name: "Development",
            key: "adjudon_test_x9y8z7w6v5u4t3s2r1q0p9o8n7m6l5k4",
            permissions: ["read"],
            createdAt: "2025-11-15T00:00:00Z",
            lastUsed: "2026-03-05T14:20:00Z",
          },
        ],
        createdAt: "2025-01-15T00:00:00Z",
      },
    ];
  },

  // ============================================================================
  // ORGANIZATION
  // ============================================================================

  async getOrganization(): Promise<API.Organization> {
    await delay(300);
    return {
      id: "org_acme",
      name: "Acme Corporation",
      slug: "acme-corp",
      plan: "custom",
      // top-level dataRetentionDays = Live-Shape (Backend GET/PATCH /api/org/settings).
      dataRetentionDays: 1825, // BaFin-Empfehlung 5 Jahre (Nordlicht, custom)
      ssoConfig: {
        enabled: true,
        provider: 'oidc',
        domain: ['nordlicht.example'],
        issuerUrl: 'https://idp.nordlicht.example',
        clientId: 'adj-nordlicht',
        hasClientSecret: true,
        scimEnabled: true,
        scimTokenLast4: 'a1b2',
        scimTokenGeneratedAt: '2026-02-12T09:00:00Z',
        requireMfaEvenWithSso: true,
      },
      settings: {
        piiMasking: true,
        dataRetentionDays: 1825,
        allowedDomains: ["nordlicht.example"],
        ssoEnabled: true,
        twoFactorRequired: true,
        apiRateLimit: 10000,
        webhookSecret: "whsec_acme_secret_key",
      },
      createdAt: "2025-01-15T00:00:00Z",
      updatedAt: "2026-03-01T00:00:00Z",
    };
  },

  // ============================================================================
  // AUDIT LOG
  // ============================================================================

  async getAuditLog(params?: API.PaginationParams): Promise<API.AuditLogResponse> {
    await delay(300);
    // Nordlicht: ~25 Operations-Audit-Einträge über ~9 Monate (15.09.2025 → heute).
    // Actions = backend-kanonische Dot-Namespaces (AuditLog.action enum); die
    // AuditLogPage mappt sie auf deutsche Labels (ACTION_LABEL). jane.smith@acmecorp.com bleibt als
    // Org-Admin/Owner; fachliche Akteure rotieren @nordlicht.example. Scheduler =
    // synthetischer Actor 'system'. Neueste zuerst (entspricht Backend-Sortierung).
    const ADMIN = { userEmail: "jane.smith@acmecorp.com", userName: "Jane Smith", userId: "usr_admin_jane", ipAddress: "192.0.2.40", userAgent: "Mozilla/5.0" };
    const CCO = { userEmail: "cco@nordlicht.example", userName: "Dr. Petra Krause (CCO)", userId: "usr_cco", ipAddress: "192.0.2.41", userAgent: "Mozilla/5.0" };
    const COMP = { userEmail: "compliance@nordlicht.example", userName: "Markus Hoffmann (Compliance)", userId: "usr_comp", ipAddress: "192.0.2.42", userAgent: "Mozilla/5.0" };
    const DSB = { userEmail: "dsb@nordlicht.example", userName: "Sandra Vogel (DSB)", userId: "usr_dsb", ipAddress: "192.0.2.43", userAgent: "Mozilla/5.0" };
    const AKT = { userEmail: "aktuariat@nordlicht.example", userName: "Thomas Berg (Aktuariat)", userId: "usr_akt", ipAddress: "192.0.2.44", userAgent: "Mozilla/5.0" };
    const SEC = { userEmail: "security@nordlicht.example", userName: "Jan Wolff (IT-Security)", userId: "usr_sec", ipAddress: "192.0.2.45", userAgent: "Mozilla/5.0" };
    const SYS = { userEmail: "system", userName: "system", userId: "system", ipAddress: "10.0.0.1", userAgent: "ADJUDON-Scheduler/1.0" };
    const e = (
      id: string, action: string, resource: string, actor: typeof ADMIN, when: string, detail: string,
    ): API.AuditLogEntry => ({ id, action, resource, ...actor, timestamp: when, createdAt: when, detail });
    const entries: API.AuditLogEntry[] = [
      // ── Mai 2026 — neue Systeme + Audit-Vorbereitung ──────────────────
      e("audit_25", "agent.create", "agent", AKT, "2026-05-21T15:40:00Z", "KI-System 'Churn-Prädiktor' angelegt (Bestandsanalyse, Risikoklasse noch offen)."),
      e("audit_24", "policy.create", "policy", COMP, "2026-05-21T15:48:00Z", "Regel 'Neue Systeme: alles in Review (Churn-Prädiktor)' erstellt — Schutz bis zur Klassifizierung."),
      e("audit_23", "settings.update", "organization_settings", DSB, "2026-05-14T09:12:00Z", "Aufbewahrungsfrist für BaFin-Audit auf 1825 Tage (5 Jahre) erhöht."),
      e("audit_22", "policy.evidence_pack_downloaded", "policy", CCO, "2026-05-08T11:02:00Z", "Auditoren-Beweispaket Q1/2026 für PwC-Vorprüfung heruntergeladen."),
      // ── April 2026 — Drift Pricing Engine ─────────────────────────────
      e("audit_21", "policy.update", "policy", AKT, "2026-04-18T16:30:00Z", "Regel 'Großschaden > 50.000 € in Review' — Schwelle von 75.000 € auf 50.000 € gesenkt."),
      e("audit_20", "alert.create", "alert", AKT, "2026-04-12T10:20:00Z", "Kalibrierungs-Alarm 'Konfidenz-Drift Pricing Engine' aktiviert."),
      e("audit_19", "team.update_role", "user", ADMIN, "2026-04-03T13:45:00Z", "Rolle von aktuariat@nordlicht.example auf 'admin' geändert."),
      // ── März 2026 — C2PA-Aktivierung + Quartals-Bundle ────────────────
      e("audit_18", "settings.update", "organization_settings", CCO, "2026-03-19T08:30:00Z", "C2PA-Provenance-Kennzeichnung aktiviert (Art. 50); Auto-Kennzeichnung bleibt opt-in deaktiviert."),
      e("audit_17", "audit_bundle.generate", "audit_bundle", COMP, "2026-03-15T07:05:00Z", "Quartals-Beweisbündel Q1/2026 erzeugt und versiegelt."),
      e("audit_16", "webhook.update", "webhook", SEC, "2026-03-02T14:18:00Z", "Failover-SIEM-Webhook nach wiederholten TLS-Fehlern deaktiviert."),
      // ── Februar 2026 — Fail-Open-Nacht (Vorfall #3) ───────────────────
      e("audit_15", "policy.update", "policy", AKT, "2026-02-10T11:15:00Z", "Regel 'Tarif-Drift automatisch eskalieren' deaktiviert — zu viele Fehlalarme; Drift läuft über Kalibrierungs-Alarme."),
      e("audit_14", "settings.update", "organization_settings", SEC, "2026-02-18T22:47:00Z", "Notfall-Wartung API-Gateway nach Fehlkonfiguration; Fail-Open-Pass-Through temporär aktiv."),
      e("audit_13", "policy.create", "policy", AKT, "2026-02-04T10:00:00Z", "Regel 'Tarif-Drift automatisch eskalieren' (Versuch) erstellt."),
      // ── Januar 2026 — Art.-22-Programm ────────────────────────────────
      e("audit_12", "policy.create", "policy", COMP, "2026-01-12T10:15:00Z", "Regel 'Underwriting-Ablehnung immer in Review (Art. 22)' erstellt."),
      e("audit_11", "apikey.create", "api_key", SEC, "2026-01-09T11:20:00Z", "API-Schlüssel 'SIEM — Failover-Endpunkt' für Splunk-DR angelegt."),
      e("audit_10", "team.invite", "user", ADMIN, "2026-01-07T09:30:00Z", "Nutzer 'security@nordlicht.example' mit Rolle 'admin' eingeladen."),
      // ── Dezember 2025 — PII-Regel + Bundle ────────────────────────────
      e("audit_9", "audit_bundle.generate", "audit_bundle", COMP, "2025-12-15T07:05:00Z", "Quartals-Beweisbündel Q4/2025 erzeugt und versiegelt."),
      e("audit_8", "policy.create", "policy", DSB, "2025-12-03T13:20:00Z", "Regel 'PII-Anomalie blockieren' aktiviert (DSGVO Art. 5/32)."),
      // ── November 2025 — Attestierungs-Programm startet ────────────────
      e("audit_7", "policy.create", "policy", AKT, "2025-11-15T11:00:00Z", "Regel 'Großschaden > 50.000 € in Review' erstellt."),
      e("audit_6", "webhook.create", "webhook", SEC, "2025-11-04T09:00:00Z", "Slack-Webhook 'Compliance-Alarme' verbunden."),
      e("audit_5", "team.invite", "user", ADMIN, "2025-11-03T15:10:00Z", "Nutzer 'compliance@nordlicht.example' mit Rolle 'admin' eingeladen."),
      // ── Oktober 2025 — erste Regeln + SIEM ────────────────────────────
      e("audit_4", "policy.create", "policy", COMP, "2025-10-08T09:30:00Z", "Regel 'Niedrig-Konfidenz blockieren (< 70 %)' erstellt — erste Enforcement-Regel."),
      e("audit_3", "webhook.create", "webhook", SEC, "2025-10-21T14:30:00Z", "SIEM-Webhook (Splunk HEC) für Trace-Stream verbunden."),
      // ── September 2025 — Onboarding ───────────────────────────────────
      e("audit_2", "apikey.create", "api_key", ADMIN, "2025-09-18T08:00:00Z", "Produktions-SDK-Schlüssel für Trace-Ingestion erstellt."),
      e("audit_1", "settings.update", "organization", ADMIN, "2025-09-15T08:00:00Z", "Organisation 'Nordlicht Versicherung AG' eingerichtet; Datenresidenz Frankfurt."),
    ];
    // Scheduler-Purge als jüngster Systemeintrag oben einreihen.
    entries.unshift(
      e("audit_26", "data.purge", "trace", SYS, new Date(Date.now() - 6 * 3600 * 1000).toISOString(), "Automatischer Lösch-Lauf: Traces älter als die Aufbewahrungsfrist entfernt (Payload nullifiziert, Chain-Shell erhalten)."),
    );
    const page = params?.page || 1;
    const limit = params?.limit || 20;
    const total = entries.length;
    const start = (page - 1) * limit;
    return {
      entries: entries.slice(start, start + limit),
      pagination: {
        total,
        page,
        limit,
        pages: Math.ceil(total / limit),
      },
    };
  },

  // ============================================================================
  // ANOMALIES & DRIFT DETECTION
  // ============================================================================

  async getAnomalies(_params?: { sinceHours?: number; baselineDays?: number }): Promise<unknown[]> {
    await delay(400);
    // Mock matches backend `anomalyService.js` shape:
    // - blockRate: 0–100 integer percentage
    // - confidenceScore: 0.0–1.0 fraction
    const now = Date.now();
    return [
      {
        id: "anom-mock-1",
        agent_id: "agent_pricing",
        agent_name: "Pricing Engine Komposit",
        metric: "blockRate",
        type: "error_rate",
        baseline: 2,
        current: 12,
        severity: "high",
        deviation: 10,
        timestamp: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
        message: "Blockrate gestiegen 2 % → 12 % — deutliche Drift im Tarifierungs-Muster.",
      },
      {
        id: "anom-mock-2",
        agent_id: "agent_uw_leben",
        agent_name: "Underwriting Assistent Leben",
        metric: "confidenceScore",
        type: "confidence",
        baseline: 0.93,
        current: 0.89,
        severity: "medium",
        deviation: -0.04,
        timestamp: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
        message: "Durchschnittskonfidenz gefallen 93 % → 89 % — Modell-Drift möglich.",
      },
      {
        id: "anom-mock-3",
        agent_id: "agent_fraud",
        agent_name: "Fraud Detector",
        metric: "blockRate",
        type: "error_rate",
        baseline: 2,
        current: 8,
        severity: "medium",
        deviation: 6,
        timestamp: new Date(now - 6 * 60 * 60 * 1000).toISOString(),
        message: "Blockrate gestiegen 2 % → 8 % — erhöhte Betrugserkennungs-Aktivität.",
      },
    ];
  },

  // ============================================================================
  // FEEDBACK & CORRECTIONS ANALYTICS
  // ============================================================================

  async getFeedbackAnalytics(params?: { days?: number }): Promise<any> {
    await delay(400);
    
    const days = params?.days || 30;
    
    // Calculate agent data first to ensure consistency. `traffic` = 30-Tage-
    // Volumen der 6 Nordlicht-Systeme (SSOT: MASTER_AGENTS); `rate` = Korrektur-
    // Quote in %. Pricing (degraded) hat die höchste Quote.
    const byAgent = [
      { id: "agent_fraud",    name: "Fraud Detector",               traffic: MASTER_AGENTS[0].traffic, rate: 2.4 },
      { id: "agent_uw_leben", name: "Underwriting Assistent Leben", traffic: MASTER_AGENTS[1].traffic, rate: 3.1 },
      { id: "agent_doc",      name: "Dokument-Klassifizierer",      traffic: MASTER_AGENTS[2].traffic, rate: 1.2 },
      { id: "agent_pricing",  name: "Pricing Engine Komposit",      traffic: MASTER_AGENTS[3].traffic, rate: 4.6 },
      { id: "agent_chat",     name: "Kunden-Chat-Assistent",        traffic: MASTER_AGENTS[4].traffic, rate: 2.0 },
    ].map(agent => {
      const total = Math.round((agent.traffic / 30) * days);
      const corrections = Math.round(total * (agent.rate / 100));
      return {
        agentId: agent.id,
        agentName: agent.name,
        total,
        corrections,
        correctionRate: agent.rate,
      };
    });
    
    // Calculate totals from agents (ensures consistency)
    const totalDecisions = byAgent.reduce((sum, a) => sum + a.total, 0);
    const humanCorrections = byAgent.reduce((sum, a) => sum + a.corrections, 0);
    const correctionRate = Math.round((humanCorrections / totalDecisions) * 10000) / 100;
    
    // Generate daily corrections that sum EXACTLY to humanCorrections
    const dailyCorrections: number[] = [];
    const base = Math.floor(humanCorrections / days);
    const remainder = humanCorrections - (base * days);

    for (let i = 0; i < days; i++) {
      const extra = i < remainder ? 1 : 0;
      const variance = [0, 1, -1, 2, -2, 3, -3][i % 7];
      dailyCorrections.push(Math.max(1, base + extra + variance));
    }

    // Adjust last day to match EXACT sum
    const currentSum = dailyCorrections.reduce((a, b) => a + b, 0);
    dailyCorrections[days - 1] += (humanCorrections - currentSum);

    // Daily totals proportional to the agent-derived totalDecisions, so the
    // chart's two lines (total vs corrections) stay self-consistent.
    const dailyTotals: number[] = [];
    const baseTotal = Math.floor(totalDecisions / days);
    const remainderTotal = totalDecisions - (baseTotal * days);
    for (let i = 0; i < days; i++) {
      const extra = i < remainderTotal ? 1 : 0;
      const variance = [0, 20, -15, 35, -25, 40, -30][i % 7];
      dailyTotals.push(Math.max(1, baseTotal + extra + variance));
    }
    const totalsSum = dailyTotals.reduce((a, b) => a + b, 0);
    dailyTotals[days - 1] += (totalDecisions - totalsSum);

    // Rolling ISO date keyed off `days` — matches backend format "%Y-%m-%d"
    // so real-backend and mock responses are render-compatible.
    const byDay = Array.from({ length: days }).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - i));
      const isoDate = d.toISOString().slice(0, 10);
      const corrections = Math.max(0, dailyCorrections[i] ?? 0);
      const total = Math.max(corrections, dailyTotals[i] ?? 0);
      return { date: isoDate, total, corrections };
    });

    return {
      summary: {
        totalDecisions,
        humanCorrections,
        correctionRate,
        approvedWithoutOverride: totalDecisions - humanCorrections,
      },
      byAgent,
      byDay,
      topCorrectionReasons: [
        // Proportional to humanCorrections (calculated from agents)
        { reason: "Low confidence threshold", count: Math.round(humanCorrections * 0.29) },
        { reason: "PII detected - false positive", count: Math.round(humanCorrections * 0.22) },
        { reason: "Context misunderstood", count: Math.round(humanCorrections * 0.16) },
        { reason: "Business rule exception", count: Math.round(humanCorrections * 0.13) },
        { reason: "Anomaly detected - benign", count: Math.round(humanCorrections * 0.11) },
        { reason: "Edge case not covered", count: Math.round(humanCorrections * 0.09) },
      ],
    };
  },

  // ============================================================================
  // USAGE & QUOTA TRACKING
  // ============================================================================

  async getUsageCurrent(): Promise<any> {
    await delay(300);

    // Derived from MASTER_STATS: 1203 decisions/day × 10 traces/decision = ~12,030 traces/day
    // Current month (Mar 1-17, 17 days): 17 × 12,030 = ~204,510 traces
    const daysIntoMonth = 17;
    const tracesPerDay = Math.round((MASTER_STATS.totalDecisions / 7) * 10); // ~12,030
    const used = daysIntoMonth * tracesPerDay; // ~204,510
    const lastMonthUsed = Math.round(tracesPerDay * 28); // ~336,840 — prior month total
    const limit = 500000; // 500K traces/month for Professional plan
    // Match backend precision: usageController returns Math.round((thisMonth / limit) * 100)
    // — integer percentage, not 1-decimal. Drift here causes UI percentages to differ
    // between mock and real env even when underlying ratios are identical.
    const percentUsed = Math.round((used / limit) * 100);
    const remaining = limit - used;
    const monthOverMonthChange =
      lastMonthUsed > 0
        ? Math.round(((used - lastMonthUsed) / lastMonthUsed) * 100)
        : (used > 0 ? 100 : 0);

    return {
      // Flat fields — match backend `usageController.getCurrentUsage` shape exactly.
      plan: "Professional",
      limit,
      unlimited: false,
      thisMonth: used,
      percentUsed,
      remaining,
      resetDate: "2026-04-01T00:00:00Z",
      today: tracesPerDay, // approximation — current-day so far
      dailyAverage: tracesPerDay,
      projectedMonthly: Math.round((used / daysIntoMonth) * 30),
      lastMonth: lastMonthUsed,
      monthOverMonthChange,
      totalAllTime: used + lastMonthUsed + Math.round(tracesPerDay * 30 * 4),
      // Match backend thresholds: >= 100 → exceeded, >= 90 → warning. Previous mock
      // used 90/80 which drifted from the controller and meant identical data
      // showed different banners between mock and real env.
      status: percentUsed >= 100 ? "exceeded" : percentUsed >= 90 ? "warning" : "ok",
      // Nested shape — kept for legacy consumers (NotificationCenter, etc.) that
      // pre-date the flat-shape normalization in api.ts.
      traces: {
        used,
        limit,
        percentage: percentUsed,
        remaining,
      },
    };
  },

  async getUsageHistory(): Promise<any> {
    await delay(300);
    
    // tracesPerDay calculated same as in getUsageCurrent()
    const tracesPerDay = Math.round((MASTER_STATS.totalDecisions / 7) * 10); // 12,029
    const monthlyBase = tracesPerDay * 30; // 360,870
    const daysIntoMarch = 17;
    const marchActual = daysIntoMarch * tracesPerDay; // 204,493 (matches current.traces.used!)
    
    return [
      { label: "Apr 2025", count: Math.round(monthlyBase * 0.85) },
      { label: "May 2025", count: Math.round(monthlyBase * 0.92) },
      { label: "Jun 2025", count: Math.round(monthlyBase * 0.88) },
      { label: "Jul 2025", count: Math.round(monthlyBase * 0.98) },
      { label: "Aug 2025", count: Math.round(monthlyBase * 0.95) },
      { label: "Sep 2025", count: Math.round(monthlyBase * 1.02) },
      { label: "Oct 2025", count: Math.round(monthlyBase * 1.08) },
      { label: "Nov 2025", count: Math.round(monthlyBase * 1.05) },
      { label: "Dec 2025", count: Math.round(monthlyBase * 1.12) },
      { label: "Jan 2026", count: Math.round(monthlyBase * 1.18) },
      { label: "Feb 2026", count: Math.round(monthlyBase * 1.15) },
      { label: "Mar 2026", count: marchActual }, // ✅ EXACT match with current usage
    ];
  },

  getBillingPlans(): API.BillingPlan[] {
    return [
      {
        name: "Sandbox",
        planKey: "sandbox",
        priceMonthly: 0,
        priceAnnual: 0,
        description: "For evaluation and testing. Up to 10,000 traces/month.",
        features: ["10,000 traces/month", "Audit trail", "Policy engine", "Community support"],
      },
      {
        name: "Scale",
        planKey: "scale",
        priceMonthly: 299,
        priceAnnual: 2868,
        description: "For production teams. Unlimited traces, webhooks, exports.",
        features: ["Unlimited traces", "Webhooks & alerts", "CSV/PDF export", "Audit log", "Slack integration", "Email support"],
      },
      {
        name: "Governance",
        planKey: "governance",
        priceMonthly: 799,
        priceAnnual: 7668,
        description: "For compliance-critical orgs. SSO, scheduled reports, custom retention.",
        features: ["Everything in Scale", "SSO / SAML", "Scheduled reports", "Custom retention (up to 365 days)", "Priority support"],
      },
      {
        name: "Enterprise",
        planKey: "enterprise",
        priceMonthly: 0,
        priceAnnual: 0,
        description: "For large enterprises. Decision mining, 99.99% SLA, dedicated support.",
        features: ["Everything in Governance", "Decision mining", "CPI feedback ingest", "99.99% uptime SLA", "Dedicated CSM", "Custom contract"],
      },
      {
        name: "Custom",
        planKey: "custom",
        priceMonthly: 0,
        priceAnnual: 0,
        description: "Negotiated contract with custom terms, SLAs, and volume pricing.",
        features: ["All Enterprise features", "Custom volume pricing", "Custom SLA", "Dedicated infrastructure", "24/7 support"],
      },
    ];
  },

  getDecisionPatterns(params?: { days?: number; agentId?: string }) {
    const days = params?.days ?? 30;
    // Die 6 Nordlicht-Systeme; `total` skaliert das 30-Tage-Volumen auf das
    // gewählte Fenster. Pricing (degraded) hat die höchste Flag-/Block-Quote.
    const agents = [
      { id: "agent_fraud",    name: "Fraud Detector",               total: Math.round(MASTER_AGENTS[0].traffic * days / 30), successRate: 0.92, flagRate: 0.06, blockRate: 0.02 },
      { id: "agent_uw_leben", name: "Underwriting Assistent Leben", total: Math.round(MASTER_AGENTS[1].traffic * days / 30), successRate: 0.90, flagRate: 0.08, blockRate: 0.01 },
      { id: "agent_doc",      name: "Dokument-Klassifizierer",      total: Math.round(MASTER_AGENTS[2].traffic * days / 30), successRate: 0.96, flagRate: 0.03, blockRate: 0.01 },
      { id: "agent_pricing",  name: "Pricing Engine Komposit",      total: Math.round(MASTER_AGENTS[3].traffic * days / 30), successRate: 0.85, flagRate: 0.11, blockRate: 0.03 },
      { id: "agent_chat",     name: "Kunden-Chat-Assistent",        total: Math.round(MASTER_AGENTS[4].traffic * days / 30), successRate: 0.94, flagRate: 0.04, blockRate: 0.01 },
    ].map(a => ({
      agentId: a.id,
      agentName: a.name,
      total: a.total,
      success: Math.round(a.total * a.successRate),
      flagged: Math.round(a.total * a.flagRate),
      blocked: Math.round(a.total * a.blockRate),
      escalated: Math.round(a.total * 0.01),
    }));

    const byStatus = agents.reduce((acc, a) => ({
      success:   acc.success   + a.success,
      flagged:   acc.flagged   + a.flagged,
      blocked:   acc.blocked   + a.blocked,
      escalated: acc.escalated + a.escalated,
    }), { success: 0, flagged: 0, blocked: 0, escalated: 0 });

    const total = agents.reduce((sum, a) => sum + a.total, 0);

    const byDay = Array.from({ length: days }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - i));
      const date = d.toISOString().split('T')[0];
      const dayTotal = Math.round(total / days * (0.8 + Math.random() * 0.4));
      const s = Math.round(dayTotal * 0.87);
      const f = Math.round(dayTotal * 0.09);
      const b = Math.round(dayTotal * 0.03);
      return { date, total: dayTotal, success: s, flagged: f, blocked: b, escalated: Math.round(dayTotal * 0.01) };
    });

    return { total, byStatus, byAgent: agents, byDay };
  },

  async getFounderAnalytics(): Promise<API.FounderAnalytics> {
    await delay(600);
    const months = ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'];
    const funnelMock: API.FunnelStep[] = [
      { label: 'Total Signups', value: 847, stage: 'signup' },
      { label: 'Organizations', value: 312, stage: 'org' },
      { label: 'Created Agent', value: 198, stage: 'agent' },
      { label: 'Sent First Trace', value: 156, stage: 'activated', isActivation: true },
      { label: 'Created Policy', value: 112, stage: 'policy' },
      { label: 'Completed Review', value: 87, stage: 'review' },
      { label: 'Active Last 30d', value: 89, stage: 'retained' },
    ];
    const dropOffsMock: API.FunnelDropOff[] = [];
    for (let i = 1; i < funnelMock.length; i++) {
      const prev = funnelMock[i - 1].value;
      const cur = funnelMock[i].value;
      const pct = prev > 0 ? Math.round(((prev - cur) / prev) * 100) : 0;
      dropOffsMock.push({
        from: funnelMock[i - 1].stage ?? '',
        to: funnelMock[i].stage ?? '',
        fromLabel: funnelMock[i - 1].label,
        toLabel: funnelMock[i].label,
        lost: Math.max(0, prev - cur),
        dropOffPct: Math.max(0, pct),
        conversionPct: Math.max(0, 100 - pct),
      });
    }
    const biggestDropOffMock = dropOffsMock.reduce<API.FunnelDropOff | null>(
      (max, d) => (!max || d.dropOffPct > max.dropOffPct ? d : max),
      null
    );
    return {
      funnel: funnelMock,
      activationRate: 50, // 156 / 312 ≈ 50%
      dropOffs: dropOffsMock,
      biggestDropOff: biggestDropOffMock,
      cohorts: months.map((m, i) => ({
        month: m,
        size: [42, 56, 63, 71, 48, 32][i],
        week1: [88, 84, 91, 87, 83, 90][i],
        week2: [72, 69, 76, 74, 70, null][i],
        week4: [58, 54, 62, 60, null, null][i],
        week8: [41, 38, 45, null, null, null][i],
        week12: [33, 31, null, null, null, null][i],
      })),
      engagement: { dau: 34, wau: 67, mau: 89 },
      activityTrend: Array.from({ length: 30 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (29 - i));
        return {
          date: d.toISOString().slice(0, 10),
          activeOrgs: Math.floor(25 + Math.random() * 20 + (i * 0.3)),
        };
      }),
      featureAdoption: [
        { feature: 'Traces Ingested', adopters: 156, total: 312 },
        { feature: 'Policies Created', adopters: 112, total: 312 },
        { feature: 'Reviews Completed', adopters: 87, total: 312 },
        { feature: 'Webhooks Configured', adopters: 64, total: 312 },
        { feature: 'Alerts Configured', adopters: 43, total: 312 },
      ],
      npsTrend: months.map((m, i) => ({
        month: m,
        nps: [32, 38, 41, 45, 48, 52][i],
        responses: [18, 24, 31, 27, 22, 14][i],
      })),
      revenue: {
        mrr: 12_450,
        mrrChange: 8.3,
        churnRate: 3.2,
        ltv: 2_840,
        arpu: 39.90,
        note: 'Demo data — connect Stripe for live metrics.',
      },
      generatedAt: new Date().toISOString(),
    };
  },

  // ── In-App Nudges (#7 Activation — Block D) ─────────────────────────
  // Demo shows the "traces_no_policy" nudge because demo state has traces
  // flowing but the user hasn't created a policy yet — the single most
  // common activation gap.
  getActiveNudges() {
    return {
      nudges: [
        {
          id: 'traces_no_policy' as const,
          title: 'Add your first policy',
          body: 'You have traces flowing but no policies. Policies decide what ADJUDON blocks, flags, or approves — without one, nothing is being enforced.',
          cta: 'Create Policy',
          href: '/dashboard/policies',
          severity: 'warning' as const,
        },
      ],
      state: {
        agentCount: 3,
        traceCount: 42,
        policyCount: 0,
        reviewedCount: 0,
        teamMemberCount: 1,
      },
    };
  },

  // ── Phase 1 — Hash-Chain Audit ──────────────────────────────────────
  // Demo-Persona „Nordlicht Versicherung AG" (~9 Monate Nutzung, ~1.100 Entsch./Tag):
  // ~178.400 versiegelte Einträge. Letzter Anker vor ~2 h, Verify vor ~1 h OK.
  async getHashChainStatus() {
    return {
      totalEntries: 178400,
      lastSequence: 178400,
      lastChainHash: 'a3f1c8e4d2b9'.repeat(5).slice(0, 64),
      lastEntryAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
      anchorCount: 271,
      lastAnchorAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      lastVerifiedAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      lastVerificationOk: true,
      algorithm: 'sha256' as const,
      canonicalization: 'rfc8785' as const,
      appendLatencyP95Ms: 3.4,
      appendLatency: {
        sampleSize: 1024,
        p50Ms: 1.7,
        p95Ms: 3.4,
        p99Ms: 6.1,
        reason: null,
      },
    };
  },

  async verifyHashChain() {
    return {
      ok: true,
      totalChecked: 178400,
      brokenAtSequence: null,
      brokenReason: null,
      durationMs: 4820,
      verifiedAt: new Date().toISOString(),
    };
  },

  // Lücken-Bericht — bewusst MIT den 11 historischen Lücken der „Fail-Open-Nacht"
  // (18.02.2026, Vorfall #3), damit der Demo-Viewer das Alleinstellungsmerkmal
  // sieht: Lücken werden OFFEN ausgewiesen, nicht versteckt. Zahlen konsistent zu
  // getHashChainStatus (178.400 versiegelt): 178.408 Entscheidungen passiert → 8
  // Fail-Open-Lücken (unsealedDecisions = decisionTraces − sealedEntries); Sequenz-
  // Zähler 178.403 → 3 Insert-Fehler-Lücken (missingSequences = allocatedSequences
  // − sealedEntries). Summe 11 Lücken, complete=false. coveragePct 99,9 (1-Dezimal-
  // UI-Vertrag; Story-Headline „99,97 %" — beides „nahezu vollständig, NICHT 100 %").
  async getHashChainCoverage() {
    return {
      organizationId: 'org_demo',
      decisionTraces: 178408,
      sealedEntries: 178400,
      allocatedSequences: 178403,
      unsealedDecisions: 8,
      missingSequences: 3,
      coveragePct: 99.9,
      complete: false,
      gapSequences: [40221, 40233, 40238],
      gapSequencesTruncated: false,
      generatedAt: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    };
  },

  async listHashChainEntries(params?: { limit?: number; cursor?: string }) {
    const limit = params?.limit ?? 50;
    // Deterministic FNV-1a derived 64-char hex hash from a seed —
    // produces realistic-looking SHA-256-shaped strings so prefix search
    // in the dashboard actually returns hits in mock mode.
    const seedHash = (seed: string): string => {
      let h = 0x811c9dc5;
      for (let i = 0; i < seed.length; i += 1) {
        h = Math.imul(h ^ seed.charCodeAt(i), 0x01000193) >>> 0;
      }
      const hex8 = (h.toString(16) + '00000000').slice(0, 8);
      return hex8.repeat(8).slice(0, 64);
    };
    const entries = Array.from({ length: limit }).map((_, i) => {
      const seq = 178400 - i;
      // Pending = no Merkle anchor yet. The cron rolls up anchors at
      // 00:05 UTC, so the most-recent ~5% of entries (today's traces)
      // are typically still pending. Reflecting that in mock makes the
      // "Pending" filter pill demonstrable on the Entries & Anchors tab.
      const isPending = i < Math.max(1, Math.floor(limit * 0.05));
      return {
        id: `hc_${seq}`,
        organizationId: 'org_demo',
        sequence: seq,
        prevHash: i === limit - 1 ? null : seedHash(`prev-${seq - 1}`),
        payloadHash: seedHash(`pl-${seq}`),
        chainHash: seedHash(`ch-${seq}`),
        traceId: `trc_${seq}`,
        // Kein decisionId: die Live-HashChainEntry projiziert keinen Verdikt
        // (der liegt am DecisionTrace) — Mock-Shape an Backend angeglichen.
        eidasTimestamp: i % 100 === 0 ? {
          value: 'MIIH...mock-tsa-token...==',
          issuer: 'CN=Mock TSA, O=ADJUDON DEV',
          issuedAt: new Date(Date.now() - i * 60 * 1000).toISOString(),
          serial: `0x${seq.toString(16).padStart(16, '0')}`,
        } : null,
        merkleAnchorId: isPending ? null : `anc_${Math.floor(seq / 100)}`,
        createdAt: new Date(Date.now() - i * 60 * 1000).toISOString(),
      };
    });
    // hasMore wie das Backend: voll gefüllte Seite UND noch tiefere Sequenzen vorhanden.
    const lowest = entries.length ? entries[entries.length - 1].sequence : 0;
    return {
      entries,
      hasMore: entries.length === limit && lowest > 1,
      nextCursor: entries.length === limit ? `cursor_${lowest}` : null,
    };
  },

  async getHashChainEntry(id: string) {
    return {
      id,
      organizationId: 'org_demo',
      sequence: 178400,
      prevHash: 'prev'.padEnd(64, '0'),
      payloadHash: 'payload'.padEnd(64, '0'),
      chainHash: 'chain'.padEnd(64, '0'),
      traceId: 'trc_demo',
      decisionId: 'approved',
      canonicalPayload: {
        traceId: 'trc_demo',
        organizationId: 'org_demo',
        agentId: 'agent_fraud',
        decision: 'approved',
        confidence: 0.94,
        timestamp: new Date().toISOString(),
      },
      eidasTimestamp: {
        value: 'MIIH...mock-tsa-token...==',
        issuer: 'CN=Mock TSA, O=ADJUDON DEV',
        issuedAt: new Date().toISOString(),
        serial: '0x000000000000abcd',
      },
      merkleAnchorId: 'anc_128',
      createdAt: new Date().toISOString(),
    };
  },

  async getHashChainEntryByTrace(traceId: string) {
    const entry = await this.getHashChainEntry(traceId);
    const proof = await this.getMerkleProof(entry.merkleAnchorId || 'anc_demo', entry.sequence);
    return { entry: { ...entry, traceId }, proof };
  },

  async listMerkleAnchors(params?: { limit?: number }) {
    const limit = params?.limit ?? 30;
    return Array.from({ length: limit }).map((_, i) => {
      const day = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      const dateStr = day.toISOString().slice(0, 10);
      return {
        id: `anc_${92 - i}`,
        organizationId: 'org_demo',
        anchorDate: dateStr,
        merkleRoot: `mr_${dateStr}`.padEnd(64, '0'),
        entryCount: 142 - i * 2,
        firstSequence: 12847 - (i + 1) * 142,
        lastSequence: 12847 - i * 142,
        algorithm: 'sha256' as const,
        eidasTimestamp: {
          value: 'MIIH...mock-tsa-anchor-token...==',
          issuer: 'CN=Mock TSA, O=ADJUDON DEV',
          issuedAt: new Date(day.getTime() + 5 * 60 * 1000).toISOString(),
          serial: `0x${(92 - i).toString(16).padStart(16, '0')}`,
        },
        createdAt: new Date(day.getTime() + 5 * 60 * 1000).toISOString(),
      };
    });
  },

  async getMerkleProof(anchorId: string, sequence: number) {
    // Build a cryptographically valid Merkle proof so client-side verification
    // succeeds in demo mode. 8 deterministic leaves, depth 3 path.
    const LEAF_COUNT = 8;
    const targetIndex = ((sequence % LEAF_COUNT) + LEAF_COUNT) % LEAF_COUNT;

    const hexToBytes = (hex: string): Uint8Array => {
      const out = new Uint8Array(hex.length / 2);
      for (let i = 0; i < hex.length; i += 2) out[i / 2] = parseInt(hex.slice(i, i + 2), 16);
      return out;
    };
    const bytesToHex = (bytes: Uint8Array): string =>
      Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    const sha256 = async (input: Uint8Array): Promise<string> => {
      // Copy into a fresh ArrayBuffer-backed view so the argument satisfies
      // BufferSource (TS 5.7 typed-array generic narrows to ArrayBuffer here).
      const bytes = new Uint8Array(input);
      const buf = await crypto.subtle.digest('SHA-256', bytes.buffer);
      return bytesToHex(new Uint8Array(buf));
    };
    const sha256Str = (s: string): Promise<string> => sha256(new TextEncoder().encode(s));
    const concat = async (left: string, right: string): Promise<string> => {
      const l = hexToBytes(left);
      const r = hexToBytes(right);
      const merged = new Uint8Array(l.length + r.length);
      merged.set(l, 0);
      merged.set(r, l.length);
      return sha256(merged);
    };

    const leaves: string[] = [];
    for (let i = 0; i < LEAF_COUNT; i++) {
      leaves.push(await sha256Str(`${anchorId}:leaf:${i}`));
    }

    const path: Array<{ position: 'left' | 'right'; hash: string }> = [];
    let level = leaves;
    let idx = targetIndex;
    while (level.length > 1) {
      const isLeft = idx % 2 === 0;
      const siblingIdx = isLeft ? idx + 1 : idx - 1;
      path.push({ position: isLeft ? 'right' : 'left', hash: level[siblingIdx] });
      const next: string[] = [];
      for (let i = 0; i < level.length; i += 2) {
        next.push(await concat(level[i], level[i + 1]));
      }
      level = next;
      idx = Math.floor(idx / 2);
    }

    return {
      merkleRoot: level[0],
      leafHash: leaves[targetIndex],
      leafSequence: sequence,
      path,
    };
  },

  async exportHashChainBundle(params: { fromSequence?: number; toSequence?: number; format?: 'json' | 'jsonl' }) {
    return {
      format: (params.format ?? 'json') as 'json' | 'jsonl',
      entryCount: (params.toSequence ?? 12847) - (params.fromSequence ?? 1) + 1,
      fromSequence: params.fromSequence ?? 1,
      toSequence: params.toSequence ?? 12847,
      generatedAt: new Date().toISOString(),
      bundleHash: 'bundle'.padEnd(64, '0'),
    };
  },

  // ── Phase 1 — Compliance Mapping ────────────────────────────────────
  async getComplianceMapping(_framework: string) {
    return {
      organizationId: 'org_demo',
      framework: 'iso-42001' as const,
      coverage: { covered: 18, partial: 6, gap: 3, notApplicable: 2, total: 29, coveragePct: 62 },
      notes: [
        { clauseId: 'A.6.2.4', status: 'covered' as const, evidence: 'Hash-Chain Audit Log + DecisionLog UI', reviewedAt: new Date(Date.now() - 7 * 86400000).toISOString(), reviewedBy: 'compliance@demo' },
        { clauseId: 'A.6.2.6', status: 'covered' as const, evidence: 'Review Queue UI + 4-eyes sign-off', reviewedAt: new Date(Date.now() - 14 * 86400000).toISOString(), reviewedBy: 'compliance@demo' },
        { clauseId: 'A.7.4', status: 'partial' as const, evidence: 'Confidence engine in place; explainability docs WIP', reviewedAt: null, reviewedBy: null },
        { clauseId: 'A.8.3', status: 'gap' as const, evidence: 'Need DPIA template', reviewedAt: null, reviewedBy: null },
      ],
      updatedAt: new Date().toISOString(),
    };
  },

  async updateComplianceMappingNote(payload: any) {
    return {
      clauseId: payload.clauseId,
      status: payload.status,
      evidence: payload.evidence,
      reviewedAt: new Date().toISOString(),
      reviewedBy: 'demo@adjudon.com',
    };
  },

  // ── Phase 2 — Incidents (Multi-Clock) ───────────────────────────────
  async listIncidents() {
    // Demo-Persona „Nordlicht Versicherung AG": 7 Vorfälle über ~9 Monate.
    // 5 geschlossen (#1–#5) + 2 aktiv (#6 investigating · #7 mitigated). Genau
    // 1 überfällige Frist (#7 gdpr-Abschlussbericht), 0 unklassifiziert.
    // SSOT: docs/DEMO_PERSONA.md.
    return PERSONA_INCIDENT_IDS.map((id) => buildPersonaIncident(id));
  },
  async getIncident(id: string) {
    // Bekannte Persona-IDs liefern den passenden Vorfall; sonst generischer Fallback.
    return PERSONA_INCIDENTS[id] ? buildPersonaIncident(id) : mockIncident(id, 'high', 'investigating', -2 * 3600000);
  },
  async createIncident(payload: any) {
    // Mock-Paritaet: title/regulators des Nutzers uebernehmen, nicht verwerfen.
    const base = mockIncident(`inc_${Date.now()}`, payload.severity ?? 'medium', 'open', 0);
    return { ...base, ...(payload.title ? { title: payload.title } : {}), ...(payload.regulators ? { regulators: payload.regulators } : {}) };
  },
  async updateIncident(id: string, payload: any) {
    const base = PERSONA_INCIDENTS[id] ? buildPersonaIncident(id) : mockIncident(id, 'high', 'investigating', -2 * 3600000);
    return { ...base, ...payload, id };
  },
  async recordIncidentRegulatorSubmission(id: string, code: string, payload: any) {
    // Backend-Guard-Spiegel: 404 wenn der Regulator nicht am Vorfall hängt
    // (abgeleitet aus den Uhren des Persona-Vorfalls; #7 trägt die gdpr-Uhr).
    const inc: API.Incident = PERSONA_INCIDENTS[id] ? buildPersonaIncident(id) : (mockIncident(id, 'high', 'investigating', -2 * 3600000) as unknown as API.Incident);
    const DASH: Record<string, string> = { gdpr: 'gdpr-art-33', aiact: 'ai-act-art-73', dora: 'dora-art-19', nis2: 'nis2-art-23', cra: 'cra-art-14' };
    const dash = code.includes('-art-') ? code : DASH[code] ?? code;
    const onIncident = (inc.clocks ?? []).some((c: any) => String(c.regulator) === dash);
    if (!onIncident) throw this._vvgErr('NOT_FOUND');
    return {
      ...inc,
      regulators: [
        ...(inc.regulators ?? []),
        {
          code: dash,
          name: null,
          contactEmail: null,
          submittedAt: payload.submittedAt ?? new Date().toISOString(),
          submissionRef: payload.submissionRef ?? null,
          authorityCaseId: payload.authorityCaseId ?? null,
          responseReceived: false,
          responseAt: null,
        },
      ],
    };
  },
  async closeIncident(id: string, payload: any) {
    const base = PERSONA_INCIDENTS[id] ? buildPersonaIncident(id) : mockIncident(id, 'high', 'closed', -2 * 3600000);
    return { ...base, ...payload, closedAt: new Date().toISOString(), status: 'closed' as const };
  },
  async completeIncidentCheckpoint(incidentId: string, _checkpointId: string, _payload: any) {
    const base = PERSONA_INCIDENTS[incidentId] ? buildPersonaIncident(incidentId) : mockIncident(incidentId, 'high', 'investigating', -2 * 3600000);
    return base.clocks[0];
  },

  // ── DORA-Hub Mocks (Art. 8 / 17–19 / 28–30) — Shapes spiegeln die echten
  // Backend-Verträge (ictAssetRegisterService · classification · CyberThreat ·
  // doraRegisterController). Demo-Stores sind Objekt-Properties (Session-only).

  async getIctAssetRegister(): Promise<API.IctAssetRegisterResponse> {
    await delay(300);
    const fn = (functionId: string, name: string, purpose: string, provider: string, model: string, version: string, status: API.IctOperationalStatus, risk: string | null, elevated: boolean, reason: string, registeredAt: string, lastActiveMinutesAgo: number): API.IctAssetFunction => ({
      functionId,
      name,
      purpose,
      assetType: 'ai-decision-system' as const,
      ictComponent: { provider, model, version, outputType: 'decision', declaredRiskClass: risk },
      operationalStatus: status,
      adjudonCriticalitySignal: elevated ? ('elevated' as const) : ('standard' as const),
      criticalitySignalReason: reason,
      criticalityClassification: 'to-be-classified' as const,
      ictThirdPartyDependency: 'Adjudon',
      registeredAt,
      lastActive: status === 'inactive'
        ? new Date(Date.now() - 6 * 86400000).toISOString()
        : new Date(Date.now() - lastActiveMinutesAgo * 60 * 1000).toISOString(),
    });
    return {
      register: {
        generatedAt: new Date().toISOString(),
        article: 'DORA Art. 8 — Identifizierung',
        regulation: 'VO (EU) 2022/2554 + Del. VO (EU) 2024/1774',
        customerOrg: { id: 'org_demo', name: 'Acme Corporation', role: 'Finanzunternehmen (Art. 2)' },
        ictThirdParty: {
          primary: { name: 'Adjudon', role: 'IKT-Drittdienstleister (Art. 28)', residency: 'Frankfurt (eu-central-1)', supportsFunction: 'Audit-/Nachweis-Schicht der KI-Entscheidungen' },
          subProcessors: [
            { name: 'MongoDB Atlas', purpose: 'Primärdatenbank', location: 'EU (Frankfurt)', isEU: true },
            { name: 'Fly.io', purpose: 'Compute', location: 'EU (Frankfurt)', isEU: true },
          ],
          subProcessorCount: 2,
          note: 'Rang-2-Unterauftragskette der Adjudon-Schicht (Art. 8(5)).',
        },
        functions: [
          fn('agent_fraud',    'Fraud Detector',               'Schaden-Betrugserkennung',   'Eigenbetrieb (Frankfurt)', 'GBM-Gradient-Boosting v3', 'v3.1.0', 'healthy',  'high-risk', true,  'Deklarierte Risikoklasse hoch (Annex III)', '2025-09-18T10:00:00Z', 14),
          fn('agent_uw_leben', 'Underwriting Assistent Leben', 'Risikoprüfung Leben',        'Eigenbetrieb (Frankfurt)', 'Tabular-Klassifikator v2', 'v2.2.0', 'healthy',  'high-risk', true,  'Deklarierte Risikoklasse hoch (Annex III)', '2025-10-12T10:00:00Z', 22),
          fn('agent_pricing',  'Pricing Engine Komposit',      'Tarifierung Komposit',       'Eigenbetrieb (Frankfurt)', 'GLM-Tarifmodell v2',       'v2.0.0', 'degraded', 'high-risk', true,  'Deklarierte Risikoklasse hoch + Betriebsstatus beeinträchtigt', '2026-01-08T10:00:00Z', 9),
          fn('agent_doc',      'Dokument-Klassifizierer',      'Posteingangs-Triage',        'Eigenbetrieb (Frankfurt)', 'Text-/Layout-Klassifikator', 'v1.3.0', 'healthy', 'minimal',   false, 'Keine erhöhten Signale', '2025-11-20T10:00:00Z', 5),
          fn('agent_chat',     'Kunden-Chat-Assistent',        'Service-FAQ',                'Azure OpenAI (EU)',        'GPT-4o-mini',              'v1.1.0', 'healthy',  'limited',   false, 'Keine erhöhten Signale', '2026-02-03T10:00:00Z', 3),
          fn('agent_churn',    'Churn-Prädiktor',              'Bestands-/Churn-Analyse',    'Eigenbetrieb (Frankfurt)', 'Logistische Regression',   'v0.9.0', 'inactive', null,        true,  'Risikoklasse noch nicht klassifiziert — Einstufung offen', '2026-05-14T10:00:00Z', 0),
        ],
        summary: {
          totalFunctions: 6,
          elevatedSignal: 4,
          byOperationalStatus: { healthy: 4, degraded: 1, critical: 0, inactive: 1 },
          subProcessors: 2,
          classificationPending: 6,
        },
        controllerDuties: [
          { key: 'dora_art8_asset_identification', article: 'Art. 8(1)/(4)' },
          { key: 'dora_ict_risk_framework', article: 'Art. 5–6' },
        ],
        disclaimer: 'Die kritisch/wichtig-Einstufung (Art. 8(1)/(4)) ist die nicht-delegierbare Entscheidung des Finanzunternehmens. Adjudon liefert das Register + ein nicht-bindendes Signal.',
      },
    };
  },

  async getIctAssetRegisterMarkdown() {
    await delay(200);
    const reg = (await this.getIctAssetRegister()).register;
    const rows = reg.functions.map((f: any) => `| ${f.name} | ${f.purpose} | ${f.operationalStatus} | ${f.adjudonCriticalitySignal} |`).join('\n');
    return {
      filename: 'dora-art8-ict-asset-register.md',
      markdown: `# DORA Art. 8 — ICT-Asset-Register (KI-Schicht)\n\nOrganisation: ${reg.customerOrg.name}\nStand: ${reg.generatedAt}\n\n| Funktion | Zweck | Betriebsstatus | Signal |\n|---|---|---|---|\n${rows}\n\n> ${reg.disclaimer}\n`,
    };
  },

  // Art. 18 — spiegelt die RTS-2024/1772-Schwellen des Backends (NaN-sicher),
  // damit der Demo-Assistent ECHT rechnet statt zu raten.
  async classifyDoraIncident(criteria: any) {
    await delay(400);
    const c = criteria || {};
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const yes = (v: any) => v === true;
    const gateConditions = {
      critical_or_important_function_affected: yes(c.criticalOrImportantFunctionAffected) || yes(c.criticalServicesAffected),
      authorised_financial_service_affected: yes(c.authorisedServiceAffected),
      malicious_unauthorised_access: yes(c.maliciousUnauthorisedAccess),
    };
    const gate = Object.values(gateConditions).some(Boolean);
    const crit = {
      clients_counterparts_transactions: {
        met:
          num(c.clientsPctAffected) > 10 || num(c.clientsAffected) > 100000 || num(c.counterpartsPctAffected) > 30 ||
          num(c.txnNumberPctAffected) > 10 || num(c.txnValuePctAffected) > 10 || yes(c.relevantClientsOrCounterpartsAffected),
        article: 'RTS Art. 1+9(1)',
      },
      reputational_impact: { met: yes(c.reputationalImpact), article: 'RTS Art. 2+9(2)' },
      duration_service_downtime: {
        met: num(c.durationHours) > 24 || num(c.downtimeHours) > 2,
        article: 'RTS Art. 3+9(3)',
        durationHours: num(c.durationHours),
        downtimeHours: num(c.downtimeHours),
        durationThresholdMet: num(c.durationHours) > 24,
        downtimeThresholdMet: num(c.downtimeHours) > 2,
      },
      geographical_spread: { met: num(c.memberStatesAffected) >= 2, article: 'RTS Art. 4+9(4)', memberStatesAffected: num(c.memberStatesAffected) },
      data_losses: { met: yes(c.dataLossImpact) || yes(c.maliciousUnauthorisedAccess), article: 'RTS Art. 5+9(5)', impactOnDataSecurity: yes(c.dataLossImpact), maliciousUnauthorisedAccess: yes(c.maliciousUnauthorisedAccess) },
      economic_impact: { met: num(c.economicImpactEur) > 100000 || yes(c.economicImpactLikelyExceeds), article: 'RTS Art. 6+9(6)', economicImpactEur: num(c.economicImpactEur) },
    };
    const thresholdsMet = Object.values(crit).filter((x: any) => x.met).length;
    const byMalicious = gateConditions.critical_or_important_function_affected && gateConditions.malicious_unauthorised_access;
    const byTwo = gate && thresholdsMet >= 2;
    const isMajor = byMalicious || byTwo;
    const reasoning: string[] = [];
    reasoning.push(gate
      ? 'Art.-6-Gate erfüllt: kritische/wichtige Funktion bzw. zugelassener Finanzdienst betroffen.'
      : 'Art.-6-Gate NICHT erfüllt: keine kritische/wichtige Funktion betroffen — Schwellen greifen nicht.');
    Object.entries(crit).forEach(([k, v]: [string, any]) => {
      if (v.met) reasoning.push(`Kriterium erfüllt: ${k} (${v.article}).`);
    });
    if (byMalicious) reasoning.push('Art. 8(1)(a): böswilliger unbefugter Zugriff auf kritische Funktion → major.');
    if (byTwo) reasoning.push(`Art. 8(1)(b): ${thresholdsMet} von 6 Schwellen erfüllt (≥ 2) → major.`);
    if (!isMajor) reasoning.push('Empfehlung non-major: weder Art. 8(1)(a) noch ≥ 2 Schwellen (Art. 8(1)(b)).');
    return {
      framework: 'dora' as const,
      article: 'Art. 18 + Del. VO (EU) 2024/1772',
      assessmentType: 'incident' as const,
      criticality: { impacted: gate, article: 'RTS Art. 6', conditions: gateConditions },
      criteria: crit,
      thresholdsMet,
      majorTriggers: { byMaliciousAccess_8_1_a: byMalicious, byTwoOrMoreThresholds_8_1_b: byTwo },
      isMajorRecommended: isMajor,
      recommendation: isMajor ? ('major' as const) : ('non-major' as const),
      recurring: { applies: false, occurrencesInSixMonths: 0, sameRootCause: false, note: '' },
      reasoning,
      disclaimer: 'Entscheidungs-Unterstützung — die Einstufung (Art. 18) trifft das Finanzunternehmen.',
    };
  },

  async listDpaRegistry(): Promise<{ dpas: API.DpaRegistryEntry[] }> {
    await delay(200);
    return {
      dpas: [
        { code: 'de-bfdi', name: 'BfDI — Bundesbeauftragter für den Datenschutz', country: 'de', region: 'Federal', submissionUrl: 'https://www.bfdi.bund.de/meldung', email: 'poststelle@bfdi.bund.de', language: 'de' },
        { code: 'de-nrw', name: 'LDI Nordrhein-Westfalen', country: 'de', region: 'NRW', submissionUrl: 'https://www.ldi.nrw.de/meldung', email: 'poststelle@ldi.nrw.de', language: 'de' },
        { code: 'de-by', name: 'BayLDA Bayern', country: 'de', region: 'BY', submissionUrl: 'https://www.lda.bayern.de/meldung', email: null, language: 'de' },
        { code: 'at-dsb', name: 'Österreichische Datenschutzbehörde', country: 'at', region: 'National', submissionUrl: 'https://www.dsb.gv.at/meldung', email: 'dsb@dsb.gv.at', language: 'de' },
      ],
    };
  },
  async getDpaIncidentTemplate(incidentId: string, dpaCode: string) {
    await delay(300);
    return `# DSGVO Art. 33 — Meldung einer Verletzung des Schutzes personenbezogener Daten

Vorfall: ${incidentId}
Behörde: ${dpaCode}

## 1. Art der Verletzung
[ausfüllen]

## 2. Kategorien und ungefähre Zahl der Betroffenen
[ausfüllen]

## 3. Wahrscheinliche Folgen
[ausfüllen]

## 4. Ergriffene Maßnahmen
[ausfüllen]

> Entwurf — vor Übermittlung mit dem DSB prüfen. Adjudon übermittelt nicht.`;
  },
  async getDoraIncidentReport(incidentId: string, stage: string) {
    await delay(400);
    const STAGE_TITLES: Record<string, string> = {
      initial: 'Erstmeldung (≤ 4 h / ≤ 24 h)',
      intermediate: 'Zwischenmeldung (≤ 72 h)',
      final: 'Abschlussmeldung (≤ 1 Monat nach Zwischenmeldung)',
      'client-notice': 'Kunden-Benachrichtigung (Art. 19(3))',
      'cyber-threat': 'Freiwillige Cyber-Bedrohungs-Meldung (Art. 19(2))',
    };
    return `# DORA Art. 19 — ${STAGE_TITLES[stage] ?? stage}\n\nVorfall: ${incidentId}\nMeldeweg: BaFin MVP-Portal, Fachverfahren „DORA"\nGrundlage: RTS (EU) 2025/301 · Formulare: ITS (EU) 2025/302\n\n> Vorbefüllter Entwurf (Demo). Impact-Ist-Zahlen, Einstufung und Übermittlung verantwortet das Finanzunternehmen (Art. 19(5)).\n`;
  },

  _cyberThreatStore: null as null | API.CyberThreatRecord[],
  _cyberThreatSeed(): API.CyberThreatRecord[] {
    if (!this._cyberThreatStore) {
      this._cyberThreatStore = [
        {
          id: 'ct_001',
          organizationId: 'org_demo',
          title: 'Phishing-Kampagne gegen Underwriting-Postfächer',
          summary: 'Gezielte Mails mit gefälschten Makler-Absendern; Ziel sind Zugangsdaten der Antragsstrecke.',
          source: 'BSI CERT-Bund Warnung',
          externalRef: 'CB-K26/0412',
          detectedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
          relatedAgentIds: ['agent_uw_leben'],
          assessment: {
            assessedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
            couldAffectCriticalFunctions: true,
            highProbabilityOfMaterialisation: true,
            couldMeetMaterialityThresholds: false,
            recommendation: 'not-significant',
            assessedByEmail: 'ciso@nordlicht.example',
          },
          decision: 'not-significant',
          voluntaryNotification: { decided: false, notifiedAt: null, reference: null },
          status: 'assessed',
          reportedBy: 'ciso@nordlicht.example',
          closedAt: null,
          createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
          updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
        },
        {
          // Historisch signifikant eingestuft → freiwillig an BaFin gemeldet (Art. 19(2)).
          id: 'ct_003',
          organizationId: 'org_demo',
          title: 'Koordinierter Credential-Stuffing-Angriff auf Kundenportal',
          summary: 'Verteilte Login-Versuche aus Botnetz gegen das Versicherungsnehmer-Portal; WAF-Regeln verschärft, MFA erzwungen.',
          source: 'Eigene SOC-Erkennung',
          externalRef: 'SOC-2026-0118',
          detectedAt: new Date(Date.now() - 120 * 86400000).toISOString(),
          relatedAgentIds: [],
          assessment: {
            assessedAt: new Date(Date.now() - 119 * 86400000).toISOString(),
            couldAffectCriticalFunctions: true,
            highProbabilityOfMaterialisation: true,
            couldMeetMaterialityThresholds: true,
            recommendation: 'significant',
            assessedByEmail: 'ciso@nordlicht.example',
          },
          decision: 'significant',
          voluntaryNotification: { decided: true, notifiedAt: new Date(Date.now() - 118 * 86400000).toISOString(), reference: 'BaFin-DORA-2026-CT-0044' },
          status: 'closed',
          reportedBy: 'ciso@nordlicht.example',
          closedAt: new Date(Date.now() - 100 * 86400000).toISOString(),
          createdAt: new Date(Date.now() - 120 * 86400000).toISOString(),
          updatedAt: new Date(Date.now() - 100 * 86400000).toISOString(),
        },
        {
          id: 'ct_002',
          organizationId: 'org_demo',
          title: 'Kritische Schwachstelle in LLM-Gateway-Bibliothek',
          summary: 'CVE mit RCE-Potenzial in einer vorgelagerten Gateway-Komponente; Patch verfügbar.',
          source: 'Anbieter-Advisory',
          externalRef: 'CVE-2026-31337',
          detectedAt: new Date(Date.now() - 86400000).toISOString(),
          relatedAgentIds: [],
          assessment: null,
          decision: null,
          voluntaryNotification: { decided: false, notifiedAt: null, reference: null },
          status: 'open',
          reportedBy: 'ciso@nordlicht.example',
          closedAt: null,
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          updatedAt: new Date(Date.now() - 86400000).toISOString(),
        },
      ];
    }
    return this._cyberThreatStore;
  },

  async listCyberThreats(params?: any): Promise<API.CyberThreatListResponse> {
    await delay(300);
    let threats = [...this._cyberThreatSeed()];
    if (params?.status) threats = threats.filter((t) => t.status === params.status);
    return { threats };
  },

  async createCyberThreat(payload: any): Promise<API.CyberThreatRecord> {
    await delay(350);
    const t: API.CyberThreatRecord = {
      id: `ct_${Math.random().toString(36).slice(2, 8)}`,
      organizationId: 'org_demo',
      title: payload?.title ?? '',
      summary: payload?.summary ?? '',
      source: payload?.source ?? null,
      externalRef: payload?.externalRef ?? null,
      detectedAt: payload?.detectedAt ?? new Date().toISOString(),
      relatedAgentIds: payload?.relatedAgentIds ?? [],
      assessment: null,
      decision: null,
      voluntaryNotification: { decided: false, notifiedAt: null, reference: null },
      status: 'open',
      reportedBy: 'ciso@nordlicht.example',
      closedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._cyberThreatSeed().unshift(t);
    return t;
  },

  async assessCyberThreat(id: string, payload: any) {
    await delay(400);
    const a = payload?.couldAffectCriticalFunctions === true;
    const b = payload?.highProbabilityOfMaterialisation === true;
    const cc = payload?.couldMeetMaterialityThresholds === true;
    const significant = a && b && cc;
    const recommendation = significant ? 'significant' : 'not-significant';
    const assessment = {
      framework: 'dora' as const,
      article: 'Art. 18(2) + RTS Art. 10',
      assessmentType: 'cyber-threat' as const,
      conditions: {
        could_affect_critical_functions: a,
        high_probability_of_materialisation: b,
        could_meet_materiality_thresholds: cc,
      },
      isSignificantRecommended: significant,
      recommendation: recommendation as 'significant' | 'not-significant',
      reasoning: [
        a ? 'Bedrohung könnte kritische/wichtige Funktionen betreffen (RTS Art. 10(a)).' : 'Kritische Funktionen nicht betroffen (RTS Art. 10(a) nicht erfüllt).',
        b ? 'Hohe Eintrittswahrscheinlichkeit (RTS Art. 10(b)).' : 'Eintrittswahrscheinlichkeit nicht hoch (RTS Art. 10(b) nicht erfüllt).',
        cc ? 'Materialitäts-Schwellen könnten erreicht werden (RTS Art. 10(c)).' : 'Materialitäts-Schwellen voraussichtlich nicht erreicht (RTS Art. 10(c) nicht erfüllt).',
        significant ? 'Alle drei Bedingungen erfüllt → signifikant.' : 'Nicht alle drei Bedingungen erfüllt → nicht signifikant.',
      ],
      disclaimer: 'Entscheidungs-Unterstützung — die Bewertung trifft das Finanzunternehmen.',
    };
    const store = this._cyberThreatSeed();
    const idx = store.findIndex((t) => t.id === id);
    if (idx >= 0) {
      store[idx] = {
        ...store[idx],
        assessment: {
          assessedAt: new Date().toISOString(),
          couldAffectCriticalFunctions: a,
          highProbabilityOfMaterialisation: b,
          couldMeetMaterialityThresholds: cc,
          recommendation,
          assessedByEmail: 'ciso@nordlicht.example',
        },
        decision: payload?.decision ?? recommendation,
        status: 'assessed',
        updatedAt: new Date().toISOString(),
      };
      return { threat: store[idx], assessment };
    }
    // Backend: 404 NOT_FOUND wenn die Threat-ID nicht existiert — Mock spiegelt den Guard.
    throw this._vvgErr('NOT_FOUND');
  },

  async updateCyberThreat(id: string, payload: any) {
    await delay(300);
    const store = this._cyberThreatSeed();
    const idx = store.findIndex((t) => t.id === id);
    if (idx < 0) throw new Error('not found');
    store[idx] = {
      ...store[idx],
      ...(payload?.title !== undefined ? { title: payload.title } : {}),
      ...(payload?.summary !== undefined ? { summary: payload.summary } : {}),
      ...(payload?.source !== undefined ? { source: payload.source } : {}),
      ...(payload?.status !== undefined ? { status: payload.status, closedAt: payload.status === 'closed' ? new Date().toISOString() : store[idx].closedAt } : {}),
      ...(payload?.voluntaryNotification !== undefined ? { voluntaryNotification: { ...store[idx].voluntaryNotification, ...payload.voluntaryNotification } } : {}),
      updatedAt: new Date().toISOString(),
    };
    return store[idx];
  },

  _doraArrangementStore: null as null | any[],
  _doraArrangementSeed() {
    if (!this._doraArrangementStore) {
      this._doraArrangementStore = [
        {
          id: 'dra_001',
          arrangementRef: 'ADJ-SELF-001',
          status: 'active',
          isAdjudonSelf: true,
          providerName: 'Adjudon',
          providerCountryHq: 'DE',
          ictServiceType: 'Audit-/Nachweis-Schicht (KI-Entscheidungen)',
          supportsCriticalFunction: true,
          functionName: 'KI-Entscheidungs-Nachweis',
          governingLawCountry: 'DE',
          exitStrategy: { exists: true, tested: true },
          art29Assessment: { substitutability: 'medium_complexity' },
          subcontractors: [
            { name: 'MongoDB Atlas', rank: 2, country: 'DE' },
            { name: 'Fly.io', rank: 2, country: 'DE' },
          ],
        },
        {
          id: 'dra_002',
          arrangementRef: 'CC-2024-007',
          status: 'active',
          isAdjudonSelf: false,
          providerName: 'ClaimsCore Software GmbH',
          providerCountryHq: 'DE',
          ictServiceType: 'Kernsystem Schaden/Leistung',
          supportsCriticalFunction: true,
          functionName: 'Schadenbearbeitung',
          governingLawCountry: 'DE',
          exitStrategy: { exists: true, tested: false },
          art29Assessment: { substitutability: 'high_complexity' },
          subcontractors: [{ name: 'AWS Frankfurt', rank: 2, country: 'DE' }],
        },
        {
          id: 'dra_003',
          arrangementRef: 'AZ-2023-114',
          status: 'active',
          isAdjudonSelf: false,
          providerName: 'Microsoft Azure (Frankfurt)',
          providerCountryHq: 'US',
          ictServiceType: 'Cloud-Hosting (Nicht-Kern)',
          supportsCriticalFunction: false,
          functionName: 'Intranet/Kollaboration',
          governingLawCountry: 'DE',
          exitStrategy: {},
          art29Assessment: {},
          subcontractors: [],
        },
        {
          id: 'dra_004',
          arrangementRef: 'RZ-2022-031',
          status: 'active',
          isAdjudonSelf: false,
          providerName: 'NordRZ Rechenzentrum GmbH',
          providerCountryHq: 'DE',
          ictServiceType: 'Rechenzentrums-Betrieb (Kern-Hosting Bestandsführung)',
          supportsCriticalFunction: true,
          functionName: 'Bestandsführung Leben/Komposit',
          governingLawCountry: 'DE',
          exitStrategy: { exists: true, tested: true },
          art29Assessment: { substitutability: 'high_complexity' },
          subcontractors: [{ name: 'AWS Frankfurt', rank: 2, country: 'DE' }],
        },
        {
          id: 'dra_005',
          arrangementRef: 'CRM-2025-009',
          status: 'active',
          isAdjudonSelf: false,
          providerName: 'VertriebsCloud SaaS GmbH',
          providerCountryHq: 'DE',
          ictServiceType: 'CRM/Vertriebssystem (SaaS)',
          supportsCriticalFunction: false,
          functionName: 'Vertrieb/CRM',
          governingLawCountry: 'DE',
          exitStrategy: { exists: true, tested: false },
          art29Assessment: { substitutability: 'low_complexity' },
          subcontractors: [{ name: 'AWS Frankfurt', rank: 2, country: 'DE' }],
        },
      ];
    }
    return this._doraArrangementStore;
  },

  async listDoraArrangements(_params?: any): Promise<API.DoraArrangementsListResponse> {
    await delay(300);
    return {
      arrangements: [...this._doraArrangementSeed()],
      meta: {
        ictServiceTypes: ['Cloud-Hosting', 'Kernsystem', 'Audit-/Nachweis-Schicht (KI-Entscheidungen)', 'Netzwerk', 'Sonstige'],
        art30Clauses: [
          { key: 'serviceDescription', paragraph: '30(2)(a)', label: 'Leistungsbeschreibung', appliesTo: 'all' },
          { key: 'dataLocations', paragraph: '30(2)(b)', label: 'Daten-/Verarbeitungsorte', appliesTo: 'all' },
          { key: 'availabilityIntegrity', paragraph: '30(2)(c)', label: 'Verfügbarkeit/Integrität', appliesTo: 'all' },
          { key: 'terminationRights', paragraph: '30(2)(h)', label: 'Kündigungsrechte', appliesTo: 'all' },
          { key: 'exitSupport', paragraph: '30(3)(f)', label: 'Exit-Unterstützung', appliesTo: 'critical' },
          { key: 'auditRights', paragraph: '30(3)(e)', label: 'Audit-/Zugangs-Rechte', appliesTo: 'critical' },
        ],
      },
    };
  },

  async getDoraRegisterCompliance() {
    await delay(250);
    return {
      items: [
        { id: 'dra_001', arrangementRef: 'ADJ-SELF-001', supportsCriticalFunction: true, completeness: { applicable: 15, present: 15, percent: 100, missing: [] } },
        { id: 'dra_002', arrangementRef: 'CC-2024-007', supportsCriticalFunction: true, completeness: { applicable: 15, present: 9, percent: 60, missing: [{ paragraph: '30(3)(e)', label: 'Audit-/Zugangs-Rechte' }, { paragraph: '30(3)(f)', label: 'Exit-Unterstützung' }] } },
        { id: 'dra_004', arrangementRef: 'RZ-2022-031', supportsCriticalFunction: true, completeness: { applicable: 15, present: 15, percent: 100, missing: [] } },
        { id: 'dra_003', arrangementRef: 'AZ-2023-114', supportsCriticalFunction: false, completeness: { applicable: 9, present: 8, percent: 89, missing: [{ paragraph: '30(2)(h)', label: 'Kündigungsrechte' }] } },
        { id: 'dra_005', arrangementRef: 'CRM-2025-009', supportsCriticalFunction: false, completeness: { applicable: 9, present: 7, percent: 78, missing: [{ paragraph: '30(2)(h)', label: 'Kündigungsrechte' }, { paragraph: '30(3)(f)', label: 'Exit-Unterstützung' }] } },
      ],
      summary: { arrangements: 5, criticalArrangements: 3, fullyCompliant: 3, overallClausePercent: 85 },
    };
  },

  async getDoraRegisterConcentration() {
    await delay(250);
    return {
      totalArrangements: 5,
      criticalCount: 3,
      providerCount: 5,
      providers: [],
      flags: {
        // Konzentrations-Flag: AWS Frankfurt liegt als Rang-2-Unterauftragnehmer unter
        // mehreren (auch kritischen) Verträgen — gebündelte Abhängigkeit.
        multiCriticalSameProvider: ['AWS Frankfurt'],
        notSubstitutableCritical: ['ClaimsCore Software GmbH'],
        criticalWithoutExitPlan: [],
        criticalWithoutTestedExit: ['ClaimsCore Software GmbH'],
        thirdCountryChains: [],
        longSubcontractingChains: [],
        insolvencyNotConsidered: ['ClaimsCore Software GmbH'],
      },
    };
  },

  async getDoraRegisterYearlyReport() {
    await delay(200);
    return { year: 2026, totalArrangements: 5, newArrangementsThisYear: 1, criticalFunctionsCount: 3 };
  },

  async createDoraArrangement(payload: any) {
    await delay(400);
    const a = {
      id: `dra_${Math.random().toString(36).slice(2, 8)}`,
      status: 'active',
      isAdjudonSelf: false,
      exitStrategy: {},
      art29Assessment: {},
      subcontractors: [],
      ...payload,
    };
    this._doraArrangementSeed().push(a);
    return a;
  },

  async updateDoraArrangement(id: string, payload: any) {
    await delay(350);
    const store = this._doraArrangementSeed();
    const idx = store.findIndex((x) => x.id === id);
    if (idx < 0) throw new Error('not found');
    store[idx] = { ...store[idx], ...payload };
    return store[idx];
  },

  async terminateDoraArrangement(id: string) {
    await delay(300);
    const store = this._doraArrangementSeed();
    const idx = store.findIndex((x) => x.id === id);
    if (idx < 0) throw new Error('not found');
    store[idx] = { ...store[idx], status: 'terminated', terminatedAt: new Date().toISOString() };
    return store[idx];
  },

  async seedDoraAdjudonSelf() {
    await delay(300);
    return { created: false };
  },

  // ── GwG/AML-Hub Mocks (§ 5 / § 6 / §§ 10–15) — Shapes spiegeln die echten
  // Controller-Projektionen. Demo-Stores als Objekt-Properties (Session-only).

  async getCurrentRiskAssessment() {
    await delay(250);
    return { riskAssessment: this._riskAssessmentSeed()[0] };
  },

  async listRiskAssessmentVersions() {
    await delay(250);
    return { versions: [...this._riskAssessmentSeed()] };
  },

  async exportRiskAssessmentMarkdown(version?: number) {
    await delay(300);
    const v = this._riskAssessmentSeed().find((r: any) => r.version === (version ?? this._riskAssessmentSeed()[0].version)) ?? this._riskAssessmentSeed()[0];
    return {
      version: v.version,
      markdown: `# § 5 GwG — Risikoanalyse (Version ${v.version})\n\nOrganisation: Acme Corporation\nGesamt-Risikoniveau: ${v.overallRiskLevel}\nNächste Überprüfung: ${v.nextReassessmentDue}\nContent-Hash: ${v.contentHash}\n\n> BaFin-Export (Demo). Inhalt + Einstufung verantwortet der Geldwäschebeauftragte.\n`,
    };
  },

  _riskAssessmentStore: null as null | any[],
  _riskAssessmentSeed() {
    if (!this._riskAssessmentStore) {
      const factor = (category: string, riskType: string, description: string, inherent: string, mitigation: string, residual: string) =>
        ({ category, riskType, description, inherentRisk: inherent, mitigation, residualRisk: residual });
      this._riskAssessmentStore = [
        {
          id: 'ra_002',
          organizationId: 'org_demo',
          version: 2,
          supersedesVersion: 1,
          title: 'GwG § 5 Risikoanalyse — Lebensversicherung & bAV',
          periodStart: '2026-01-01T00:00:00Z',
          periodEnd: '2026-12-31T00:00:00Z',
          methodology: 'BaFin-AuA 02/2025 · Anlage 1/2 GwG · Nationale Risikoanalyse',
          riskFactors: [
            factor('customer', 'ml', 'HNW-Klientel mit Einmalbeiträgen > 100T€', 'high', 'EDD nach § 15 + Mittelherkunftsnachweis', 'medium'),
            factor('product', 'ml', 'Rückkaufsfähige Lebensversicherungen', 'medium', 'Frühstorno-Monitoring (§ 6-Kontrolle)', 'low'),
            factor('geography', 'tf', 'Bezüge zu Hochrisiko-Drittstaaten (Anlage 2)', 'medium', 'Screening je Vertragsänderung', 'low'),
            factor('channel', 'both', 'Fern-Abschluss ohne persönlichen Kontakt', 'medium', 'Video-Ident + qualifizierte Signatur', 'low'),
          ],
          overallRiskLevel: 'medium',
          summary: 'Gesamtrisiko mittel; GW-Schwerpunkt Einmalbeiträge/Frühstorno, TF-Schwerpunkt Auslandsbezug.',
          approvedByEmail: 'vorstand@nordlicht.example',
          nextReassessmentDue: new Date(Date.now() + 120 * 86400000).toISOString(),
          changeSummary: 'GW/TF-Trennung je Faktor ergänzt (BaFin-AuA 2025).',
          contentHash: 'c4f1a9e2'.repeat(8),
          integrityVerified: true,
          createdAt: '2026-02-10T09:00:00Z',
        },
        {
          id: 'ra_001',
          organizationId: 'org_demo',
          version: 1,
          supersedesVersion: null,
          title: 'GwG § 5 Risikoanalyse — Erstfassung',
          periodStart: '2025-01-01T00:00:00Z',
          periodEnd: '2025-12-31T00:00:00Z',
          methodology: 'BaFin-AuA · Anlage 1/2 GwG',
          riskFactors: [
            factor('customer', 'both', 'HNW-Klientel', 'high', 'EDD', 'medium'),
          ],
          overallRiskLevel: 'medium',
          summary: 'Erstfassung.',
          approvedByEmail: 'vorstand@nordlicht.example',
          nextReassessmentDue: '2026-02-01T00:00:00Z',
          changeSummary: '',
          contentHash: 'a1b2c3d4'.repeat(8),
          integrityVerified: true,
          createdAt: '2025-02-01T09:00:00Z',
        },
      ];
    }
    return this._riskAssessmentStore;
  },

  _transactionAlertStore: null as null | any[],
  _transactionAlertSeed() {
    if (!this._transactionAlertStore) {
      const mk = (id: string, alertRef: string, ruleId: string, severity: string, status: string, disposition: string, hoursOpen: number, slaHours: number) => ({
        id,
        organizationId: 'org_demo',
        alertRef,
        ruleId,
        ruleVersion: 'v3.1',
        severity,
        status,
        disposition,
        assignedToRef: status === 'investigating' ? 'analyst_mk' : null,
        openedAt: new Date(Date.now() - hoursOpen * 3600000).toISOString(),
        closedAt: status === 'closed' ? new Date(Date.now() - 2 * 3600000).toISOString() : null,
        slaHours,
        slaBreached: status !== 'closed' && hoursOpen > slaHours,
        hoursOpen,
        createdAt: new Date(Date.now() - hoursOpen * 3600000).toISOString(),
        updatedAt: new Date().toISOString(),
      });
      this._transactionAlertStore = [
        // 2 offen + 1 investigating + dispositionierte Historie (~9 Monate Betrieb)
        mk('ta_001', 'ALERT-2026-0612', 'Frühstorno > 80 % Rückkaufswert', 'high', 'open', 'none', 91, 72),
        mk('ta_002', 'ALERT-2026-0615', 'Einmalbeitrag > 100T€ ohne Mittelherkunft', 'high', 'investigating', 'none', 30, 72),
        mk('ta_003', 'ALERT-2026-0618', 'Auffällige Beitragsrückzahlung auf Drittkonto', 'medium', 'open', 'none', 14, 72),
        mk('ta_004', 'ALERT-2026-0598', 'Drittstaaten-Überweisung auf Police', 'medium', 'closed', 'false_positive', 220, 72),
        mk('ta_005', 'ALERT-2026-0571', 'Rascher Storno nach Einmalbeitrag', 'high', 'closed', 'sar_filed', 360, 72),
        mk('ta_006', 'ALERT-2026-0540', 'Mehrfach-Police gleicher wB ohne Bezug', 'medium', 'closed', 'false_positive', 520, 72),
        mk('ta_007', 'ALERT-2026-0488', 'Beitragszahlung in Teilbeträgen (Smurfing-Muster)', 'high', 'closed', 'escalated', 700, 72),
        mk('ta_008', 'ALERT-2026-0431', 'Adressänderung in Hochrisiko-Drittstaat', 'low', 'closed', 'false_positive', 900, 72),
        mk('ta_009', 'ALERT-2026-0377', 'Begünstigtenwechsel kurz vor Auszahlung', 'medium', 'closed', 'false_positive', 1100, 72),
        mk('ta_010', 'ALERT-2025-0921', 'Frühstorno > 80 % Rückkaufswert', 'high', 'closed', 'false_positive', 1900, 72),
        mk('ta_011', 'ALERT-2025-0844', 'Bareinzahlung Erstbeitrag über Schwellenwert', 'medium', 'closed', 'escalated', 2400, 72),
      ];
    }
    return this._transactionAlertStore;
  },

  async listTransactionAlerts(params?: any) {
    await delay(300);
    let alerts = [...this._transactionAlertSeed()];
    if (params?.status) alerts = alerts.filter((a) => a.status === params.status);
    if (params?.severity) alerts = alerts.filter((a) => a.severity === params.severity);
    return { alerts };
  },

  async transitionTransactionAlert(id: string, payload: any) {
    await delay(300);
    const store = this._transactionAlertSeed();
    const idx = store.findIndex((a) => a.id === id);
    if (idx < 0) throw new Error('not found');
    const closing = payload?.status === 'closed';
    // Backend-Guard gespiegelt (TXALERT_DISPOSITION_REQUIRED): Schliessen ohne
    // Disposition lehnt der echte Service ab — der Mock darf nicht weicher sein.
    if (closing && !payload?.disposition && store[idx].disposition === 'none') {
      throw new Error('TXALERT_DISPOSITION_REQUIRED');
    }
    store[idx] = {
      ...store[idx],
      status: payload?.status ?? store[idx].status,
      ...(payload?.disposition ? { disposition: payload.disposition } : {}),
      ...(payload?.assignedToRef !== undefined ? { assignedToRef: payload.assignedToRef } : {}),
      ...(closing ? { closedAt: new Date().toISOString(), slaBreached: false } : {}),
      updatedAt: new Date().toISOString(),
    };
    return { alert: store[idx] };
  },

  async disposeTransactionAlert(id: string, disposition: string) {
    await delay(300);
    // Backend-Semantik gespiegelt: dispose = Schliessen-mit-Disposition;
    // 'none' ist ungueltig (TXALERT_INVALID_DISPOSITION).
    if (!['false_positive', 'escalated', 'sar_filed'].includes(disposition)) {
      throw new Error('TXALERT_INVALID_DISPOSITION');
    }
    const store = this._transactionAlertSeed();
    const idx = store.findIndex((a) => a.id === id);
    if (idx < 0) throw new Error('not found');
    store[idx] = {
      ...store[idx],
      disposition,
      status: 'closed',
      closedAt: new Date().toISOString(),
      slaBreached: false,
      updatedAt: new Date().toISOString(),
    };
    return { alert: store[idx] };
  },

  async listEddAssessments(params?: any): Promise<API.EddListResponse> {
    await delay(300);
    const mk = (id: string, customerRef: string, triggerReason: API.EddAssessmentRecord['triggerReason'], status: API.EddStatus, completeness: number, conclusion: API.EddAssessmentRecord['riskConclusion'], measures: API.EddMeasure[]): API.EddAssessmentRecord => ({
      id,
      organizationId: 'org_demo',
      customerRef,
      triggerReason,
      measures,
      evidenceRefs: ['doc_a1'],
      riskConclusion: conclusion,
      riskJustification: conclusion ? 'Mittelherkunft plausibilisiert; Restrisiko tragbar.' : '',
      status,
      approvedByRef: status === 'approved' ? 'mlro_nordlicht' : null,
      approvedAt: status === 'approved' ? '2026-05-12T10:00:00Z' : null,
      recheckDate: status === 'approved' ? new Date(Date.now() + 200 * 86400000).toISOString() : null,
      completeness,
      isReadyForApproval: completeness === 100,
      createdAt: '2026-05-01T10:00:00Z',
      updatedAt: '2026-05-12T10:00:00Z',
    });
    let assessments = [
      // 1 offen (in_review) + 2 abgeschlossen
      mk('edd_001', 'KD-100533', 'pep', 'in_review', 67, '', [
        { label: 'Mittelherkunft erheben (§ 15 Abs. 4 Nr. 1)', completed: true, completedAt: '2026-05-05T10:00:00Z', outcome: 'Verkaufserlös Immobilie belegt' },
        { label: 'Zustimmung Führungsebene (§ 15 Abs. 4 Nr. 2)', completed: true, completedAt: '2026-05-06T10:00:00Z', outcome: 'erteilt' },
        { label: 'Verstärkte laufende Überwachung (§ 15 Abs. 4 Nr. 3)', completed: false, completedAt: null, outcome: '' },
      ]),
      mk('edd_002', 'KD-100571', 'high_risk_country', 'approved', 100, 'mitigated', [
        { label: 'Mittelherkunft erheben', completed: true, completedAt: '2026-04-20T10:00:00Z', outcome: 'belegt' },
        { label: 'Zustimmung Führungsebene', completed: true, completedAt: '2026-04-21T10:00:00Z', outcome: 'erteilt' },
      ]),
      mk('edd_003', 'KD-100702', 'pep', 'approved', 100, 'mitigated', [
        { label: 'Mittelherkunft erheben (§ 15 Abs. 4 Nr. 1)', completed: true, completedAt: '2026-03-11T10:00:00Z', outcome: 'Gehaltsnachweise belegt' },
        { label: 'Zustimmung Führungsebene (§ 15 Abs. 4 Nr. 2)', completed: true, completedAt: '2026-03-12T10:00:00Z', outcome: 'erteilt' },
        { label: 'Verstärkte laufende Überwachung (§ 15 Abs. 4 Nr. 3)', completed: true, completedAt: '2026-03-12T10:00:00Z', outcome: 'eingerichtet' },
      ]),
    ];
    if (params?.status) assessments = assessments.filter((a) => a.status === params.status);
    return { assessments };
  },

  async listScreenings(params?: any): Promise<API.ScreeningListResponse> {
    await delay(300);
    const mk = (id: string, customerRef: string, screeningType: API.ScreeningRecord['screeningType'], verdict: API.ScreeningVerdict, status: API.ScreeningStatus, hitCount: number, needsReview: boolean, overdue: boolean): API.ScreeningRecord => ({
      id,
      organizationId: 'org_demo',
      customerRef,
      screeningType,
      vendor: 'Dow Jones Risk & Compliance',
      verdict,
      hitCount,
      reviewedByRef: status !== 'open' ? 'analyst_mk' : null,
      disposition: status === 'reviewed' ? 'false_positive' : 'none',
      rescreenDue: overdue ? new Date(Date.now() - 5 * 86400000).toISOString() : new Date(Date.now() + 90 * 86400000).toISOString(),
      status,
      reviewedAt: status !== 'open' ? '2026-05-10T10:00:00Z' : null,
      needsReview,
      isOverdueRescreen: overdue,
      createdAt: '2026-05-01T10:00:00Z',
      updatedAt: '2026-05-10T10:00:00Z',
    });
    let screenings = [
      mk('scr_001', 'KD-100533', 'pep', 'potential_hit', 'open', 2, true, false),
      mk('scr_002', 'KD-100744', 'sanctions', 'clear', 'closed', 0, false, true),
      mk('scr_003', 'KD-100571', 'adverse_media', 'clear', 'closed', 0, false, false),
      mk('scr_004', 'KD-100629', 'sanctions', 'clear', 'reviewed', 0, false, false),
    ];
    if (params?.verdict) screenings = screenings.filter((s) => s.verdict === params.verdict);
    if (params?.status) screenings = screenings.filter((s) => s.status === params.status);
    return { screenings };
  },

  async listAmlBeneficiaries(params?: any): Promise<API.AmlBeneficiaryListResponse> {
    await delay(300);
    const mk = (id: string, customerRef: string, uboRef: string, pct: number, controlType: API.AmlBeneficiaryRecord['controlType'], isPep: boolean, status: API.AmlBeneficiaryStatus, flagReason: string): API.AmlBeneficiaryRecord => ({
      id,
      organizationId: 'org_demo',
      customerRef,
      uboRef,
      uboDisplayName: '',
      ownershipPercent: pct,
      controlType,
      isPep,
      isControllingInterest: pct > 25 || controlType !== 'ownership',
      verificationMethod: 'register',
      verificationDate: status === 'verified' ? '2026-04-15T10:00:00Z' : null,
      status,
      flagReason,
      createdAt: '2026-04-01T10:00:00Z',
      updatedAt: '2026-04-15T10:00:00Z',
    });
    let beneficiaries = [
      mk('ubo_001', 'KD-100517', 'ubo_a9f1', 40, 'ownership', false, 'verified', ''),
      mk('ubo_002', 'KD-100517', 'ubo_c3e7', 30, 'ownership', true, 'unverified', ''),
      mk('ubo_003', 'KD-100629', 'ubo_d512', 0, 'senior_managing', false, 'flagged', 'Transparenzregister-Diskrepanz (§ 23a GwG)'),
    ];
    if (params?.status) beneficiaries = beneficiaries.filter((b) => b.status === params.status);
    return { beneficiaries };
  },

  // Solvency II Säule 1 — Modell-Evidenz (Shape = aggregateModelEvidence)
  async getSolvencyPillar1Evidence(params?: any) {
    await delay(300);
    const evidence = {
      methodologyVersion: 'pillar1-evidence/v1',
      windowDays: Number(params?.windowDays) || 90,
      generatedAt: new Date().toISOString(),
      useTest: {
        article: 'Art. 120',
        totalDecisions: 8420,
        distinctAgents: 6,
        humanOverrideCount: 312,
        humanOversightRate: 0.037,
        avgConfidence: 0.86,
        note: 'Audit-Trail belegt die produktive Nutzung der KI-Komponente (Use-Test-Input). Das Vorstands-Attest „breit genutzt" treffen Sie.',
      },
      dataQuality: {
        article: 'Art. 82 / 121(3)',
        validationCount: 14,
        avgScore: 91.2,
        latestScore: 94,
        latestValidatedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
        note: 'Data-Quality-Validierungen als Genau-/Vollständig-/Angemessen-Posture. Datenverzeichnis + Urteil bei Ihnen.',
      },
      validation: {
        article: 'Art. 124',
        driftAlertsTotal: 5,
        driftAlertsOpen: 1,
        latestEffectiveness: {
          precision: 0.93,
          recall: 0.89,
          f1: 0.91,
          cohenKappa: 0.84,
          sampleSize: 1240,
          windowEnd: new Date(Date.now() - 86400000).toISOString(),
        },
        note: 'Laufendes Performance-Monitoring + Test gegen Erfahrung (Input). Der unabhängige Validierungs-Sign-off (Art. 124) bleibt bei Ihnen.',
      },
      documentation: {
        article: 'Art. 125 / 115',
        modelCardCount: 6,
        policyVersionChanges: 9,
        note: 'Versionierte Model-Cards + immutable Change-Log. Spezifikations-/Annahmen-Freigabe + Vorstands-Aufsicht (Art. 116) bei Ihnen.',
      },
      note: 'Evidenz-Eingabe für die KI-/ML-Komponenten Ihrer Säule-1-Rechnung. Keine Kapital-Berechnung. SCR/MCR + Art.-48-Stellungnahme + Art.-124-Sign-off bleiben Ihre nicht-delegierbare Verantwortung (EIOPA-BoS-25-360).',
    };
    if (params?.format === 'markdown') {
      return {
        ...evidence,
        markdown: `# Solvency II Säule 1 — KI-Komponenten-Evidenz

Organisation: Acme Corporation
Fenster: ${evidence.windowDays} Tage

## Use-Test (Art. 120)
Entscheidungen: ${evidence.useTest.totalDecisions}

> Keine Kapital-Berechnung — Evidenz-Input (EIOPA-BoS-25-360).
`,
      };
    }
    return evidence;
  },

  // APIError-Paritaet: echte Fehler tragen den Code in .code (nicht .message).
  _vvgErr(code: string): Error {
    return Object.assign(new Error(code), { code });
  },

  // ── VVG/IDD-Hub Mocks (§ 60 · IDD Art. 19 · Wiedervorlage) — Shapes +
  // GUARDS spiegeln die echten Services (Mock darf nie weicher sein als
  // das Backend — Lehre aus dem §-6-Triage-Drift).

  _productComparisonStore: null as null | any[],
  _productComparisonSeed() {
    if (!this._productComparisonStore) {
      this._productComparisonStore = [
        {
          id: 'pc_001',
          organizationId: 'org_demo',
          advisoryRef: 'BER-2026-0142',
          recommendedProduct: 'PrivatRente Komfort (Allianz)',
          alternatives: [
            { name: 'RentenPlus Aktiv (ERGO)', considered: true, reasonRejected: 'Höhere Verwaltungskosten bei gleicher Garantieleistung', monthlyPremium: 182 },
            { name: 'VorsorgeFlex (Gothaer)', considered: true, reasonRejected: 'Kein BU-Zusatzbaustein kombinierbar', monthlyPremium: 168 },
            { name: 'BasisRente Pur (HUK)', considered: true, reasonRejected: 'Basisrente passt nicht zum Liquiditätsbedarf', monthlyPremium: 150 },
          ],
          marketScope: 'full_market',
          scopeJustification: '',
          status: 'finalized',
          chainHash: 'e7a1'.repeat(16),
          completeness: 100,
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-05-20T10:00:00Z',
          updatedAt: '2026-05-20T11:30:00Z',
        },
        {
          id: 'pc_003',
          organizationId: 'org_demo',
          advisoryRef: 'BER-2026-0203',
          recommendedProduct: 'BU-Schutz Premium (Swiss Life)',
          alternatives: [
            { name: 'BU Komfort (Alte Leipziger)', considered: true, reasonRejected: 'Längere Anzeigepflicht bei Berufswechsel', monthlyPremium: 64 },
            { name: 'BU Basis (Continentale)', considered: true, reasonRejected: 'Kein Verzicht auf abstrakte Verweisung', monthlyPremium: 51 },
            { name: 'Grundfähigkeitsschutz (Canada Life)', considered: true, reasonRejected: 'Geringerer Leistungsumfang als gewünscht', monthlyPremium: 42 },
          ],
          marketScope: 'full_market',
          scopeJustification: '',
          status: 'finalized',
          chainHash: 'd4c2'.repeat(16),
          completeness: 100,
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-04-22T10:00:00Z',
          updatedAt: '2026-04-22T11:15:00Z',
        },
        {
          id: 'pc_002',
          organizationId: 'org_demo',
          advisoryRef: 'BER-2026-0158',
          recommendedProduct: 'Hausrat Premium (AXA)',
          alternatives: [
            { name: 'Hausrat Basis (DEVK)', considered: true, reasonRejected: '', monthlyPremium: 12 },
          ],
          marketScope: 'limited',
          scopeJustification: 'Bestandskunde wünscht Anbieter mit vorhandenem Vertragskonto.',
          status: 'draft',
          chainHash: null,
          completeness: 80,
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-06-08T09:00:00Z',
          updatedAt: '2026-06-08T09:00:00Z',
        },
      ];
    }
    return this._productComparisonStore;
  },

  async listProductComparisons(params?: any) {
    await delay(300);
    let comparisons = [...this._productComparisonSeed()];
    if (params?.status) comparisons = comparisons.filter((c) => c.status === params.status);
    if (params?.advisoryRef) comparisons = comparisons.filter((c) => c.advisoryRef === params.advisoryRef);
    return { comparisons };
  },

  async createProductComparison(payload: any): Promise<API.ProductComparisonDetailResponse> {
    await delay(350);
    if (!payload?.advisoryRef || !payload?.recommendedProduct) throw this._vvgErr('VALIDATION_ERROR');
    const c: API.ProductComparison = {
      id: `pc_${Math.random().toString(36).slice(2, 8)}`,
      organizationId: 'org_demo',
      advisoryRef: payload.advisoryRef,
      recommendedProduct: payload.recommendedProduct,
      alternatives: payload.alternatives ?? [],
      marketScope: payload.marketScope ?? 'full_market',
      scopeJustification: payload.scopeJustification ?? '',
      status: 'draft',
      chainHash: null,
      completeness: 40,
      createdBy: 'usr_demo',
      createdByEmail: 'makler@nordlicht.example',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._productComparisonSeed().unshift(c);
    return { comparison: c };
  },

  async updateProductComparison(id: string, payload: any) {
    await delay(300);
    const store = this._productComparisonSeed();
    const idx = store.findIndex((c) => c.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status === 'finalized') throw this._vvgErr('COMPARISON_LOCKED');
    store[idx] = { ...store[idx], ...payload, updatedAt: new Date().toISOString() };
    return { comparison: store[idx] };
  },

  async finalizeProductComparison(id: string) {
    await delay(400);
    const store = this._productComparisonSeed();
    const idx = store.findIndex((c) => c.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    const c = store[idx];
    if (c.status !== 'draft') throw this._vvgErr('COMPARISON_BAD_TRANSITION');
    // Backend-Guards gespiegelt:
    if (!c.recommendedProduct || c.alternatives.length < 1) throw this._vvgErr('COMPARISON_INCOMPLETE');
    if (c.marketScope === 'full_market' && c.alternatives.length < 3) throw this._vvgErr('COMPARISON_SCOPE_MISMATCH');
    if (c.marketScope !== 'full_market' && !c.scopeJustification) throw this._vvgErr('COMPARISON_SCOPE_REASON_REQUIRED');
    store[idx] = { ...c, status: 'finalized', chainHash: 'f0ab'.repeat(16), completeness: 100, updatedAt: new Date().toISOString() };
    return { comparison: store[idx] };
  },

  async deleteProductComparison(id: string) {
    await delay(250);
    const store = this._productComparisonSeed();
    const idx = store.findIndex((c) => c.id === id);
    if (idx < 0 || store[idx].status !== 'draft') throw this._vvgErr('NOT_FOUND');
    store.splice(idx, 1);
    return { deleted: true };
  },

  _commissionStore: null as null | any[],
  _commissionSeed() {
    if (!this._commissionStore) {
      this._commissionStore = [
        {
          id: 'cd_001',
          organizationId: 'org_demo',
          workspaceId: null,
          linkedAdvisoryRef: 'BER-2026-0142',
          externalCustomerRef: 'kunde_77a1',
          products: [
            { name: 'PrivatRente Komfort (Allianz)', commissionAmount: 1840, commissionPct: 4.0, category: 'Leben' },
            { name: 'RentenPlus Aktiv (ERGO)', commissionAmount: 1620, commissionPct: 3.5, category: 'Leben' },
            { name: 'VorsorgeFlex (Gothaer)', commissionAmount: 1390, commissionPct: 3.1, category: 'Leben' },
          ],
          recommendedProductName: 'PrivatRente Komfort (Allianz)',
          higherCommissionOnRecommended: true,
          disclosedToCustomer: true,
          disclosedAt: '2026-05-20T12:00:00Z',
          disclosedByRef: 'makler_mk',
          status: 'disclosed',
          completeness: 100,
          isCompliant: true,
          retentionUntil: '2033-05-20T12:00:00Z',
          chainHash: 'a1c9'.repeat(16),
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-05-20T10:00:00Z',
          updatedAt: '2026-05-20T12:00:00Z',
        },
        {
          id: 'cd_002',
          organizationId: 'org_demo',
          workspaceId: null,
          linkedAdvisoryRef: 'BER-2026-0158',
          externalCustomerRef: 'kunde_19fc',
          products: [
            { name: 'Hausrat Premium (AXA)', commissionAmount: 95, commissionPct: 18, category: 'Sach' },
            { name: 'Hausrat Basis (DEVK)', commissionAmount: 110, commissionPct: 22, category: 'Sach' },
          ],
          recommendedProductName: 'Hausrat Premium (AXA)',
          higherCommissionOnRecommended: false,
          disclosedToCustomer: true,
          disclosedAt: '2026-04-30T12:00:00Z',
          disclosedByRef: 'makler_mk',
          status: 'disclosed',
          completeness: 100,
          isCompliant: true,
          retentionUntil: '2033-04-30T12:00:00Z',
          chainHash: 'c3e7'.repeat(16),
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-04-30T10:00:00Z',
          updatedAt: '2026-04-30T12:00:00Z',
        },
        {
          id: 'cd_003',
          organizationId: 'org_demo',
          workspaceId: null,
          linkedAdvisoryRef: 'BER-2026-0203',
          externalCustomerRef: 'kunde_55d0',
          products: [
            { name: 'BU-Schutz Premium (Swiss Life)', commissionAmount: 980, commissionPct: 3.2, category: 'Leben' },
            { name: 'BU Komfort (Alte Leipziger)', commissionAmount: 910, commissionPct: 3.0, category: 'Leben' },
          ],
          recommendedProductName: 'BU-Schutz Premium (Swiss Life)',
          higherCommissionOnRecommended: true,
          disclosedToCustomer: true,
          disclosedAt: '2026-04-22T12:00:00Z',
          disclosedByRef: 'makler_mk',
          status: 'acknowledged',
          completeness: 100,
          isCompliant: true,
          retentionUntil: '2033-04-22T12:00:00Z',
          chainHash: 'f1a8'.repeat(16),
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-04-22T10:00:00Z',
          updatedAt: '2026-04-22T12:30:00Z',
        },
        {
          id: 'cd_004',
          organizationId: 'org_demo',
          workspaceId: null,
          linkedAdvisoryRef: 'BER-2025-0447',
          externalCustomerRef: 'kunde_4471',
          products: [
            { name: 'Kranken-Zusatz Plus (Barmenia)', commissionAmount: 210, commissionPct: 6.0, category: 'Kranken' },
            { name: 'Kranken-Zusatz Basis (HanseMerkur)', commissionAmount: 240, commissionPct: 7.0, category: 'Kranken' },
          ],
          recommendedProductName: 'Kranken-Zusatz Plus (Barmenia)',
          higherCommissionOnRecommended: false,
          disclosedToCustomer: true,
          disclosedAt: '2026-02-14T12:00:00Z',
          disclosedByRef: 'makler_mk',
          status: 'disclosed',
          completeness: 100,
          isCompliant: true,
          retentionUntil: '2033-02-14T12:00:00Z',
          chainHash: 'a902'.repeat(16),
          createdBy: 'usr_demo',
          createdByEmail: 'makler@nordlicht.example',
          createdAt: '2026-02-14T10:00:00Z',
          updatedAt: '2026-02-14T12:00:00Z',
        },
      ];
    }
    return this._commissionStore;
  },

  async listCommissionDisclosures(params?: any) {
    await delay(300);
    let disclosures = [...this._commissionSeed()];
    if (params?.status) disclosures = disclosures.filter((d) => d.status === params.status);
    return { disclosures };
  },

  async createCommissionDisclosure(payload: any): Promise<API.CommissionDisclosureDetailResponse> {
    await delay(350);
    if (!payload?.recommendedProductName) throw this._vvgErr('VALIDATION_ERROR');
    const products = payload.products ?? [];
    const maxAmount = Math.max(0, ...products.map((p: any) => Number(p.commissionAmount) || 0));
    const rec = products.find((p: any) => p.name === payload.recommendedProductName);
    const d: API.CommissionDisclosure = {
      id: `cd_${Math.random().toString(36).slice(2, 8)}`,
      organizationId: 'org_demo',
      workspaceId: null,
      linkedAdvisoryRef: payload.linkedAdvisoryRef ?? null,
      externalCustomerRef: payload.externalCustomerRef ?? null,
      products,
      recommendedProductName: payload.recommendedProductName,
      higherCommissionOnRecommended: !!rec && maxAmount > 0 && (Number(rec.commissionAmount) || 0) >= maxAmount,
      disclosedToCustomer: false,
      disclosedAt: null,
      disclosedByRef: null,
      status: 'draft',
      completeness: products.length > 0 ? 90 : 40,
      isCompliant: false,
      retentionUntil: null,
      chainHash: null,
      createdBy: 'usr_demo',
      createdByEmail: 'makler@nordlicht.example',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._commissionSeed().unshift(d);
    return { disclosure: d };
  },

  async updateCommissionDisclosure(id: string, payload: any) {
    await delay(300);
    const store = this._commissionSeed();
    const idx = store.findIndex((d) => d.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status !== 'draft') throw this._vvgErr('COMMISSION_LOCKED');
    store[idx] = { ...store[idx], ...payload, updatedAt: new Date().toISOString() };
    return { disclosure: store[idx] };
  },

  async discloseCommissionDisclosure(id: string) {
    await delay(400);
    const store = this._commissionSeed();
    const idx = store.findIndex((d) => d.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    const d = store[idx];
    if (d.status !== 'draft') throw this._vvgErr('COMMISSION_BAD_TRANSITION');
    if (!d.products.length) throw this._vvgErr('COMMISSION_INCOMPLETE_PRODUCTS');
    if (!d.recommendedProductName) throw this._vvgErr('COMMISSION_INCOMPLETE_RECOMMENDED');
    if (!d.products.some((p: any) => p.name === d.recommendedProductName)) throw this._vvgErr('COMMISSION_RECOMMENDED_NOT_FOUND');
    store[idx] = {
      ...d,
      status: 'disclosed',
      disclosedToCustomer: true,
      disclosedAt: new Date().toISOString(),
      disclosedByRef: 'makler_mk',
      isCompliant: true,
      retentionUntil: new Date(Date.now() + 7 * 365 * 86400000).toISOString(),
      chainHash: 'b2d8'.repeat(16),
      updatedAt: new Date().toISOString(),
    };
    return { disclosure: store[idx] };
  },

  async acknowledgeCommissionDisclosure(id: string) {
    await delay(300);
    const store = this._commissionSeed();
    const idx = store.findIndex((d) => d.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status !== 'disclosed') throw this._vvgErr('COMMISSION_BAD_TRANSITION');
    store[idx] = { ...store[idx], status: 'acknowledged', updatedAt: new Date().toISOString() };
    return { disclosure: store[idx] };
  },

  async archiveCommissionDisclosure(id: string) {
    await delay(300);
    const store = this._commissionSeed();
    const idx = store.findIndex((d) => d.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (!['disclosed', 'acknowledged'].includes(store[idx].status)) throw this._vvgErr('COMMISSION_BAD_TRANSITION');
    store[idx] = { ...store[idx], status: 'archived', updatedAt: new Date().toISOString() };
    return { disclosure: store[idx] };
  },

  _periodicReviewStore: null as null | any[],
  _periodicReviewSeed() {
    if (!this._periodicReviewStore) {
      const mk = (id: string, advisoryRef: string, customerRef: string, dueOffsetDays: number, status: string, reason: string) => ({
        id,
        organizationId: 'org_demo',
        advisoryRef,
        customerRef,
        lastReviewDate: status === 'completed' ? new Date(Date.now() - 10 * 86400000).toISOString() : null,
        nextReviewDue: new Date(Date.now() + dueOffsetDays * 86400000).toISOString(),
        status,
        reviewReason: reason,
        completedByRef: status === 'completed' ? 'makler_mk' : null,
        completedAt: status === 'completed' ? new Date(Date.now() - 10 * 86400000).toISOString() : null,
        whatChanged: status === 'completed' ? 'Keine Änderung der Lebensumstände; Empfehlung bestätigt.' : '',
        createdAt: new Date(Date.now() - 100 * 86400000).toISOString(),
        updatedAt: new Date().toISOString(),
      });
      this._periodicReviewStore = [
        // 1 überfällig · 2 fällig demnächst · 5 erledigt · 1 geplant
        mk('pr_001', 'BER-2025-0871', 'kunde_77a1', -12, 'overdue', 'IBIP-Jahresprüfung (IDD Art. 30(5))'),
        mk('pr_002', 'BER-2026-0142', 'kunde_77a1', 18, 'due', 'Eignungs-Überprüfung nach Berufswechsel'),
        mk('pr_003', 'BER-2026-0158', 'kunde_19fc', 25, 'due', 'IBIP-Jahresprüfung (IDD Art. 30(5))'),
        mk('pr_004', 'BER-2026-0203', 'kunde_55d0', 80, 'scheduled', 'Turnus 90 Tage'),
        mk('pr_005', 'BER-2025-0512', 'kunde_8810', 30, 'completed', 'IBIP-Jahresprüfung'),
        mk('pr_006', 'BER-2025-0447', 'kunde_4471', 60, 'completed', 'Eignungs-Überprüfung'),
        mk('pr_007', 'BER-2025-0398', 'kunde_2290', 90, 'completed', 'IBIP-Jahresprüfung'),
        mk('pr_008', 'BER-2025-0321', 'kunde_6612', 120, 'completed', 'Turnus 90 Tage'),
        mk('pr_009', 'BER-2025-0274', 'kunde_9904', 150, 'completed', 'IBIP-Jahresprüfung'),
      ];
    }
    return this._periodicReviewStore;
  },

  async listPeriodicReviews(params?: any) {
    await delay(300);
    let reviews = [...this._periodicReviewSeed()];
    if (params?.status) reviews = reviews.filter((r) => r.status === params.status);
    return { reviews };
  },

  async createPeriodicReview(payload: any): Promise<API.PeriodicReviewDetailResponse> {
    await delay(350);
    if (!payload?.advisoryRef) throw this._vvgErr('VALIDATION_ERROR');
    const r: API.PeriodicReviewTask = {
      id: `pr_${Math.random().toString(36).slice(2, 8)}`,
      organizationId: 'org_demo',
      advisoryRef: payload.advisoryRef,
      customerRef: payload.customerRef ?? null,
      lastReviewDate: null,
      nextReviewDue: payload.nextReviewDue ?? new Date(Date.now() + 90 * 86400000).toISOString(),
      status: 'scheduled',
      reviewReason: payload.reviewReason ?? '',
      completedByRef: null,
      completedAt: null,
      whatChanged: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._periodicReviewSeed().unshift(r);
    return { review: r };
  },

  async completePeriodicReview(id: string, payload: any) {
    await delay(350);
    if (!payload?.whatChanged) throw this._vvgErr('VALIDATION_ERROR');
    const store = this._periodicReviewSeed();
    const idx = store.findIndex((r) => r.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status === 'completed') throw this._vvgErr('REVIEW_ALREADY_COMPLETED');
    store[idx] = {
      ...store[idx],
      status: 'completed',
      whatChanged: payload.whatChanged,
      completedByRef: 'makler_mk',
      completedAt: new Date().toISOString(),
      lastReviewDate: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    return { review: store[idx] };
  },

  async reschedulePeriodicReview(id: string, payload: any) {
    await delay(300);
    if (!payload?.nextReviewDue) throw this._vvgErr('VALIDATION_ERROR');
    const store = this._periodicReviewSeed();
    const idx = store.findIndex((r) => r.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status === 'completed') throw this._vvgErr('REVIEW_COMPLETED_IMMUTABLE');
    const due = new Date(payload.nextReviewDue).getTime();
    const status = due < Date.now() ? 'overdue' : due < Date.now() + 30 * 86400000 ? 'due' : 'scheduled';
    store[idx] = { ...store[idx], nextReviewDue: payload.nextReviewDue, status, updatedAt: new Date().toISOString() };
    return { review: store[idx] };
  },

  // ── EIOPA-Model-Cards Mocks — Shapes + 404-Code-Paritaet ─────────────
  _MODEL_CARD_LABELS: {
    businessObjective: 'Geschäftsziel',
    itIntegration: 'IT-Integration',
    staffRoles: 'Rollen & Verantwortlichkeiten',
    dataCollection: 'Datenerhebung',
    dataPreparation: 'Datenaufbereitung',
    technicalChoices: 'Technische Entscheidungen',
    code: 'Code & Implementierung',
    modelPerformance: 'Modell-Performance',
    modelSecurity: 'Modell-Sicherheit',
  } as Record<API.ModelCardFieldKey, string>,

  _modelCardStore: null as null | Record<string, any[]>,
  _modelCardSeed() {
    if (!this._modelCardStore) {
      // Demo-Persona „Nordlicht Versicherung AG": 5 von 6 KI-Systemen haben eine
      // dokumentierte 9-Felder-Model-Card; der Churn-Prädiktor (Mai 2026 neu,
      // unklassifiziert) hat KEINE Karte → 404-Pfad bleibt sichtbar (Lücke).
      const card = (
        agentId: string,
        version: number,
        supersedes: number | null,
        changeSummary: string,
        fields: Record<API.ModelCardFieldKey, string>,
        ageDays: number,
        authoredByEmail: string,
        seed: string,
      ) => ({
        id: `mc_${agentId}_${version}`,
        organizationId: 'org_demo',
        agentId,
        version,
        supersedesVersion: supersedes,
        fields,
        changeSummary,
        contentHash: (seed + version).padEnd(64, 'f').slice(0, 64),
        linkedBomDigest: `bom_${seed.slice(0, 4)}`,
        integrityVerified: true,
        authoredByEmail,
        createdAt: new Date(Date.now() - ageDays * 86400000).toISOString(),
      });
      const fraudFields: Record<API.ModelCardFieldKey, string> = {
        businessObjective: 'Betrugs-Scoring eingehender Schadenmeldungen zur Priorisierung der manuellen Prüfung.',
        itIntegration: 'REST-Anbindung an das Schadensystem; Adjudon-SDK vor dem Agenten (Fail-Open).',
        staffRoles: 'Fachverantwortung Schaden-Leitung · Modell-Owner Data-Team · Aufsicht Compliance.',
        dataCollection: 'Schadenmeldungs-Metadaten + Historie; keine besonderen Kategorien (Art. 9 DSGVO).',
        dataPreparation: 'Pseudonymisierung vor Training; Klassen-Balancierung dokumentiert.',
        technicalChoices: 'Gradient-Boosting + LLM-Vorverdichtung; Schwellen je Risikoklasse.',
        code: 'Repo fraud-detector; Versionierung über Release-Tags; Review-Pflicht 4-Augen.',
        modelPerformance: 'AUC 0,91 (Validierung 2026-Q1); Drift-Überwachung via Adjudon-Kalibrierung.',
        modelSecurity: 'Zugriff RBAC; Adversarial-Test 2026-03; kein Direktzugriff aus dem Internet.',
      };
      const uwFields: Record<API.ModelCardFieldKey, string> = {
        businessObjective: 'Risikoprüfung in der Lebensversicherung — Vorklassifizierung der Antragsrisiken.',
        itIntegration: 'Anbindung an das Bestands-/Antragssystem; Adjudon-SDK vor dem Agenten (Fail-Open).',
        staffRoles: 'Fachverantwortung Underwriting Leben · Modell-Owner Aktuariat · Aufsicht Compliance.',
        dataCollection: 'Antragsdaten + Gesundheitsangaben (Art. 9 DSGVO, gesonderte Rechtsgrundlage).',
        dataPreparation: 'Pseudonymisierung; Bias-Stratifikation je geschützter Kohorte dokumentiert.',
        technicalChoices: 'Logistische Regression + GBM-Ensemble; konformale Coverage-Schwellen.',
        code: 'Repo uw-leben; Release-Tags; 4-Augen-Freigabe durch Aktuariat.',
        modelPerformance: 'AUC 0,88 (Validierung 2026-Q1); ECE 0,03; Bias-Audit bestanden.',
        modelSecurity: 'RBAC; jährlicher Pen-Test; in-Region-Verarbeitung (Frankfurt).',
      };
      const pricingFields: Record<API.ModelCardFieldKey, string> = {
        businessObjective: 'Tarifierung Komposit — Prämienermittlung über Tarif- und Risikomerkmale.',
        itIntegration: 'Anbindung an das Tarifierungssystem; Adjudon-SDK vor dem Agenten (Fail-Open).',
        staffRoles: 'Fachverantwortung Produkt Komposit · Modell-Owner Aktuariat · Aufsicht Compliance.',
        dataCollection: 'Tarif-/Vertrags-/Schadenmerkmale; keine besonderen Kategorien.',
        dataPreparation: 'Feature-Engineering dokumentiert; eine Proxy-Variable nach Bias-Befund entfernt.',
        technicalChoices: 'GLM + GBM-Korrekturterm; Schwellen je Sparte.',
        code: 'Repo pricing-komposit; Release-Tags; 4-Augen-Freigabe.',
        modelPerformance: 'Gini 0,42 (Validierung 2026-Q1); aktuell Drift-Alarm offen (unter Aufsicht).',
        modelSecurity: 'RBAC; Adversarial-Review 2026-02; kein Internet-Direktzugriff.',
      };
      const docFields: Record<API.ModelCardFieldKey, string> = {
        businessObjective: 'Klassifizierung des Posteingangs zur automatischen Zuordnung an Fachbereiche.',
        itIntegration: 'Anbindung an das Dokumenten-Management; Adjudon-SDK vor dem Agenten (Fail-Open).',
        staffRoles: 'Fachverantwortung Betrieb/Input-Management · Modell-Owner Data-Team.',
        dataCollection: 'Dokument-Metadaten + Textmerkmale; PII-Scrubbing vor Verarbeitung.',
        dataPreparation: 'Tokenisierung; keine besonderen Kategorien im Trainingsset.',
        technicalChoices: 'Textklassifikator (Embeddings + lineares Modell); Konfidenz-Routing.',
        code: 'Repo doc-classifier; Release-Tags.',
        modelPerformance: 'F1 0,95 (Validierung 2026-Q1); minimal-Risiko-Einstufung.',
        modelSecurity: 'RBAC; in-Region-Verarbeitung; kein Direktzugriff aus dem Internet.',
      };
      const chatFields: Record<API.ModelCardFieldKey, string> = {
        businessObjective: 'Service-FAQ-Assistent — Beantwortung allgemeiner Kundenanfragen (keine Entscheidungen).',
        itIntegration: 'Anbindung an das Kundenportal; Adjudon-SDK vor dem Agenten (Fail-Open).',
        staffRoles: 'Fachverantwortung Kundenservice · Modell-Owner Data-Team.',
        dataCollection: 'Chat-Verlauf; PII-Scrubbing vor Verarbeitung; keine besonderen Kategorien.',
        dataPreparation: 'Prompt-Templates + Wissensbasis kuratiert; Halluzinations-Guardrails.',
        technicalChoices: 'LLM mit Retrieval-Augmentation; limited-risk-Transparenzhinweis (Art. 50).',
        code: 'Repo chat-assistant; Release-Tags.',
        modelPerformance: 'Eskalationsquote an Mensch dokumentiert; limited-Risiko-Einstufung.',
        modelSecurity: 'RBAC; in-Region-Verarbeitung; Eingabe-/Ausgabe-Filter aktiv.',
      };
      this._modelCardStore = {
        agent_fraud: [
          card('agent_fraud', 2, 1, 'Modell-Sicherheit nach Adversarial-Test ergänzt.', fraudFields, 60, 'compliance@nordlicht.example', 'd4e5'),
          card('agent_fraud', 1, null, 'Erstfassung.', fraudFields, 200, 'compliance@nordlicht.example', 'd4e5'),
        ],
        agent_uw_leben: [
          card('agent_uw_leben', 2, 1, 'Bias-Audit-Ergebnisse + Coverage-Schwellen ergänzt.', uwFields, 26, 'aktuariat@nordlicht.example', 'a17c'),
          card('agent_uw_leben', 1, null, 'Erstfassung.', uwFields, 184, 'aktuariat@nordlicht.example', 'a17c'),
        ],
        agent_pricing: [
          card('agent_pricing', 2, 1, 'Proxy-Variable nach Bias-Befund entfernt; Drift-Hinweis ergänzt.', pricingFields, 12, 'aktuariat@nordlicht.example', 'b820'),
          card('agent_pricing', 1, null, 'Erstfassung.', pricingFields, 150, 'aktuariat@nordlicht.example', 'b820'),
        ],
        agent_doc: [card('agent_doc', 1, null, 'Erstfassung.', docFields, 120, 'compliance@nordlicht.example', 'c93a')],
        agent_chat: [card('agent_chat', 1, null, 'Erstfassung.', chatFields, 95, 'compliance@nordlicht.example', 'e44d')],
        // agent_churn: bewusst KEINE Karte (Lücke; 404-Pfad bleibt erreichbar).
      };
    }
    return this._modelCardStore;
  },

  async getCurrentModelCard(agentId: string): Promise<API.ModelCardCurrentResponse> {
    await delay(250);
    const store = this._modelCardSeed();
    const versions = store[agentId];
    if (!versions || versions.length === 0) throw this._vvgErr('MODELCARD_NOT_FOUND');
    return { modelCard: versions[0], labels: { ...this._MODEL_CARD_LABELS } };
  },

  async listModelCardVersions(agentId: string) {
    await delay(250);
    const store = this._modelCardSeed();
    return { versions: [...(store[agentId] ?? [])] };
  },

  async exportModelCard(agentId: string) {
    await delay(300);
    const store = this._modelCardSeed();
    const cur = (store[agentId] ?? [])[0];
    if (!cur) throw this._vvgErr('MODELCARD_NOT_FOUND');
    return { markdown: `# EIOPA-Model-Card — ${agentId} (v${cur.version})

Content-Hash: ${cur.contentHash}

> Insurer-attestierter Governance-Layer (Demo).
` };
  },

  async createModelCardVersion(agentId: string, payload: any) {
    await delay(400);
    const store = this._modelCardSeed();
    const prev = (store[agentId] ?? [])[0] ?? null;
    const version = prev ? prev.version + 1 : 1;
    const card = {
      id: `mc_${agentId}_${version}`,
      organizationId: 'org_demo',
      agentId,
      version,
      supersedesVersion: prev ? prev.version : null,
      fields: { ...(prev?.fields ?? {}), ...(payload?.fields ?? {}) },
      changeSummary: payload?.changeSummary ?? '',
      contentHash: ('a1b2' + version).padEnd(64, 'e').slice(0, 64),
      linkedBomDigest: prev?.linkedBomDigest ?? null,
      integrityVerified: true,
      authoredByEmail: 'compliance@nordlicht.example',
      createdAt: new Date().toISOString(),
    };
    store[agentId] = [card, ...(store[agentId] ?? [])];
    return { modelCard: card };
  },

  // ── Phase 2 — Auto-Approval Engine ──────────────────────────────────
  async listApprovalPatterns() {
    return [
      mockPattern('pat_001', 'mature', 152, 0.97, true),
      mockPattern('pat_002', 'observing', 27, 0.96, false),
      mockPattern('pat_003', 'paused', 89, 0.91, true),
    ];
  },
  async getApprovalPattern(id: string) { return mockPattern(id, 'mature', 152, 0.97, true); },
  async createApprovalPattern(payload: any) { return mockPattern(`pat_${Date.now()}`, 'observing', 0, 0, false, payload); },
  async pauseApprovalPattern(id: string) { return mockPattern(id, 'paused', 152, 0.97, true); },
  async signoffApprovalPattern(id: string, payload: { reviewerRole: 'primary' | 'secondary' }) {
    const p = mockPattern(id, 'mature', 152, 0.97, true);
    if (payload.reviewerRole === 'primary') p.signedOffByPrimary = 'demo@adjudon.com';
    else p.signedOffBySecondary = 'demo2@adjudon.com';
    return p;
  },
  async listAutoApprovalDecisions(params?: { patternId?: string; limit?: number }) {
    const limit = params?.limit ?? 20;
    // Routinemäßige Auto-Freigaben rotieren über die 3 verkehrsstärksten
    // Systeme (Fraud · Underwriting · Dokument-Klassifizierer).
    const autoAgents = ['agent_fraud', 'agent_uw_leben', 'agent_doc'];
    return Array.from({ length: limit }).map((_, i) => ({
      id: `aad_${i}`,
      patternId: params?.patternId ?? 'pat_001',
      traceId: `trc_${10000 + i}`,
      organizationId: 'org_demo',
      agentId: autoAgents[i % 3],
      decidedAt: new Date(Date.now() - i * 5 * 60 * 1000).toISOString(),
      decision: 'approved' as const,
      reason: 'Pattern match: confidence ≥ 95% AND policy=routine-summary',
    }));
  },

  // ── Phase 2 — Deployer Profile ──────────────────────────────────────
  async getDeployerProfile() { return mockDeployerProfile(); },
  async updateDeployerProfile(payload: any) { return { ...mockDeployerProfile(), ...payload }; },
  async ingestDeployerInstruction(payload: any) {
    return {
      id: `instr_${Date.now()}`,
      source: (payload.source ?? 'provider') as 'provider' | 'internal',
      title: payload.title,
      body: payload.body,
      ingestedAt: new Date().toISOString(),
      acknowledgedAt: null,
      acknowledgedBy: null,
    };
  },
  async acknowledgeDeployerInstruction(id: string) {
    return {
      id,
      source: 'provider' as const,
      title: 'Mock Instruction',
      body: 'Mock body',
      ingestedAt: new Date(Date.now() - 86400000).toISOString(),
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: 'demo@adjudon.com',
    };
  },
  async linkFriaToDeployer(_friaId: string) { return mockDeployerProfile(); },
  async getDeployerDisclosureNotice(params: { type: 'affected_persons' | 'workers'; locale?: 'de' | 'en'; systemName?: string; purpose?: string }) {
    return mockDisclosureNotice(params);
  },

  // ── EU AI Act — Controller-Responsibility catalog ───────────────────
  async getControllerResponsibilities(framework?: string) { return mockControllerResponsibilities(framework); },
  async getControllerResponsibilityTemplate(key: string, _system?: string) { return mockControllerResponsibilityTemplate(key); },
  async getFrameworkReadiness(framework: string, entityId?: string | null): Promise<API.FrameworkReadiness | null> { return mockFrameworkReadiness(framework, entityId ?? null); },
  // Board-Bericht: Monats-Serien je Hub. Konsistenz-Garantie: der LETZTE Punkt
  // wird live aus mockFrameworkReadiness abgeleitet (gleiche Zahl wie der Hub
  // heute zeigt); frühere Monate = deterministische Persona-Progression
  // (Programm-Start Nov 2025, data-act bleibt flach — kein Math.random).
  async getReadinessHistory(months = 12): Promise<API.ReadinessHistoryResponse> {
    const HUBS = ['eu-ai-act', 'gdpr', 'dora', 'solvency-vag', 'beratung', 'eiopa', 'aml-gwg', 'data-act-fida'];
    // Wachstums-Charakter je Hub: [Startanteil des heutigen Werts, Delle-Monat-Index]
    const SHAPE: Record<string, { start: number; dip: number | null }> = {
      dora: { start: 0.15, dip: 5 },
      gdpr: { start: 0.2, dip: null },
      gwgFallback: { start: 0.2, dip: null },
      'aml-gwg': { start: 0.25, dip: null },
      'eu-ai-act': { start: 0.05, dip: 6 },
      'solvency-vag': { start: 0.2, dip: null },
      beratung: { start: 0.3, dip: 4 },
      eiopa: { start: 0.0, dip: null },
      'data-act-fida': { start: 0.5, dip: null }, // flach niedrig
    };
    const now = new Date();
    const span = Math.max(2, Math.min(12, months));
    const series: API.ReadinessHistorySeries[] = [];
    for (const fw of HUBS) {
      const current = await mockFrameworkReadiness(fw, null);
      const att = current?.attestations ?? { pct: 0, valid: 0, total: 0 };
      const shape = SHAPE[fw] ?? { start: 0.2, dip: null };
      const points: API.ReadinessHistoryPoint[] = [];
      for (let i = span - 1; i >= 0; i--) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        const period = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        // Vor Programm-Start (Nov 2025) keine Punkte.
        if (d < new Date(Date.UTC(2025, 10, 1))) continue;
        const progress = span - 1 === 0 ? 1 : (span - 1 - i) / (span - 1);
        let frac = shape.start + (1 - shape.start) * progress;
        if (shape.dip != null && span - 1 - i === shape.dip) frac -= 0.08; // Delle (abgelaufene Attestierungen)
        if (i === 0) frac = 1; // heute = exakt der Live-Wert
        const pct = Math.max(0, Math.min(100, Math.round(att.pct * frac)));
        const valid = i === 0 ? att.valid : Math.round((att.total * pct) / 100);
        points.push({
          period,
          pct,
          valid,
          total: att.total,
          capturedAt: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 6)).toISOString(),
        });
      }
      series.push({ framework: fw, points });
    }
    return { series, months: span, generatedAt: new Date().toISOString() };
  },

  // Cockpit-Sammel-Contract — gleiche 8 kanonischen Hub-Slugs wie Backend HUB_FRAMEWORKS.
  async getAllFrameworksReadiness(entityId: string | null = null): Promise<API.AllFrameworksReadinessResponse> {
    const HUBS = ['eu-ai-act', 'gdpr', 'dora', 'solvency-vag', 'beratung', 'eiopa', 'aml-gwg', 'data-act-fida'];
    const frameworks = (await Promise.all(HUBS.map((f) => mockFrameworkReadiness(f, entityId)))).filter(
      (r): r is API.FrameworkReadiness => r != null,
    );
    return {
      frameworks,
      entityCount: 1,
      selectedEntityId: entityId,
      generatedAt: new Date().toISOString(),
    };
  },
  async getEntities(): Promise<API.RegulatedEntity[]> { return mockEntities(); },

  // ── GwGMeldV / goAML-XML Verdachtsmeldung ───────────────────────────
  async getGoAmlProfile(): Promise<API.GoAmlProfileResponse> {
    return { profile: mockGoAmlProfile(), configured: true };
  },
  async updateGoAmlProfile(payload: Partial<API.GoAmlReportingProfile>): Promise<API.GoAmlProfileResponse> {
    const base = mockGoAmlProfile();
    return {
      profile: {
        ...base,
        ...payload,
        reportingPerson: { ...base.reportingPerson, ...(payload.reportingPerson || {}) },
        location: { ...base.location, ...(payload.location || {}) },
        defaultReportType: payload.defaultReportType === 'VMT' ? 'VMT' : 'VMA',
        currencyCode: (payload.currencyCode || base.currencyCode || 'EUR').toUpperCase(),
        updatedAt: new Date().toISOString(),
      },
      configured: true,
    };
  },
  async getGoAmlExport(suspicionId: string, reportType?: 'VMA' | 'VMT'): Promise<API.GoAmlExportResult> {
    return mockGoAmlExport(suspicionId, reportType);
  },
  async attestResponsibility(key: string, body?: API.AttestPayload) {
    return {
      attestation: {
        responsibilityKey: key,
        attestedByEmail: 'demo@adjudon.com',
        attestedByFunction: body?.attestedByFunction ?? '',
        attestedAt: body?.performedAt || new Date().toISOString(),
        note: body?.note ?? '',
        evidenceUrl: body?.evidenceUrl ?? '',
      },
    };
  },
  async revokeResponsibility(key: string) { return { revoked: true, key }; },
  async getResponsibilityHistory(_key: string): Promise<API.ControllerAttestationEvent[]> {
    return [
      { action: 'attested', attestedByEmail: 'demo@adjudon.com', attestedByFunction: 'Compliance', note: '', evidenceUrl: '', validUntil: null, performedAt: new Date().toISOString(), recordedAt: new Date().toISOString() },
    ];
  },

  // ── Phase 2 — FRIA ──────────────────────────────────────────────────
  async listFRIAs() {
    // 4 FRIAs covering every lifecycle status so the status-filter pills
    // are demonstrable in mock mode (founder rule: features must render
    // in mock so prospects + tests can see them).
    return [
      mockFRIA('fria_001', 'approved', 100),
      mockFRIA('fria_002', 'review', 78),
      mockFRIA('fria_003', 'draft', 45),
      mockFRIA('fria_004', 'archived', 100),
    ];
  },
  async getFRIA(id: string) { return mockFRIA(id, 'draft', 65); },
  async createFRIA(payload: any) { return mockFRIA(`fria_${Date.now()}`, 'draft', 10, payload); },
  async updateFRIA(id: string, payload: any) { return { ...mockFRIA(id, 'draft', 65), ...payload }; },
  async submitFRIAForReview(id: string) { return mockFRIA(id, 'review', 90); },
  async approveFRIA(id: string) {
    return { ...mockFRIA(id, 'approved', 100), approvedAt: new Date().toISOString(), approvedBy: 'demo@adjudon.com' };
  },
  async getInsuranceFriaTemplate(useCase?: string) { return mockInsuranceFriaTemplate(useCase); },
  async getCorrectiveActions(): Promise<API.CorrectiveAction[]> {
    return [
      { id: 'ca_1', agentId: 'agent_pricing', source: 'pmm', title: 'Drift in der Pricing Engine überprüfen', description: '', status: 'open', dueDate: '2026-07-01T00:00:00Z', completedAt: null, createdByEmail: 'aktuariat@nordlicht.example' },
      { id: 'ca_2', agentId: null, source: 'bias', title: 'Bias-Befund Altersgruppe 60+ schließen', description: '', status: 'in_progress', dueDate: '2026-06-20T00:00:00Z', completedAt: null, createdByEmail: 'demo@adjudon.com' },
    ];
  },
  async createCorrectiveAction(payload: { title: string; source?: string }): Promise<API.CorrectiveAction> {
    return { id: `ca_${Date.now()}`, agentId: null, source: (payload.source as API.CorrectiveActionSource) ?? 'manual', title: payload.title, description: '', status: 'open', dueDate: null, completedAt: null, createdByEmail: 'demo@adjudon.com' };
  },
  async updateCorrectiveAction(id: string, payload: { status?: string }): Promise<API.CorrectiveAction> {
    return { id, agentId: null, source: 'manual', title: 'Korrekturmaßnahme', description: '', status: (payload.status as API.CorrectiveActionStatus) ?? 'open', dueDate: null, completedAt: null, createdByEmail: 'demo@adjudon.com' };
  },
  async getFriaCoverage(): Promise<API.FriaCoverage> {
    // Demo-Persona „Nordlicht Versicherung AG": 6 KI-Systeme. FRIA-pflichtig sind die
    // 3 Hochrisiko-Systeme (Annex III): Fraud Detector + Underwriting Assistent Leben
    // (beide genehmigt) + Pricing Engine Komposit (in Review). Dokument-Klassifizierer
    // (minimal) + Kunden-Chat (limited) brauchen keine FRIA → als abgedeckt gezählt.
    // Churn-Prädiktor (unklassifiziert, Mai 2026 neu) = die EINE Lücke (uncovered).
    const items = [
      { id: 'fria_fraud', systemName: 'Fraud Detector', status: 'approved', approved: true, approvedAt: new Date(Date.now() - 165 * 86400000).toISOString(), linkedAgentIds: ['agent_fraud'], updatedAt: new Date(Date.now() - 165 * 86400000).toISOString() },
      { id: 'fria_uw_leben', systemName: 'Underwriting Assistent Leben', status: 'approved', approved: true, approvedAt: new Date(Date.now() - 184 * 86400000).toISOString(), linkedAgentIds: ['agent_uw_leben'], updatedAt: new Date(Date.now() - 184 * 86400000).toISOString() },
      { id: 'fria_pricing', systemName: 'Pricing Engine Komposit', status: 'review', approved: false, approvedAt: null, linkedAgentIds: ['agent_pricing'], updatedAt: new Date(Date.now() - 9 * 86400000).toISOString() },
    ];
    const agents = [
      { agentId: 'agent_fraud', agentName: 'Fraud Detector', hasApprovedFria: true },
      { agentId: 'agent_uw_leben', agentName: 'Underwriting Assistent Leben', hasApprovedFria: true },
      { agentId: 'agent_pricing', agentName: 'Pricing Engine Komposit', hasApprovedFria: false },
      { agentId: 'agent_doc', agentName: 'Dokument-Klassifizierer', hasApprovedFria: true },
      { agentId: 'agent_chat', agentName: 'Kunden-Chat-Assistent', hasApprovedFria: true },
      { agentId: 'agent_churn', agentName: 'Churn-Prädiktor', hasApprovedFria: false },
    ];
    // coveredAgents = genehmigte FRIA ODER nicht FRIA-pflichtig (Doc/Chat) → 5/6.
    // Pricing zählt NICHT als abgedeckt (in Review), bleibt aber unter den 5, weil
    // Doc + Chat keine FRIA brauchen; einzige echte Lücke ist der Churn-Prädiktor.
    return {
      items,
      agents,
      summary: { totalSystems: 3, approvedSystems: 2, pendingSystems: 1, totalAgents: 6, coveredAgents: 5, uncoveredAgents: 1, allCovered: false },
    };
  },
  async exportFRIA(id: string) {
    return {
      artifact: { id, systemName: 'Customer Triage Agent', status: 'approved' },
      signature: 'eyJhbGciOiJFUzI1NiJ9.eyJpZCI6ImZyaWFfMDAxIn0.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
      signatureAlg: 'ecdsa-p256-jws',
      signedAt: new Date().toISOString(),
      signingFailed: false,
    };
  },

  // ── VVG §61/§62 — Advisory Records ──────────────────────────────────
  async listAdvisories(params?: any) {
    // ~12 Beratungen über 9 Monate: 9 finalized/acknowledged · 2 draft · 1 mit §60-Marktvergleich
    const mk = (id: string, status: API.AdvisoryStatus, completeness: number, customerRef: string, displayName: string, extra?: any) =>
      mockAdvisory(id, status, completeness, customerRef, {
        customerDisplayName: displayName,
        advisorName: 'M. Krüger (Makler)',
        createdByEmail: 'makler@nordlicht.example',
        ...extra,
      });
    const all = [
      // mit §60-Marktvergleich verknüpft (gleicher Kunde wie ProductComparison pc_001)
      mk('adv_001', 'acknowledged', 100, 'kunde_77a1', 'Privatkunde Altersvorsorge', { productCategory: 'life' as API.AdvisoryProductCategory, recommendation: 'PrivatRente Komfort (Allianz) — §60-Vollmarktvergleich BER-2026-0142', recommendationReason: 'Beste Garantieleistung bei vertretbaren Kosten; 4 Anbieter verglichen.' }),
      mk('adv_002', 'acknowledged', 100, 'kunde_19fc', 'Bestandskunde Hausrat', { productCategory: 'property' as API.AdvisoryProductCategory }),
      mk('adv_003', 'finalized', 95, 'kunde_8810', 'Gewerbekunde Haftpflicht', { productCategory: 'liability' as API.AdvisoryProductCategory }),
      mk('adv_004', 'acknowledged', 100, 'kunde_55d0', 'Privatkunde BU-Vorsorge', { productCategory: 'life' as API.AdvisoryProductCategory }),
      mk('adv_005', 'finalized', 92, 'kunde_4471', 'Privatkunde Kranken-Zusatz', { productCategory: 'health' as API.AdvisoryProductCategory }),
      mk('adv_006', 'acknowledged', 100, 'kunde_2290', 'Familienkunde Sachschutz', { productCategory: 'property' as API.AdvisoryProductCategory }),
      mk('adv_007', 'finalized', 88, 'kunde_6612', 'Gewerbekunde Inhalt', { productCategory: 'property' as API.AdvisoryProductCategory }),
      mk('adv_008', 'archived', 100, 'kunde_3318', 'Privatkunde Risiko-Leben', { consultationScope: 'limited', limitedScopeReason: 'Kunde wünscht nur Beratung zur Risikolebensversicherung', productCategory: 'life' as API.AdvisoryProductCategory }),
      mk('adv_009', 'acknowledged', 100, 'kunde_9904', 'Privatkunde Unfall', { productCategory: 'liability' as API.AdvisoryProductCategory }),
      // 2 Entwürfe
      mk('adv_010', 'draft', 45, 'kunde_7053', 'Interessent Altersvorsorge', { productCategory: 'life' as API.AdvisoryProductCategory }),
      mk('adv_011', 'draft', 30, 'kunde_8127', 'Interessent Gewerbe', { productCategory: 'liability' as API.AdvisoryProductCategory }),
    ];
    let filtered = all;
    if (params?.status && params.status !== 'all') {
      filtered = filtered.filter((a) => a.status === params.status);
    }
    if (params?.search) {
      const q = params.search.toLowerCase();
      filtered = filtered.filter((a) => a.customerRef.toLowerCase().includes(q) || a.customerDisplayName.toLowerCase().includes(q));
    }
    if (params?.customerRef) {
      filtered = filtered.filter((a) => a.customerRef === params.customerRef);
    }
    const limit = params?.limit ?? 50;
    return { advisories: filtered.slice(0, limit) };
  },
  async getAdvisory(id: string) {
    return { advisory: mockAdvisory(id, 'draft', 65) };
  },
  async createAdvisory(payload: any) {
    return mockAdvisory(`adv_${Date.now()}`, 'draft', 10, undefined, payload);
  },
  async updateAdvisory(id: string, payload: any) {
    return { ...mockAdvisory(id, 'draft', 65), ...payload };
  },
  async finalizeAdvisory(id: string, payload?: any) {
    return { ...mockAdvisory(id, 'finalized', 95), advisedAt: new Date().toISOString(), ...payload };
  },
  async acknowledgeAdvisory(id: string, payload: any) {
    return {
      ...mockAdvisory(id, 'acknowledged', 100),
      acknowledgment: {
        acknowledged: true,
        acknowledgedAt: new Date().toISOString(),
        method: payload?.method ?? 'text-form',
        acknowledgedByRef: payload?.acknowledgedByRef,
        statement: payload?.statement ?? '',
      },
    };
  },
  async archiveAdvisory(id: string) {
    return mockAdvisory(id, 'archived', 100);
  },
  async getAdvisoryProtocol(id: string) {
    const advisory = mockAdvisory(id, 'finalized', 95);
    const markdown = `# Beratungsprotokoll §62 VVG

**Datum:** ${new Date(advisory.createdAt!).toLocaleDateString('de-DE')}

## Beratene Person
- **Name:** ${advisory.customerDisplayName}
- **Referenz:** ${advisory.customerRef}

## Berater
- **Name:** ${advisory.advisorName}
- **Typ:** ${advisory.advisorType}

## Bedarfsanalytic und Empfehlungen

### Kundenbedarf
${advisory.demandsAndNeeds}

### Beratungsumfang
${advisory.consultationScope === 'full' ? 'Vollberatung' : `Teilberatung: ${advisory.limitedScopeReason}`}

### Empfehlung
${advisory.recommendation}

**Begründung:** ${advisory.recommendationReason}

### Berücksichtigte Alternativen
${advisory.alternativesConsidered}

## Kundenerklärung
Diese Beratung erfolgt auf Grundlage der erfassten Informationen und dem ermittelten Bedarf.`;
    return { protocol: markdown, advisory };
  },
  async deleteAdvisory(_id: string) {
    return { deleted: true };
  },

  // ============================================================================
  // COMPLIANCE MODULES (11 Modules)
  // ============================================================================

  // 1. CONSENT MANAGEMENT — ~30 Einwilligungen, davon 3 widerrufen
  async listConsents(_params?: any): Promise<API.ConsentListResponse> {
    await delay(300);
    const day = 86400000;
    const purposesPool = ['marketing', 'analytics', 'profiling', 'newsletter'];
    const withdrawnIdx = new Set([4, 12, 23]); // 3 widerrufene Einträge
    const consents: API.ConsentRecord[] = Array.from({ length: 30 }, (_v, i) => {
      const grantedDaysAgo = 240 - i * 7;
      const grantedAt = new Date(Date.now() - grantedDaysAgo * day).toISOString();
      const primary = purposesPool[i % purposesPool.length];
      const isWithdrawn = withdrawnIdx.has(i);
      const withdrawnAt = isWithdrawn ? new Date(Date.now() - (grantedDaysAgo - 40) * day).toISOString() : null;
      const purposes: API.ConsentPurpose[] = [
        { purpose: primary, granted: !isWithdrawn, legalBasis: 'consent', grantedAt, withdrawnAt, active: !isWithdrawn },
      ];
      // Einige Subjekte haben einen zweiten, nicht erteilten Zweck (granulare Einwilligung).
      if (i % 3 === 0) {
        purposes.push({ purpose: 'analytics', granted: false, legalBasis: 'consent', grantedAt: null, withdrawnAt: null, active: false });
      }
      return {
        id: `cons_${String(i + 1).padStart(3, '0')}`,
        organizationId: 'org_demo',
        subjectRef: `subj_${String(101 + i)}`,
        purposes,
        createdAt: grantedAt,
        updatedAt: withdrawnAt ?? grantedAt,
      };
    });
    return { consents };
  },

  async getConsent(subjectRef: string): Promise<API.ConsentDetailResponse> {
    await delay(200);
    return {
      consent: {
        id: `cons_${subjectRef}`,
        organizationId: 'org_demo',
        subjectRef,
        purposes: [{ purpose: 'marketing', granted: true, legalBasis: 'consent', grantedAt: '2026-01-15T10:00:00Z', withdrawnAt: null, active: true }],
        createdAt: '2026-01-15T10:00:00Z',
        updatedAt: '2026-05-20T14:30:00Z',
      },
    };
  },

  async createConsent(subjectRef: string, purposes: any[]) {
    await delay(400);
    return {
      consent: {
        id: `cons_${Date.now()}`,
        organizationId: 'org_demo',
        subjectRef,
        purposes: purposes.map((p) => ({ ...p, grantedAt: new Date().toISOString(), active: p.granted })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async withdrawConsent(subjectRef: string, purpose: string): Promise<API.ConsentDetailResponse> {
    await delay(300);
    return {
      consent: {
        id: `cons_${subjectRef}`,
        organizationId: 'org_demo',
        subjectRef,
        purposes: [{ purpose, granted: false, legalBasis: 'consent', grantedAt: '2026-01-15T10:00:00Z', withdrawnAt: new Date().toISOString(), active: false }],
        createdAt: '2026-01-15T10:00:00Z',
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // 2. DATA SUBJECT ACCESS REQUESTS — 14 erledigt · 2 offen in Frist · 1 bald fällig · 0 überfällig
  async listDsars(params?: any): Promise<API.DsarListResponse> {
    await delay(300);
    const day = 86400000;
    const mkFulfilled = (
      n: number,
      subjectRef: string,
      requestType: API.DsarRequestType,
      receivedDaysAgo: number,
      fulfilledDaysAgo: number,
      handler: string,
    ): API.DsarRecord => {
      const receivedAt = new Date(Date.now() - receivedDaysAgo * day).toISOString();
      return {
        id: `dsar_${String(n).padStart(3, '0')}`,
        organizationId: 'org_demo',
        subjectRef,
        requestType,
        status: 'fulfilled',
        receivedAt,
        deadlineAt: new Date(Date.now() - (receivedDaysAgo - 30) * day).toISOString(),
        extendedDeadlineAt: null,
        fulfilledAt: new Date(Date.now() - fulfilledDaysAgo * day).toISOString(),
        rejectionReason: '',
        handledBy: handler,
        notes: requestType === 'erasure' ? 'Auskunft erteilt; Daten gemäß Aufbewahrungsfristen gelöscht.' : 'Auskunft nach Art. 15 vollständig erteilt.',
        isOverdue: false,
        createdAt: receivedAt,
        updatedAt: new Date(Date.now() - fulfilledDaysAgo * day).toISOString(),
      };
    };
    const handlers = ['dsb@nordlicht.example', 'compliance@nordlicht.example'];
    const types: API.DsarRequestType[] = ['access', 'access', 'erasure', 'rectification', 'access', 'portability', 'access', 'erasure', 'access', 'objection', 'access', 'rectification', 'access', 'erasure'];
    const fulfilled = types.map((t, i) =>
      mkFulfilled(i + 1, `subj_${String(101 + i)}`, t, 250 - i * 16, 250 - i * 16 - 12, handlers[i % 2]),
    );
    const open: API.DsarRecord[] = [
      {
        // bald fällig — Frist in ~5 Tagen
        id: 'dsar_201',
        organizationId: 'org_demo',
        subjectRef: 'subj_201',
        requestType: 'access',
        status: 'in_progress',
        receivedAt: new Date(Date.now() - 25 * day).toISOString(),
        deadlineAt: new Date(Date.now() + 5 * day).toISOString(),
        extendedDeadlineAt: null,
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: 'dsb@nordlicht.example',
        notes: 'Datenzusammenstellung läuft; Bestandssysteme bereits abgefragt.',
        isOverdue: false,
        createdAt: new Date(Date.now() - 25 * day).toISOString(),
        updatedAt: new Date(Date.now() - 2 * day).toISOString(),
      },
      {
        id: 'dsar_202',
        organizationId: 'org_demo',
        subjectRef: 'subj_202',
        requestType: 'erasure',
        status: 'open',
        receivedAt: new Date(Date.now() - 8 * day).toISOString(),
        deadlineAt: new Date(Date.now() + 22 * day).toISOString(),
        extendedDeadlineAt: null,
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: null,
        notes: 'Prüfung gesetzlicher Aufbewahrungspflichten (§ 257 HGB / VAG) vor Löschung.',
        isOverdue: false,
        createdAt: new Date(Date.now() - 8 * day).toISOString(),
        updatedAt: new Date(Date.now() - 3 * day).toISOString(),
      },
      {
        id: 'dsar_203',
        organizationId: 'org_demo',
        subjectRef: 'subj_203',
        requestType: 'access',
        status: 'open',
        receivedAt: new Date(Date.now() - 3 * day).toISOString(),
        deadlineAt: new Date(Date.now() + 27 * day).toISOString(),
        extendedDeadlineAt: null,
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: null,
        notes: 'Eingang bestätigt; Identitätsprüfung abgeschlossen.',
        isOverdue: false,
        createdAt: new Date(Date.now() - 3 * day).toISOString(),
        updatedAt: new Date(Date.now() - 3 * day).toISOString(),
      },
    ];
    let dsars = [...open, ...fulfilled];
    if (params?.status) dsars = dsars.filter((d) => d.status === params.status);
    if (params?.requestType) dsars = dsars.filter((d) => d.requestType === params.requestType);
    return { dsars };
  },

  async getDsar(id: string): Promise<API.DsarDetailResponse> {
    await delay(200);
    return {
      dsar: {
        id,
        organizationId: 'org_demo',
        subjectRef: 'subj_001',
        requestType: 'access',
        status: 'open',
        receivedAt: '2026-05-15T10:00:00Z',
        deadlineAt: '2026-06-15T10:00:00Z',
        extendedDeadlineAt: null,
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: null,
        notes: 'Awaiting data collection',
        isOverdue: false,
        createdAt: '2026-05-15T10:00:00Z',
        updatedAt: '2026-05-20T14:30:00Z',
      },
    };
  },

  async createDsar(data: any) {
    await delay(400);
    const now = new Date();
    const deadline = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      dsar: {
        id: `dsar_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        status: 'open',
        receivedAt: now.toISOString(),
        deadlineAt: deadline.toISOString(),
        extendedDeadlineAt: null,
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: null,
        notes: '',
        isOverdue: false,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      },
    };
  },

  async updateDsar(id: string, data: any) {
    await delay(300);
    return { dsar: { id, organizationId: 'org_demo', ...data } };
  },

  async transitionDsarStatus(id: string, status: string, data?: any): Promise<API.DsarDetailResponse> {
    await delay(300);
    const nextStatus = oneOf<API.DsarStatus>(['open', 'in_progress', 'fulfilled', 'rejected'], status, 'open');
    return {
      dsar: {
        id,
        organizationId: 'org_demo',
        subjectRef: 'subj_001',
        requestType: 'access',
        status: nextStatus,
        receivedAt: '2026-05-15T10:00:00Z',
        deadlineAt: '2026-06-15T10:00:00Z',
        extendedDeadlineAt: null,
        fulfilledAt: status === 'fulfilled' ? new Date().toISOString() : null,
        rejectionReason: data?.rejectionReason || '',
        handledBy: data?.handledBy || null,
        notes: data?.notes || '',
        isOverdue: false,
        createdAt: '2026-05-15T10:00:00Z',
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async extendDsarDeadline(id: string, notes?: string): Promise<API.DsarDetailResponse> {
    await delay(300);
    const extendedDate = new Date(new Date('2026-06-15T10:00:00Z').getTime() + 60 * 24 * 60 * 60 * 1000);
    return {
      dsar: {
        id,
        organizationId: 'org_demo',
        subjectRef: 'subj_001',
        requestType: 'access',
        status: 'open',
        receivedAt: '2026-05-15T10:00:00Z',
        deadlineAt: '2026-06-15T10:00:00Z',
        extendedDeadlineAt: extendedDate.toISOString(),
        fulfilledAt: null,
        rejectionReason: '',
        handledBy: null,
        notes: notes || 'Extended per Art. 12(3)',
        isOverdue: false,
        createdAt: '2026-05-15T10:00:00Z',
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async deleteDsar(_id: string) {
    await delay(300);
    return { deleted: true };
  },

  // 3. SUB-PROCESSOR REGISTRY — 4 aktiv (alle EU), 1 mit historisch beigelegtem Einspruch
  async listSubProcessors(params?: any): Promise<API.SubProcessorListResponse> {
    await delay(300);
    let subProcessors: API.SubProcessorRecord[] = [
      {
        id: 'sp_001',
        organizationId: 'org_demo',
        name: 'Adjudon GmbH',
        purpose: 'Audit-/Nachweis-Schicht der KI-Entscheidungen',
        category: 'ai_compute',
        location: 'EU (Frankfurt)',
        isEU: true,
        dpaUrl: 'https://adjudon.com/dpa',
        status: 'active',
        criticalIctService: true,
        noticeSentAt: '2025-08-25T10:00:00Z',
        objectionDeadline: '2025-09-08T10:00:00Z',
        objectionRaisedAt: null,
        objectionReason: '',
        objectionResolvedAt: null,
        addedAt: '2025-09-15T09:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        notes: 'Auftragsverarbeiter nach Art. 28; kritischer IKT-Dienst nach DORA.',
        euCompliant: true,
      },
      {
        id: 'sp_002',
        organizationId: 'org_demo',
        name: 'ClaimsCore Software GmbH',
        purpose: 'Kernsystem Schaden/Leistung',
        category: 'infrastructure',
        location: 'EU (Deutschland)',
        isEU: true,
        dpaUrl: 'https://claimscore.example/dpa',
        status: 'active',
        criticalIctService: true,
        noticeSentAt: '2025-09-20T10:00:00Z',
        objectionDeadline: '2025-10-04T10:00:00Z',
        objectionRaisedAt: '2025-09-28T10:00:00Z',
        objectionReason: 'Audit-Rechte (Art. 28(3)(h)) im Entwurf zu eng gefasst.',
        objectionResolvedAt: '2025-10-15T10:00:00Z',
        addedAt: '2025-09-15T09:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        notes: 'Einspruch beigelegt: erweiterte Audit-Klausel nachverhandelt.',
        euCompliant: true,
      },
      {
        id: 'sp_003',
        organizationId: 'org_demo',
        name: 'Mollie B.V.',
        purpose: 'Beitragseinzug / Zahlungsabwicklung',
        category: 'payment',
        location: 'EU (Niederlande)',
        isEU: true,
        dpaUrl: 'https://mollie.com/dpa',
        status: 'active',
        criticalIctService: false,
        noticeSentAt: '2025-11-03T10:00:00Z',
        objectionDeadline: '2025-11-17T10:00:00Z',
        objectionRaisedAt: null,
        objectionReason: '',
        objectionResolvedAt: null,
        addedAt: '2025-11-20T09:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        notes: '',
        euCompliant: true,
      },
      {
        id: 'sp_004',
        organizationId: 'org_demo',
        name: 'Brevo (Sendinblue) — EU',
        purpose: 'Transaktions-E-Mail an Versicherungsnehmer',
        category: 'email',
        location: 'EU (Frankreich)',
        isEU: true,
        dpaUrl: 'https://brevo.com/dpa',
        status: 'active',
        criticalIctService: false,
        noticeSentAt: '2026-02-10T10:00:00Z',
        objectionDeadline: '2026-02-24T10:00:00Z',
        objectionRaisedAt: null,
        objectionReason: '',
        objectionResolvedAt: null,
        addedAt: '2026-02-28T09:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        notes: '',
        euCompliant: true,
      },
    ];
    if (params?.status) subProcessors = subProcessors.filter((s) => s.status === params.status);
    return { subProcessors };
  },

  async getSubProcessor(id: string): Promise<API.SubProcessorDetailResponse> {
    await delay(200);
    return {
      subProcessor: {
        id,
        organizationId: 'org_demo',
        name: 'Stripe Payments',
        purpose: 'Zahlungsabwicklung',
        category: 'payment',
        location: 'US',
        isEU: false,
        dpaUrl: 'https://stripe.com/dpa',
        status: 'active',
        criticalIctService: false,
        noticeSentAt: null,
        objectionDeadline: null,
        objectionRaisedAt: null,
        objectionReason: '',
        objectionResolvedAt: null,
        addedAt: '2026-01-10T10:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        notes: '',
        euCompliant: true,
      },
    };
  },

  async createSubProcessor(data: any) {
    await delay(400);
    return {
      subProcessor: {
        id: `sp_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        status: 'pending',
        addedAt: new Date().toISOString(),
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: null,
        euCompliant: (data.location === 'EU' || data.location === 'EEA'),
      },
    };
  },

  async updateSubProcessor(id: string, data: any) {
    await delay(300);
    return { subProcessor: { id, organizationId: 'org_demo', ...data } };
  },

  // Art. 28(3)(d) — Einspruchs-Workflow (Demo simuliert nur den Status-Flip).
  async objectSubProcessor(id: string, reason: string): Promise<API.SubProcessorDetailResponse> {
    await delay(300);
    const base = (await this.getSubProcessor(id)).subProcessor;
    return { subProcessor: { ...base, objectionRaisedAt: new Date().toISOString(), objectionReason: reason } };
  },

  async resolveSubProcessorObjection(id: string, approve: boolean): Promise<API.SubProcessorDetailResponse> {
    await delay(300);
    const base = (await this.getSubProcessor(id)).subProcessor;
    return {
      subProcessor: {
        ...base,
        status: approve ? 'active' : 'terminated',
        objectionResolvedAt: new Date().toISOString(),
        terminatedAt: approve ? null : new Date().toISOString(),
      },
    };
  },

  async terminateSubProcessor(id: string): Promise<API.SubProcessorDetailResponse> {
    await delay(300);
    return {
      subProcessor: {
        id,
        organizationId: 'org_demo',
        name: 'Stripe Payments',
        purpose: 'Zahlungsabwicklung',
        category: 'payment',
        location: 'US',
        isEU: false,
        dpaUrl: 'https://stripe.com/dpa',
        status: 'terminated',
        criticalIctService: false,
        noticeSentAt: null,
        objectionDeadline: null,
        objectionRaisedAt: null,
        objectionReason: '',
        objectionResolvedAt: null,
        addedAt: '2026-01-10T10:00:00Z',
        addedByEmail: 'dsb@nordlicht.example',
        terminatedAt: new Date().toISOString(),
        notes: 'Service discontinued',
        euCompliant: true,
      },
    };
  },

  // 4. DATA ACCESS REQUESTS (EU Data Act)
  async listDataAccessRequests(params?: any): Promise<API.DataAccessListResponse> {
    await delay(300);
    const day = 86400000;
    const dataAccessRequests: API.DataAccessRequest[] = [
      {
        // Freigegeben in Frist
        id: 'dar_001', organizationId: 'org_demo',
        requesterRef: 'partner-telematik-abc', requesterType: 'third_party',
        dataScope: 'Telematik-Fahrdaten 2026-Q1', purpose: 'Nutzungsbasierte Tarifierung',
        status: 'approved', consentRef: 'dac_001',
        decidedAt: new Date(Date.now() - 26 * day).toISOString(), decisionReason: 'Einwilligung dac_001 verifiziert (DSGVO Art. 6(1)(a)).',
        revokedAt: null, lawfulBasis: 'consent', isActive: true,
        deadlineAt: new Date(Date.now() + 4 * day).toISOString(), isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: new Date(Date.now() - 28 * day).toISOString(), updatedAt: new Date(Date.now() - 26 * day).toISOString(),
      },
      {
        // Freigegeben in Frist
        id: 'dar_003', organizationId: 'org_demo',
        requesterRef: 'werkstatt-partner-nord', requesterType: 'third_party',
        dataScope: 'Fahrzeug-Sensordaten (Schadenfall SCH-2026-0418)', purpose: 'Schadenrekonstruktion',
        status: 'approved', consentRef: 'dac_003',
        decidedAt: new Date(Date.now() - 10 * day).toISOString(), decisionReason: 'Einwilligung dac_003 verifiziert; Zweckbindung Schaden.',
        revokedAt: null, lawfulBasis: 'consent', isActive: true,
        deadlineAt: new Date(Date.now() + 18 * day).toISOString(), isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: new Date(Date.now() - 12 * day).toISOString(), updatedAt: new Date(Date.now() - 10 * day).toISOString(),
      },
      {
        // Auto-entzogen, weil Einwilligung dac_002 widerrufen wurde (Widerruf sofort wirksam).
        id: 'dar_002', organizationId: 'org_demo',
        requesterRef: 'rueckversicherer-partner', requesterType: 'third_party',
        dataScope: 'Schadenhistorie 2020–2024', purpose: 'Rückversicherungs-Beurteilung',
        status: 'revoked', consentRef: 'dac_002',
        decidedAt: new Date(Date.now() - 36 * day).toISOString(),
        decisionReason: 'Auto-Revoke: Einwilligung widerrufen — Zugriff sofort entzogen (DSGVO Art. 7 Abs. 3; EU Data Act Art. 6).',
        revokedAt: new Date(Date.now() - 13 * day).toISOString(), lawfulBasis: 'consent', isActive: false,
        deadlineAt: new Date(Date.now() - 8 * day).toISOString(), isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: new Date(Date.now() - 38 * day).toISOString(), updatedAt: new Date(Date.now() - 13 * day).toISOString(),
      },
      {
        // Offen, noch innerhalb der Frist (kein Überfall).
        id: 'dar_004', organizationId: 'org_demo',
        requesterRef: 'insurtech-partner-flotte', requesterType: 'third_party',
        dataScope: 'Flotten-Telematik 2026-Q2', purpose: 'Flotten-Benchmarking',
        status: 'pending', consentRef: null,
        decidedAt: null, decisionReason: '',
        revokedAt: null, lawfulBasis: 'consent', isActive: false,
        deadlineAt: new Date(Date.now() + 23 * day).toISOString(), isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: new Date(Date.now() - 7 * day).toISOString(), updatedAt: new Date(Date.now() - 7 * day).toISOString(),
      },
    ];
    const filtered = params?.status ? dataAccessRequests.filter((r) => r.status === params.status) : dataAccessRequests;
    return { dataAccessRequests: filtered };
  },

  async getDataAccessRequest(id: string): Promise<API.DataAccessDetailResponse> {
    await delay(200);
    return {
      dataAccessRequest: {
        id, organizationId: 'org_demo',
        requesterRef: 'partner-abc', requesterType: 'third_party',
        dataScope: 'telematics 2026-Q1', purpose: 'usage-based pricing',
        status: 'approved', consentRef: 'dac_001',
        decidedAt: '2026-05-18T14:00:00Z', decisionReason: 'Consent dac_001 verified (DSGVO Art. 6(1)(a))',
        revokedAt: null, lawfulBasis: 'consent', isActive: true,
        deadlineAt: '2026-06-14T10:00:00Z', isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: '2026-05-15T10:00:00Z', updatedAt: '2026-05-18T14:00:00Z',
      },
    };
  },

  async createDataAccessRequest(data: any): Promise<API.DataAccessDetailResponse> {
    await delay(400);
    const now = new Date().toISOString();
    return {
      dataAccessRequest: {
        id: `dar_${Date.now()}`, organizationId: 'org_demo',
        requesterRef: data?.requesterRef || 'partner-new',
        requesterType: data?.requesterType || 'third_party',
        dataScope: data?.dataScope || '', purpose: data?.purpose || '',
        status: 'pending', consentRef: null,
        decidedAt: null, decisionReason: '',
        revokedAt: null, lawfulBasis: data?.lawfulBasis || 'consent', isActive: false,
        deadlineAt: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(), isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: now, updatedAt: now,
      },
    };
  },

  async decideDataAccessRequest(id: string, decision: string, decisionReason?: string): Promise<API.DataAccessDetailResponse> {
    await delay(300);
    const status: API.DataAccessStatus = decision === 'approve' ? 'approved' : 'denied';
    return {
      dataAccessRequest: {
        id, organizationId: 'org_demo',
        requesterRef: 'partner-abc', requesterType: 'third_party',
        dataScope: 'telematics 2026-Q1', purpose: 'usage-based pricing',
        status, consentRef: 'dac_001',
        decidedAt: new Date().toISOString(), decisionReason: decisionReason || 'Decision recorded',
        revokedAt: null, lawfulBasis: 'consent', isActive: status === 'approved',
        deadlineAt: '2026-06-14T10:00:00Z', isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: '2026-05-15T10:00:00Z', updatedAt: new Date().toISOString(),
      },
    };
  },

  async revokeDataAccessRequest(id: string): Promise<API.DataAccessDetailResponse> {
    await delay(300);
    return {
      dataAccessRequest: {
        id, organizationId: 'org_demo',
        requesterRef: 'partner-abc', requesterType: 'third_party',
        dataScope: 'telematics 2026-Q1', purpose: 'usage-based pricing',
        status: 'revoked', consentRef: 'dac_001',
        decidedAt: '2026-05-18T14:00:00Z', decisionReason: 'Access revoked',
        revokedAt: new Date().toISOString(), lawfulBasis: 'consent', isActive: false,
        deadlineAt: '2026-06-14T10:00:00Z', isOverdue: false,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        createdAt: '2026-05-15T10:00:00Z', updatedAt: new Date().toISOString(),
      },
    };
  },

  // 4a. ACCESS JOURNAL (EU Data Act "Zugänglichkeits-Log" — Art. 4–5 / FiDA Art. 8)
  async getDataAccessJournal(_params?: any): Promise<API.DataAccessJournalResponse> {
    await delay(250);
    const day = 86400000;
    const ts = (d: number) => new Date(Date.now() - d * day).toISOString();
    const A = 'dsb@nordlicht.example';
    const entries: API.DataAccessJournalEntry[] = [
      { id: 'evt_008', timestamp: ts(7), type: 'audit', action: 'dataaccess.create', eventType: 'Data-access request created', category: 'request', resource: 'dataAccessRequest', resourceId: 'dar_004', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Antrag erstellt für Empfänger insurtech-partner-flotte', chainHash: 'c91a07'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'third_party', lawfulBasis: 'data_act_art_4', currentStatus: 'pending' },
      { id: 'evt_007', timestamp: ts(10), type: 'audit', action: 'dataaccess.approve', eventType: 'Data-access request approved', category: 'request', resource: 'dataAccessRequest', resourceId: 'dar_003', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Antrag dar_003 freigegeben (Schadenrekonstruktion)', chainHash: 'b3f1c2'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'third_party', lawfulBasis: 'data_act_art_4', currentStatus: 'approved' },
      { id: 'evt_006', timestamp: ts(12), type: 'audit', action: 'dataconsent.create', eventType: 'Third-party consent granted', category: 'consent', resource: 'dataconsent', resourceId: 'dac_003', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Einwilligung erteilt: VN-telematik-3 an werkstatt-partner-nord', chainHash: 'aa1290'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'consent', lawfulBasis: null, currentStatus: 'active' },
      { id: 'evt_005', timestamp: ts(13), type: 'audit', action: 'dataaccess.revoke', eventType: 'Data-access revoked', category: 'request', resource: 'dataAccessRequest', resourceId: 'dar_002', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Auto-Revoke nach Widerruf der Einwilligung dac_002', chainHash: 'a17c9d'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'third_party', lawfulBasis: 'data_act_art_4', currentStatus: 'revoked' },
      { id: 'evt_004', timestamp: ts(13), type: 'audit', action: 'dataconsent.withdraw', eventType: 'Consent withdrawn', category: 'consent', resource: 'dataconsent', resourceId: 'dac_002', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Einwilligung dac_002 widerrufen (Versicherungsnehmer)', chainHash: '7711aa'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'consent', lawfulBasis: null, currentStatus: 'withdrawn' },
      { id: 'evt_003', timestamp: ts(26), type: 'audit', action: 'dataaccess.approve', eventType: 'Data-access request approved', category: 'request', resource: 'dataAccessRequest', resourceId: 'dar_001', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Antrag dar_001 freigegeben (nutzungsbasierte Tarifierung)', chainHash: '90de44'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'third_party', lawfulBasis: 'data_act_art_4', currentStatus: 'approved' },
      { id: 'evt_002', timestamp: ts(28), type: 'audit', action: 'dataaccess.create', eventType: 'Data-access request created', category: 'request', resource: 'dataAccessRequest', resourceId: 'dar_001', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Antrag erstellt für Empfänger partner-telematik-abc', chainHash: '5520ef'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'third_party', lawfulBasis: 'data_act_art_4', currentStatus: 'approved' },
      { id: 'evt_001', timestamp: ts(28), type: 'audit', action: 'dataconsent.create', eventType: 'Third-party consent granted', category: 'consent', resource: 'dataconsent', resourceId: 'dac_001', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Einwilligung erteilt: VN-telematik-1 an partner-telematik-abc', chainHash: '33c8b1'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'consent', lawfulBasis: null, currentStatus: 'active' },
      { id: 'evt_000', timestamp: ts(150), type: 'audit', action: 'dataconsent.create', eventType: 'Third-party consent granted', category: 'consent', resource: 'dataconsent', resourceId: 'dac_004', actorEmail: A, actorRole: 'admin', status: 'success', detail: 'Einwilligung erteilt: VN-telematik-4 an insurtech-partner-flotte', chainHash: '0ad9f2'.padEnd(64, '0'), chainNamespace: 'operations', requesterType: 'consent', lawfulBasis: null, currentStatus: 'expired' },
    ];
    return { entries, pagination: { page: 1, limit: 50, total: entries.length, pages: 1 } };
  },

  async getDataAccessJournalSummary(): Promise<API.DataAccessJournalSummaryResponse> {
    await delay(150);
    return {
      summary: {
        totalEvents: 9, requestEvents: 5, consentEvents: 4,
        approvals: 2, denials: 0, revocations: 1, withdrawals: 1,
        lastEventAt: new Date(Date.now() - 7 * 86400000).toISOString(),
        byAction: { 'dataaccess.create': 2, 'dataaccess.approve': 2, 'dataaccess.revoke': 1, 'dataconsent.create': 3, 'dataconsent.withdraw': 1 },
      },
    };
  },

  // 4b. THIRD-PARTY DATA-SHARING CONSENT (EU Data Act + FiDA) — Auto-Revoke surface.
  async listDataAccessConsents(params?: any): Promise<API.DataAccessConsentListResponse> {
    await delay(300);
    const day = 86400000;
    const consents: API.DataAccessConsent[] = [
      {
        id: 'dac_001', organizationId: 'org_demo',
        subjectRef: 'VN-telematik-1', recipientRef: 'partner-telematik-abc',
        dataScope: 'Telematik-Fahrdaten 2026-Q1', purpose: 'Nutzungsbasierte Tarifierung (Pay-as-you-drive)',
        grantedAt: new Date(Date.now() - 28 * day).toISOString(), expiresAt: null,
        withdrawnAt: null, withdrawalReason: null, status: 'active',
        isActive: true, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: '', withdrawnByEmail: '',
        createdAt: new Date(Date.now() - 28 * day).toISOString(), updatedAt: new Date(Date.now() - 28 * day).toISOString(),
      },
      {
        id: 'dac_003', organizationId: 'org_demo',
        subjectRef: 'VN-telematik-3', recipientRef: 'werkstatt-partner-nord',
        dataScope: 'Fahrzeug-Sensordaten (Schadenfall SCH-2026-0418)', purpose: 'Schadenrekonstruktion',
        grantedAt: new Date(Date.now() - 12 * day).toISOString(), expiresAt: new Date(Date.now() + 60 * day).toISOString(),
        withdrawnAt: null, withdrawalReason: null, status: 'active',
        isActive: true, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: '', withdrawnByEmail: '',
        createdAt: new Date(Date.now() - 12 * day).toISOString(), updatedAt: new Date(Date.now() - 12 * day).toISOString(),
      },
      {
        // Widerrufen → der verknüpfte freigegebene Antrag (dar_002) wurde auto-entzogen.
        id: 'dac_002', organizationId: 'org_demo',
        subjectRef: 'VN-telematik-2', recipientRef: 'rueckversicherer-partner',
        dataScope: 'Schadenhistorie 2020–2024', purpose: 'Rückversicherungs-Beurteilung',
        grantedAt: new Date(Date.now() - 38 * day).toISOString(), expiresAt: null,
        withdrawnAt: new Date(Date.now() - 13 * day).toISOString(), withdrawalReason: 'Versicherungsnehmer hat Einwilligung widerrufen',
        status: 'withdrawn', isActive: false, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: 'dsb@nordlicht.example', withdrawnByEmail: 'dsb@nordlicht.example',
        createdAt: new Date(Date.now() - 38 * day).toISOString(), updatedAt: new Date(Date.now() - 13 * day).toISOString(),
      },
      {
        // Abgelaufen — Befristung erreicht, kein Widerruf nötig.
        id: 'dac_004', organizationId: 'org_demo',
        subjectRef: 'VN-telematik-4', recipientRef: 'insurtech-partner-flotte',
        dataScope: 'Flotten-Telematik 2025-Q4', purpose: 'Flotten-Benchmarking',
        grantedAt: new Date(Date.now() - 150 * day).toISOString(), expiresAt: new Date(Date.now() - 20 * day).toISOString(),
        withdrawnAt: null, withdrawalReason: null, status: 'expired',
        isActive: false, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: '', withdrawnByEmail: '',
        createdAt: new Date(Date.now() - 150 * day).toISOString(), updatedAt: new Date(Date.now() - 20 * day).toISOString(),
      },
    ];
    const filtered = params?.status ? consents.filter((c) => c.status === params.status) : consents;
    return { consents: filtered };
  },

  async getDataAccessConsent(id: string): Promise<API.DataAccessConsentDetailResponse> {
    await delay(200);
    return {
      consent: {
        id, organizationId: 'org_demo',
        subjectRef: 'driver-1', recipientRef: 'partner-abc',
        dataScope: 'telematics 2026-Q1', purpose: 'usage-based pricing',
        grantedAt: '2026-05-15T10:00:00Z', expiresAt: null,
        withdrawnAt: null, withdrawalReason: null, status: 'active',
        isActive: true, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: '', withdrawnByEmail: '',
        createdAt: '2026-05-15T10:00:00Z', updatedAt: '2026-05-15T10:00:00Z',
      },
    };
  },

  async createDataAccessConsent(data: any): Promise<API.DataAccessConsentDetailResponse> {
    await delay(400);
    const now = new Date().toISOString();
    return {
      consent: {
        id: `dac_${Date.now()}`, organizationId: 'org_demo',
        subjectRef: data?.subjectRef || 'driver-new', recipientRef: data?.recipientRef || 'partner-new',
        dataScope: data?.dataScope || '', purpose: data?.purpose || '',
        grantedAt: now, expiresAt: data?.expiresAt || null,
        withdrawnAt: null, withdrawalReason: null, status: 'active',
        isActive: true, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: '', withdrawnByEmail: '',
        createdAt: now, updatedAt: now,
      },
    };
  },

  async withdrawDataAccessConsent(id: string, withdrawalReason?: string): Promise<API.DataAccessConsentWithdrawResponse> {
    await delay(300);
    const now = new Date().toISOString();
    return {
      consent: {
        id, organizationId: 'org_demo',
        subjectRef: 'driver-1', recipientRef: 'partner-abc',
        dataScope: 'telematics 2026-Q1', purpose: 'usage-based pricing',
        grantedAt: '2026-05-15T10:00:00Z', expiresAt: null,
        withdrawnAt: now, withdrawalReason: withdrawalReason || 'Widerruf',
        status: 'withdrawn', isActive: false, completeness: 100,
        createdBy: 'dsb@nordlicht.example', createdByEmail: 'dsb@nordlicht.example',
        withdrawnBy: 'dsb@nordlicht.example', withdrawnByEmail: 'dsb@nordlicht.example',
        createdAt: '2026-05-15T10:00:00Z', updatedAt: now,
      },
      autoRevokedCount: 1,
    };
  },

  // 5. BIAS STRATIFICATION AUDITS
  // Session-Store (wie _policyStore) — Bias-Audits, persona-konsistent mit den
  // Fairness-Remediationen unten (gleiche audit-IDs). Schwellen-Story der maxGap:
  // ≥0.04 = breach, ≥0.02 = watch, sonst healthy.
  _biasAuditStore: null as API.BiasStratificationAudit[] | null,
  _biasAuditSeed(): API.BiasStratificationAudit[] {
    if (!this._biasAuditStore) this._biasAuditStore = this._buildBiasAuditSeeds();
    return this._biasAuditStore;
  },
  _buildBiasAuditSeeds(): API.BiasStratificationAudit[] {
    const day = 86400000;
    const iso = (d: number) => new Date(Date.now() - d * day).toISOString();
    return [
      {
        id: 'bias_uw_leben_q2',
        organizationId: 'org_demo',
        agentId: 'agent_uw_leben',
        period: 'Q2 2026',
        dimensions: [
          {
            name: 'Alter',
            groups: [
              { label: '18–34', sampleSize: 3120, ece: 0.021, brier: 0.084 },
              { label: '35–54', sampleSize: 2400, ece: 0.024, brier: 0.086 },
              { label: '55+', sampleSize: 1180, ece: 0.062, brier: 0.103 },
            ],
            maxBetweenGroupGap: 0.041,
          },
          {
            name: 'Geschlecht',
            groups: [
              { label: 'weiblich', sampleSize: 3300, ece: 0.030, brier: 0.088 },
              { label: 'männlich', sampleSize: 3400, ece: 0.028, brier: 0.087 },
            ],
            maxBetweenGroupGap: 0.002,
          },
        ],
        overallStatus: 'breach',
        requiredAttributes: ['Geschlecht', 'Alter', 'Region'],
        testedAttributes: ['Geschlecht', 'Alter'],
        unTestedAttributes: ['Region'],
        coveragePercent: 67,
        assessedAt: iso(10),
        metadata: { note: 'Abweichung Altersgruppe 55+ über Schwelle — Remediation rem_003 angestoßen.' },
        createdAt: iso(10),
        updatedAt: iso(10),
      },
      {
        id: 'bias_pricing_q1',
        organizationId: 'org_demo',
        agentId: 'agent_pricing',
        period: 'Q1 2026',
        dimensions: [
          {
            name: 'Region',
            groups: [
              { label: 'Nord', sampleSize: 2600, ece: 0.031, brier: 0.090 },
              { label: 'Süd', sampleSize: 2400, ece: 0.038, brier: 0.094 },
              { label: 'Ost', sampleSize: 1500, ece: 0.059, brier: 0.108 },
              { label: 'West', sampleSize: 2100, ece: 0.034, brier: 0.092 },
            ],
            maxBetweenGroupGap: 0.028,
          },
        ],
        overallStatus: 'watch',
        requiredAttributes: ['Region', 'Alter'],
        testedAttributes: ['Region'],
        unTestedAttributes: ['Alter'],
        coveragePercent: 50,
        assessedAt: iso(80),
        metadata: { note: 'Regionale Prämienspreizung über Beobachtungsschwelle — Remediation rem_002 angenommen, Umsetzung ausstehend.' },
        createdAt: iso(80),
        updatedAt: iso(55),
      },
      {
        id: 'bias_fraud_q1',
        organizationId: 'org_demo',
        agentId: 'agent_fraud',
        period: 'Q1 2026',
        dimensions: [
          {
            name: 'Geschlecht',
            groups: [
              { label: 'weiblich', sampleSize: 5400, ece: 0.039, brier: 0.071 },
              { label: 'männlich', sampleSize: 5600, ece: 0.051, brier: 0.078 },
            ],
            maxBetweenGroupGap: 0.012,
          },
        ],
        overallStatus: 'healthy',
        requiredAttributes: ['Geschlecht', 'Region'],
        testedAttributes: ['Geschlecht', 'Region'],
        unTestedAttributes: [],
        coveragePercent: 100,
        assessedAt: iso(120),
        metadata: { note: 'Innerhalb Toleranz; verstärktes Monitoring (rem_001).' },
        createdAt: iso(130),
        updatedAt: iso(110),
      },
    ];
  },
  _cloneBiasAudit(a: API.BiasStratificationAudit): API.BiasStratificationAudit {
    return { ...a, dimensions: a.dimensions.map((d) => ({ ...d, groups: d.groups.map((g) => ({ ...g })) })) };
  },

  async listBiasAudits(params?: { agentId?: string; status?: string }) {
    await delay(300);
    let audits = this._biasAuditSeed().map((a) => this._cloneBiasAudit(a));
    if (params?.agentId) audits = audits.filter((a) => a.agentId === params.agentId);
    if (params?.status) audits = audits.filter((a) => a.overallStatus === params.status);
    return { audits };
  },

  async getBiasAudit(id: string) {
    await delay(200);
    const a = this._biasAuditSeed().find((x) => x.id === id);
    if (!a) throw this._vvgErr('NOT_FOUND');
    return { audit: this._cloneBiasAudit(a) };
  },

  async createBiasAudit(data: any) {
    await delay(400);
    const store = this._biasAuditSeed();
    const required: string[] = Array.isArray(data?.requiredAttributes) ? data.requiredAttributes : [];
    const tested: string[] = Array.isArray(data?.dimensions) ? data.dimensions.map((d: any) => d?.name).filter(Boolean) : [];
    const unTested = required.filter((r) => !tested.includes(r));
    const now = new Date().toISOString();
    const created: API.BiasStratificationAudit = {
      id: `bias_${_hashAgentId((data?.agentId ?? '') + now).toString(16).slice(0, 8)}`,
      organizationId: 'org_demo',
      agentId: data?.agentId ?? null,
      period: data?.period ?? '—',
      dimensions: data?.dimensions ?? [],
      overallStatus: 'healthy',
      requiredAttributes: required,
      testedAttributes: tested,
      unTestedAttributes: unTested,
      coveragePercent: required.length > 0 ? Math.round(((required.length - unTested.length) / required.length) * 100) : 100,
      assessedAt: now,
      metadata: {},
      createdAt: now,
      updatedAt: now,
    };
    store.unshift(created);
    return { audit: this._cloneBiasAudit(created) };
  },

  // 6. DATA QUALITY VALIDATIONS (Solvency II Art. 82 / 121(3)) — 4 Läufe, 1 mit Befund
  async listDataQualityValidations(_params?: any) {
    await delay(300);
    const day = 86400000;
    return {
      validations: [
        {
          id: 'dq_004',
          organizationId: 'org_demo',
          sourceRef: 'underwriting_inputs_leben',
          checks: [
            { name: 'null_check', passed: true, detail: 'Keine Nullwerte in Pflichtfeldern.' },
            { name: 'uniqueness_check', passed: true, detail: 'Alle Vertrags-IDs eindeutig.' },
            { name: 'format_check', passed: true, detail: 'Datumsfelder gültiges ISO-Format.' },
          ],
          score: 94,
          worstCheck: null,
          validatedAt: new Date(Date.now() - 3 * day).toISOString(),
          createdAt: new Date(Date.now() - 3 * day).toISOString(),
          updatedAt: new Date(Date.now() - 3 * day).toISOString(),
        },
        {
          id: 'dq_003',
          organizationId: 'org_demo',
          sourceRef: 'pricing_inputs_komposit',
          checks: [
            { name: 'null_check', passed: true, detail: 'Keine Nullwerte in Pflichtfeldern.' },
            { name: 'completeness_check', passed: false, detail: 'Regionalschlüssel bei 2,3 % der Datensätze fehlend (Befund).' },
            { name: 'format_check', passed: true, detail: 'Tarifmerkmale formatkonform.' },
          ],
          score: 81,
          worstCheck: 'completeness_check',
          validatedAt: new Date(Date.now() - 18 * day).toISOString(),
          createdAt: new Date(Date.now() - 18 * day).toISOString(),
          updatedAt: new Date(Date.now() - 18 * day).toISOString(),
        },
        {
          id: 'dq_002',
          organizationId: 'org_demo',
          sourceRef: 'fraud_inputs_schaden',
          checks: [
            { name: 'null_check', passed: true, detail: 'Keine Nullwerte in Pflichtfeldern.' },
            { name: 'uniqueness_check', passed: true, detail: 'Schadennummern eindeutig.' },
            { name: 'format_check', passed: true, detail: 'Beträge numerisch validiert.' },
          ],
          score: 92,
          worstCheck: null,
          validatedAt: new Date(Date.now() - 40 * day).toISOString(),
          createdAt: new Date(Date.now() - 40 * day).toISOString(),
          updatedAt: new Date(Date.now() - 40 * day).toISOString(),
        },
        {
          id: 'dq_001',
          organizationId: 'org_demo',
          sourceRef: 'underwriting_inputs_leben',
          checks: [
            { name: 'null_check', passed: true, detail: 'Keine Nullwerte in Pflichtfeldern.' },
            { name: 'uniqueness_check', passed: true, detail: 'Alle IDs eindeutig.' },
            { name: 'format_check', passed: true, detail: 'Datumsfelder gültiges ISO-Format.' },
          ],
          score: 90,
          worstCheck: null,
          validatedAt: new Date(Date.now() - 75 * day).toISOString(),
          createdAt: new Date(Date.now() - 75 * day).toISOString(),
          updatedAt: new Date(Date.now() - 75 * day).toISOString(),
        },
      ],
    };
  },

  async getDataQualityValidation(id: string) {
    await delay(200);
    return {
      validation: {
        id,
        organizationId: 'org_demo',
        sourceRef: 'underwriting_inputs_leben',
        checks: [],
        score: 95,
        worstCheck: null,
        validatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async createDataQualityValidation(data: any) {
    await delay(400);
    return {
      validation: {
        id: `dq_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        score: 85,
        worstCheck: null,
        validatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // 7. ARTICLE 22 EXPLANATIONS
  async listArt22Explanations(_params?: any): Promise<API.Art22ListResponse> {
    await delay(300);
    return {
      explanations: [
        {
          id: 'exp_001',
          organizationId: 'org_demo',
          traceId: 'tr_uw_44821',
          requestedByRef: 'subj_117',
          factors: [
            { label: 'Gesundheitsangaben Antrag', contribution: 'opposing', weight: 0.42, source: 'application_form' },
            { label: 'Risikoprüfungs-Score Leben', contribution: 'opposing', weight: 0.33, source: 'uw_model' },
            { label: 'Bestandshistorie', contribution: 'supporting', weight: 0.25, source: 'policy_history' },
          ],
          summary: 'Erschwerung der Lebenversicherung auf Basis der Risikoprüfung; manuelle Vorlage erfolgt.',
          derivedFromStoredTrace: true,
          appealStatus: 'none',
          appealRequestedAt: null,
          dataSubjectStatement: '',
          appealReviewNotes: '',
          appealResolvedAt: null,
          appealResolvedByRef: null,
          createdAt: '2026-05-18T10:00:00Z',
          updatedAt: '2026-05-18T10:00:00Z',
        },
        {
          // Historisch: Betroffener hat angefochten, Prüfung ergab Korrektur (overturned).
          id: 'exp_002',
          organizationId: 'org_demo',
          traceId: 'tr_uw_41097',
          requestedByRef: 'subj_092',
          factors: [
            { label: 'Bonitätshistorie', contribution: 'opposing', weight: 0.38, source: 'bureau_data' },
            { label: 'Einkommensquote', contribution: 'supporting', weight: 0.37, source: 'income_proof' },
            { label: 'Vertragslaufzeit', contribution: 'supporting', weight: 0.25, source: 'policy_history' },
          ],
          summary: 'Ablehnung Ratenzahlungsoption; Betroffener legte korrigierten Einkommensnachweis vor.',
          derivedFromStoredTrace: true,
          appealStatus: 'overturned',
          appealRequestedAt: '2026-03-04T09:00:00Z',
          dataSubjectStatement: 'Einkommensnachweis war veraltet; aktueller Nachweis beigefügt.',
          appealReviewNotes: 'Sachbearbeitung bestätigt korrigierten Nachweis; Entscheidung manuell revidiert.',
          appealResolvedAt: '2026-03-11T14:00:00Z',
          appealResolvedByRef: 'dsb@nordlicht.example',
          createdAt: '2026-02-26T10:00:00Z',
          updatedAt: '2026-03-11T14:00:00Z',
        },
      ],
    };
  },

  async getArt22Explanation(id: string): Promise<API.Art22DetailResponse> {
    await delay(200);
    return {
      explanation: {
        id,
        organizationId: 'org_demo',
        traceId: 'tr_001',
        requestedByRef: null,
        factors: [],
        summary: 'Explanation',
        derivedFromStoredTrace: true,
        appealStatus: 'none',
        appealRequestedAt: null,
        dataSubjectStatement: '',
        appealReviewNotes: '',
        appealResolvedAt: null,
        appealResolvedByRef: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async getArt22ExplanationForTrace(traceId: string): Promise<API.Art22DetailResponse> {
    await delay(200);
    return {
      explanation: {
        id: `exp_${traceId}`,
        organizationId: 'org_demo',
        traceId,
        requestedByRef: null,
        factors: [],
        summary: 'Auto-generated explanation',
        derivedFromStoredTrace: true,
        appealStatus: 'none',
        appealRequestedAt: null,
        dataSubjectStatement: '',
        appealReviewNotes: '',
        appealResolvedAt: null,
        appealResolvedByRef: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async requestArt22Appeal(id: string, dataSubjectStatement?: string): Promise<API.Art22DetailResponse> {
    await delay(300);
    return {
      explanation: {
        id,
        organizationId: 'org_demo',
        traceId: 'tr_001',
        requestedByRef: 'subj_001',
        factors: [],
        summary: 'Explanation',
        derivedFromStoredTrace: true,
        appealStatus: 'under_review',
        appealRequestedAt: new Date().toISOString(),
        dataSubjectStatement: dataSubjectStatement || '',
        appealReviewNotes: '',
        appealResolvedAt: null,
        appealResolvedByRef: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async reviewArt22Appeal(id: string, status: string, notes?: string): Promise<API.Art22DetailResponse> {
    await delay(300);
    const appealStatus = oneOf<API.Art22AppealStatus>(['none', 'requested', 'under_review', 'upheld', 'overturned'], status, 'under_review');
    return {
      explanation: {
        id,
        organizationId: 'org_demo',
        traceId: 'tr_001',
        requestedByRef: 'subj_001',
        factors: [],
        summary: 'Explanation',
        derivedFromStoredTrace: true,
        appealStatus,
        appealRequestedAt: '2026-05-20T10:00:00Z',
        dataSubjectStatement: '',
        appealReviewNotes: notes || '',
        appealResolvedAt: new Date().toISOString(),
        appealResolvedByRef: 'dsb@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // 8. KYC DECISIONS (GwG) — Register ~9 Monate Bestand; ~85 % current, 2 review_due
  async listKycDecisions(params?: any): Promise<API.KycListResponse> {
    await delay(300);
    const mk = (
      id: string,
      customerRef: string,
      stufe: API.KycDecision['sorgfaltsstufe'],
      method: API.KycDecision['verificationMethod'],
      pep: boolean,
      uboIdentified: boolean,
      risk: API.KycDecision['riskRating'],
      status: API.KycDecision['status'],
      verifiedDaysAgo: number | null,
      reviewDueDaysAhead: number | null,
      notes: string,
    ): API.KycDecision => {
      const verifiedAt = verifiedDaysAgo !== null ? new Date(Date.now() - verifiedDaysAgo * 86400000).toISOString() : null;
      return {
        id,
        organizationId: 'org_demo',
        customerRef,
        sorgfaltsstufe: stufe,
        verificationMethod: method,
        pepFlag: pep,
        beneficialOwnerIdentified: uboIdentified,
        riskRating: risk,
        nextReviewDate: verifiedAt ? new Date(Date.now() + (365 + (reviewDueDaysAhead ?? 0)) * 86400000).toISOString() : null,
        decisionRef: status === 'verified' || status === 'review_due' ? `kyc_dec_${id.slice(-3)}` : null,
        status,
        notes,
        verifiedAt,
        reviewDueAt: status === 'review_due' ? new Date(Date.now() - 6 * 86400000).toISOString() : null,
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: verifiedAt ?? new Date().toISOString(),
        updatedAt: new Date(Date.now() - (verifiedDaysAgo ?? 0) * 86400000).toISOString(),
      };
    };
    let kyc = [
      mk('kyc_001', 'KD-100482', 'allgemein', 'video_call', false, true, 'medium', 'verified', 18, 200, 'Lebenpolice; Standard-Sorgfalt abgeschlossen.'),
      mk('kyc_002', 'KD-100517', 'allgemein', 'id_document', false, true, 'low', 'verified', 35, 240, 'bAV-Vertrag über Arbeitgeber.'),
      mk('kyc_003', 'KD-100533', 'verstaerkt', 'eidas_eseal', true, true, 'high', 'verified', 41, 120, 'PEP — EDD-Fall edd_001 verknüpft, verstärkte Überwachung aktiv.'),
      mk('kyc_004', 'KD-100558', 'allgemein', 'video_call', false, true, 'medium', 'verified', 60, 180, ''),
      mk('kyc_005', 'KD-100571', 'verstaerkt', 'video_call', false, true, 'high', 'verified', 72, 90, 'HNW-Klientel, Einmalbeitrag > 100T€; Mittelherkunft belegt.'),
      mk('kyc_006', 'KD-100604', 'allgemein', 'id_document', false, true, 'low', 'verified', 95, 260, ''),
      mk('kyc_007', 'KD-100629', 'allgemein', 'video_call', false, false, 'medium', 'review_due', 380, null, 'Wirtschaftlich Berechtigter zu re-identifizieren; Jahresturnus fällig (§ 10 Abs. 3a GwG).'),
      mk('kyc_008', 'KD-100655', 'allgemein', 'id_document', false, true, 'low', 'verified', 110, 220, ''),
      mk('kyc_009', 'KD-100702', 'verstaerkt', 'eidas_eseal', true, true, 'high', 'verified', 130, 70, 'Politisch exponierte Person aus dem Inland.'),
      mk('kyc_010', 'KD-100744', 'allgemein', 'video_call', false, true, 'medium', 'verified', 155, 190, ''),
      mk('kyc_011', 'KD-100788', 'allgemein', 'id_document', false, true, 'low', 'verified', 190, 230, ''),
      mk('kyc_012', 'KD-100810', 'allgemein', 'video_call', false, true, 'medium', 'review_due', 400, null, 'Risikobasierter Aktualisierungs-Turnus erreicht; Anschrift verifizieren.'),
      mk('kyc_013', 'KD-100845', 'einfach', 'id_document', false, true, 'low', 'verified', 50, 300, 'Vereinfachte Sorgfalt (geringes Risiko, § 14 GwG).'),
      mk('kyc_014', 'KD-100861', 'allgemein', 'video_call', false, true, 'medium', 'rejected', null, null, 'Identifizierung gescheitert — Vertrag nicht zustande gekommen.'),
    ];
    if (params?.status) kyc = kyc.filter((k) => k.status === params.status);
    return { kyc };
  },

  async getKycDecision(id: string): Promise<API.KycDetailResponse> {
    await delay(200);
    return {
      kyc: {
        id,
        organizationId: 'org_demo',
        customerRef: 'CUST_001',
        sorgfaltsstufe: 'allgemein',
        verificationMethod: 'id_document',
        pepFlag: false,
        beneficialOwnerIdentified: true,
        riskRating: 'medium',
        nextReviewDate: '2027-05-20T10:00:00Z',
        decisionRef: null,
        status: 'open',
        notes: '',
        verifiedAt: null,
        reviewDueAt: null,
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async createKycDecision(data: any) {
    await delay(400);
    return {
      kyc: {
        id: `kyc_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        status: 'open',
        verifiedAt: null,
        reviewDueAt: null,
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async updateKycDecision(id: string, data: any) {
    await delay(300);
    return { kyc: { id, organizationId: 'org_demo', ...data } };
  },

  async verifyKycDecision(id: string): Promise<API.KycDetailResponse> {
    await delay(300);
    return {
      kyc: {
        id,
        organizationId: 'org_demo',
        customerRef: 'CUST_001',
        sorgfaltsstufe: 'allgemein',
        verificationMethod: 'id_document',
        pepFlag: false,
        beneficialOwnerIdentified: true,
        riskRating: 'medium',
        nextReviewDate: '2027-05-20T10:00:00Z',
        decisionRef: `kyc_dec_${Date.now()}`,
        status: 'verified',
        notes: '',
        verifiedAt: new Date().toISOString(),
        reviewDueAt: null,
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async rejectKycDecision(id: string, reason?: string): Promise<API.KycDetailResponse> {
    await delay(300);
    return {
      kyc: {
        id,
        organizationId: 'org_demo',
        customerRef: 'CUST_001',
        sorgfaltsstufe: 'allgemein',
        verificationMethod: 'id_document',
        pepFlag: false,
        beneficialOwnerIdentified: false,
        riskRating: 'high',
        nextReviewDate: null,
        decisionRef: `kyc_dec_${Date.now()}`,
        status: 'rejected',
        notes: reason || 'Verification failed',
        verifiedAt: null,
        reviewDueAt: null,
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async markKycReviewDue(id: string): Promise<API.KycDetailResponse> {
    await delay(300);
    const reviewDueDate = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      kyc: {
        id,
        organizationId: 'org_demo',
        customerRef: 'CUST_001',
        sorgfaltsstufe: 'allgemein',
        verificationMethod: 'id_document',
        pepFlag: false,
        beneficialOwnerIdentified: true,
        riskRating: 'medium',
        nextReviewDate: '2027-05-20T10:00:00Z',
        decisionRef: 'kyc_dec_001',
        status: 'review_due',
        notes: 'Review required',
        verifiedAt: '2026-05-20T10:00:00Z',
        reviewDueAt: reviewDueDate.toISOString(),
        createdBy: 'compliance@nordlicht.example',
        createdByEmail: 'compliance@nordlicht.example',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // 9. SUSPICION CASES (GwG §43) — 1 SAR gemeldet (Feb, goAML/FIU-Ref) + 1 Entwurf in Prüfung
  async listSuspicionCases(params?: any): Promise<API.SuspicionListResponse> {
    await delay(300);
    let cases = [
      {
        // Submitted SAR — entschieden, an die FIU gemeldet (goAML-Export erzeugt), versiegelt.
        id: 'susp_demo_3',
        organizationId: 'org_demo',
        caseRef: 'SUSP-2026-02-0007',
        triggerFact: 'Rascher Storno nach Einmalbeitrag > 100T€ mit Auszahlung auf Drittkonto (Alert ALERT-2026-0571).',
        triggeredByRef: 'ta_005',
        investigationStatus: 'decided' as const,
        decision: 'sar_filed' as const,
        decisionRationale: 'Layering-Muster nicht plausibilisierbar; Mittelherkunft unbelegt. Verdachtsmeldung nach § 43 GwG abgegeben.',
        sarFiledAt: '2026-02-19T11:20:00Z',
        fiuReferenceNumber: 'FIU-DE-2026-0007731',
        assignedToRef: 'mlro_nordlicht',
        integrityHash: '3b1f8c2e9a7d4b605c8e1f2a3d4b5c6e7f8091a2b3c4d5e6f708192a3b4c5d6e',
        isReportable: true,
        daysOpen: 6,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: '2026-02-13T09:00:00Z',
        updatedAt: '2026-02-19T11:20:00Z',
      },
      {
        // Laufender Entwurf — noch in Untersuchung, keine Meldeentscheidung.
        id: 'susp_demo_2',
        organizationId: 'org_demo',
        caseRef: 'SUSP-2026-06-0002',
        triggerFact: 'Smurfing-Muster: Erstbeitrag in Teilbeträgen unter Schwellenwert (Alert ALERT-2026-0488).',
        triggeredByRef: 'ta_007',
        investigationStatus: 'investigating' as const,
        decision: 'none' as const,
        decisionRationale: '',
        sarFiledAt: null,
        fiuReferenceNumber: null,
        assignedToRef: 'analyst_mk',
        integrityHash: null,
        isReportable: false,
        daysOpen: 4,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
      },
    ];
    if (params?.investigationStatus) cases = cases.filter((c) => c.investigationStatus === params.investigationStatus);
    if (params?.decision) cases = cases.filter((c) => c.decision === params.decision);
    return { cases };
  },

  async getSuspicionCase(id: string): Promise<API.SuspicionDetailResponse> {
    await delay(200);
    return {
      case: {
        id,
        organizationId: 'org_demo',
        caseRef: `CASE_${id}`,
        triggerFact: 'Trigger fact description',
        triggeredByRef: null,
        investigationStatus: 'open',
        decision: 'none',
        decisionRationale: '',
        sarFiledAt: null,
        fiuReferenceNumber: null,
        assignedToRef: null,
        integrityHash: null,
        isReportable: false,
        daysOpen: 0,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async createSuspicionCase(data: any) {
    await delay(400);
    return {
      case: {
        id: `sus_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        investigationStatus: 'open',
        decision: 'none',
        decisionRationale: '',
        sarFiledAt: null,
        fiuReferenceNumber: null,
        assignedToRef: null,
        integrityHash: null,
        isReportable: false,
        daysOpen: 0,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async updateSuspicionCase(id: string, data: any) {
    await delay(300);
    return { case: { id, organizationId: 'org_demo', ...data } };
  },

  async transitionSuspicionStatus(id: string, status: string): Promise<API.SuspicionDetailResponse> {
    await delay(300);
    const investigationStatus = oneOf<API.SuspicionInvestigationStatus>(['open', 'investigating', 'decided'], status, 'open');
    return {
      case: {
        id,
        organizationId: 'org_demo',
        caseRef: 'CASE_001',
        triggerFact: 'Suspicious activity',
        triggeredByRef: null,
        investigationStatus,
        decision: 'none',
        decisionRationale: '',
        sarFiledAt: null,
        fiuReferenceNumber: null,
        assignedToRef: null,
        integrityHash: null,
        isReportable: false,
        daysOpen: 5,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async decideSuspicionCase(id: string, decision: string, decisionRationale?: string, fiuReferenceNumber?: string): Promise<API.SuspicionDetailResponse> {
    await delay(300);
    const sarFiled = decision === 'sar_filed';
    const caseDecision = oneOf<API.SuspicionDecision>(['none', 'no_sar', 'sar_filed'], decision, 'none');
    return {
      case: {
        id,
        organizationId: 'org_demo',
        caseRef: 'CASE_001',
        triggerFact: 'Suspicious activity',
        triggeredByRef: null,
        investigationStatus: 'decided',
        decision: caseDecision,
        decisionRationale: decisionRationale || '',
        sarFiledAt: sarFiled ? new Date().toISOString() : null,
        fiuReferenceNumber: sarFiled ? (fiuReferenceNumber || `FIU_${Date.now()}`) : null,
        assignedToRef: null,
        // decided cases are sealed with a SHA-256 tamper-evidence digest (mirrors backend).
        integrityHash: '3b1f8c2e9a7d4b605c8e1f2a3d4b5c6e7f8091a2b3c4d5e6f708192a3b4c5d6e',
        isReportable: sarFiled,
        daysOpen: 5,
        createdBy: 'system',
        createdByEmail: 'system@adjudon.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // 10. FAIRNESS REMEDIATION — 1 proposed (zu Vorfall #5 Underwriting-Bias) + 2 implemented
  // Session-Store — Fairness-Remediationen (referenzieren die Bias-Audit-IDs oben).
  _fairnessRemStore: null as API.FairnessRemediation[] | null,
  _fairnessRemSeed(): API.FairnessRemediation[] {
    if (!this._fairnessRemStore) this._fairnessRemStore = this._buildFairnessRemSeeds();
    return this._fairnessRemStore;
  },
  _buildFairnessRemSeeds(): API.FairnessRemediation[] {
    const day = 86400000;
    const iso = (d: number) => new Date(Date.now() - d * day).toISOString();
    return [
      {
        // Offener Vorschlag — Folge des Underwriting-Bias-Verdachts (Mai 2026).
        id: 'rem_003', organizationId: 'org_demo', biasAuditRef: 'bias_uw_leben_q2', agentId: 'agent_uw_leben',
        dimension: 'Alter', observedGap: 0.041, recommendation: 'retrain',
        rationale: 'Stratifizierte Kalibrierung zeigt erhöhte Ablehnungsrate in der Altersgruppe 55+; Re-Kalibrierung/Neutraining der Schwelle vorgeschlagen (Anknüpfung an Vorfall Underwriting-Bias-Verdacht).',
        status: 'proposed', decidedBy: '', decidedByEmail: null, decidedAt: null, rejectionReason: '', implementedAt: null, createdAt: iso(9),
      },
      {
        id: 'rem_002', organizationId: 'org_demo', biasAuditRef: 'bias_pricing_q1', agentId: 'agent_pricing',
        dimension: 'Region', observedGap: 0.028, recommendation: 'adjust_pricing',
        rationale: 'Regionale Prämienspreizung über Schwelle; Neugewichtung der Tarifmerkmale angenommen — Umsetzung im Aktuariat ausstehend.',
        status: 'accepted', decidedBy: 'aktuariat@nordlicht.example', decidedByEmail: 'aktuariat@nordlicht.example',
        decidedAt: iso(20), rejectionReason: '', implementedAt: null, createdAt: iso(80),
      },
      {
        id: 'rem_001', organizationId: 'org_demo', biasAuditRef: 'bias_fraud_q1', agentId: 'agent_fraud',
        dimension: 'Geschlecht', observedGap: 0.012, recommendation: 'monitor',
        rationale: 'Abweichung knapp über Beobachtungsschwelle; verstärktes Monitoring eingerichtet.',
        status: 'implemented', decidedBy: 'aktuariat@nordlicht.example', decidedByEmail: 'aktuariat@nordlicht.example',
        decidedAt: iso(120), rejectionReason: '', implementedAt: iso(110), createdAt: iso(130),
      },
    ];
  },

  async listFairnessRemediations(params?: { status?: string; agentId?: string; dimension?: string }) {
    await delay(300);
    let rems = this._fairnessRemSeed().map((r) => ({ ...r }));
    if (params?.status) rems = rems.filter((r) => r.status === params.status);
    if (params?.agentId) rems = rems.filter((r) => r.agentId === params.agentId);
    if (params?.dimension) rems = rems.filter((r) => r.dimension === params.dimension);
    return { remediations: rems };
  },

  async getFairnessRemediation(id: string) {
    await delay(200);
    const r = this._fairnessRemSeed().find((x) => x.id === id);
    if (!r) throw this._vvgErr('NOT_FOUND');
    return { remediation: { ...r } };
  },

  async createFairnessRemediation(data: any) {
    await delay(400);
    const store = this._fairnessRemSeed();
    const now = new Date().toISOString();
    const created: API.FairnessRemediation = {
      id: `rem_${_hashAgentId((data?.dimension ?? '') + now).toString(16).slice(0, 8)}`,
      organizationId: 'org_demo',
      biasAuditRef: data?.biasAuditRef ?? null,
      agentId: data?.agentId ?? null,
      dimension: data?.dimension ?? '—',
      observedGap: data?.observedGap ?? 0,
      recommendation: data?.recommendation ?? 'monitor',
      rationale: data?.rationale ?? '',
      status: 'proposed', decidedBy: '', decidedByEmail: null, decidedAt: null, rejectionReason: '', implementedAt: null,
      createdAt: now,
    };
    store.unshift(created);
    return { remediation: { ...created } };
  },

  // Transitions spiegeln das Backend (fairnessRemediationService) — der Mock darf
  // nie weicher sein, sonst täuscht die Demo Übergänge vor, die live 409en.
  async decideFairnessRemediation(id: string, decision: string, rejectionReason?: string) {
    await delay(300);
    const store = this._fairnessRemSeed();
    const idx = store.findIndex((r) => r.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status !== 'proposed') throw this._vvgErr('FAIRNESS_BAD_TRANSITION');
    const status: API.FairnessRemediationStatus = decision === 'accepted' ? 'accepted' : 'rejected';
    store[idx] = {
      ...store[idx], status,
      decidedBy: 'demo@nordlicht.example', decidedByEmail: 'demo@nordlicht.example', decidedAt: new Date().toISOString(),
      rejectionReason: status === 'rejected' ? (rejectionReason || '') : '',
    };
    return { remediation: { ...store[idx] } };
  },

  async markFairnessRemediationImplemented(id: string) {
    await delay(300);
    const store = this._fairnessRemSeed();
    const idx = store.findIndex((r) => r.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status !== 'accepted') throw this._vvgErr('FAIRNESS_BAD_TRANSITION');
    store[idx] = { ...store[idx], status: 'implemented', implementedAt: new Date().toISOString() };
    return { remediation: { ...store[idx] } };
  },

  // revert = accepted → rejected (späte Ablehnung), exakt wie das Backend.
  async revertFairnessRemediation(id: string, rejectionReason?: string) {
    await delay(300);
    const store = this._fairnessRemSeed();
    const idx = store.findIndex((r) => r.id === id);
    if (idx < 0) throw this._vvgErr('NOT_FOUND');
    if (store[idx].status !== 'accepted') throw this._vvgErr('FAIRNESS_BAD_TRANSITION');
    store[idx] = {
      ...store[idx], status: 'rejected', implementedAt: null,
      decidedBy: 'demo@nordlicht.example', decidedByEmail: 'demo@nordlicht.example', decidedAt: new Date().toISOString(),
      rejectionReason: rejectionReason || '',
    };
    return { remediation: { ...store[idx] } };
  },

  // 11. AUTOMATED DECISION REGISTER
  async listAutomatedDecisions(params?: any): Promise<API.AutomatedDecisionListResponse> {
    await delay(300);
    let automatedDecisions: API.AutomatedDecisionRecord[] = [
      {
        id: 'ad_001',
        organizationId: 'org_demo',
        agentId: 'agent_uw_leben',
        agentName: 'Underwriting Assistent Leben',
        agentVersion: 'v2.2.0',
        decisionType: 'underwriting' as const,
        hasLegalEffect: true,
        humanInLoop: 'review' as const,
        safeguards: 'Vier-Augen-Prüfung bei Ablehnung/Erschwerung; Anfechtung über Beschwerdestelle dokumentiert; Confidence-Schwelle < 0,8 erzwingt manuelle Vorlage.',
        art22Applicable: true,
        reasonIfNotApplicable: '',
        requiresArt22Safeguards: true,
        legalBasis: 'bdsg37_insurance',
        usesSpecialCategoryData: true,
        art22Assessment: { requiresSafeguards: true, legalBasis: 'bdsg37_insurance', legalBasisLabel: '§ 37 BDSG — Leistungserbringung nach einem Versicherungsvertrag', specialCategoryData: true, specialCategoryMeasuresRequired: true, notes: [] },
        lastReviewedAt: '2026-05-15T10:00:00Z',
        lastReviewedBy: 'dsb@nordlicht.example',
        status: 'active' as const,
        retiredAt: null,
        retirementReason: '',
        contactEmail: 'datenschutz@nordlicht.example',
        documentationUrl: 'https://nordlicht.example/docs/uw-leben',
        createdAt: '2025-10-12T10:00:00Z',
        updatedAt: '2026-05-15T10:00:00Z',
      },
      {
        id: 'ad_002',
        organizationId: 'org_demo',
        agentId: 'agent_fraud',
        agentName: 'Fraud Detector',
        agentVersion: 'v3.1.0',
        decisionType: 'fraud-detection' as const,
        hasLegalEffect: true,
        humanInLoop: 'approval' as const,
        safeguards: 'Kein automatischer Leistungsausschluss; Treffer erzeugt Prüffall mit menschlicher Letztentscheidung; Betroffenen-Auskunft nach Art. 22(3).',
        art22Applicable: true,
        reasonIfNotApplicable: '',
        requiresArt22Safeguards: true,
        legalBasis: 'bdsg37_insurance',
        usesSpecialCategoryData: false,
        art22Assessment: { requiresSafeguards: true, legalBasis: 'bdsg37_insurance', legalBasisLabel: '§ 37 BDSG — Leistungserbringung nach einem Versicherungsvertrag', specialCategoryData: false, specialCategoryMeasuresRequired: false, notes: [] },
        lastReviewedAt: '2026-04-28T10:00:00Z',
        lastReviewedBy: 'dsb@nordlicht.example',
        status: 'active' as const,
        retiredAt: null,
        retirementReason: '',
        contactEmail: 'datenschutz@nordlicht.example',
        documentationUrl: 'https://nordlicht.example/docs/fraud-detector',
        createdAt: '2025-09-20T10:00:00Z',
        updatedAt: '2026-04-28T10:00:00Z',
      },
      {
        id: 'ad_003',
        organizationId: 'org_demo',
        agentId: 'agent_pricing',
        agentName: 'Pricing Engine Komposit',
        agentVersion: 'v2.0.0',
        decisionType: 'pricing' as const,
        hasLegalEffect: true,
        humanInLoop: 'review' as const,
        safeguards: 'Tarifvorschläge mit Erklärbarkeits-Faktoren; Underwriter-Freigabe vor Policierung; aktuell verschärfte Aufsicht wegen Drift-Alarm.',
        art22Applicable: true,
        reasonIfNotApplicable: '',
        requiresArt22Safeguards: true,
        legalBasis: 'art22_2a_contract',
        usesSpecialCategoryData: false,
        art22Assessment: { requiresSafeguards: true, legalBasis: 'art22_2a_contract', legalBasisLabel: 'Art. 22(2)(a) — erforderlich für Vertragsabschluss', specialCategoryData: false, specialCategoryMeasuresRequired: false, notes: ['Drift-Alarm offen — engmaschige Überwachung'] },
        lastReviewedAt: '2026-05-30T10:00:00Z',
        lastReviewedBy: 'dsb@nordlicht.example',
        status: 'active' as const,
        retiredAt: null,
        retirementReason: '',
        contactEmail: 'datenschutz@nordlicht.example',
        documentationUrl: 'https://nordlicht.example/docs/pricing-engine',
        createdAt: '2026-01-08T10:00:00Z',
        updatedAt: '2026-05-30T10:00:00Z',
      },
      {
        id: 'ad_004',
        organizationId: 'org_demo',
        agentId: 'agent_scoring_legacy',
        agentName: 'Bonitäts-Scoring (Alt)',
        agentVersion: 'v1.0.0',
        decisionType: 'underwriting' as const,
        hasLegalEffect: true,
        humanInLoop: 'review' as const,
        safeguards: '',
        art22Applicable: true,
        reasonIfNotApplicable: '',
        requiresArt22Safeguards: true,
        legalBasis: 'bdsg37_insurance',
        usesSpecialCategoryData: false,
        art22Assessment: { requiresSafeguards: true, legalBasis: 'bdsg37_insurance', legalBasisLabel: '§ 37 BDSG — Leistungserbringung nach einem Versicherungsvertrag', specialCategoryData: false, specialCategoryMeasuresRequired: false, notes: [] },
        lastReviewedAt: '2025-12-01T10:00:00Z',
        lastReviewedBy: 'dsb@nordlicht.example',
        status: 'retired' as const,
        retiredAt: '2026-02-15T10:00:00Z',
        retirementReason: 'Abgelöst durch Underwriting Assistent Leben.',
        contactEmail: 'datenschutz@nordlicht.example',
        documentationUrl: '',
        createdAt: '2025-09-16T10:00:00Z',
        updatedAt: '2026-02-15T10:00:00Z',
      },
    ];
    if (params?.status) automatedDecisions = automatedDecisions.filter((a) => a.status === params.status);
    return { automatedDecisions };
  },

  async getAutomatedDecision(id: string): Promise<API.AutomatedDecisionDetailResponse> {
    await delay(200);
    return {
      automatedDecision: {
        id,
        organizationId: 'org_demo',
        agentId: 'agent_uw_leben',
        agentName: 'Underwriting Assistent Leben',
        agentVersion: 'v2.3.1',
        decisionType: 'other',
        hasLegalEffect: false,
        humanInLoop: 'none',
        safeguards: '',
        art22Applicable: false,
        reasonIfNotApplicable: 'Supportive tool only',
        requiresArt22Safeguards: false,
        legalBasis: 'unassessed',
        usesSpecialCategoryData: false,
        art22Assessment: { requiresSafeguards: false, legalBasis: 'unassessed', legalBasisLabel: 'Rechtsgrundlage noch nicht dokumentiert', specialCategoryData: false, specialCategoryMeasuresRequired: false, notes: [] },
        lastReviewedAt: null,
        lastReviewedBy: '',
        status: 'active',
        retiredAt: null,
        retirementReason: '',
        contactEmail: '',
        documentationUrl: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async createAutomatedDecision(data: any) {
    await delay(400);
    return {
      automatedDecision: {
        id: `ad_${Date.now()}`,
        organizationId: 'org_demo',
        ...data,
        status: 'active',
        retiredAt: null,
        retirementReason: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  async updateAutomatedDecision(id: string, data: any) {
    await delay(300);
    return { automatedDecision: { id, organizationId: 'org_demo', ...data } };
  },

  async retireAutomatedDecision(id: string, reason?: string): Promise<API.AutomatedDecisionDetailResponse> {
    await delay(300);
    return {
      automatedDecision: {
        id,
        organizationId: 'org_demo',
        agentId: 'agent_uw_leben',
        agentName: 'Underwriting Assistent Leben',
        agentVersion: 'v2.3.1',
        decisionType: 'underwriting',
        hasLegalEffect: true,
        humanInLoop: 'review',
        safeguards: '',
        art22Applicable: true,
        reasonIfNotApplicable: '',
        requiresArt22Safeguards: true,
        legalBasis: 'bdsg37_insurance',
        usesSpecialCategoryData: true,
        art22Assessment: { requiresSafeguards: true, legalBasis: 'bdsg37_insurance', legalBasisLabel: '§ 37 BDSG — Leistungserbringung nach einem Versicherungsvertrag', specialCategoryData: true, specialCategoryMeasuresRequired: true, notes: [] },
        lastReviewedAt: '2026-05-15T10:00:00Z',
        lastReviewedBy: 'dsb@nordlicht.example',
        status: 'retired',
        retiredAt: new Date().toISOString(),
        retirementReason: reason || 'Agent decommissioned',
        contactEmail: 'datenschutz@nordlicht.example',
        documentationUrl: 'https://nordlicht.example/docs/uw-leben',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  },

  // ── Phase 3 — Federated ─────────────────────────────────────────────
  async listPatternFamilies() {
    return [
      mockPatternFamily('fam_001', 'EU-Versicherungs-Compliance-Muster', 'insurance', 12),
      mockPatternFamily('fam_002', 'Schaden-Betrugsmuster (Komposit)', 'fraud', 8),
    ];
  },
  async getPatternFamily(id: string) { return mockPatternFamily(id, 'EU-Versicherungs-Compliance-Muster', 'insurance', 12); },
  async joinPatternFamily(id: string) { return mockPatternFamily(id, 'EU-Versicherungs-Compliance-Muster', 'insurance', 13); },
  async leavePatternFamily(id: string) { return mockPatternFamily(id, 'EU-Versicherungs-Compliance-Muster', 'insurance', 11); },
  async listFederatedRounds(familyId: string) {
    return [
      mockFederatedRound('round_001', familyId, 1, 'completed'),
      mockFederatedRound('round_002', familyId, 2, 'completed'),
      mockFederatedRound('round_003', familyId, 3, 'aggregating'),
    ];
  },
  async kickoffFederatedRound(familyId: string, payload: any) {
    return mockFederatedRound(`round_${Date.now()}`, familyId, 4, 'collecting', payload);
  },
  async getFederatedRound(roundId: string) { return mockFederatedRound(roundId, 'fam_001', 3, 'aggregating'); },
  async signFederatedRoundEvidence(roundId: string) {
    return {
      ...mockFederatedRound(roundId, 'fam_001', 3, 'completed'),
      sigstoreBundleRef: 'sigstore://mock-rekor/12345',
      attestationRef: 'tee_attest_xyz',
    };
  },
  async submitFederatedClientUpdate(roundId: string, payload: { updateVector: number[]; weight?: number; attestationRef?: string }) {
    const v = Array.isArray(payload?.updateVector) ? payload.updateVector : [];
    const norm = Math.sqrt(v.reduce((s, x) => s + (Number.isFinite(x) ? x * x : 0), 0));
    return {
      id: `clu_${Date.now()}`,
      roundId,
      organizationId: 'org_demo',
      updateVector: v,
      weight: typeof payload?.weight === 'number' && payload.weight > 0 ? payload.weight : 1,
      l2Norm: norm,
      clipped: norm > 1,
      attestationRef: payload?.attestationRef,
      submittedAt: new Date().toISOString(),
    };
  },
  async runFederatedAggregation(roundId: string) {
    return {
      ...mockFederatedRound(roundId, 'fam_001', 3, 'completed'),
      participantCount: 4,
      rejectedCount: 0,
      aggregateHash: 'a1b2c3d4e5f6'.padEnd(64, '0'),
      globalModelRef: 'aidt://aggregate/a1b2c3d4',
      sigstoreBundleRef: 'sigstore://mock-rekor/67890',
      contributionWeights: [
        { organizationId: 'org_demo', weight: 1 },
        { organizationId: 'org_partner_a', weight: 2 },
        { organizationId: 'org_partner_b', weight: 1 },
        { organizationId: 'org_partner_c', weight: 1 },
      ],
      completedAt: new Date().toISOString(),
    };
  },

  // ── Phase 3 — Privacy Budget ────────────────────────────────────────
  async getPrivacyBudget() {
    return {
      organizationId: 'org_demo',
      epsilonCap: 10.0,
      epsilonSpent: 3.4,
      epsilonRemaining: 6.6,
      deltaCap: 1e-4,
      deltaSpent: 3.2e-5,
      deltaRemaining: 6.8e-5,
      windowDurationMs: 30 * 86400000,
      windowStartedAt: new Date(Date.now() - 12 * 86400000).toISOString(),
      windowResetsAt: new Date(Date.now() + 18 * 86400000).toISOString(),
      // 6 Zwecke (Filter rendert ab len > 5). Versicherungsnahe föderierte
      // Runden + DP-begrenzte Analytik. Summe epsilon = 3,4 = epsilonSpent
      // (≈ 34 % verbraucht, ~66 % verbleibend — gelebte Mitte des Fensters).
      spendByPurpose: [
        { purpose: 'federated-round:schadenmodell-q2', epsilon: 1.2, delta: 1.0e-5, spentAt: new Date(Date.now() - 11 * 86400000).toISOString() },
        { purpose: 'federated-round:betrugserkennung-v2', epsilon: 1.0, delta: 1.0e-5, spentAt: new Date(Date.now() - 9 * 86400000).toISOString() },
        { purpose: 'analytics:system-performance-rollup', epsilon: 0.3, delta: 2.0e-6, spentAt: new Date(Date.now() - 7 * 86400000).toISOString() },
        { purpose: 'analytics:fairness-kohorten-vergleich', epsilon: 0.4, delta: 3.0e-6, spentAt: new Date(Date.now() - 5 * 86400000).toISOString() },
        { purpose: 'federated-round:tarif-kalibrierung-komposit', epsilon: 0.3, delta: 5.0e-6, spentAt: new Date(Date.now() - 3 * 86400000).toISOString() },
        { purpose: 'analytics:entscheidungsvolumen-export', epsilon: 0.2, delta: 2.0e-6, spentAt: new Date(Date.now() - 1 * 86400000).toISOString() },
      ],
      status: 'active' as const,
    };
  },
  async updatePrivacyBudgetCaps(payload: any) {
    const current = await mockData.getPrivacyBudget();
    return {
      ...current,
      ...payload,
      epsilonRemaining: (payload.epsilonCap ?? current.epsilonCap) - current.epsilonSpent,
    };
  },

  // ── Phase 3 — MCP ───────────────────────────────────────────────────
  async getMCPManifest() {
    return {
      name: 'adjudon-mcp',
      version: '0.1.0',
      description: 'Read-only MCP server exposing ADJUDON audit data to AI agents',
      resources: [
        { uri: 'adjudon://policies', name: 'Active Policies', description: 'List of active policies for this organization', mimeType: 'application/json' },
        { uri: 'adjudon://traces/recent', name: 'Recent Traces', description: 'Most recent decision traces (PII-scrubbed)', mimeType: 'application/json' },
        { uri: 'adjudon://hash-chain/status', name: 'Hash Chain Status', description: 'Hash chain integrity status', mimeType: 'application/json' },
      ],
      tools: [
        { name: 'lookup_decision', description: 'Look up a single decision trace by ID', inputSchema: { type: 'object', properties: { traceId: { type: 'string' } }, required: ['traceId'] } },
        { name: 'verify_chain_segment', description: 'Verify a segment of the hash chain', inputSchema: { type: 'object', properties: { fromSequence: { type: 'number' }, toSequence: { type: 'number' } }, required: ['fromSequence', 'toSequence'] } },
      ],
    };
  },
  async readMCPResource(uri: string) {
    return { uri, mimeType: 'application/json', text: JSON.stringify({ mock: true, uri, fetchedAt: new Date().toISOString() }, null, 2) };
  },
  async callMCPTool(name: string, args: Record<string, any>) {
    return { tool: name, args, result: { mock: true, ok: true }, calledAt: new Date().toISOString() };
  },
  // ── Phase 2b — Provenance (C2PA + PROV-O) ────────────────────────────
  async getProvenanceSettings() {
    // C2PA aktiv seit März 2026. Auto-Kennzeichnung bleibt ehrlich opt-in
    // deaktiviert (Enforcement-Frist 02.12.2026) — der Org-Feature-Flag steuert
    // die Pipeline; hier nur der org-weite C2PA-Schalter + Plan.
    return { c2paEnabled: true, plan: 'enterprise' };
  },
  async updateProvenanceSettings(payload: { c2paEnabled: boolean }) {
    return { c2paEnabled: payload.c2paEnabled, plan: 'enterprise' };
  },
  async listC2PAManifests(params?: { limit?: number; traceId?: string }): Promise<{ manifests: API.C2PAManifest[]; count: number }> {
    // Nordlicht: ~8 C2PA-Manifeste der letzten ~90 Tage. Provenance-Stempel auf
    // generative Ausgaben (Kunden-Chat-Assistent + Dokument-Klassifizierer).
    // Issuer = did:web:nordlicht.example; 1 Test-Manifest widerrufen. traceIds im
    // Stil der kuratierten Decision-Traces. Deterministische Hashes (reload-stabil).
    const now = Date.now();
    const day = 86_400 * 1000;
    const hex = (seed: string): string => {
      let h = 0x811c9dc5;
      for (let i = 0; i < seed.length; i += 1) h = Math.imul(h ^ seed.charCodeAt(i), 0x01000193) >>> 0;
      return (h.toString(16) + '0000000').slice(0, 8).repeat(8).slice(0, 64);
    };
    type Seed = { id: string; traceId: string; ageDays: number; revoked?: string };
    const seeds: Seed[] = [
      { id: 'c2pa_nl_08', traceId: 'trace_efg123', ageDays: 2 },
      { id: 'c2pa_nl_07', traceId: 'trace_def456', ageDays: 9 },
      { id: 'c2pa_nl_06', traceId: 'trace_hij456', ageDays: 17 },
      { id: 'c2pa_nl_05', traceId: 'trace_klm789', ageDays: 28 },
      { id: 'c2pa_nl_04', traceId: 'trace_test_provenance', ageDays: 36, revoked: 'test-manifest superseded' },
      { id: 'c2pa_nl_03', traceId: 'trace_nop012', ageDays: 51 },
      { id: 'c2pa_nl_02', traceId: 'trace_def456', ageDays: 68 },
      { id: 'c2pa_nl_01', traceId: 'trace_efg123', ageDays: 84 },
    ];
    const all: API.C2PAManifest[] = seeds.map((s) => {
      const issued = new Date(now - s.ageDays * day).toISOString();
      return {
        _id: s.id,
        organizationId: 'org_acme',
        traceId: s.traceId,
        label: `urn:c2pa:adjudon:${s.traceId}:${now - s.ageDays * day}`,
        contentHash: hex(`content-${s.id}`),
        signatureAlg: 'ES256',
        signature: hex(`sig-${s.id}`),
        signerKid: hex(`kid-${s.id}`),
        signerMode: 'prod-jwk',
        issuer: 'did:web:nordlicht.example',
        assertions: {
          actions: [{ action: 'c2pa.created', when: issued }],
          ingredients: [],
          creativeWork: null,
          aiTrainingMining: { use: 'notAllowed' },
        },
        issuedAt: issued,
        revokedAt: s.revoked ? new Date(now - (s.ageDays - 1) * day).toISOString() : null,
        revocationReason: s.revoked ?? null,
        createdAt: issued,
        updatedAt: issued,
      };
    });
    const filtered = params?.traceId ? all.filter((m) => m.traceId === params.traceId) : all;
    const manifests = params?.limit ? filtered.slice(0, params.limit) : filtered;
    return { manifests, count: manifests.length };
  },
  async issueC2PAManifest(payload: { traceId: string; contentHash: string; label?: string; issuer?: string; assertions?: Record<string, any> }): Promise<{ manifest: API.C2PAManifest }> {
    const now = new Date().toISOString();
    return {
      manifest: {
        _id: `c2pa_nl_${Date.now()}`,
        organizationId: 'org_acme',
        traceId: payload.traceId,
        label: payload.label || `urn:c2pa:adjudon:${payload.traceId}:${Date.now()}`,
        contentHash: payload.contentHash,
        signatureAlg: 'ES256',
        signature: 'c'.repeat(64),
        signerKid: 'nl_kid_'.padEnd(64, '0').slice(0, 64),
        signerMode: 'prod-jwk',
        issuer: payload.issuer || 'did:web:nordlicht.example',
        assertions: {
          actions: payload.assertions?.actions || [{ action: 'c2pa.created', when: now }],
          ingredients: payload.assertions?.ingredients || [],
          creativeWork: payload.assertions?.creativeWork || null,
          aiTrainingMining: payload.assertions?.aiTrainingMining || { use: 'notAllowed' },
        },
        issuedAt: now,
        revokedAt: null,
        revocationReason: null,
        createdAt: now,
        updatedAt: now,
      },
    };
  },
  async revokeC2PAManifest(id: string, reason?: string): Promise<{ manifest: API.C2PAManifest }> {
    const now = new Date().toISOString();
    return {
      manifest: {
        _id: id,
        organizationId: 'org_acme',
        traceId: 'trace_efg123',
        label: `urn:c2pa:adjudon:${id}`,
        contentHash: 'a'.repeat(64),
        signatureAlg: 'ES256',
        signature: 'b'.repeat(64),
        signerKid: 'nl_kid_'.padEnd(64, '0').slice(0, 64),
        signerMode: 'prod-jwk',
        issuer: 'did:web:nordlicht.example',
        assertions: { actions: [], ingredients: [], creativeWork: null, aiTrainingMining: null },
        issuedAt: now,
        revokedAt: now,
        revocationReason: reason || 'manual',
        createdAt: now,
        updatedAt: now,
      },
    };
  },
  async verifyC2PAManifest(id: string) {
    return { manifestId: id, valid: true, reason: 'ok' };
  },
  async getProvOTrace(traceId: string) {
    return {
      provo: {
        '@context': {
          prov: 'http://www.w3.org/ns/prov#',
          adjudon: 'https://adjudon.com/ns/prov#',
        },
        '@graph': [
          { '@id': `urn:adjudon:org_acme:activity:${traceId}`, '@type': ['prov:Activity', 'adjudon:Decision'], 'adjudon:traceId': traceId },
        ],
        'adjudon:exportedAt': new Date().toISOString(),
        'adjudon:traceCount': 1,
      },
    };
  },
  async getC2PAKmsStatus() {
    return {
      provider: 'mongo',
      ready: true,
      mode: 'mongo-kms',
      kid: 'demo-kid-r5T7yh3mP9aAqQ8nB2cZk4F6gW0jL1xV',
      alg: 'ES256',
      certChainDepth: 0,
      history: [
        {
          kid: 'demo-kid-r5T7yh3mP9aAqQ8nB2cZk4F6gW0jL1xV',
          generation: 3,
          status: 'active' as const,
          activatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
          retiredAt: null,
        },
        {
          kid: 'demo-kid-mZ2hY7eD3kN9qB5tA1cV8sW6rX4uJ0pL',
          generation: 2,
          status: 'retired' as const,
          activatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 35).toISOString(),
          retiredAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
        },
        {
          kid: 'demo-kid-fG4kH8sM1pT9bN7yC5dQ3vW2eR6xZ0aL',
          generation: 1,
          status: 'retired' as const,
          activatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString(),
          retiredAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 35).toISOString(),
        },
      ],
    };
  },

  async rotateC2PAKmsKey() {
    return {
      kid: 'demo-kid-' + Math.random().toString(36).slice(2, 26),
      generation: 4,
      rotatedAt: new Date().toISOString(),
    };
  },

  // ─── Phase 1 Workspace Onboarding ───────────────────────────────────
  async getOnboardingState(): Promise<API.OnboardingStateResponse> {
    await delay(100);
    return {
      completedSteps: ['workspace_created', 'agent_created'],
      currentStep: 'trace_received',
      persona: 'regulatory',
      industry: null,
      progress: { completed: 2, total: 5, percent: 40 },
      dpaAccepted: true,
      recentEvents: [
        { type: 'agent_created', persona: 'regulatory', createdAt: new Date(Date.now() - 86400000).toISOString() },
        { type: 'workspace_created', persona: null, createdAt: new Date(Date.now() - 2 * 86400000).toISOString() },
      ],
    };
  },

  async emitOnboardingEvent(payload: API.EmitOnboardingEventRequest): Promise<API.OnboardingEvent> {
    await delay(80);
    return {
      _id: 'evt_demo_' + Math.random().toString(36).slice(2, 10),
      organizationId: 'org_demo',
      userId: 'user_demo',
      type: payload.type,
      persona: payload.persona ?? null,
      industry: payload.industry ?? null,
      metadata: payload.metadata ?? {},
      createdAt: new Date().toISOString(),
    };
  },

  async acceptDpa(payload: API.AcceptDpaRequest): Promise<API.AcceptDpaResponse> {
    await delay(150);
    const sha = Array.from({ length: 64 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
    const chain = Array.from({ length: 64 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
    return {
      chainEntryId: 'chain_demo_' + Math.random().toString(36).slice(2, 10),
      chainHash: chain,
      documentVersion: '2026-05-04',
      documentSha256: sha,
      locale: payload.locale ?? 'de',
      eventId: 'evt_demo_' + Math.random().toString(36).slice(2, 10),
      acceptedAt: new Date().toISOString(),
    };
  },

  async getDpaVerification(): Promise<API.DpaVerificationResponse> {
    await delay(80);
    return { verified: true, entryCount: 1 };
  },

  async seedDemoData(payload: { persona?: string }): Promise<API.SeedDemoResponse> {
    await delay(200);
    return {
      seeded: 3,
      alreadySeeded: false,
      persona: payload.persona ?? 'regulatory',
      traceIds: ['demo-1', 'demo-2', 'demo-3'],
    };
  },

  async getLegalDocs(): Promise<API.LegalDocMeta[]> {
    await delay(100);
    const docs: Array<API.LegalDocMeta['document']> = ['dpa', 'tos', 'privacy', 'cookies'];
    const locales: Array<'de' | 'en'> = ['de', 'en'];
    const titlesDE = { dpa: 'Auftragsverarbeitungsvertrag', tos: 'AGB', privacy: 'Datenschutz', cookies: 'Cookies' };
    const titlesEN = { dpa: 'Data Processing Agreement', tos: 'Terms of Service', privacy: 'Privacy Policy', cookies: 'Cookie Policy' };
    const out: API.LegalDocMeta[] = [];
    for (const doc of docs) {
      for (const locale of locales) {
        out.push({
          document: doc,
          locale,
          version: '2026-05-04',
          title: locale === 'de' ? titlesDE[doc] : titlesEN[doc],
          canonicalUrl: `https://adjudon.com/legal/${doc}`,
          sha256: Array.from({ length: 64 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join(''),
        });
      }
    }
    return out;
  },

  async getLegalDoc(doc: string, locale: string): Promise<API.LegalDoc> {
    await delay(100);
    const meta = (await this.getLegalDocs()).find(d => d.document === doc && d.locale === locale);
    return {
      ...(meta ?? {
        document: doc as API.LegalDocMeta['document'],
        locale: locale as 'de' | 'en',
        version: '2026-05-04',
        title: doc.toUpperCase(),
        canonicalUrl: `https://adjudon.com/legal/${doc}`,
        sha256: '0'.repeat(64),
      }),
      body: `# ${doc.toUpperCase()} (${locale.toUpperCase()})\n\nDemo-Mode: full content available in production.\n\nSee canonical: https://adjudon.com/legal/${doc}`,
    };
  },
};

// ── Helper builders for Phase 2 + 3 mocks ─────────────────────────────

// ── Demo-Persona „Nordlicht Versicherung AG" — 7 Vorfälle über ~9 Monate ──────
// SSOT: docs/DEMO_PERSONA.md. Genau 2 aktiv (#6 investigating · #7 mitigated),
// 1 überfällige Frist (#7 gdpr-Abschlussbericht), Rest geschlossen. Versicherungs-
// nahe deutsche Titel + rootCause/remediation/category; einige mit regulators[].
type PersonaClockSpec = {
  regulator: API.RegulatorCode;
  status: API.IncidentClock['status'];
  startOffsetMs: number;
  // checkpoints: dueOffsetMs ab detectedAt, optional completedAfterMs (relativ zu detectedAt)
  checkpoints: Array<{ label: string; dueOffsetMs: number; completedAfterMs?: number; status: API.CheckpointStatus }>;
};
type PersonaIncidentSpec = {
  title: string;
  summary: string;
  severity: API.IncidentSeverity;
  category: API.IncidentCategory;
  status: API.IncidentStatus;
  detectedDaysAgo: number;
  closedDaysAgo?: number;
  rootCause?: string;
  remediation?: string;
  severityClass?: API.IncidentSeverityClass;
  highRiskToSubjects?: boolean;
  affectedAgents: string[];
  reportedBy: string;
  doraClassification?: API.Incident['doraClassification'];
  reportingState?: API.Incident['reportingState'];
  clocks: PersonaClockSpec[];
  regulators?: API.IncidentRegulatorSubmission[];
};

const H_MS = 60 * 60 * 1000;
const D_MS = 24 * H_MS;

const PERSONA_INCIDENTS: Record<string, PersonaIncidentSpec> = {
  // #1 — E-Mail-Postfach-Phishing (operational/data_breach), geschlossen, Nov 2025,
  //      gdpr-Uhr, BfDI-Meldung mit submissionRef.
  inc_001: {
    title: 'Phishing-Zugriff auf Schaden-Postfach',
    summary: 'Ein kompromittiertes Sachbearbeiter-Postfach ermöglichte kurzzeitig Lesezugriff auf Schadenkorrespondenz. Betroffenenkreis eingegrenzt, Zugang gesperrt.',
    severity: 'high',
    category: 'data_breach',
    status: 'closed',
    detectedDaysAgo: 215,
    closedDaysAgo: 205,
    rootCause: 'Phishing-Mail umging das MFA-Fallback eines Sachbearbeiter-Kontos; Postfach für ~6 h fremdzugänglich.',
    remediation: 'Konto gesperrt + neu provisioniert, MFA-Fallback deaktiviert, betroffene Versicherungsnehmer informiert, Awareness-Schulung wiederholt.',
    highRiskToSubjects: true,
    affectedAgents: ['agent_doc'],
    reportedBy: 'ciso@nordlicht.example',
    reportingState: 'final',
    clocks: [
      {
        regulator: 'gdpr-art-33', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Erstmeldung BfDI (72h)', dueOffsetMs: 72 * H_MS, completedAfterMs: 41 * H_MS, status: 'completed' },
          { label: 'Betroffenen-Benachrichtigung (Art. 34)', dueOffsetMs: 5 * D_MS, completedAfterMs: 3 * D_MS, status: 'completed' },
        ],
      },
    ],
    regulators: [
      { code: 'gdpr-art-33', name: 'BfDI', contactEmail: null, submittedAtDaysAgo: 213, submissionRef: 'BfDI-2025-0481', authorityCaseId: 'BfDI/AZ/2025/0481', responseReceived: true, responseAtDaysAgo: 188 } as unknown as API.IncidentRegulatorSubmission,
    ],
  },
  // #2 — Ausfall Dokument-Klassifizierer (operational), resolved, Jan 2026, dora-Uhr,
  //      DORA major=non-major-Entscheidung dokumentiert.
  inc_002: {
    title: 'Ausfall Dokument-Klassifizierer (Posteingang-Stau)',
    summary: 'Der Dokument-Klassifizierer fiel nach einem fehlerhaften Modell-Rollout für ~3 h aus; Posteingang wurde manuell verarbeitet. Als nicht-schwerwiegend eingestuft.',
    severity: 'medium',
    category: 'operational',
    status: 'resolved',
    detectedDaysAgo: 150,
    closedDaysAgo: 148,
    rootCause: 'Inkompatibler Modell-Artefakt im Rollout; Klassifizierungs-Service crashte beim Start, Health-Check schlug an.',
    remediation: 'Rollback auf die vorherige Modellversion, Canary-Stufe im Deploy ergänzt, RTO/RPO-Annahme im BCP bestätigt.',
    affectedAgents: ['agent_doc'],
    reportedBy: 'ict-risk@nordlicht.example',
    doraClassification: { recommendation: 'non-major', decision: 'non-major' },
    reportingState: 'none',
    clocks: [
      {
        regulator: 'dora-art-19', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Klassifizierung (major/non-major)', dueOffsetMs: 24 * H_MS, completedAfterMs: 6 * H_MS, status: 'completed' },
        ],
      },
    ],
  },
  // #3 — Fehlkonfiguration API-Gateway (cyber) — die „Fail-Open-Nacht" 18.02.2026,
  //      Ursache der historischen Hash-Chain-Lücken. dora + gdpr, geschlossen.
  inc_003: {
    title: 'Fehlkonfiguration API-Gateway — Fail-Open-Nacht',
    summary: 'Ein fehlerhaftes Gateway-Timeout ließ die Audit-Anbindung in der Nacht zum 18.02.2026 für ~40 min in den Fail-Open-Modus laufen. Kunden-Entscheidungen liefen ungebremst weiter — einige Spur-Einträge wurden nicht versiegelt (offen ausgewiesene Lücken).',
    severity: 'high',
    category: 'cyber',
    status: 'closed',
    detectedDaysAgo: 114,
    closedDaysAgo: 100,
    rootCause: 'Reverse-Proxy-Timeout zu niedrig; unter Lastspitze fiel die Audit-Anbindung fail-open zurück — by design kein Kunden-Block, aber 11 Spur-Einträge ohne Siegel.',
    remediation: 'Timeout + Retry-Budget korrigiert, Fail-Open-Alarm verschärft, Lücken-Bericht erstellt und die 11 betroffenen Sequenzen dokumentiert (nicht rekonstruierbar — ehrlich ausgewiesen).',
    affectedAgents: ['agent_fraud', 'agent_pricing'],
    reportedBy: 'ciso@nordlicht.example',
    doraClassification: { recommendation: 'major', decision: 'non-major' },
    reportingState: 'final',
    clocks: [
      {
        regulator: 'dora-art-19', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Erstmeldung (4h)', dueOffsetMs: 4 * H_MS, completedAfterMs: 3 * H_MS, status: 'completed' },
          { label: 'Zwischenbericht (72h)', dueOffsetMs: 72 * H_MS, completedAfterMs: 52 * H_MS, status: 'completed' },
          { label: 'Abschlussbericht (1 Monat)', dueOffsetMs: 30 * D_MS, completedAfterMs: 26 * D_MS, status: 'completed' },
        ],
      },
      {
        regulator: 'gdpr-art-33', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Risikobewertung (72h)', dueOffsetMs: 72 * H_MS, completedAfterMs: 40 * H_MS, status: 'completed' },
        ],
      },
    ],
    regulators: [
      { code: 'dora-art-19', name: 'BaFin', contactEmail: null, submittedAtDaysAgo: 113, submissionRef: 'BaFin-DORA-2026-0033', authorityCaseId: null, responseReceived: true, responseAtDaysAgo: 95 } as unknown as API.IncidentRegulatorSubmission,
  ],
  },
  // #4 — Drift Pricing Engine (ai_serious_incident), geschlossen, Apr 2026, aiact-Uhr,
  //      severityClass default. Passt zu „degraded" Pricing Engine + Kalibrierungs-Alarm.
  inc_004: {
    title: 'Kalibrierungs-Drift Pricing Engine Komposit',
    summary: 'Nächtliche Drift-Erkennung meldete eine signifikante Kalibrierungs-Abweichung (ECE/KS) der Pricing Engine Komposit. Als schwerwiegender KI-Vorfall geprüft, Auto-Refit ausgelöst.',
    severity: 'high',
    category: 'ai_serious_incident',
    status: 'closed',
    detectedDaysAgo: 58,
    closedDaysAgo: 44,
    severityClass: 'default',
    rootCause: 'Saisonaler Bestandsmix verschob die Eingangsverteilung; das Tarif-Modell driftete über die ECE-Schwelle.',
    remediation: 'Auto-Refit + manuelle Validierung durch das Aktuariat, Schwellen-Policy nachgeschärft, Pricing-System unter verstärkte Aufsicht gestellt (Status degraded).',
    affectedAgents: ['agent_pricing'],
    reportedBy: 'aktuariat@nordlicht.example',
    reportingState: 'final',
    clocks: [
      {
        regulator: 'ai-act-art-73', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Schwere-Einstufung (Art. 73)', dueOffsetMs: 15 * D_MS, completedAfterMs: 9 * D_MS, status: 'completed' },
        ],
      },
    ],
  },
  // #5 — Underwriting-Bias-Verdacht (underwriting_bias), geschlossen, Mai 2026,
  //      aiact + gdpr. MaGo-Kategorie sichtbar. Passt zu Fairness-Remediation #5.
  inc_005: {
    title: 'Bias-Verdacht Underwriting Assistent Leben',
    summary: 'Ein Fairness-Audit zeigte einen erhöhten Zwischen-Gruppen-ECE-Abstand im Underwriting Assistent Leben. Verdacht auf unzulässige Ungleichbehandlung geprüft; Remediation eingeleitet.',
    severity: 'medium',
    category: 'underwriting_bias',
    status: 'closed',
    detectedDaysAgo: 31,
    closedDaysAgo: 12,
    rootCause: 'Eine Proxy-Variable korrelierte mit einem geschützten Merkmal (AGG § 1); der Disparitäts-Abstand überschritt die Watch-Schwelle.',
    remediation: 'Merkmal aus dem Feature-Set entfernt, Nachkontroll-Audit bestanden, Vorgang als offene Fairness-Remediation dokumentiert.',
    affectedAgents: ['agent_uw_leben'],
    reportedBy: 'compliance@nordlicht.example',
    reportingState: 'final',
    clocks: [
      {
        regulator: 'ai-act-art-73', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Schwere-Einstufung (Art. 73)', dueOffsetMs: 15 * D_MS, completedAfterMs: 11 * D_MS, status: 'completed' },
        ],
      },
      {
        regulator: 'gdpr-art-33', status: 'completed', startOffsetMs: 0,
        checkpoints: [
          { label: 'Art.-22-Schutzmaßnahmen-Prüfung', dueOffsetMs: 10 * D_MS, completedAfterMs: 7 * D_MS, status: 'completed' },
        ],
      },
    ],
  },
  // #6 — Latenz-Spitzen Fraud Detector (operational), INVESTIGATING, vor 2 Tagen,
  //      dora on-track, 72h-Checkpoint offen (in Zukunft) → kein overdue.
  inc_006: {
    title: 'Latenz-Spitzen Fraud Detector',
    summary: 'Der Fraud Detector zeigt seit zwei Tagen wiederkehrende Latenz-Spitzen über dem p95-Ziel. Ursachenanalyse läuft; bisher kein Entscheidungs-Ausfall.',
    severity: 'medium',
    category: 'operational',
    status: 'investigating',
    detectedDaysAgo: 2,
    rootCause: undefined,
    remediation: undefined,
    affectedAgents: ['agent_fraud'],
    reportedBy: 'ict-risk@nordlicht.example',
    doraClassification: { recommendation: 'non-major', decision: null },
    reportingState: 'initial',
    clocks: [
      {
        regulator: 'dora-art-19', status: 'on-track', startOffsetMs: 0,
        checkpoints: [
          { label: 'Erstmeldung (4h)', dueOffsetMs: 4 * H_MS, completedAfterMs: 3 * H_MS, status: 'completed' },
          { label: 'Zwischen-Update (24h)', dueOffsetMs: 24 * H_MS, completedAfterMs: 22 * H_MS, status: 'completed' },
          { label: 'Zwischenbericht (72h)', dueOffsetMs: 72 * H_MS, status: 'pending' },
        ],
      },
    ],
  },
  // #7 — Datenexport-Anomalie (cyber), MITIGATED, vor 5 Tagen, gdpr — GENAU EIN
  //      überfälliger Abschluss-Checkpoint (die EINE gerissene Frist).
  inc_007: {
    title: 'Anomalie bei Bestandsdaten-Export',
    summary: 'Ein anomal großer Bestandsdaten-Export löste den z-Score-Alarm aus. Zugang sofort eingedämmt, kein Abfluss nach außen bestätigt. Eindämmung erfolgt, Abschlussbericht steht aus.',
    severity: 'high',
    category: 'cyber',
    status: 'mitigated',
    detectedDaysAgo: 5,
    rootCause: 'Ein fehlerhaft parametrierter Batch-Job zog einen größeren Bestandsausschnitt als vorgesehen; intern geblieben.',
    remediation: 'Job gestoppt, Zugriff eingeschränkt, BfDI-Erstmeldung fristgerecht abgegeben — der Abschlussbericht ist überfällig.',
    highRiskToSubjects: false,
    affectedAgents: ['agent_churn'],
    reportedBy: 'dsb@nordlicht.example',
    reportingState: 'intermediate',
    clocks: [
      {
        regulator: 'gdpr-art-33', status: 'breached', startOffsetMs: 0,
        checkpoints: [
          { label: 'Erstmeldung BfDI (72h)', dueOffsetMs: 72 * H_MS, completedAfterMs: 50 * H_MS, status: 'completed' },
          { label: 'Abschlussbericht', dueOffsetMs: 4 * D_MS, status: 'breached' },
        ],
      },
    ],
  },
};

function buildPersonaIncident(id: string): API.Incident {
  const spec = PERSONA_INCIDENTS[id];
  const detectedAt = new Date(Date.now() - spec.detectedDaysAgo * D_MS).toISOString();
  const detMs = new Date(detectedAt).getTime();
  const clocks: API.IncidentClock[] = spec.clocks.map((c) => {
    const startedAt = new Date(detMs + c.startOffsetMs).toISOString();
    const checkpoints: API.IncidentCheckpoint[] = c.checkpoints.map((cp, j) => ({
      id: `cp_${id}_${c.regulator}_${j}`,
      regulator: c.regulator,
      label: cp.label,
      dueAt: new Date(detMs + cp.dueOffsetMs).toISOString(),
      completedAt: cp.completedAfterMs != null ? new Date(detMs + cp.completedAfterMs).toISOString() : null,
      completedBy: cp.completedAfterMs != null ? spec.reportedBy : null,
      status: cp.status,
      evidenceUrl: null,
      notes: null,
    }));
    // Gesamt-Deadline der Uhr = letzter Checkpoint (handlungsrelevant ist der
    // nächste offene; nextDeadlineOf liest die Checkpoints).
    const lastDue = c.checkpoints[c.checkpoints.length - 1]?.dueOffsetMs ?? 72 * H_MS;
    return {
      id: `clk_${id}_${c.regulator}`,
      incidentId: id,
      regulator: c.regulator,
      startedAt,
      deadlineAt: new Date(detMs + lastDue).toISOString(),
      checkpoints,
      status: c.status,
    };
  });
  // regulators[]: relative submittedAt/responseAt aus *DaysAgo-Hilfsfeldern ableiten.
  const regulators: API.IncidentRegulatorSubmission[] | undefined = spec.regulators?.map((r) => {
    const raw = r as unknown as Record<string, unknown>;
    const sub = typeof raw.submittedAtDaysAgo === 'number' ? new Date(Date.now() - (raw.submittedAtDaysAgo as number) * D_MS).toISOString() : r.submittedAt ?? null;
    const resp = typeof raw.responseAtDaysAgo === 'number' ? new Date(Date.now() - (raw.responseAtDaysAgo as number) * D_MS).toISOString() : r.responseAt ?? null;
    return {
      code: r.code,
      name: r.name ?? null,
      contactEmail: r.contactEmail ?? null,
      submittedAt: sub,
      submissionRef: r.submissionRef ?? null,
      authorityCaseId: r.authorityCaseId ?? null,
      responseReceived: r.responseReceived ?? false,
      responseAt: resp,
    };
  });
  return {
    id,
    organizationId: 'org_demo',
    title: spec.title,
    summary: spec.summary,
    severity: spec.severity,
    category: spec.category,
    severityClass: spec.severityClass,
    highRiskToSubjects: spec.highRiskToSubjects,
    status: spec.status,
    detectedAt,
    reportedBy: spec.reportedBy,
    affectedAgents: spec.affectedAgents,
    affectedTraces: [],
    rootCause: spec.rootCause,
    remediation: spec.remediation,
    closedAt: spec.closedDaysAgo != null ? new Date(Date.now() - spec.closedDaysAgo * D_MS).toISOString() : null,
    clocks,
    regulators,
    reportingState: spec.reportingState,
    doraClassification: spec.doraClassification ?? null,
    createdAt: detectedAt,
    updatedAt: new Date(Date.now() - (spec.closedDaysAgo ?? 0) * D_MS).toISOString(),
  };
}

const PERSONA_INCIDENT_IDS = ['inc_001', 'inc_002', 'inc_003', 'inc_004', 'inc_005', 'inc_006', 'inc_007'];

function mockIncident(
  id: string,
  severity: 'low' | 'medium' | 'high' | 'critical',
  status: 'open' | 'investigating' | 'mitigated' | 'closed',
  detectedOffsetMs: number,
  clockStatus: 'on-track' | 'at-risk' | 'breached' = 'on-track',
  // Realistisch: 1-3 Melde-Uhren je Vorfall (alle 5 = Demo-Chaos, Founder-Befund
  // 2026-06-12). gdpr bleibt im Default (recordRegulatorSubmission-Mock-Guard).
  regulators: Array<'gdpr-art-33' | 'ai-act-art-73' | 'dora-art-19' | 'nis2-art-23' | 'cra-art-14'> =
    ['gdpr-art-33', 'dora-art-19', 'ai-act-art-73'],
) {
  const detectedAt = new Date(Date.now() + detectedOffsetMs).toISOString();
  const checkpoints = [
    { offsetMs: 60 * 60 * 1000, label: 'Initial notification' },
    { offsetMs: 24 * 60 * 60 * 1000, label: '24h preliminary report' },
    { offsetMs: 72 * 60 * 60 * 1000, label: '72h full report' },
  ];
  const clocks = regulators.map((reg, i) => ({
    id: `clk_${id}_${reg}`,
    incidentId: id,
    regulator: reg,
    startedAt: detectedAt,
    deadlineAt: new Date(new Date(detectedAt).getTime() + 72 * 60 * 60 * 1000).toISOString(),
    checkpoints: checkpoints.map((cp, j) => ({
      id: `cp_${id}_${reg}_${j}`,
      regulator: reg,
      label: cp.label,
      dueAt: new Date(new Date(detectedAt).getTime() + cp.offsetMs).toISOString(),
      completedAt: i === 0 && j === 0 ? new Date(new Date(detectedAt).getTime() + 30 * 60 * 1000).toISOString() : null,
      completedBy: i === 0 && j === 0 ? 'demo@adjudon.com' : null,
      status: ((i === 0 && j === 0) ? 'completed' : 'pending') as 'pending' | 'completed' | 'breached' | 'na',
      evidenceUrl: null,
      notes: null,
    })),
    status: clockStatus as 'on-track' | 'at-risk' | 'breached' | 'completed',
  }));
  // Realistische deutsche Vorfalls-Titel statt "Incident inc_001" — die
  // Fristen-Card im Status-Tab zeigt den Titel als Hauptzeile (Demo = Prod).
  const INCIDENT_TITLES: Record<string, string> = {
    inc_001: 'Underwriting-Scoring liefert abweichende Risikoklassen',
    inc_002: 'Fraud Detector blockiert legitime Schadenmeldungen',
    inc_003: 'Dokument-Klassifizierer legt Posteingang falsch ab',
  };
  return {
    id,
    organizationId: 'org_demo',
    title: INCIDENT_TITLES[id] ?? `Vorfall ${id}`,
    summary: 'KI-System erzeugte auffällige Ausgaben, die Kunden betroffen haben können.',
    severity,
    status,
    detectedAt,
    reportedBy: 'demo@adjudon.com',
    affectedAgents: ['agent_fraud'],
    affectedTraces: ['trc_demo_001', 'trc_demo_002'],
    rootCause: undefined,
    remediation: undefined,
    closedAt: status === 'closed' ? new Date().toISOString() : null,
    clocks,
    createdAt: detectedAt,
    updatedAt: new Date().toISOString(),
  };
}

function mockPattern(id: string, status: 'observing' | 'mature' | 'paused' | 'expired', observations: number, approvalRate: number, primarySigned: boolean, payload?: any) {
  return {
    id,
    organizationId: 'org_demo',
    name: payload?.name ?? `Pattern ${id}`,
    description: payload?.description ?? 'High-confidence approvals on routine summary tasks',
    match: payload?.match ?? { confidenceMin: 0.95, tags: ['routine'] },
    observations,
    approvals: Math.round(observations * approvalRate),
    rejections: Math.round(observations * (1 - approvalRate)),
    approvalRate,
    status,
    signedOffAt: primarySigned ? new Date(Date.now() - 7 * 86400000).toISOString() : null,
    signedOffByPrimary: primarySigned ? 'compliance@demo' : null,
    signedOffBySecondary: status === 'mature' ? 'reviewer@demo' : null,
    lastRevalidatedAt: status === 'mature' ? new Date(Date.now() - 30 * 86400000).toISOString() : null,
    expiresAt: status === 'mature' ? new Date(Date.now() + 60 * 86400000).toISOString() : null,
    createdAt: new Date(Date.now() - 90 * 86400000).toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function mockDeployerProfile() {
  return {
    organizationId: 'org_demo',
    oversight: {
      designatedRoleTitle: 'Head of AI Compliance',
      designatedUserId: 'user_demo',
      procedures: 'Weekly review queue triage; quarterly audit; incident drills bi-annually.',
      trainingProvided: true,
      lastTrainingAt: new Date(Date.now() - 60 * 86400000).toISOString(),
    },
    monitoring: {
      metricsTracked: ['confidence', 'block-rate', 'review-latency', 'pattern-drift'],
      reportingFrequency: 'weekly' as const,
      lastReviewAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    },
    dpia: {
      completed: true,
      completedAt: new Date(Date.now() - 120 * 86400000).toISOString(),
      summary: 'DPIA covers customer-service and triage agents; high-risk decisions routed to human review.',
      documentUrl: 'https://demo.adjudon.com/dpia/v3.pdf',
    },
    instructions: [
      {
        id: 'instr_001',
        source: 'provider' as const,
        title: 'Foundation Model v2 — Updated Use Constraints',
        body: 'Do not deploy for medical advice or legal binding decisions. Updated 2026-Q1.',
        ingestedAt: new Date(Date.now() - 30 * 86400000).toISOString(),
        acknowledgedAt: new Date(Date.now() - 28 * 86400000).toISOString(),
        acknowledgedBy: 'demo@adjudon.com',
      },
    ],
    linkedFriaIds: ['fria_001'],
    completeness: 92,
    updatedAt: new Date().toISOString(),
  };
}

// ── EU AI Act — Controller-Responsibility catalog (demo) ────────────────
// Representative subset of the backend catalog so demo mode shows the ⚪
// "you must do this" attestation surface (incl. the Art. 26 deployer duties).
function mockGoAmlProfile(): API.GoAmlReportingProfile {
  return {
    organizationId: 'org_demo',
    rentityId: 'DE-FIU-NORDLICHT-4711',
    rentityBranch: '',
    entityName: 'Nordlicht Versicherung AG',
    reportingPerson: { gender: '', title: 'Dr.', firstName: 'Sabine', lastName: 'Geldwäschebeauftragte', email: 'mlro@nordlicht.example', phone: '+49 221 0000' },
    location: { address: 'Beispielallee 1', city: 'Köln', postcode: '50667', countryCode: 'DE' },
    defaultReportType: 'VMA',
    currencyCode: 'EUR',
    updatedByEmail: 'mlro@nordlicht.example',
    updatedAt: new Date(Date.now() - 7 * 24 * H).toISOString(),
    createdAt: new Date(Date.now() - 30 * 24 * H).toISOString(),
  };
}

function mockGoAmlExport(suspicionId: string, reportType?: 'VMA' | 'VMT'): API.GoAmlExportResult {
  const rt: 'VMA' | 'VMT' = reportType === 'VMT' ? 'VMT' : 'VMA';
  const caseRef = suspicionId === 'susp_demo_2' ? 'SUSP-2026-06-0002' : suspicionId === 'susp_demo_3' ? 'SUSP-2026-02-0007' : 'SUSP-2026-06-0001';
  const stamp = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  const body = rt === 'VMT'
    ? '  <transaction>\n    <transactionnumber><!-- PFLICHT --></transactionnumber>\n    <amount_local><!-- PFLICHT --></amount_local>\n  </transaction>'
    : '  <activity>\n    <!-- VERSICHERER: Beteiligte + wirtschaftlich Berechtigte aus Ihrer KYC-/Beneficiary-Evidenz ergänzen -->\n    <report_party>\n      <involved_party><person><first_name><!-- PFLICHT --></first_name><last_name><!-- PFLICHT --></last_name></person></involved_party>\n    </report_party>\n  </activity>';
  const xml = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!-- goAML-VERDACHTSMELDUNG - ENTWURF (Demo-Modus, von Adjudon aus dem SAR-Trail vorausgefüllt). Vor Abgabe in goAML Web gegen Ihr FIU-XSD validieren. -->',
    '<report>',
    '  <rentity_id>DE-FIU-NORDLICHT-4711</rentity_id>',
    '  <submission_code>E</submission_code>',
    `  <report_code>${rt}</report_code>`,
    `  <entity_reference>${caseRef}</entity_reference>`,
    `  <submission_date>${stamp}</submission_date>`,
    '  <currency_code_local>EUR</currency_code_local>',
    '  <reason>Demo-Verdacht: auffällige Rückkaufmuster mit rascher Kündigung. Der vollständige, manipulationssichere SAR-Trail ist über die Adjudon-Audit-Schnittstelle rekonstruierbar.</reason>',
    '  <report_indicators>',
    '    <!-- VERSICHERER: Meldegrund (mind. einer) aus dem FIU-Auswahlkatalog wählen (GwGMeldV § 3) -->',
    '  </report_indicators>',
    body,
    '</report>',
    '',
  ].join('\n');
  return {
    xml,
    filename: `goaml-${rt.toLowerCase()}-${caseRef}.xml`,
    reportType: rt,
    completeness: {
      present: ['submission_code (E — elektronisch)', 'report_code (Meldungstyp VMA/VMT)', 'submission_date', 'currency_code_local', 'entity_reference (internes Aktenkennzeichen / caseRef)', 'reason (Verdachts-Narrativ aus dem SAR-Trail)', 'rentity_id (FIU-Meldenden-ID)', 'reporting_person (meldende Person / Geldwäschebeauftragte/r)', 'location (Anschrift des Verpflichteten)'],
      missing: ['Meldegrund (FIU-Auswahlkatalog, GwGMeldV § 3)', 'Beteiligte Personen/Entitäten (GwGMeldV § 3)', 'Wirtschaftlich Berechtigte (§ 8/§ 11 GwG)'],
      placeholders: ['Meldegrund (FIU-Auswahlkatalog, GwGMeldV § 3)', 'Beteiligte Personen/Entitäten (GwGMeldV § 3)', 'Wirtschaftlich Berechtigte (§ 8/§ 11 GwG)'],
      gwgMeldVMinimum: { internesAktenkennzeichen: true, meldegrund: false, parallelStrafanzeige: null, beteiligte: false, wirtschaftlichBerechtigte: false },
      isSubmittableDraft: false,
    },
    generatedAt: stamp,
    profileConfigured: true,
  };
}

function mockControllerResponsibilityTemplate(key: string) {
  const titles: Record<string, { title: string; filename: string }> = {
    dataact_share_decision: { title: 'Daten-Teilungs-Entscheidung & Drittempfänger-Eignung (EU Data Act Art. 4–5)', filename: 'data-act-art4-5-datenteilungs-entscheidung-walkthrough.md' },
    dataact_third_party_vetting: { title: 'Zweckbindung & Empfänger-Verpflichtung (EU Data Act Art. 5/6)', filename: 'data-act-art5-6-zweckbindung-empfaenger-verpflichtung-walkthrough.md' },
    dataact_fair_terms: { title: 'Missbräuchliche-Klauseln-Fairness-Selbstprüfung (Data Act Art. 13)', filename: 'data-act-art13-fair-terms-self-assessment.md' },
    vvg_suitability: { title: 'Geeignetheitsprüfung — Versicherungsanlageprodukt (IDD Art. 30(1) / VVG § 7c Abs. 1)', filename: 'idd-art30-1-vvg-7c-geeignetheitspruefung-ibip.md' },
    vvg_appropriateness: { title: 'Angemessenheitsprüfung + Warnhinweise + Execution-only (IDD Art. 30(2)-(3) / VVG § 7c Abs. 2-3)', filename: 'idd-art30-2-3-vvg-7c-angemessenheit-execution-only.md' },
    vvg_suitability_statement: { title: 'Geeignetheitserklärung + regelmäßige Berichte (IDD Art. 30(4)-(5) / VVG § 7c Abs. 4-5)', filename: 'idd-art30-4-5-vvg-7c-geeignetheitserklaerung-berichte.md' },
    vag_business_organisation: { title: 'Ordnungsgemäße Geschäftsorganisation — KI-Schicht (VAG § 23 + MaGo)', filename: 'vag-section23-geschaeftsorganisation-worksheet.md' },
    vag_fit_proper: { title: 'Fit & Proper — Schlüsselpersonen der KI-Governance (VAG § 24)', filename: 'vag-section24-fit-proper-register.md' },
    vag_internal_control_system: { title: 'Internes Kontrollsystem & Compliance-Funktion — KI-Schicht (VAG § 29)', filename: 'vag-section29-internal-control-system-worksheet.md' },
    eu_ai_act_art14_human_oversight: {
      title: 'Nachweis: Benennung & Kompetenz der menschlichen Aufsicht (Art. 14)',
      filename: 'art14-human-oversight-designation-record.md',
    },
    eu_ai_act_art14_sla_escalation: {
      title: 'Richtlinie: Review-Fristen (SLA) & Eskalation (Art. 14)',
      filename: 'art14-review-sla-escalation-policy.md',
    },
    dora_ict_risk_framework: {
      title: 'ICT-Risikomanagement-Rahmenwerk — Governance & Freigabe (DORA Art. 5/6)',
      filename: 'dora-art5-6-ict-risk-framework-record.md',
    },
    dora_art7_systems: {
      title: 'ICT-Systeme/-Tools — Eignung/Zuverlässigkeit/Kapazität/Resilienz (DORA Art. 7)',
      filename: 'dora-art7-systems-reliability-worksheet.md',
    },
    dora_art8_asset_identification: {
      title: 'ICT-Asset- & Geschäftsfunktions-Klassifizierung (DORA Art. 8)',
      filename: 'dora-art8-asset-identification-worksheet.md',
    },
    dora_art9_10_protection_detection: {
      title: 'Schutz- & Erkennungsmaßnahmen — KI-Schicht (DORA Art. 9/10)',
      filename: 'dora-art9-10-protection-detection-worksheet.md',
    },
    dora_art11_continuity_policy: { title: 'IKT-Geschaeftsfortfuehrungs-Leitlinie + RTO/RPO + Plaene (DORA Art. 11)', filename: 'dora-art11-ict-business-continuity-policy.md' },
    dora_art11_bcm_testing: { title: 'IKT-BCM-Testplan + jaehrlicher Testbericht (DORA Art. 11(6) / RTS Art. 25)', filename: 'dora-art11-bcm-test-report.md' },
    dora_art12_backup_recovery: { title: 'Backup-, Wiederherstellungs- und Recovery-Policy (DORA Art. 12)', filename: 'dora-art12-backup-recovery-policy.md' },
    dora_art13_post_incident_review: { title: 'Post-Incident-Review / Lessons Learned (DORA Art. 13)', filename: 'dora-art13-post-incident-review.md' },
    dora_art14_crisis_communication: { title: 'Krisenkommunikationsplan (DORA Art. 14)', filename: 'dora-art14-crisis-communication-plan.md' },
    dora_tlpt: { title: 'TLPT Readiness- & Scoping-Arbeitsblatt (DORA Art. 26/27)', filename: 'dora-art26-27-tlpt-readiness-worksheet.md' },
    vag_risk_appetite: { title: 'Risikostrategie & Risikotoleranzschwellen — KI-Schicht (VAG § 26 / MaGo)', filename: 'vag-art26-risk-strategy-thresholds-worksheet.md' },
    vag_internal_audit: { title: 'Pruefungsprogramm Interne Revision — KI-Entscheidungs-Schicht (VAG § 30)', filename: 'vag-paragraph30-interne-revision-pruefungsprogramm.md' },
    vag_outsourcing: { title: 'Auslagerungsregister-Eintrag + laufende Überwachung (§ 32 VAG / MaGo)', filename: 'vag-32-auslagerungsregister-walkthrough.md' },
    vag_outsourcing_risk_analysis: { title: 'Risikoanalyse + Wesentlichkeitsbewertung (§ 32 Abs. 2/3 VAG)', filename: 'vag-32-risikoanalyse-wesentlichkeit.md' },
    vag_outsourcing_agreement: { title: 'Auslagerungsvertrag — Pflichtinhalte (§ 32 Abs. 2/4 VAG + 274(4))', filename: 'vag-32-auslagerungsvertrag-klauseln.md' },
    vag_outsourcing_notification: { title: 'BaFin-Anzeige der Ausgliederung (§ 47 Nr. 8/9 VAG + VersAusgl-AnzV)', filename: 'vag-47-bafin-ausgliederungsanzeige.md' },
    solvency_art49_outsourcing_policy: { title: 'Auslagerungs-Leitlinie + Wesentlichkeitsbestimmung (Solvency II Art. 49 / 274(1))', filename: 'solvency-art49-outsourcing-policy.md' },
    solvency_art49_due_diligence: { title: 'Vorvertragliche Due Diligence (Solvency II Art. 274(3)/(5))', filename: 'solvency-art49-due-diligence.md' },
    solvency_art49_written_agreement: { title: 'Schriftliche Auslagerungsvereinbarung — Klausel-Checkliste (Art. 274(4))', filename: 'solvency-art49-written-agreement.md' },
    solvency_art49_supervisory_notification: { title: 'Aufsichtliche Anzeige der Auslagerung (Art. 49(3) / § 47 Nr. 8 VAG)', filename: 'solvency-art49-supervisory-notification.md' },
    vvg_information_form: { title: 'Zeitpunkt & Form der Information (§ 62 VVG, Textform / § 126b BGB)', filename: 'vvg-section62-textform-zeitpunkt-worksheet.md' },
    eiopa_board_governance: { title: 'KI-Governance auf Vorstandsebene — Verantwortlichkeits- & Aufsichts-Nachweis (EIOPA-BoS-25-360)', filename: 'eiopa-board-governance-accountability-record.md' },
    eiopa_proportionality: { title: 'Risikobasierte KI-Klassifizierung & Proportionalität (EIOPA-BoS-25-360)', filename: 'eiopa-proportionality-risk-tiering-worksheet.md' },
    eiopa_record_keeping: { title: 'Aufzeichnungspflicht für KI-Entscheidungen (EIOPA-BoS-25-360 Prinzip #5)', filename: 'eiopa-bos25360-5-record-keeping.md' },
    eiopa_transparency: { title: 'Transparenz & Erklärbarkeit von KI-Entscheidungen (EIOPA-BoS-25-360 Prinzip #6)', filename: 'eiopa-bos25360-6-transparency-explainability.md' },
    eiopa_human_oversight: { title: 'Menschliche Aufsicht über KI (EIOPA-BoS-25-360 Prinzip #7)', filename: 'eiopa-bos25360-7-human-oversight.md' },
    vvg_market_test: { title: 'Walkthrough + Arbeitsblatt: Ausgewogene Markt-/Produktauswahl (§ 60 VVG)', filename: 'vvg-60-marktauswahl-produktvergleich-walkthrough.md' },
      vvg_advice_content: { title: 'Beratungsgrund-Arbeitsblatt — Beratung dokumentieren + Empfehlung begründen (VVG § 61 Abs. 1)', filename: 'vvg-paragraph61-abs1-beratungsgrund-arbeitsblatt.md' },
    dataact_switching_exit: { title: 'Anbieterwechsel / Exit aus Adjudon (Data Act Art. 23–31)', filename: 'dataact-art23-31-switching-exit-walkthrough.md' },
    dataact_switching_information: { title: 'Wechsel-Information (Data Act Art. 26) — Adjudon-Selbstauskunft', filename: 'dataact-art26-switching-information.md' },
    dataact_switching_clauses: { title: 'Wechsel-Vertragsklauseln (Data Act Art. 25) — Adjudon-AGB/DPA', filename: 'dataact-art25-switching-clauses-checklist.md' },
  };
  const meta = titles[key] || { title: 'Vorlage', filename: `${key}.md` };
  return {
    key,
    title: meta.title,
    filename: meta.filename,
    markdown: `# ${meta.title}\n\n> Demo-Vorlage. In der Live-Umgebung wird sie mit Ihrem Organisationsnamen vorbefüllt.\n\n| Feld | Eintrag |\n|---|---|\n| Betreiber | [Ihr Unternehmen] |\n| Erstellt am | __________ |\n\n_Ausfüllen, freigeben, anschließend das Item bestätigen._\n`,
  };
}

// Server-authoritative obligation → regulatory-domain (pillar) map — mirrors the
// backend controllerResponsibilityService.OBLIGATION_PILLAR so the demo Reifegrad
// groups exactly like prod. Keyed by catalog obligation key.
const MOCK_OBLIGATION_PILLAR: Record<string, string> = {
  // DORA
  dora_ict_risk_framework: 'IKT-Risikomanagement', dora_art7_systems: 'IKT-Risikomanagement', dora_art8_asset_identification: 'IKT-Risikomanagement', dora_art9_10_protection_detection: 'IKT-Risikomanagement', dora_art11_continuity_policy: 'IKT-Risikomanagement', dora_art11_bcm_testing: 'IKT-Risikomanagement', dora_art12_backup_recovery: 'IKT-Risikomanagement', dora_art13_post_incident_review: 'IKT-Risikomanagement', dora_art14_crisis_communication: 'IKT-Risikomanagement',
  dora_art17_incident_management_process: 'Vorfall-Meldung', dora_incident_classification: 'Vorfall-Meldung', dora_art18_significant_cyber_threat: 'Vorfall-Meldung', dora_regulator_filing: 'Vorfall-Meldung',
  dora_tlpt: 'Resilienz-Tests',
  dora_art28_register_of_information: 'Drittparteien-Risiko', dora_art30_contractual_provisions: 'Drittparteien-Risiko', dora_art29_concentration_risk: 'Drittparteien-Risiko', dora_art28_8_exit_strategy: 'Drittparteien-Risiko',
  // GDPR
  gdpr_art6_legal_basis: 'Rechtsgrundlagen & Transparenz', gdpr_art13_14_privacy_notice: 'Rechtsgrundlagen & Transparenz', gdpr_bdsg26_employee_data: 'Rechtsgrundlagen & Transparenz', gdpr_tdddg25_cookie_consent: 'Rechtsgrundlagen & Transparenz',
  gdpr_art12_dsar_fulfillment: 'Betroffenenrechte', gdpr_art16_rectification_execution: 'Betroffenenrechte', gdpr_art17_erasure_decision: 'Betroffenenrechte', gdpr_art18_restriction_execution: 'Betroffenenrechte', gdpr_art21_objection_disposition: 'Betroffenenrechte', gdpr_art22_human_review: 'Betroffenenrechte',
  gdpr_art28_2_subprocessor_approval: 'Auftragsverarbeitung', gdpr_art29_dpa_verification: 'Auftragsverarbeitung', gdpr_art28_3_dpa_conclusion: 'Auftragsverarbeitung', gdpr_art28_3a_documented_instructions: 'Auftragsverarbeitung',
  gdpr_art32_security_risk_assessment: 'Sicherheit der Verarbeitung', gdpr_art32_effectiveness_testing: 'Sicherheit der Verarbeitung', gdpr_art32_tom_acceptance: 'Sicherheit der Verarbeitung',
  gdpr_art35_dpia: 'Risiko & Meldung', gdpr_art33_34_breach_decision: 'Risiko & Meldung',
  gdpr_art44_49_transfer_assessment: 'Internationale Übermittlung', gdpr_art30_ropa_maintenance: 'Verarbeitungsverzeichnis',
  // EU AI Act
  eu_ai_act_art11_technical_documentation: 'Dokumentation & Aufzeichnung', eu_ai_act_art47_declaration: 'Dokumentation & Aufzeichnung', eu_ai_act_art12_retention_decision: 'Dokumentation & Aufzeichnung',
  eu_ai_act_art13_instructions_use: 'Transparenz & Anweisungen', eu_ai_act_art13_provider_ifu: 'Transparenz & Anweisungen',
  eu_ai_act_art14_human_oversight: 'Menschliche Aufsicht', eu_ai_act_art14_sla_escalation: 'Menschliche Aufsicht',
  eu_ai_act_art15_accuracy_policy: 'Genauigkeit & Bias', eu_ai_act_art15_bias_audits: 'Genauigkeit & Bias', eu_ai_act_art15_remediation: 'Genauigkeit & Bias',
  eu_ai_act_art26_monitoring: 'Betreiberpflichten (Art. 26)', eu_ai_act_art26_input_data: 'Betreiberpflichten (Art. 26)', eu_ai_act_art26_11_affected_persons: 'Betreiberpflichten (Art. 26)', eu_ai_act_art26_7_worker_info: 'Betreiberpflichten (Art. 26)', eu_ai_act_art26_8_registration: 'Betreiberpflichten (Art. 26)', eu_ai_act_art26_cooperation: 'Betreiberpflichten (Art. 26)',
  eu_ai_act_art27_fria_completion: 'Grundrechte-Folgenabschätzung',
  eu_ai_act_art50_watermark_embedding: 'Provenance & Kennzeichnung', eu_ai_act_art50_2_autowatermark_decision: 'Provenance & Kennzeichnung', eu_ai_act_art50_1_4_disclosure: 'Provenance & Kennzeichnung',
  eu_ai_act_art73_incident_reporting: 'Vorfallmeldung',
  // Solvency II + VAG
  solvency_governance_keyfunctions: 'Governance & Schlüsselfunktionen', solvency_art41_written_policies: 'Governance & Schlüsselfunktionen', solvency_art41_key_functions: 'Governance & Schlüsselfunktionen', vag_business_organisation: 'Governance & Schlüsselfunktionen', vag_fit_proper: 'Governance & Schlüsselfunktionen', vag_internal_control_system: 'Governance & Schlüsselfunktionen', vag_internal_audit: 'Governance & Schlüsselfunktionen',
  solvency_orsa_narrative: 'Risikomanagement & ORSA', solvency_esg_classification: 'Risikomanagement & ORSA', solvency_art45a_climate_scenario: 'Risikomanagement & ORSA', vag_risk_appetite: 'Risikomanagement & ORSA', vag_orsa: 'Risikomanagement & ORSA',
  solvency_scr_mcr: 'Internes Modell (Säule 1)', solvency_pillar1_ai_inventory: 'Internes Modell (Säule 1)', solvency_pillar1_data_quality: 'Internes Modell (Säule 1)', solvency_pillar1_use_test: 'Internes Modell (Säule 1)', solvency_pillar1_validation: 'Internes Modell (Säule 1)', solvency_pillar1_documentation: 'Internes Modell (Säule 1)', solvency_internal_model_validation: 'Internes Modell (Säule 1)',
  solvency_art49_outsourcing_policy: 'Ausgliederung', solvency_art49_due_diligence: 'Ausgliederung', solvency_art49_written_agreement: 'Ausgliederung', solvency_art49_supervisory_notification: 'Ausgliederung', vag_outsourcing: 'Ausgliederung', vag_outsourcing_risk_analysis: 'Ausgliederung', vag_outsourcing_agreement: 'Ausgliederung', vag_outsourcing_notification: 'Ausgliederung',
  // VVG / IDD
  vvg_advice_content: 'Beratung & Dokumentation', vvg_information_form: 'Beratung & Dokumentation', vvg_liability_defense: 'Beratung & Dokumentation',
  vvg_suitability: 'Geeignetheit & Angemessenheit', vvg_appropriateness: 'Geeignetheit & Angemessenheit', vvg_suitability_statement: 'Geeignetheit & Angemessenheit',
  vvg_conflict_disclosure: 'Interessenkonflikt & Markttest', vvg_market_test: 'Interessenkonflikt & Markttest',
  vvg_cpd_weiterbildung: 'Weiterbildung',
  // EIOPA
  eiopa_board_governance: 'Risikomanagement & Governance', eiopa_proportionality: 'Risikomanagement & Governance', eiopa_model_approval: 'Risikomanagement & Governance', eiopa_orsa_attestation: 'Risikomanagement & Governance',
  eiopa_data_quality: 'Datengovernance',
  eiopa_fairness_monitoring: 'Fairness & Ethik', eiopa_fairness_remediation: 'Fairness & Ethik',
  eiopa_robustness: 'Genauigkeit & Robustheit', eiopa_record_keeping: 'Dokumentation & Aufzeichnung', eiopa_transparency: 'Transparenz & Erklärbarkeit', eiopa_human_oversight: 'Menschliche Aufsicht',
  // GwG
  gwg_risk_analysis: 'Risikomanagement & Sicherungsmaßnahmen', gwg_control_plan: 'Risikomanagement & Sicherungsmaßnahmen', gwg_alert_sla_policy: 'Risikomanagement & Sicherungsmaßnahmen', gwg_mlro_role: 'Risikomanagement & Sicherungsmaßnahmen',
  gwg_customer_acceptance: 'Sorgfaltspflichten (KYC)', gwg_beneficial_owner: 'Sorgfaltspflichten (KYC)', gwg_screening_evaluation: 'Sorgfaltspflichten (KYC)',
  gwg_edd_judgment: 'Verstärkte Sorgfaltspflichten',
  gwg_sar_decision: 'Verdachtsmeldung', gwg_goaml_export: 'Verdachtsmeldung',
  // Data Act / FiDA
  dataact_share_decision: 'Datenzugang & -teilung', dataact_third_party_vetting: 'Datenzugang & -teilung', dataact_access_journal: 'Datenzugang & -teilung',
  dataact_fair_terms: 'Faire Bedingungen & Entschädigung', dataact_compensation: 'Faire Bedingungen & Entschädigung',
  dataact_retention_policy: 'Aufbewahrung & Zweckbindung',
  dataact_switching_exit: 'Wechsel & Exit', dataact_switching_information: 'Wechsel & Exit', dataact_switching_clauses: 'Wechsel & Exit',
  fida_scheme_membership: 'FiDA-Schema',
};

function mockControllerResponsibilities(framework?: string) {
  const all = [
    {
      key: 'dataact_share_decision', framework: 'data-act', article: 'Art. 4–5',
      title: 'Über Daten-Teilung entscheiden + Drittempfänger-Eignung prüfen',
      whatYouMustDo: 'So gehen Sie vor: (1) Bestimmen Sie als Dateninhaber, welche Daten ohne Weiteres verfügbar sind (Art. 2/4) — und ob Sie im Anwendungsbereich sind (Art. 7 ist die KMU-Ausnahme, NICHT der Widerruf). (2) Drittempfänger-Eignung prüfen (Art. 5(3): kein Torwächter nach DMA Art. 3). (3) Rechtsgrundlage klären, wenn der Nutzer NICHT die betroffene Person ist (Art. 5(7) → DSGVO Art. 6). (4) Daten unentgeltlich für den Nutzer + maschinenlesbar bereitstellen (Art. 5(1)). (5) Geschäftsgeheimnisse auf das unbedingt Erforderliche begrenzen (Art. 5(9)). (6) Teilungs-Entscheidung im Daten-Zugriffs-Register dokumentieren. (7) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die Export-Funktion mit Frist-Verfolgung, das maschinenlesbare Format (JSON-LD/CSV) mit Format-Prüfung, die automatische Schwärzung personenbezogener Daten in jedem Export, das manipulationssichere Daten-Zugriffs-Journal als Art.-5-Nachweis und ein Entscheidungs-/Eignungs-Arbeitsblatt; welche Daten verfügbar sind, die Empfänger-Eignung + die Teilungs-Entscheidung treffen Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/data-act-fida', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_third_party_vetting', framework: 'data-act', article: 'Art. 5/6',
      title: 'Empfänger prüfen + Zweckbindung sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) Datenempfänger prüfen (Identität, Sitz, Zuverlässigkeit; kein Torwächter). (2) Zweck präzise festlegen — der Dritte darf die Daten nach Art. 6(1) NUR für die vereinbarten Zwecke + Bedingungen verarbeiten (Zweckbindung) und muss bei Zweckfortfall löschen. (3) Empfänger vertraglich an die Art.-6(2)-Verbote binden: kein Profiling außer notwendig (b), keine Weitergabe ohne Nutzer-Autorisierung (c), keine Torwächter (d), kein Konkurrenzprodukt (e), Geschäftsgeheimnis-Schutz (g). (4) Einwilligung erfassen + Widerruf ermöglichen (DSGVO Art. 7 Abs. 3) — Adjudon entzieht den Zugriff bei Widerruf/Ablauf automatisch. (5) FiDA-Vorblick (~2027): Permission-Dashboard (Art. 5/6/8). (6) Arbeitsblatt herunterladen, durch Recht freigeben, abhaken.',
      adjudonSupport: 'Wir verfolgen den Einwilligungs-Lebenszyklus lückenlos, ermöglichen den Widerruf und entziehen den verknüpften Zugriff bei Widerruf/Ablauf automatisch (Anker: DSGVO Art. 7 Abs. 3 + Data Act Art. 6(1) — NICHT Data Act Art. 7, die KMU-Ausnahme), belegen den Lebenszyklus im manipulationssicheren Prüfprotokoll und liefern eine Zweckbindungs-/Art.-6(2)-Verbote-Checkliste; die Empfänger-Prüfung + die vertragliche Zweckbindung verantworten Sie (Hogan Lovells; § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/data-act-fida', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_fair_terms', framework: 'data-act', article: 'Art. 13',
      title: 'Missbräuchliche Vertragsklauseln prüfen + faire (FRAND) Bedingungen sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) Prüfen Sie, ob die Klausel unter Art. 13 fällt (Datenzugang/-nutzung ODER Haftung/Rechtsbehelfe bei datenbezogenen Pflichten; B2B; horizontal — gilt für jedes Unternehmen, nicht nur KMU). (2) Bestimmen Sie Ihre Rolle: Dateninhaber, der Bedingungen auferlegt (z.B. Telematik-Daten → Partner) — oder Datenempfänger, dem auferlegt wird. (3) Einseitigkeits-Test (Art. 13(6): einseitig gestellt + keine Einflussmöglichkeit trotz Verhandlungsversuch; Beweislast bei der stellenden Partei). (4) Schwarze Liste (Art. 13(4)) + Graue Liste (Art. 13(5)) + Generalklausel (Art. 13(3)) prüfen — unwirksame Klausel ist nicht bindend (Art. 13(1)). (5) Als bereitstellungspflichtiger Dateninhaber FRAND + Vergütung einhalten (Art. 8/9; KMU/Forschung nur Kostendeckung). (6) Kommissions-Mustervertragsklauseln (Art. 41) als Safe Harbor. (7) Zeitliche Einordnung (Art. 50: ab 12.09.2025 sofort; davor erst 12.09.2027). (8) Arbeitsblatt herunterladen, von der Rechtsabteilung freigeben, abhaken.',
      adjudonSupport: 'Wir dokumentieren die Vereinbarung + Zeitstempel und liefern die faktische Beweis-Grundlage (Daten-Zugriffs-Journal Art. 4–6, Portabilität/Kopie-Export, Einwilligung + Art.-7-Widerruf, manipulationssicherer Audit-Trail) plus eine ausfüllbare Art.-13-Selbstprüfungs-Vorlage (Schwarze/Graue Liste, Einseitigkeits-Test, FRAND-Begleitung Art. 8/9, MCT-Verweis); die Missbräuchlichkeits-Bewertung + den Vertrag verantworten Sie (Rechtsabteilung/Hogan Lovells; § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/data-act-fida', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_access_journal', framework: 'data-act', article: 'Art. 4–5 / 4(5)',
      title: 'Zugänglichkeit nachweisen (Zugriffs-Journal)',
      whatYouMustDo: 'So gehen Sie vor: (1) Bestimmen Sie, welche Daten „ohne Weiteres verfügbar" sind (Art. 2/4). (2) Legen Sie die berechtigten Empfänger + deren Rechtsgrundlage fest. (3) Wägen Sie die Aufbewahrung des Zugriffs-Journals gegen die Art.-4(5)-Log-Minimierung ab. (4) Nutzen Sie das Adjudon-Zugriffs-Journal + den maschinenlesbaren Export als Zugänglichkeits-Nachweis. (5) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir führen das append-only, SHA-256-verkettete Zugriffs-Journal über jede Daten-Zugriffs-Entscheidung (create/approve/deny/revoke) + liefern den abfragbaren, maschinenlesbaren Export (JSON-LD/CSV, PII-bereinigt, Data-Act-formatgeprüft) und die Hash-Chain-Verifikation; die rechtliche Einordnung treffen Sie. Cardinal #8: EU Data Act Art. 9 = „Ausgleich", trägt KEINE Logging-Pflicht — der Nachweis-Anker ist Art. 4–5 + Art. 4(5).',
      templateAvailable: true, templateRef: '/dashboard/data-act-fida', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_compensation', framework: 'data-act', article: 'Art. 9',
      title: 'Kompensation für Datenbereitstellung festlegen (der echte Art. 9)',
      whatYouMustDo: 'Falls Sie als bereitstellungspflichtiger Dateninhaber Daten gegen Entgelt teilen: (1) Legen Sie eine nicht-diskriminierende + angemessene Kompensation fest (Art. 9(1), Marge zulässig). (2) Beachten Sie den KMU-/Non-Profit-Kostendeckel (höchstens Kosten). (3) Legen Sie dem Empfänger die Berechnungsgrundlage hinreichend detailliert offen. (4) Prüfen Sie die Nicht-Diskriminierung über vergleichbare Empfänger. (5) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir dokumentieren die Vereinbarung + den Zeitstempel im manipulationssicheren Audit-Trail; Höhe, Angemessenheit (FRAND), KMU-Einordnung + Offenlegung der Berechnungsgrundlage sind rechtlich-kaufmännische Entscheidungen, die bei Ihnen verbleiben (Art. 9 nicht delegierbar).',
      templateAvailable: true, templateRef: '/dashboard/data-act-fida', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_retention_policy', framework: 'data-act', article: 'Art. 4 Abs. 5 / DSGVO Art. 5(1)(e)',
      title: 'Aufbewahrungs-Policy festlegen',
      whatYouMustDo: 'Sie legen Ihre Retention-Policy fest und wägen die Art.-4(5)-Log-Minimierung gegen Ihren Accountability-/Audit-Bedarf ab (BaFin/VAG typisch ≥ 5 Jahre), abgestimmt mit dem DSGVO-Art.-5(1)(e)-Speicherbegrenzungsgrundsatz. Die gewählte Dauer (dataRetentionDays) erzwingt Adjudon technisch; die Abwägung treffen Sie. Das „Wie" des Nachweises deckt das Zugriffs-Journal-Arbeitsblatt ab.',
      adjudonSupport: 'Wir setzen Ihre gewählte Aufbewahrungsdauer automatisch durch; die Abwägung + Wahl der Dauer treffen Sie. Dieses Item hat bewusst keine eigene Vorlage — der Zugänglichkeits-Nachweis liegt im Zugriffs-Journal-Arbeitsblatt.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'fida_scheme_membership', framework: 'data-act', article: 'FiDA (~2027)',
      title: 'FDSS-Scheme-Mitgliedschaft + FiDA-Pflichten erfüllen',
      whatYouMustDo: 'Hinweis: FiDA (Financial Data Access) ist noch nicht in Kraft (Vorschlag COM/2023/360, Anwendung ~2027) — dies ist eine Vorbereitung, keine heute geltende Pflicht. Ab FiDA-Go-Live treten Sie einem Financial-Data-Sharing-Scheme (FDSS) bei und erfüllen die Dateninhaber-Pflichten (permission-based access, Permission-Dashboard). Verfolgen Sie den finalen Verordnungstext, bevor Sie Maßnahmen ableiten.',
      adjudonSupport: 'Wir sind schema-ready für die FDSS-Integration; die Scheme-Mitgliedschaft + Pflichten verantworten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_switching_exit', framework: 'data-act', article: 'Art. 23–31',
      title: 'Anbieterwechsel / Exit aus Adjudon vollziehen (Switching-Readiness)',
      whatYouMustDo: 'So gehen Sie vor: (1) Wechsel/Kündigung in Textform anstoßen (max. 2 Monate Kündigungsfrist, Art. 25(2)(a)). (2) In der Übergangsfrist (max. 30 Tage, Art. 25(2)(d)) die exportierbaren Daten + digitalen Assets abrufen — Audit-Bundle, GDPR-Art.-20-Export, Policies/Agents. (3) Bundle offline mit verify.sh prüfen. (4) Im 30-Tage-Abrufzeitraum beim Nachfolger/In-house re-integrieren. (5) Löschung bei Adjudon bestätigen. (6) Bei kritischer Funktion: als DORA-Exit-Test (Art. 28(8)) erfassen.',
      adjudonSupport: 'Wir liefern die Export-Werkzeuge (Audit-Trail-Bundle + offline verify.sh, GDPR-Art.-20-Export), den Art.-30-Format-Konformitätsprüfer, die EU-only-Datenresidenz (Frankfurt, Art. 28) + den Exit-Walkthrough; die Wechsel-Entscheidung + den Exit-Test verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_switching_information', framework: 'data-act', article: 'Art. 26',
      title: 'Wechsel-Information dokumentieren (Art. 26) — Adjudon-Selbstauskunft',
      whatYouMustDo: 'So gehen Sie vor: (1) Adjudon-Wechsel-Information herunterladen (Verfahren, Porting-Methoden/-Formate, bekannte Beschränkungen, Datenresidenz). (2) In die Lieferantenakte / DORA-Ausstiegsstrategie legen. (3) Gegen Ihren Adjudon-Vertrag abgleichen. (4) Abhaken.',
      adjudonSupport: 'Wir generieren die Art.-26-Wechsel-Information vorbefüllt mit Adjudons Posture (maschinenlesbare Formate/Methoden, bekannte Limitierungen, Frankfurt EU-only); die formelle Veröffentlichung + vertragliche Bestätigung sind Founder/Legal (Hogan Lovells).',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dataact_switching_clauses', framework: 'data-act', article: 'Art. 25',
      title: 'Wechsel-Vertragsklauseln prüfen (Art. 25) — Adjudon-AGB/DPA',
      whatYouMustDo: 'So gehen Sie vor: (1) Prüfen, dass Ihr Adjudon-Vertrag/DPA die Art.-25-Pflichtklauseln enthält (max. 2-Monats-Kündigung, max. 30-Tage-Übergang, erschöpfende Daten-/Asset-Liste, 30-Tage-Abruf + Löschung, Wechselentgelte = 0 ab 12.01.2027, Art. 29). (2) Rechtsverbindliche Fassung mit der Rechtsabteilung verhandeln + zeichnen. (3) Abhaken.',
      adjudonSupport: 'Wir liefern die Art.-25-Klausel-Checkliste mit Adjudon-Posture je Klausel + den Verweis auf die erschöpfende exportierbare-Daten-/Asset-Liste (Art. 26) und die Löschungs-/Egress-Posture; die rechtsverbindliche Endfassung verantworten Sie (Hogan Lovells, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_advice_content', framework: 'vvg-idd', article: '§ 61 Abs. 1 VVG',
      title: 'Beratung durchführen + Empfehlung begründen (Beratungsgrund)',
      whatYouMustDo: 'So gehen Sie vor: (1) Beratungsanlass prüfen (Schwierigkeit des Produkts ODER Person/Situation des Kunden; Makler: regelmäßig volle Beratung). (2) Wünsche + Bedürfnisse erfragen + erfassen (IDD Art. 20). (3) Beratungstiefe verhältnismäßig zur Prämie; eine eingeschränkte Beratung begründen (§ 61 Abs. 1 S. 1). (4) Den BERATUNGSGRUND formulieren — die Gründe für jeden zu einer bestimmten Versicherung erteilten Rat: konkret, an den festgestellten Bedarf geknüpft, personalisiert (generische Floskeln genügen nicht — BGH III ZR 544/13; Beweislastumkehr, § 63). (5) Bei KI-gestütztem Rat: KI offenlegen, menschlich lesbaren Grund verantworten, Decision-Trace verknüpfen (EIOPA BoS-25-360). (6) Vor Vertragsschluss in Textform dokumentieren (§ 62) — im Advisory-Tool finalisieren + übergeben. (7) Arbeitsblatt herunterladen, dokumentieren, abhaken.',
      adjudonSupport: 'Wir liefern das Beratungsprotokoll-Tool mit der § 61-Struktur (Wünsche/Bedürfnisse, Empfehlung, Beratungsgrund, Alternativen, Umfang), den Konsistenz-Check, den § 62-Textform-/PDF-Export, den SHA-256-Integritäts-Hash beim Finalisieren + das 10-J-Archiv und ein Beratungsgrund-Arbeitsblatt. Die Beratung, das Formulieren des Beratungsgrundes + die Haftung (§ 63 VVG) verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_information_form', framework: 'vvg-idd', article: '§ 62 VVG',
      title: 'Pflichtinformationen fristgerecht + in Textform übermitteln',
      whatYouMustDo: 'So gehen Sie vor: (1) Übermitteln Sie die § 60-Abs-2-Information VOR Abgabe der Vertragserklärung in Textform (§ 62 Abs. 1). (2) Übermitteln Sie das § 61-Abs-1-Beratungsprotokoll VOR dem Abschluss des Vertrags in Textform. (3) Wahren Sie die Textform nach § 126b BGB (lesbar, Erklärender benannt, dauerhafter Datenträger). (4) Bei mündlicher Übermittlung (§ 62 Abs. 2): Textform unverzüglich nach Vertragsschluss nachliefern, spätestens mit dem Versicherungsschein — Ausnahme gilt NICHT bei Pflichtversicherung mit vorläufiger Deckung. (5) Arbeitsblatt herunterladen, im Advisory-Modul dokumentieren, abhaken.',
      adjudonSupport: 'Wir erzeugen den Textform-Beleg als Datei (Markdown und PDF), benennen den Erklärenden (§ 126b BGB Nr. 2), versiegeln ihn mit einem fälschungssicheren digitalen Siegel, bilden den § 62-Abs-2-Ausnahmeweg ab und archivieren 10 Jahre revisionssicher; den Übermittlungs-Akt führen Sie durch (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_suitability', framework: 'vvg-idd', article: 'IDD Art. 30(1) / § 7c VVG',
      title: 'Geeignetheit beurteilen (IBIP, bei Beratung)',
      whatYouMustDo: 'So gehen Sie vor (nur bei Versicherungsanlageprodukten MIT Beratung): (1) Holen Sie die Kundenangaben ein (§ 7c Abs. 1 / DelVO Art. 9): Kenntnisse + Erfahrung, finanzielle Verhältnisse INKL. Verlustfähigkeit, Anlageziele INKL. Risikotoleranz und die Nachhaltigkeitspräferenzen (seit 02.08.2022). (2) Beurteilen Sie die Geeignetheit; ohne ausreichende Angaben oder wenn kein Produkt geeignet ist, empfehlen Sie KEINES. (3) Erfassen + finalisieren Sie im Advisory-Record. (4) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern das Beratungs-Erfassungs-Modul, den Export auf dauerhaftem Datenträger, die 10-Jahres-Aufbewahrung mit fälschungssicherem Siegel und ein Geeignetheits-Arbeitsblatt; das Geeignetheits-URTEIL verantworten Sie (§ 63 VVG / § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_appropriateness', framework: 'vvg-idd', article: 'IDD Art. 30(2)-(3) / § 7c VVG',
      title: 'Angemessenheit prüfen + Warnhinweise + Execution-only (IBIP, ohne Beratung)',
      whatYouMustDo: 'So gehen Sie vor (IBIP-Vertrieb OHNE Beratung): (1) Erfragen Sie Kenntnisse + Erfahrung und beurteilen Sie die Angemessenheit (§ 7c Abs. 2 / DelVO Art. 15). (2) Geben Sie den Pflicht-Warnhinweis ab (unangemessen ODER unzureichende Angaben). (3) Execution-only nur bei nicht-komplexem Produkt (DelVO Art. 16), auf Kundenveranlassung, mit klarem Hinweis auf den fehlenden Schutz (§ 7c Abs. 3). (4) Dokumentieren + finalisieren. (5) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern das Advisory-Record-Modul, den Export + die Aufbewahrung und ein Angemessenheits-/Execution-only-Arbeitsblatt mit Warntexten + Art-16-Checkliste; die Beurteilung + die Warnhinweise verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_suitability_statement', framework: 'vvg-idd', article: 'IDD Art. 30(4)-(5) / § 7c VVG',
      title: 'Geeignetheitserklärung + regelmäßige Berichte erstellen (IBIP)',
      whatYouMustDo: 'So gehen Sie vor: (1) Stellen Sie bei Beratung VOR Vertragsabschluss auf dauerhaftem Datenträger eine Geeignetheitserklärung bereit, die darlegt, WIE die Empfehlung den Wünschen, Anlagezielen + Nachhaltigkeitspräferenzen entspricht (§ 7c Abs. 5 / DelVO Art. 14). (2) Bei zugesagter regelmäßiger Eignungsbeurteilung: mindestens jährlich neu beurteilen. (3) Vereinbarungsaufzeichnung führen (§ 7c Abs. 4). (4) Advisory-PDF-Export + Wiedervorlage nutzen. (5) Arbeitsblatt herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern den Geeignetheitserklärungs-/§62-Export auf dauerhaftem Datenträger (PDF mit Integritäts-Hash), die 10-J-Aufbewahrung, die Wiedervorlage (periodic-reviews) und ein Erklärungs-Arbeitsblatt; die Inhalte verantworten Sie. qSeal + § 371a-Beweistauglichkeit sind Founder-/extern (Hogan Lovells), nicht Adjudon-Backend (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_business_organisation', framework: 'vag-mago', article: '§ 23 VAG',
      title: 'Ordnungsgemäße Geschäftsorganisation gestalten + für Dritte nachvollziehbar dokumentieren',
      whatYouMustDo: 'So gehen Sie vor: (1) Klare Zuweisung + Trennung der Zuständigkeiten der KI-Entscheidungs-Schicht (wer definiert Policies, wer prüft, wer gibt frei) + wirksames Kommunikationssystem (§ 23(1)). (2) Regelmäßige interne Vorstands-Überprüfung (§ 23(2)). (3) Schriftliche Leitlinien (RM/IKS/Revision/Ausgliederung), vorab vom Vorstand genehmigt, jährlich geprüft (§ 23(3)). (4) Notfall-/Kontinuitätsvorkehrungen (§ 23(4)). (5) Aufbau-/Ablauforganisation + IKS für Dritte nachvollziehbar dokumentieren (§ 23(5)). (6) KI-gestützte Prozesse: Nachvollziehbarkeit + Steuerung (MaGo RS 09/2025 (VA)). Vorlage herunterladen, vom Vorstand freigeben, abhaken.',
      adjudonSupport: 'Wir liefern den manipulationssicheren Audit-Trail als „für Dritte nachvollziehbare Dokumentation" der KI-Schicht (§ 23(5)), die erzwungene 3-Rollen-Funktionstrennung (§ 23(1)), die unveränderliche Policy-Versionierung (§ 23(3)) + Fail-Open-Architektur (§ 23(4)) und eine Geschäftsorganisations-Vorlage; Organisation, Rollenbesetzung + Board-Freigabe verantworten Sie (§ 32 VAG).',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 28 * 86400000).toISOString(), attestedByEmail: 'vorstand@demo.com', note: 'GO-Doku der KI-Schicht v1 freigegeben; IKS-Mappe verlinkt.', evidenceUrl: '',
    },
    {
      key: 'vag_fit_proper', framework: 'vag-mago', article: '§ 24 VAG',
      title: 'Fit & Proper der Schlüsselpersonen sicherstellen + der BaFin anzeigen',
      whatYouMustDo: 'So gehen Sie vor: (1) Geschäftsleiter (§ 24(2)) + Inhaber der vier Schlüsselfunktionen + die KI-Aufsichts-Verantwortlichen identifizieren. (2) Fachliche Eignung (Versicherungskenntnis + Leitungserfahrung; für KI-Rollen KI-Kompetenz, EU AI Act Art. 4) + Zuverlässigkeit je Person beurteilen (§ 24(1)). (3) Mandatsgrenzen prüfen (max. 2 Geschäftsleiter-Mandate § 24(3); max. 5 Kontrollmandate § 24(4)). (4) Beabsichtigte Bestellung der BaFin vorab anzeigen (§ 47 Nr. 1 VAG, MVP-Portal). Register führen (Vorlage), freigeben, abhaken.',
      adjudonSupport: 'Adjudon beurteilt oder zertifiziert KEINE Person (§ 5 UWG). Wir liefern die Register-Struktur (Designation + Eignungs-/Zuverlässigkeits-/Mandats-Felder), den Querverweis auf den Art-14-Human-Oversight-Kompetenz-Record + eine Fit-&-Proper-Register-Vorlage; Beurteilung, Mandatsgrenzen + § 47-Anzeige verantworten ausschließlich Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_internal_control_system', framework: 'vag-mago', article: '§ 29 VAG',
      title: 'Internes Kontrollsystem + Compliance-Funktion einrichten + betreiben',
      whatYouMustDo: 'So gehen Sie vor: (1) Wirksames IKS für die KI-Entscheidungs-Schicht — Verwaltungs-/Rechnungslegungsverfahren, internes Kontrollrahmenwerk, Berichterstattung auf allen Ebenen, Compliance-Funktion (§ 29(1)). (2) Compliance-Funktion erfüllt § 29(2): Beratung des Vorstands, Beurteilung von Rechtsumfeld-Änderungen, Identifizierung + Beurteilung des Compliance-Risikos. (3) MaGo (RS 09/2025 (VA)): Unabhängigkeit (nicht in überwachte operative Prozesse eingebunden), direkte ad-hoc-Berichterstattung an das Leitungsorgan, systematisches Überwachungsprogramm. Vorlage herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die Policy-Engine als internes Kontrollrahmenwerk (§ 29(1)) mit 3-Rollen-Funktionstrennung, den Audit-Trail als Kontroll-Evidenz, Alerts/Webhooks/Incident-Hub als Meldewege, das Frist-Tracking (Rechtsumfeld-Änderungen, § 29(2)) + Anomalie-/Drift-Signale als Compliance-Risiko-Indikatoren und eine IKS-/Compliance-Funktions-Vorlage; Ausgestaltung + Betrieb der Compliance-Funktion verantworten Sie (§ 32 VAG).',
      templateAvailable: true, templateRef: '/dashboard/policies', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_risk_appetite', framework: 'vag-mago', article: '§ 26 VAG',
      title: 'Risikostrategie + Risikotoleranzschwellen definieren',
      whatYouMustDo: 'So gehen Sie vor: (1) Ordnen Sie die KI-/Automatisierungs-Schicht als operationelles Risiko (§ 26 Abs. 5 Nr. 5 VAG) in Ihre Risikostrategie ein (§ 26 Abs. 2; Geltungsbereich MaGo Abschnitt 9.5: Underwriting/Schaden/Bestand). (2) Setzen Sie je Risikosignal Ihre Risikotoleranzschwellen/Limits (Confidence-Untergrenze, z-Score-Anomalie, Block-/Override-Rate, Kalibrierungs-Drift, Review-SLA), abgestimmt mit Ihrer Risikotragfähigkeit. (3) Definieren Sie die Eskalation bis zur Agent-Aussetzung + die Anbindung an die Risikocontrollingfunktion (§ 26 Abs. 8). (4) Sichern Sie Steuerung/Überwachung/Dokumentation der automatisierten Abläufe (MaGo 9.5). (5) Laden Sie das Arbeitsblatt herunter, holen Sie die Freigabe und haken Sie das Item ab.',
      adjudonSupport: 'Wir messen + alarmieren gegen Ihre Schwellen (Anomalie-z-Score auf Confidence + Block-Rate, Kalibrierungs-Drift-Webhooks, 3-Säulen-Confidence-Triangulation) und liefern den manipulationssicheren Audit-Trail als Nachvollziehbarkeits-Beleg (MaGo 9.5) + ein ausfüllbares Risikostrategie-/Limit-Arbeitsblatt; die Schwellen, die Strategie + die Risikotragfähigkeit verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/anomalies', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_internal_audit', framework: 'vag-mago', article: '§ 30 VAG',
      title: 'Unabhängige interne Revision betreiben',
      whatYouMustDo: 'So gehen Sie vor: (1) Verankern Sie die KI-Entscheidungs-Schicht als Prüfungsobjekt in Ihrem risikoorientierten Prüfungsuniversum (§ 30(1)). (2) Sichern Sie Unabhängigkeit + Objektivität des Prüfteams (§ 30(2)). (3) Prüfen Sie die automatisierten Geschäftsabläufe (MaGo: Nachvollziehbarkeit, unabhängige Bewertung, Qualitätssicherung). (4) Prüfen Sie die Auslagerung an Adjudon (§ 32). (5) Berichten Sie Ergebnisse + Empfehlungen direkt an den Vorstand und verfolgen Sie die Maßnahmen. (6) Laden Sie das Prüfungsprogramm herunter, lassen Sie es freigeben und haken Sie das Item ab.',
      adjudonSupport: 'Wir liefern Audit-Trail + unabhängige Integritäts-Verifikation + Auditor-Evidence-Pack + forensischen Export + Portable-Evidence-Bundle und ein § 30/MaGo-verankertes Prüfungsprogramm; die unabhängige Revision, das Prüfungsurteil + die Vorstandsberichterstattung verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_outsourcing', framework: 'vag-mago', article: '§ 32 VAG',
      title: 'Auslagerungsregister führen + Auslagerung an Adjudon überwachen',
      whatYouMustDo: 'So gehen Sie vor: (1) Führen Sie ein zentrales Auslagerungsregister über ALLE Auslagerungen — die Adjudon-Vereinbarung ist eine Zeile darin. (2) Auslagerungsregister (MaGo) + DORA-Informationsregister koexistieren (BaFin 15.01.2025). (3) Benennen Sie bei Schlüsselfunktionen eine/n Auslagerungsbeauftragte/n (RS 09/2025 Rn. 266-273). (4) Richten Sie die laufende Überwachung ein. (5) Arbeiten Sie Risikoanalyse, Vertrag + BaFin-Anzeige ab. § 32 Abs. 1: die Verantwortung bleibt bei Ihnen.',
      adjudonSupport: 'Wir liefern die vorbefüllte Adjudon-Selbstauskunft (EU-Residenz Frankfurt, Sub-Prozessoren, Exit-Bundle, Fail-Open, Performance-Verträge) als Registerzeile + Überwachungs-Werkzeuge + ein Register-/Überwachungs-Arbeitsblatt; Register + Einstufung + Auslagerungsbeauftragte/r verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_outsourcing_risk_analysis', framework: 'vag-mago', article: '§ 32 Abs. 2/3 VAG',
      title: 'Risikoanalyse + Wesentlichkeit der Ausgliederung bestimmen',
      whatYouMustDo: 'Bestimmen Sie nach der funktionalen MaGo-Definition (RS 09/2025: „würde ansonsten intern erbracht"), ob eine Ausgliederung vorliegt, stufen Sie die Wichtigkeit ein (§ 32 Abs. 3) und führen Sie den Beeinträchtigungs-Selbstcheck (§ 32 Abs. 2) durch.',
      adjudonSupport: 'Wir liefern verifizierbare Architektur-Fakten als Eingangssignal (Fail-Open, Audit-/Zugangsrechte, EU-Residenz) + ein Risikoanalyse-/Wesentlichkeits-Arbeitsblatt; die Einstufung treffen Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_outsourcing_agreement', framework: 'vag-mago', article: '§ 32 Abs. 2/4 VAG',
      title: 'Auslagerungsvertrag mit Pflicht-Mindestinhalten schließen',
      whatYouMustDo: 'Sichern Sie die gesetzlichen Mindestinhalte des § 32 VAG (Datenzugriff, Aufsichts-Kooperation + Raumzutritt, Auskunfts-/Weisungsrechte, Risikomanagement-Einbezug, ggf. Zustellungsbevollmächtigte/r) + alle 12 Klauseln der DVO Art. 274(4)(a)-(l).',
      adjudonSupport: 'Wir liefern die § 32-Abs-2/4- + 274(4)(a)-(l)-Klausel-Checkliste mit Adjudon-Posture, Audit-/Zugangs-Mapping + Exit-Mechanismus; die rechtsverbindliche Endfassung verantworten Sie (Hogan Lovells, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_outsourcing_notification', framework: 'vag-mago', article: '§ 47 Nr. 8/9 VAG',
      title: 'BaFin-Anzeige der Ausgliederung wichtiger Funktionen einreichen',
      whatYouMustDo: 'Bei wichtiger Ausgliederung: zeigen Sie die Absicht vorab mit Vertragsentwurf an (§ 47 Nr. 8), befüllen Sie die § 2-VersAusgl-AnzV-Pflichtangaben, zeigen Sie nachträgliche wesentliche Umstände an (§ 47 Nr. 9 / § 3) und reichen Sie alles elektronisch über die BaFin-MVP ein.',
      adjudonSupport: 'Wir liefern die vorbefüllte Anbieter-Selbstauskunft (Datenort Frankfurt, Cloud-Dienst-/Bereitstellungsmodell, Sub-Prozessoren) als § 2-Baustein + Überwachungs-Werkzeuge; Einstufung + MVP-Übermittlung verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vag_orsa', framework: 'vag-mago', article: '§ 27 VAG',
      title: 'Unternehmenseigene Risiko-/Solvabilitätsbeurteilung durchführen',
      whatYouMustDo: 'So gehen Sie vor: Führen Sie die ORSA regelmäßig sowie bei wesentlichen Risikoprofil-Änderungen unverzüglich durch (§ 27 Abs. 1 VAG) und beurteilen Sie die drei Pflichtelemente nach § 27 Abs. 2 (Gesamtsolvabilitätsbedarf, jederzeitige Erfüllbarkeit/Risikotragfähigkeit, Abweichung von den SCR-Annahmen). Beziehen Sie für KI-gestützte Entscheidungen das KI-Betriebs- und Modellrisiko ein (EIOPA-BoS-25-360; MaGo RS 09/2025 VA), rechnen Sie Stresstests und übermitteln Sie den ORSA-Aufsichtsbericht innerhalb von zwei Wochen nach Abschluss an die BaFin (diese Frist steht nicht in § 27 VAG selbst, sondern in Art. 312 DelVO (EU) 2015/35).',
      adjudonSupport: 'Wir liefern das ORSA Input Pack (operative + Modell-Risiko-Signale Ihrer KI-Entscheidungen, gemappt auf die drei § 27-Abs-2-Elemente), das ORSA-KI-Risiko-Arbeitsblatt, den manipulationssicheren Audit-Trail und die Calibration-/Anomalie-/Incident-Dashboards. Die ORSA-Beurteilung, die Stresstests, das Kapitalurteil und die Zwei-Wochen-BaFin-Meldung (Art. 312 DelVO) verantworten Sie (nicht-delegierbar, § 27 Abs. 1 VAG).',
      templateAvailable: true, templateRef: '/dashboard/solvency-vag', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_monitoring', framework: 'eu-ai-act', article: 'Art. 26(5)',
      title: 'Betrieb überwachen + Risiken/Vorfälle melden',
      whatYouMustDo: 'Sie überwachen den Betrieb gemäß Gebrauchsanweisung und melden Risiken/schwerwiegende Vorfälle. Als Versicherer gilt die reine Überwachungspflicht (UAbs. 1) nach dem letzten Unterabsatz von Art. 26(5) durch Ihre interne Governance (Solvency II/VAG/DORA) als erfüllt — Risiko-Eskalation + Vorfallmeldung bleiben bestehen.',
      adjudonSupport: 'Wir liefern Anomalie-Erkennung + Incident-Hub als Evidenz; die Meldeentscheidung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/anomalies',
      attested: true, attestedAt: new Date(Date.now() - 14 * 86400000).toISOString(), attestedByEmail: 'compliance@demo.com', note: 'Interne Governance gemäß VAIT dokumentiert.', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_input_data', framework: 'eu-ai-act', article: 'Art. 26(4)',
      title: 'Eingabedaten-Relevanz sicherstellen',
      whatYouMustDo: 'Sie stellen sicher, dass die Eingabedaten für den Zweck relevant + hinreichend repräsentativ sind.',
      adjudonSupport: 'Wir liefern Data-Quality-/Bias-Stratifikations-Signale; die Datenverantwortung tragen Sie.',
      templateAvailable: false, templateRef: null,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_11_affected_persons', framework: 'eu-ai-act', article: 'Art. 26(11)',
      title: 'Betroffene natürliche Personen informieren',
      whatYouMustDo: 'Bei Anhang-III-Entscheidungen über natürliche Personen informieren Sie die betroffene Person, dass sie der Nutzung eines Hochrisiko-KI-Systems unterliegt (unbeschadet Art. 50).',
      adjudonSupport: 'Wir generieren eine anpassbare DE/EN-Hinweis-Vorlage (inkl. Art.-22(3)-DSGVO-Rechte), vorbefüllt mit Ihrem Namen + Systemkontext; Versand + Endprüfung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/deployer',
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_7_worker_info', framework: 'eu-ai-act', article: 'Art. 26(7)',
      title: 'Beschäftigte + Arbeitnehmervertretung informieren',
      whatYouMustDo: 'Falls Sie ein Hochrisiko-KI-System am Arbeitsplatz auf eigene Beschäftigte anwenden: vor Inbetriebnahme informieren Sie Beschäftigte + Betriebsrat (§§ 80/87/90 BetrVG).',
      adjudonSupport: 'Wir generieren eine anpassbare DE/EN-Informations-Vorlage (inkl. BetrVG-Verfahrensverweise); Abstimmung + Versand verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/deployer',
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_8_registration', framework: 'eu-ai-act', article: 'Art. 26(8)',
      title: 'EU-Datenbank-Registrierung (nur öffentliche Stellen)',
      whatYouMustDo: 'Nur falls Sie eine öffentliche Stelle sind (öffentlich-rechtlicher Versicherer als Behörde): Registrierung nach Art. 49/71. Privatrechtliche Versicherer: nicht anwendbar — attestieren Sie zur Bestätigung.',
      adjudonSupport: 'Wir führen das Registrierungs-Dossier (Art. 49/Annex VIII) als Input; Registrierung + Statusbestimmung treffen Sie.',
      templateAvailable: false, templateRef: null,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art26_cooperation', framework: 'eu-ai-act', article: 'Art. 26(12)',
      title: 'Mit der Aufsichts-/Marktüberwachungsbehörde kooperieren',
      whatYouMustDo: 'Bei jeder Maßnahme einer zuständigen Behörde (BaFin/Marktüberwachung) kooperieren Sie und stellen die angeforderten Nachweise + Protokolle fristgerecht bereit.',
      adjudonSupport: 'Wir liefern Audit-Trail + Portable-Evidence-Bundle + Auditor-Evidence-Pack als Kooperations-Nachweis; die Behörden-Kommunikation führen Sie.',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports',
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art14_human_oversight', framework: 'eu-ai-act', article: 'Art. 14',
      title: 'Wirksame menschliche Aufsicht sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) Benennen Sie je Hochrisiko-System mindestens eine geeignete Aufsichtsperson plus Vertretung. (2) Stellen Sie für jede Person sicher: versteht Fähigkeiten/Grenzen + erkennt Anomalien (14(4)(a)), ist für Automation-Bias geschult (14(4)(b)), interpretiert den Output korrekt (14(4)(c)), kann überstimmen/verwerfen (14(4)(d)) und das System stoppen (14(4)(e)). (3) Dokumentieren Sie Benennung, Kompetenz und Schulung im Nachweis-Arbeitsblatt (Vorlage zum Download) und lassen Sie es freigeben. (4) Haken Sie dieses Item anschließend ab.',
      adjudonSupport: 'Wir liefern die Review-Queue + Policy-Engine + Override-Audit + Confidence-/Kalibrierungs-Evidenz und eine ausfüllbare Designation-&-Competence-Vorlage; die Personen, ihre Kompetenz + Schulung stellen Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art14_sla_escalation', framework: 'eu-ai-act', article: 'Art. 14',
      title: 'Review-Fristen + Eskalationsrichtlinie konfigurieren',
      whatYouMustDo: 'So gehen Sie vor: (1) Legen Sie je Prioritätsband (Critical/High/Medium/Low) Ihre maximale Reaktionszeit fest. (2) Definieren Sie Ihr Abdeckungsfenster (Geschäftszeiten vs. 24/7) inkl. Vertretung. (3) Definieren Sie die Eskalationskette und die Reaktion bei review.sla_breached. (4) Halten Sie alles in der SLA-/Eskalations-Richtlinie fest (Vorlage zum Download) und haken Sie dieses Item ab.',
      adjudonSupport: 'Wir tracken die SLA je Item (5-Minuten-Breach-Sweep), dispatchen review.sla_breached und liefern eine ausfüllbare SLA-/Eskalations-Vorlage; die Fristen, Richtlinie + Reaktion definieren Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art27_fria_completion', framework: 'eu-ai-act', article: 'Art. 27',
      title: 'Grundrechte-Folgenabschätzung (FRIA) abschließen',
      whatYouMustDo: 'Vor Inbetriebnahme schließen Sie die FRIA ab + genehmigen sie.',
      adjudonSupport: 'Wir liefern den FRIA-Wizard + signierten Export + Coverage-Übersicht; die Bewertung + Genehmigung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/fria',
      attested: true, attestedAt: new Date(Date.now() - 40 * 86400000).toISOString(), attestedByEmail: 'compliance@demo.com', note: 'FRIA v2 genehmigt.', evidenceUrl: 'https://demo.adjudon.com/fria/v2.pdf',
    },
    {
      key: 'eu_ai_act_art15_accuracy_policy', framework: 'eu-ai-act', article: 'Art. 15',
      title: 'Genauigkeits-/Robustheits-Schwellen festlegen',
      whatYouMustDo: 'So gehen Sie vor: (1) Bestimmen Sie je KI-Funktion Ihr Genauigkeitsziel aus Schadenpotenzial + Grundrechtsrelevanz (Art. 15(1) angemessenes Niveau, risikobasiert nach EIOPA-BoS-25-360). (2) Setzen Sie je Signal Ihre eigene Schwelle (Confidence, ECE, Brier, KS-Drift, Konformal-Coverage) und begründen Sie die Angemessenheit. (3) Verankern Sie die Schwellen in den Risikomanagement-Leitlinien (VAG § 26 / MaGo). (4) Übernehmen Sie die Metriken in die Art-13-IFU (Art. 15(3)). (5) Vorlage ausfüllen, freigeben, abhaken.',
      adjudonSupport: 'Wir messen Genauigkeit + Robustheit laufend (3-Säulen-Confidence, Kalibrierung ECE/Brier, KS/ECE/Brier-Drift nächtlich + Auto-Refit, Vovk-Konformal-Coverage) und liefern eine ausfüllbare Schwellen-Policy-Vorlage; die Schwellen + ihre Angemessenheit bestimmen Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art15_bias_audits', framework: 'eu-ai-act', article: 'Art. 15',
      title: 'Periodische Bias-/Fairness-Audits planen + durchführen',
      whatYouMustDo: 'So gehen Sie vor: (1) Wählen Sie die geschützten Merkmale + prüfen Sie die Zulässigkeit (DSGVO Art. 9 / Art. 10(5) AI Act / AGG). (2) Legen Sie Kadenz, Mindest-Stichprobe + Toleranz für den maximalen Zwischen-Gruppen-ECE-Abstand fest. (3) Führen Sie die Audits durch + protokollieren Sie Coverage-Prozent + Befund. (4) Stellen Sie für weiterlernende Systeme sicher, dass verzerrte Outputs nicht über Feedback-Schleifen einfließen (Art. 15(4)). (5) Vorlage ausfüllen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die Bias-Stratifikation (ECE/Brier je geschützter Gruppe + maximaler Zwischen-Gruppen-ECE-Abstand) + Completeness-Tracking + eine ausfüllbare Audit-Plan-/Protokoll-Vorlage; Auswahl, Kadenz + Durchführung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art15_remediation', framework: 'eu-ai-act', article: 'Art. 15',
      title: 'Korrekturmaßnahmen bei Drift/Bias dokumentieren',
      whatYouMustDo: 'So gehen Sie vor: (1) Erfassen Sie den Drift-/Bias-Alert (KS/ECE/Brier/Bias-Abstand) + Adjudon-Alert-ID. (2) Analysieren Sie Ursache + Umfang (operationelles Risiko VAG § 26 Abs. 5 Nr. 5). (3) Entscheiden + begründen Sie die Maßnahme (Schwellen anpassen / Refit / neu trainieren / verstärkte Aufsicht / Agent aussetzen / mit Begründung akzeptieren). (4) Verantwortung + Frist + Eskalation; Serious-Incident-Check (Art. 73 / DORA Art. 19). (5) Wirksamkeit verifizieren. (6) Vorlage ausfüllen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern den Auslöser + die Beweisgrundlage (Drift-Webhook calibration.drift_detected + In-App-Benachrichtigung + immutabler CalibrationDriftAlert; ein erfolgreicher Refit setzt das Drift-Flag zurück) + eine ausfüllbare Korrekturmaßnahmen-Vorlage; die Maßnahmen-Entscheidung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art11_technical_documentation', framework: 'eu-ai-act', article: 'Art. 11 + Annex IV',
      title: 'Technische Dokumentation deklarieren + aktuell halten',
      whatYouMustDo: 'Deklarieren Sie je Hochrisiko-KI-System die Modell-Metadaten + Vor-Markt-Testberichte; Adjudon generiert das Annex-IV-Paket, Sie halten die Deklarationen aktuell (Art. 11(1)).',
      adjudonSupport: 'Wir generieren + pflegen das vollständige Annex-IV-Paket (Punkte 1–9) automatisch aus Ihren Metadaten + Laufzeit-Evidenz (Kalibrierung, Bias, PMM); die Deklarationen + Upstream-Testberichte liefern Sie.',
      templateAvailable: true, templateRef: '/dashboard/eu-ai-act?tab=tech-doc', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 26 * 86400000).toISOString(), attestedByEmail: 'provider@demo.com', note: 'Annex-IV-Paket für das Underwriting-Hochrisiko-System v3 deklariert + Upstream-Testberichte angehängt.', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art47_declaration', framework: 'eu-ai-act', article: 'Art. 47',
      title: 'EU-Konformitätserklärung unterzeichnen',
      whatYouMustDo: 'Unterzeichnen Sie als Anbieter eines Hochrisiko-Systems die EU-Konformitätserklärung (Anhang V) rechtsverbindlich und binden Sie ggf. eine Benannte Stelle ein (Art. 47).',
      adjudonSupport: 'Wir generieren das Annex-IV-Technik-Dokumentations-Paket als Grundlage; die Erklärung unterzeichnen Sie.',
      templateAvailable: true, templateRef: '/dashboard/eu-ai-act?tab=tech-doc', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art12_retention_decision', framework: 'eu-ai-act', article: 'Art. 12 / 26(6)',
      title: 'Aufbewahrungsfrist der Protokolle festlegen',
      whatYouMustDo: 'Legen Sie die Log-Aufbewahrungsdauer fest (mind. 6 Monate, Art. 26(6); länger nach Ihren Finanz-/Versicherungs-Vorgaben).',
      adjudonSupport: 'Wir setzen die gewählte Aufbewahrungsdauer automatisch durch und liefern den Art-12-Protokoll-Export; die Frist wählen + begründen Sie.',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art13_instructions_use', framework: 'eu-ai-act', article: 'Art. 13 / 26(1)',
      title: 'System nur gemäß Gebrauchsanweisung betreiben',
      whatYouMustDo: 'Betreiben Sie das KI-System gemäß der Anbieter-Gebrauchsanweisung (IFU) und stellen Sie die zweckkonforme Nutzung sicher (Art. 13 / 26(1)).',
      adjudonSupport: 'Wir generieren die Art-13-IFU; die Einhaltung im Betrieb verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/agents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art13_provider_ifu', framework: 'eu-ai-act', article: 'Art. 13(3)',
      title: 'Gebrauchsanweisung (IFU) vervollständigen + an Betreiber bereitstellen',
      whatYouMustDo: 'Vervollständigen Sie als Anbieter die nicht-ableitbaren IFU-Felder (Art. 13(3)) und stellen Sie die IFU den Betreibern bereit + halten sie aktuell.',
      adjudonSupport: 'Wir generieren die vollständige Art-13(3)(a)–(f)-IFU; Metrik-, Aufsichts- und Protokollierungs-Felder füllen wir automatisch, die ⚠️-markierten Felder zeigen exakt, was noch von Ihnen fehlt.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art50_watermark_embedding', framework: 'eu-ai-act', article: 'Art. 50(2)',
      title: 'KI-generierte Inhalte maschinenlesbar kennzeichnen (C2PA)',
      whatYouMustDo: 'Kennzeichnen Sie KI-generierte Ausgaben maschinenlesbar, wo Sie Provider nach Art. 50(2) sind, und betten Sie das Manifest SDK-seitig in die ausgelieferte Datei ein.',
      adjudonSupport: 'Wir liefern signierte C2PA-Content-Credential-Manifeste (ECDSA-P256/JWS) + W3C-PROV-O-Graphen + den öffentlichen Verify-Endpoint als Markierungs-/Nachweis-Schicht; die Rollen-Bestimmung + das Einbetten in die Datei liegen bei Ihnen.',
      templateAvailable: true, templateRef: '/dashboard/provenance', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art50_2_autowatermark_decision', framework: 'eu-ai-act', article: 'Art. 50(2)',
      title: 'Auto-Watermarking aktivieren (bis 02.12.2026)',
      whatYouMustDo: 'Aktivieren Sie vor dem 02.12.2026 das Auto-Watermarking (beide Org-Flags + Agent-outputType) für generative Ausgaben (Art. 50(2)).',
      adjudonSupport: 'Wir liefern die automatische Kennzeichnungs-Mechanik (blockiert Ihren Betrieb auch bei eigenem Ausfall nie) + die beiden Aktivierungs-Schalter auf Organisations-Ebene + die Kennzeichnung generativer KI-Systeme; die Opt-in-Aktivierung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/settings', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art50_1_4_disclosure', framework: 'eu-ai-act', article: 'Art. 50(1)/(4)',
      title: 'Nutzer-Offenlegung: KI-Interaktion + Deepfakes/öffentliche Texte',
      whatYouMustDo: 'Legen Sie die KI-Interaktion (Art. 50(1)) bzw. Deepfakes/öffentliche KI-Texte (Art. 50(4)) sichtbar auf Ihrem Kanal offen.',
      adjudonSupport: 'Wir liefern den Provenance-Nachweis (C2PA/PROV-O + Public-Verify) als Beleg, dass ein Inhalt KI-generiert ist, plus die abhakbare Attestierung; den sichtbaren Offenlegungs-Hinweis in Ihrem Kunden-Kanal setzen Sie selbst — das kann Adjudon nicht für Sie übernehmen.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eu_ai_act_art73_incident_reporting', framework: 'eu-ai-act', article: 'Art. 73',
      title: 'Schwerwiegende Vorfälle bewerten + an Behörde melden',
      whatYouMustDo: 'Bewerten Sie, ob ein Vorfall schwerwiegend ist, und melden Sie ihn fristgerecht an die Marktüberwachungsbehörde (Art. 73; 15/10/2-Tage-Fristen).',
      adjudonSupport: 'Wir liefern den Multi-Clock-Incident-Hub + vorausgefüllte Templates + Submission-Tracking; die Meldeentscheidung + Übermittlung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art13_14_privacy_notice', framework: 'gdpr', article: 'Art. 13/14',
      title: 'Datenschutzerklärung an Betroffene bereitstellen',
      whatYouMustDo: 'Informieren Sie Ihre Betroffenen (wer, Zwecke, Dauer, Rechte) — Art. 13 (Direkterhebung) / Art. 14 (Drittquellen).',
      adjudonSupport: 'Wir liefern Ihre ROPA + DPA-Vorlage als Input; die Erklärung formulieren Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 40 * 86400000).toISOString(), attestedByEmail: 'dpo@demo.com', note: 'Datenschutzerklärung v4 veröffentlicht; Art-13/14-Pflichtangaben aus ROPA übernommen.', evidenceUrl: '',
    },
    {
      key: 'gdpr_art6_legal_basis', framework: 'gdpr', article: 'Art. 6',
      title: 'Rechtsgrundlage je Verarbeitungszweck festlegen',
      whatYouMustDo: 'Legen Sie je Verarbeitungszweck genau eine Rechtsgrundlage nach Art. 6 Abs. 1 fest.',
      adjudonSupport: 'Wir erfassen + erzwingen die gewählte Basis (Consent-Gate, Widerruf-Reject) — die Wahl treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art28_2_subprocessor_approval', framework: 'gdpr', article: 'Art. 28(2)',
      title: 'Sub-Prozessoren genehmigen / widersprechen',
      whatYouMustDo: 'Genehmigen Sie neue Sub-Prozessoren oder widersprechen Sie (Art. 28(2)).',
      adjudonSupport: 'Wir listen alle Sub-Prozessoren transparent + benachrichtigen 30 Tage vorab bei Änderungen (Einspruchsfenster).',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art33_34_breach_decision', framework: 'gdpr', article: 'Art. 33/34',
      title: 'Breach-Bewertung + Meldeentscheidung',
      whatYouMustDo: 'Entscheiden Sie, ob ein Datenschutzvorfall meldepflichtig (72h, Art. 33) bzw. an Betroffene zu kommunizieren ist (Art. 34).',
      adjudonSupport: 'Wir liefern den Incident-Hub (Multi-Clock) + vorausgefüllte Art.-33/34-Vorlagen.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_bdsg26_employee_data', framework: 'gdpr', article: '§ 26 BDSG',
      title: 'Beschäftigtendaten gesondert behandeln',
      whatYouMustDo: 'Behandeln Sie Beschäftigtendaten gesondert (§ 26 BDSG; EuGH C-34/21 zur Teil-Unanwendbarkeit beachten).',
      adjudonSupport: 'Aktuell außerhalb des Adjudon-Scopes (B2B-Versicherung) — als Hinweis geführt.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art12_dsar_fulfillment', framework: 'gdpr', article: 'Art. 12(3)',
      title: 'Betroffenen-Anfragen fristgerecht beantworten + Erfüllung nachweisen',
      whatYouMustDo: 'Beantworten Sie jede Betroffenen-Anfrage (DSAR) binnen 1 Monat und weisen Sie die Erfüllung nach (Art. 12(3)).',
      adjudonSupport: 'Wir überwachen die Frist + senden Pre-Deadline- und Overdue-Alerts; Sie hinterlegen den Erfüllungsnachweis am DSAR.',
      templateAvailable: true, templateRef: '/dashboard/dsar', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art16_rectification_execution', framework: 'gdpr', article: 'Art. 16',
      title: 'Berichtigung unrichtiger Daten durchführen',
      whatYouMustDo: 'Führen Sie die Berichtigung unrichtiger Daten in Ihren Systemen durch (Art. 16).',
      adjudonSupport: 'Wir nehmen den Berichtigungs-Antrag auf, tracken Frist + Audit; die Korrektur führen Sie durch und bestätigen sie mit Nachweis.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art17_erasure_decision', framework: 'gdpr', article: 'Art. 17 / Art. 17(3)',
      title: 'Über Löschverlangen entscheiden + Aufbewahrungs-Holds abwägen',
      whatYouMustDo: 'Entscheiden Sie über Löschverlangen und wägen Sie gesetzliche Aufbewahrungs-Holds ab (Art. 17(1)/(3)).',
      adjudonSupport: 'Wir liefern die Zwei-Schritt-Payload-Nullifikation + den immutablen SHA-256-Erasure-Ledger + die 1-Monats-Fristüberwachung — bei erhaltener Hash-Chain-Shell. Die Identitäts- und Aufbewahrungs-Hold-Abwägung treffen Sie; wir löschen nur auf Ihre Weisung.',
      templateAvailable: true, templateRef: '/dashboard/erasure', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art18_restriction_execution', framework: 'gdpr', article: 'Art. 18',
      title: 'Einschränkung der Verarbeitung umsetzen',
      whatYouMustDo: 'Setzen Sie die Einschränkung der Verarbeitung technisch um (Art. 18).',
      adjudonSupport: 'Wir nehmen den Antrag auf + tracken Frist/Audit; die technische Einschränkung setzen Sie um und bestätigen sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art21_objection_disposition', framework: 'gdpr', article: 'Art. 21',
      title: 'Über Widerspruch entscheiden',
      whatYouMustDo: 'Entscheiden Sie über den Widerspruch (zwingende schutzwürdige Gründe abwägen, Art. 21).',
      adjudonSupport: 'Wir nehmen den Widerspruch auf + tracken Frist/Audit; die Abwägungs-Entscheidung treffen + begründen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art22_human_review', framework: 'gdpr', article: 'Art. 22(3) / § 37 BDSG',
      title: 'Schutzmaßnahmen bei automatisierten Entscheidungen sicherstellen (Eingreifen · Standpunkt · Anfechtung)',
      whatYouMustDo: 'Stellen Sie die drei Schutzmaßnahmen bei automatisierten Einzelentscheidungen sicher (Art. 22(3) / § 37 BDSG).',
      adjudonSupport: 'Wir liefern die faktorbasierte Erklärung, den Appeal-Workflow mit Standpunkt-Feld, das Automated-Decision-Register (Rechtsgrundlage + Gesundheitsdaten-Flag) und das beweis-sichere Audit-Log; Rechtsgrundlagen-Wahl, Prüfinstanz und Betroffenen-Kanäle stellen Sie.',
      templateAvailable: true, templateRef: '/dashboard/explanations', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art29_dpa_verification', framework: 'gdpr', article: 'Art. 28(2)/29',
      title: 'Sub-Processor-DPA inhaltlich prüfen',
      whatYouMustDo: 'Prüfen Sie jede Sub-Processor-DPA inhaltlich auf eine unterzeichnete Vereinbarung mit Weisungsbindungs-Klauseln (Art. 28(3)/29).',
      adjudonSupport: 'Wir validieren die DPA-URL (HTTPS) + zeigen sie in Ihrer ROPA; Inhalt + Signatur prüfen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art44_49_transfer_assessment', framework: 'gdpr', article: 'Art. 44–49',
      title: 'Eigene Drittlandsübermittlungen bewerten (TIA/SCC)',
      whatYouMustDo: 'Bewerten Sie Ihre eigenen Drittlandsübermittlungen (TIA/SCC, Art. 44–49).',
      adjudonSupport: 'Adjudon überträgt als Auftragsverarbeiter NICHTS außerhalb der EU (Frankfurt; Non-EU-Sub-Prozessoren hart geblockt) und liefert die Datenresidenz-Attestierung als ROPA-Baustein + eine 6-Schritt-TIA-Vorlage; Ihre eigenen Transfers bewerten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art30_ropa_maintenance', framework: 'gdpr', article: 'Art. 30',
      title: 'Vollständiges Verzeichnis von Verarbeitungstätigkeiten führen',
      whatYouMustDo: 'Führen Sie Ihr Gesamt-Verzeichnis von Verarbeitungstätigkeiten über ALLE Verarbeitungen (Art. 30).',
      adjudonSupport: 'Wir liefern den fertigen Adjudon-als-Auftragsverarbeiter-Eintrag (inkl. Versions-Ledger) als Baustein für Ihr Gesamtverzeichnis.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 12 * 86400000).toISOString(), attestedByEmail: 'dpo@demo.com', note: 'Gesamt-ROPA gepflegt; Adjudon-Baustein als Eintrag übernommen.', evidenceUrl: '',
    },
    {
      key: 'gdpr_tdddg25_cookie_consent', framework: 'gdpr', article: '§ 25 TDDDG',
      title: 'Cookie-/Tracking-Einwilligung einholen + nachweisen',
      whatYouMustDo: 'Holen Sie die Cookie-/Tracking-Einwilligung ein und weisen Sie sie nach (§ 25 TDDDG).',
      adjudonSupport: 'Wir liefern die beweis-sichere Einwilligungs-Aufzeichnung (Record/Check/Widerruf + 12-Monats-Ablauf, org-isoliert) → erfüllt die Nachweispflicht Art. 7 Abs. 1 DSGVO. Plus eine ausfüllbare Umsetzungs-/Inventar-Vorlage.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art32_security_risk_assessment', framework: 'gdpr', article: 'Art. 32(1)/(2)',
      title: 'Angemessenes Schutzniveau bestimmen + Abwägung dokumentieren',
      whatYouMustDo: 'Bestimmen Sie das für Ihr Risiko angemessene Schutzniveau und dokumentieren Sie die Abwägung (Art. 32(1)/(2)).',
      adjudonSupport: 'Wir liefern das laufzeit-verifizierte TOM-Register + ein ausfüllbares Abwägungs-Arbeitsblatt mit BSI-Grundschutz-/ISO-27001-/SDM-Ankern; die Abwägung + Ihre Maßnahmen dokumentieren Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art32_effectiveness_testing', framework: 'gdpr', article: 'Art. 32(1)(d)',
      title: 'Eigene regelmäßige Wirksamkeitsprüfung der TOMs durchführen',
      whatYouMustDo: 'Führen Sie die regelmäßige Wirksamkeitsprüfung Ihrer TOMs durch (Art. 32(1)(d)).',
      adjudonSupport: 'Wir liefern den automatisierten TOM-Wirksamkeits-Self-Test (täglich) + die kontinuierliche Hash-Chain-Verifikation + ein append-only Register für Ihre eigenen Test-Nachweise; Plan + Durchführung verantworten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art32_tom_acceptance', framework: 'gdpr', article: 'Art. 32(1)',
      title: 'Adjudon-TOMs als für Ihr Risiko angemessen prüfen + akzeptieren',
      whatYouMustDo: 'Prüfen + akzeptieren Sie die Adjudon-TOMs als für Ihr Risiko angemessen (Art. 32(1)).',
      adjudonSupport: 'Wir liefern das vollständige TOM-Register (JSON + Markdown, laufzeit-verifiziert) + ein Akzeptanz-Protokoll-Template; die Prüfung + Akzeptanz treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art28_3_dpa_conclusion', framework: 'gdpr', article: 'Art. 28(3)/(9)',
      title: 'Auftragsverarbeitungsvertrag (DPA) mit Adjudon abschließen',
      whatYouMustDo: 'Schließen Sie den Auftragsverarbeitungsvertrag (DPA) mit Adjudon ab (Art. 28(3)/(9)).',
      adjudonSupport: 'Wir liefern den kanonischen Vertragstext (DE/EN), die server-autoritative In-App-Annahme mit SHA-256-Textbindung, das Re-Acceptance-Gate (Version + Hash) und den unveränderlichen Signatur-Datensatz; den Abschluss + die Klausel-Prüfung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/legal/dpa', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 51 * 86400000).toISOString(), attestedByEmail: 'legal@demo.com', note: 'AV-Vertrag v3 angenommen (elektronische Form, SHA-256-textgebunden).', evidenceUrl: '',
    },
    {
      key: 'gdpr_art28_3a_documented_instructions', framework: 'gdpr', article: 'Art. 28(3)(a)',
      title: 'Dokumentierte Verarbeitungsweisungen erteilen',
      whatYouMustDo: 'Erteilen Sie dokumentierte Verarbeitungsweisungen (zulässige Zwecke, Datenkategorien, Aufbewahrung) — Art. 28(3)(a).',
      adjudonSupport: 'Wir setzen Ihre Weisungen technisch durch (Policies, erzwungene Aufbewahrung, nicht-deaktivierbares PII-Scrubbing, Zweckbindung) + auditieren jede Änderung; den Inhalt der Weisungen bestimmen Sie.',
      templateAvailable: true, templateRef: '/dashboard/policies', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 51 * 86400000).toISOString(), attestedByEmail: 'dpo@demo.com', note: 'Verarbeitungsweisungen dokumentiert; Retention + Zweckbindung als Weisung gesetzt.', evidenceUrl: '',
    },
    {
      key: 'gwg_risk_analysis', framework: 'gwg', article: '§ 5 GwG',
      title: 'Schriftliche Risiko-Analyse erstellen + aktuell halten',
      whatYouMustDo: 'Erstellen Sie Ihre schriftliche Risiko-Analyse (§ 5 GwG) und halten Sie sie mindestens jährlich aktuell.',
      adjudonSupport: 'Wir liefern das versionierte, manipulationssichere Risiko-Analyse-Modul (immutable Versionen, contentHash, Reassessment-Frist-Tracking, BaFin-Export) + ein ausfüllbares § 5-Arbeitsblatt; Inhalt, Einstufung + Leitungs-Zustimmung (§ 5 Abs. 3) verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/obligations', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 22 * 86400000).toISOString(), attestedByEmail: 'gwb@demo.com', note: 'Risiko-Analyse v2 erstellt; Leitungsebene-Zustimmung (§ 5 Abs. 3) eingeholt.', evidenceUrl: '',
    },
    {
      key: 'gwg_sar_decision', framework: 'gwg', article: '§ 43 GwG',
      title: 'Verdachtsmeldung entscheiden + an FIU übermitteln (SAR-Trail)',
      whatYouMustDo: 'Entscheiden Sie unverzüglich über die Verdachtsmeldung und übermitteln Sie sie ggf. an die FIU (§ 43 GwG; keine 72h-Frist).',
      adjudonSupport: 'Wir liefern den manipulationssicheren SAR-Trail (append-only Hash-Kette über Anlass → Status → Meldeentscheidung), das per-Fall integrityHash-Siegel, die PII-gescrubbte (§ 47-feste) Audit-Zeile + ein Verdachtsmeldungs-Arbeitsblatt; Erkennung, Beurteilung, Meldeentscheidung + FIU-Übermittlung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_goaml_export', framework: 'gwg', article: 'GwGMeldV § 2/§ 3 (i.V.m. § 45 GwG)',
      title: 'goAML-XML-Verdachtsmeldung erzeugen, validieren + an FIU übermitteln',
      whatYouMustDo: 'Erzeugen, validieren + übermitteln Sie die goAML-XML-Verdachtsmeldung (GwGMeldV § 2/§ 3 i.V.m. § 45 GwG).',
      adjudonSupport: 'Wir liefern die goAML-XML-Entwurfs-Erzeugung (vorausgefüllt aus dem SAR-Trail), die GwGMeldV-§-3-Vollständigkeitsprüfung + ein Übermittlungs-Arbeitsblatt. Adjudon validiert NICHT gegen das produktive FIU-XSD, übermittelt NICHTS und zertifiziert KEINE FIU-Annahme; Validierung + Abgabe verantworten Sie (§ 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/aml-gwg', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_mlro_role', framework: 'gwg', article: '§ 7 GwG',
      title: 'Geldwäschebeauftragten benennen',
      whatYouMustDo: 'Benennen Sie einen Geldwäschebeauftragten + Stellvertreter und melden ihn der Aufsicht (§ 7 GwG).',
      adjudonSupport: 'Außerhalb des Adjudon-Scopes (Funktions-Benennung) — als Hinweis geführt.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_customer_acceptance', framework: 'gwg', article: '§ 10 GwG',
      title: 'Allgemeine Sorgfaltspflichten erfüllen + Kundenannahme entscheiden',
      whatYouMustDo: 'Erfüllen Sie die fünf allgemeinen Sorgfaltspflichten und entscheiden Sie über die Kundenannahme (§ 10 GwG).',
      adjudonSupport: 'Wir liefern die KYC-Entscheidungs-Evidenz (KYCDecision: Sorgfaltsstufe, Verifikationsmethode, PEP-/wB-Flags, Risiko-Rating + automatische Review-Fälligkeit), den immutablen Lifecycle + Audit-Trail und ein Kundenannahme-Arbeitsblatt; Datenerhebung, Risiko-Einstufung + Annahme-Entscheidung (§ 10 Abs. 9) verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/obligations', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 16 * 86400000).toISOString(), attestedByEmail: 'gwb@demo.com', note: 'Sorgfaltspflichten-Prozess + KYCDecision-Modul aktiv; Kundenannahme-Workflow freigegeben.', evidenceUrl: '',
    },
    {
      key: 'gwg_beneficial_owner', framework: 'gwg', article: '§ 10 Abs. 1 Nr. 2 / § 11 Abs. 5 GwG',
      title: 'Wirtschaftlich Berechtigte (UBO) bestimmen + verifizieren',
      whatYouMustDo: 'Bestimmen + verifizieren Sie die wirtschaftlich Berechtigten (§ 10 Abs. 1 Nr. 2 / § 11 Abs. 5 GwG).',
      adjudonSupport: 'Wir liefern die wB-Record-Schicht (BeneficiaryRecord: ownershipPercent, controlType, isPep, Verifikation) mit über-25-Prozent-Markierung, den Audit-Trail (nur uboRef-Pseudonym, nie der Klarname) + ein wB-Bestimmungs-Arbeitsblatt; Ermittlung, Datenerhebung + eigenständige Überprüfung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/obligations', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_screening_evaluation', framework: 'gwg', article: '§ 10 Abs. 1 Nr. 4 / § 15 Abs. 4 GwG',
      title: 'Sanktions-/PEP-/Adverse-Media-Treffer auswerten',
      whatYouMustDo: 'Werten Sie Sanktions-/PEP-/Adverse-Media-Treffer aus (§ 10 Abs. 1 Nr. 4 / § 15 Abs. 4 GwG).',
      adjudonSupport: 'Wir liefern die Screening-Ergebnis-Evidenz (ScreeningResult: Anbieter, Verdikt, Trefferzahl, Disposition), die automatische rescreenDue-Kadenz + ein Treffer-Auswertungs-Arbeitsblatt; Beauftragung, Treffer-Bewertung + PEP-/Sanktions-Einordnung verantworten Sie (Adjudon ist keine Screening-Engine).',
      templateAvailable: true, templateRef: '/dashboard/obligations', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_edd_judgment', framework: 'gwg', article: '§ 15 GwG',
      title: 'Angemessenheit der verstärkten Sorgfaltspflichten (EDD) beurteilen',
      whatYouMustDo: 'Beurteilen Sie die Angemessenheit der verstärkten Sorgfaltspflichten (§ 15 GwG).',
      adjudonSupport: 'Wir liefern den EDD-Workflow (EDDAssessment: Auslöser, Maßnahmen + Status, Completeness), die erzwungene Freigabe-Vorbedingung, den recheckDate-Scheduler + ein EDD-Arbeitsblatt; Auslöser-Feststellung, Maßnahmen + Angemessenheits-Urteil verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/obligations', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gwg_control_plan', framework: 'gwg', article: '§ 6 GwG',
      title: 'Interne Sicherungsmaßnahmen + Monitoring-Verfahren definieren',
      whatYouMustDo: 'Definieren Sie Ihre internen Sicherungsmaßnahmen + das Monitoring-Verfahren (§ 6 GwG).',
      adjudonSupport: 'Wir führen Ihre konfigurierten Policies aus, erfassen jeden Alert als Record, tracken den Lebenszyklus + melden jede SLA-Überschreitung an den Geldwäschebeauftragten, und liefern den immutablen Dispositions-Audit + ein Sicherungsmaßnahmen-Arbeitsblatt; Regelwerk, Schwellen + Verdachts-Entscheidung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 8 * 86400000).toISOString(), attestedByEmail: 'gwb@demo.com', note: 'Sicherungsmaßnahmen-Konzept + Monitoring-Regelwerk schriftlich; KI-Governance (§ 6 Abs. 2 Nr. 4) dokumentiert.', evidenceUrl: '',
    },
    {
      key: 'gwg_alert_sla_policy', framework: 'gwg', article: '§ 6 GwG',
      title: 'Alert-Bearbeitungs-SLA + Eskalation festlegen',
      whatYouMustDo: 'Legen Sie die Alert-Bearbeitungs-SLA je Severity + die Eskalationskette fest (§ 6 GwG).',
      adjudonSupport: 'Wir tracken die SLA je Alert (slaHours + On-Read-Breach), melden jede Fristüberschreitung an den Geldwäschebeauftragten (5-Minuten-Sweep) und belegen jede Disposition immutabel; SLA-Werte, Eskalation + Melde-Entscheidung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_robustness', framework: 'eiopa', article: 'Genauigkeit, Robustheit & Cybersicherheit',
      title: 'Robustheit + kontinuierliche Genauigkeits-/Drift-Überwachung sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) Ordnen Sie die Verantwortung für die KI-Robustheits-Überwachung einer Funktion zu (Risikocontrolling / Aktuar); die Letztverantwortung bleibt beim Leitungsorgan (EIOPA-BoS-25-360). (2) Etablieren Sie kontinuierliche Überwachung über den Lebenszyklus (Baseline, laufende Drift-/Bias-Überwachung, unabhängige Bewertung — MaGo 9.5). (3) Setzen Sie Schwellen-Policy + Bias-Audit-Plan + Korrekturmaßnahmen-Prozess (Items eu_ai_act_art15_accuracy_policy / eu_ai_act_art15_bias_audits / eu_ai_act_art15_remediation). (4) Integrieren Sie Robustheits-Risiken in Risikomanagement + ORSA. (5) Vendor-Abhängigkeit + Ausfall-Posture regeln. (6) Vorlage ausfüllen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die gesamte Mess-/Alarm-Schicht (Confidence, Kalibrierung, nächtliche Drift-Erkennung + Auto-Refit, Bias-Stratifikation, Konformal-Coverage, Drift-Webhook + Benachrichtigung, immutabler Audit-Trail) + eine ausfüllbare Robustheits-Governance-Vorlage; Governance, Schwellen + ORSA-Integration verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'gdpr_art35_dpia', framework: 'gdpr', article: 'Art. 35',
      title: 'Datenschutz-Folgenabschätzung (DPIA) durchführen',
      whatYouMustDo: 'Bei voraussichtlich hohem Risiko führen Sie eine DPIA durch.',
      adjudonSupport: 'Wir liefern den FRIA-Wizard + Risiko-Inputs als Grundlage; die DPIA-Entscheidung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/fria',
      attested: true, attestedAt: new Date(Date.now() - 90 * 86400000).toISOString(), attestedByEmail: 'dpo@demo.com', note: '', evidenceUrl: '',
    },
    {
      key: 'dora_ict_risk_framework', framework: 'dora', article: 'Art. 5/6',
      title: 'ICT-Risikomanagement-Rahmenwerk + Leitungsorgan-Governance verantworten + freigeben',
      whatYouMustDo: 'Ihr Leitungsorgan definiert, genehmigt + überwacht das Rahmenwerk (Art. 5(2)(a)-(i)), hält sein ICT-Wissen aktuell (Art. 5(4)) und überprüft das dokumentierte Rahmenwerk mind. jährlich + per Internal Audit (Art. 6). Vorlage herunterladen, Board-Freigabe, abhaken.',
      adjudonSupport: 'Wir liefern den manipulationssicheren Audit-Trail, laufzeit-verifizierte Evidenz, das auto-generierte Art-8-ICT-Asset-Register und eine Governance-/Rahmenwerk-Vorlage; das Rahmenwerk + Board-Freigabe verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art7_systems', framework: 'dora', article: 'Art. 7',
      title: 'ICT-Systeme/-Tools auf Eignung, Zuverlässigkeit, Kapazität + Resilienz prüfen + akzeptieren',
      whatYouMustDo: 'Sie stellen sicher, dass Ihre ICT-Systeme/-Tools (inkl. der KI-Tooling-Schicht) dem Umfang Ihrer Tätigkeit angemessen (7(a)), zuverlässig (7(b)), hinreichend dimensioniert (7(c)) und technologisch resilient (7(d)) sind — und akzeptieren die Anbieter-Posture. Unterschied zu Art. 9: Eignung/Kapazität/Resilienz der Systeme selbst, nicht Sicherheitskontrollen.',
      adjudonSupport: 'Wir liefern die Anbieter-Resilienz-/Kapazitäts-Posture (Fail-Open, durable Retry-Queues, EU-Region, publizierte Performance-Verträge p50/p95/p99, konfigurierbare Rate-Limits), das TOM-Register + die SOC2/ISO/C5-Cert-Ziele und ein Eignungs-/Zuverlässigkeits-Arbeitsblatt; die Bewertung Ihrer ICT-Umgebung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/systems', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art8_asset_identification', framework: 'dora', article: 'Art. 8',
      title: 'ICT-Assets + Geschäftsfunktionen identifizieren + klassifizieren',
      whatYouMustDo: 'Sie identifizieren + dokumentieren alle ICT-gestützten Geschäftsfunktionen + Assets (Art. 8(1)), treffen die verbindliche kritisch/wichtig-Klassifizierung (Art. 8(4)), erfassen die Drittparteien-Abhängigkeiten (Art. 8(5)) und pflegen das Inventar jährlich (Art. 8(6)).',
      adjudonSupport: 'Wir generieren das Art-8-IT-Bestandsverzeichnis der KI-Schicht mit einem nicht-bindenden Kritikalitäts-Hinweis + ein Klassifizierungs-Arbeitsblatt; die verbindliche Klassifizierung + Ihr Gesamt-Inventar verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/systems', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art9_10_protection_detection', framework: 'dora', article: 'Art. 9/10',
      title: 'Schutz-/Präventions- + Erkennungsmaßnahmen + Alarmschwellen festlegen',
      whatYouMustDo: 'Sie sichern Verfügbarkeit/Integrität/Vertraulichkeit (Art. 9), erkennen anomale Aktivitäten zeitnah (Art. 10(1)) und definieren die Alarmschwellen + Kriterien, die den Vorfall-Reaktionsprozess auslösen (Art. 10(2)).',
      adjudonSupport: 'Wir liefern Anomalie-Erkennung (z-Score), Kalibrierungs-Drift + Alerts, das TOM-Register als Schutz-Nachweis und ein Schutz-/Erkennungs-Arbeitsblatt; die Alarmschwellen + Einbettung in Ihr SOC verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/anomalies', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art11_continuity_policy', framework: 'dora', article: 'Art. 11 / RTS 24+26',
      title: 'IKT-Geschaeftsfortfuehrungs-Leitlinie + RTO/RPO-Ziele + Reaktions-/Wiederherstellungsplaene erstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) IKT-Geschaeftsfortfuehrungs-Leitlinie (RTS Art. 24). (2) Business-Impact-Analyse (Art. 11(5)). (3) RTO/RPO je kritischer/wichtiger Funktion (Art. 11(2), Art. 12(6)). (4) Reaktions-/Wiederherstellungsplaene mit den RTS-Art-26(3)-Szenarien. (5) Drittparteien-Abhaengigkeit (u.a. Adjudon) aufnehmen (Art. 11(4)). (6) Krisenmanagement-Funktion (Art. 11(7)). (7) Aufzeichnungen zugaenglich halten (Art. 11(8)). Vorlage herunterladen, vom Leitungsorgan freigeben, abhaken.',
      adjudonSupport: 'Wir liefern den manipulationssicheren Audit-Trail (Art. 11(8)), Verfuegbarkeits-Telemetrie, den Incident-Hub und die offengelegte Adjudon-Provider-Resilienz (Frankfurt, Fail-open) als BCP-Baustein; Leitlinie + Ziele + Plaene erstellen Sie.',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art11_bcm_testing', framework: 'dora', article: 'Art. 11(6) / RTS 25',
      title: 'BCM-/Wiederherstellungs-Tests durchfuehren + Maengel an das Leitungsorgan berichten',
      whatYouMustDo: 'So gehen Sie vor: (1) Plaene mindestens jaehrlich + bei wesentlichen Aenderungen testen (Art. 11(6)). (2) Cyber-/Switchover-Szenarien einbeziehen. (3) BIA + IKT-Risikobewertung als Testgrundlage; immer die zur Planentwicklung genutzten Szenarien (RTS Art. 25). (4) Ergebnisse dokumentieren, Maengel beheben + dem Leitungsorgan berichten. (5) Leitlinie/Plaene aktualisieren. Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Wir liefern Trace-/Audit-Evidenz der Verfuegbarkeit, den Incident-Hub fuer Uebungsvorfaelle und eine Testbericht-Vorlage; Plan + Durchfuehrung + Maengel-Bewertung verantworten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art12_backup_recovery', framework: 'dora', article: 'Art. 12',
      title: 'Backup-, Wiederherstellungs- und Recovery-Policy festlegen',
      whatYouMustDo: 'So gehen Sie vor: (1) Backup-Leitlinie: Umfang + Mindest-Frequenz nach Kritikalitaet (Art. 12(1)(a)). (2) Restaurierungs-/Wiederherstellungsverfahren (Art. 12(1)(b)). (3) Backup-Systeme testen (Art. 12(2)). (4) Physisch + logisch getrennte Systeme (Art. 12(3)). (5) Redundante Kapazitaet (Art. 12(4)). (6) RTO/RPO je Funktion; Backup-Frequenz haelt den RPO ein (Art. 12(6)). (7) Integritaetspruefungen bei Wiederherstellung (Art. 12(7)). Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Wir liefern manipulationssichere Speicherung + On-Demand-Integritaetspruefung (Hash-Chain), kundenkonfigurierbare Aufbewahrung (7-3650 Tage) und EU-Datenresidenz; Backup-Umfang/-Frequenz, Segregation + Redundanz Ihrer eigenen Systeme verantworten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art13_post_incident_review', framework: 'dora', article: 'Art. 13',
      title: 'Post-Incident-Review nach schwerwiegenden Vorfaellen durchfuehren (Lessons Learned)',
      whatYouMustDo: 'So gehen Sie vor: (1) Nach jedem schwerwiegenden IKT-Vorfall ein Review durchfuehren (Art. 13(2)). (2) Ursachen analysieren. (3) Verbesserungen am IKT-Betrieb/an der BC-Leitlinie identifizieren (mit Verantwortlichen + Fristen). (4) Der Aufsicht auf Anfrage die umgesetzten Aenderungen mitteilen. (5) Review freigeben. Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Wir liefern den Multi-Clock-Incident-Hub als Vorfall-Akte, den Audit-Trail als Beweisgrundlage + Anomalie-/Drift-Signale und eine Review-Vorlage; die Ursachen-Bewertung + Massnahmen verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 400 * 86400000).toISOString(), attestedByEmail: 'ict-risk@demo.com', note: 'Review zu INC-2026-014 abgeschlossen.', evidenceUrl: '',
    },
    {
      key: 'dora_art14_crisis_communication', framework: 'dora', article: 'Art. 14',
      title: 'Krisenkommunikationsplan + Kommunikationsrichtlinien festlegen',
      whatYouMustDo: 'So gehen Sie vor: (1) Krisenkommunikationsplan fuer die Offenlegung schwerwiegender Vorfaelle an Kunden, Partner + ggf. Oeffentlichkeit (Art. 14(1)). (2) Interne + externe Kommunikationsrichtlinien; intern zwischen IKT-Risiko-Personal + zu informierendem Personal unterscheiden (Art. 14(2)). (3) Mindestens eine Person fuer die Kommunikationsstrategie benennen (Art. 14(3)). (4) Bausteine/Kanaele/Fristen vorbereiten. Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Wir liefern den Incident-Hub + Webhook-Events (incident.deadline_approaching/_breached) als Ausloeser-Signal und den Audit-Trail als Beleg; Inhalt, Adressaten + Zeitpunkt der Krisenkommunikation verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_tlpt', framework: 'dora', article: 'Art. 26/27',
      title: 'Threat-Led-Penetration-Testing (TLPT) durchführen + Drittparteien-Teilnahme sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) Prüfen, ob Sie nach Art. 26(8) von der Behörde als TLPT-pflichtig identifiziert wurden. (2) Phase 1 Scoping: kritische/wichtige Funktionen aus Ihrem Art-6-Rahmen ableiten, ALLE stützenden IKT-Systeme inkl. Drittdienstleister (auch Adjudon) mappen (Art. 26(3)), Scope der Behörde vorlegen (Art. 26(4)), White Team benennen. (3) Drittparteien-Teilnahme sichern (Art. 26(3)) bzw. Pooled TLPT (Art. 26(4)) koordinieren. (4) Qualifizierte Tester (Art. 27) + stets externen Threat-Intelligence-Provider beauftragen. (5) Phasen Threat Intelligence → Red-Teaming (Live-Produktion) → Purple Teaming durchführen (mind. alle 3 Jahre). (6) Closure-Report + Remediation einreichen → Attestierung (Art. 26(7)). Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Adjudon führt Ihr TLPT NICHT durch. Als in-Scope-IKT-Drittdienstleister (Art. 26(3)) liefern wir das TLPT-Readiness-/Scoping-Arbeitsblatt (TIBER-EU-5-Phasen + Tester-Checkliste Art. 27 + Pooled-Entscheidung), die vorausgefüllte Adjudon-Teilnahme-Posture (bevorzugt Pooled TLPT, Art. 26(4)) + eigene Sicherheits-Test-Evidenz; Scope, Tester + Einreichung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art17_incident_management_process', framework: 'dora', article: 'Art. 17',
      title: 'IKT-Vorfall-Management-Prozess definieren + betreiben',
      whatYouMustDo: 'Definieren + betreiben Sie einen IKT-Vorfall-Management-Prozess mit den sechs Pflicht-Komponenten nach Art. 17(1)-(3).',
      adjudonSupport: 'Wir liefern den Multi-Clock-Incident-Hub, Anomalie-/Drift-Alerts als Frühwarn-Eingang, die automatische Art-19-Fristüberwachung, den manipulationssicheren Audit-Trail als Prozess-Evidenz und eine ausfüllbare Art-17-Prozess-Policy-Vorlage; den unternehmensweiten Prozess + die Board-Freigabe verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 350 * 86400000).toISOString(), attestedByEmail: 'ciso@demo.com', note: 'IKT-Vorfall-Management-Prozess v2 vom Leitungsorgan freigegeben; an den Incident-Hub angebunden.', evidenceUrl: '',
    },
    {
      key: 'dora_incident_classification', framework: 'dora', article: 'Art. 18',
      title: 'Schwerwiegenden IKT-Vorfall klassifizieren',
      whatYouMustDo: 'Klassifizieren Sie schwerwiegende IKT-Vorfälle nach dem Kritikalitäts-Gate + den sieben Kriterien der Delegierten VO (EU) 2024/1772.',
      adjudonSupport: 'Wir liefern die auto-rechnende Klassifizierungs-Engine (exakte RTS-2024/1772-Schwellen, Art-8(1)-Regel, Begründungs-Trail) als Empfehlung, das Art-18-Klassifizierungs-Arbeitsblatt, den Multi-Clock-Incident-Hub für die Art-19-Fristen und den Audit-Trail; die Einstufung + die Meldung treffen Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art18_significant_cyber_threat', framework: 'dora', article: 'Art. 18(2)',
      title: 'Signifikante Cyber-Bedrohung bewerten + (freiwillig) melden',
      whatYouMustDo: 'Bewerten Sie signifikante Cyber-Bedrohungen nach Art. 10 der Delegierten VO (EU) 2024/1772 und entscheiden Sie über eine freiwillige Meldung (Art. 19(2)).',
      adjudonSupport: 'Wir liefern die Art-10-Bewertungs-Engine als Empfehlung, den ausfüllbaren Nachweis und den manipulationssicheren Audit-Trail; die Bewertung + die freiwillige Meldung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_regulator_filing', framework: 'dora', article: 'Art. 19',
      title: 'Meldung an die Aufsicht übermitteln',
      whatYouMustDo: 'Übermitteln Sie die Meldekette (Erst 4h / Zwischen 72h / Abschluss 1 Monat) an die zuständige Behörde (BaFin) nach Art. 19.',
      adjudonSupport: 'Wir liefern die automatische Art-19-Fristüberwachung (Erst 4h / Backstop 24h / Zwischen 72h / Abschluss 1 Monat), Pre-Breach-Reminder, die vorbefüllten Report-Entwürfe und das Submission-Tracking; die Einstufung, die inhaltliche Vervollständigung und die Übermittlung treffen Sie (nicht delegierbar, § 5 UWG; Art. 19(5): Auslagerung entbindet nicht).',
      templateAvailable: true, templateRef: '/dashboard/incidents', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art28_register_of_information', framework: 'dora', article: 'Art. 28(3)',
      title: 'Informationsregister der IKT-Drittdienstleister pflegen + jährlich einreichen',
      whatYouMustDo: 'Pflegen Sie das Informationsregister aller IKT-Drittdienstleister-Verträge und reichen Sie es jährlich (xBRL-CSV) ein (Art. 28(3)).',
      adjudonSupport: 'Wir liefern das strukturierte Informationsregister: die vorausgefüllte Adjudon-Selbstauskunft, die Erfassung aller IKT-Verträge mit den ITS-2024/2956-Feldern, die Jahresbericht-Kennzahlen + den CSV-Export. Die finale xBRL-CSV-Serialisierung + die Einreichung im BaFin-/EIOPA-Portal verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 9 * 86400000).toISOString(), attestedByEmail: 'auslagerung@demo.com', note: 'Informationsregister vollständig; Adjudon-Selbstauskunft hinterlegt, CSV-Export getestet.', evidenceUrl: '',
    },
    {
      key: 'dora_art30_contractual_provisions', framework: 'dora', article: 'Art. 30(2)+(3)',
      title: 'Pflicht-Vertragsklauseln (Art. 30) in IKT-Verträgen sicherstellen',
      whatYouMustDo: 'Stellen Sie die Pflicht-Vertragsklauseln nach Art. 30(2)+(3) in Ihren IKT-Verträgen sicher.',
      adjudonSupport: 'Wir liefern die Art-30-Klausel-Checkliste je Vertrag (alle 15 Pflichtbausteine), die Vollständigkeits-Anzeige + die ausfüllbare Klausel-Bibliothek (DE). Die rechtsverbindliche Klausel-Endfassung + die Verhandlung verantworten Sie (Rechtsabteilung / externe Kanzlei).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art29_concentration_risk', framework: 'dora', article: 'Art. 29',
      title: 'Konzentrations- + Unterauftragsrisiko vorab bewerten',
      whatYouMustDo: 'Bewerten Sie vor Vertragsschluss für kritische/wichtige Funktionen das Konzentrations- + Unterauftragsrisiko (Art. 29).',
      adjudonSupport: 'Wir liefern die Konzentrations-Aggregation über alle Verträge (Mehrfach-Abhängigkeit, nicht-ersetzbare kritische Anbieter, ungetestete Exits, Drittland-Ketten), die Register-Felder + das Vorbewertungs-Arbeitsblatt. Das Risiko-Urteil + die Make-or-Buy-Entscheidung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'dora_art28_8_exit_strategy', framework: 'dora', article: 'Art. 28(8)',
      title: 'Ausstiegsstrategien dokumentieren + testen (kritische Funktionen)',
      whatYouMustDo: 'Dokumentieren + testen Sie Ausstiegsstrategien für IKT-Dienste kritischer/wichtiger Funktionen (Art. 28(8)).',
      adjudonSupport: 'Wir liefern für die Adjudon-Zeile den Daten-/Audit-Trail-Export (Portable-Bundle) als Exit-Mechanismus, die Erfassung von Exit-Existenz/Test/Übergangsfrist je Vertrag + das Flag kritisch-ohne-getesteten-Exit in der Konzentrationsanalyse. Die Durchführung des Exit-Tests verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_orsa_narrative', framework: 'solvency-ii', article: 'Art. 45',
      title: 'ORSA-Narrativ + Risk-Appetite erstellen',
      whatYouMustDo: 'So gehen Sie vor (Art. 45(1) Solvency II): Beurteilen Sie den Gesamtsolvabilitätsbedarf prospektiv mit Stresstests (45(1)(a)), die laufende SCR/MCR-Einhaltung (45(1)(b)) und die Abweichung Ihres Risikoprofils von den SCR-Annahmen (45(1)(c)). Definieren Sie Ihren Risk-Appetite und die KI-Auto-/Mensch-Schwellen, integrieren Sie Nachhaltigkeitsrisiken, holen Sie die AMSB-Freigabe und führen Sie die ORSA jährlich + nach jeder wesentlichen Änderung durch (45(5)/(6)).',
      adjudonSupport: 'Wir liefern für die KI-Entscheidungs-Schicht die ORSA-Eingangsdaten (Konfidenz-/Kalibrierungs-Trend, Modell-Drift-Alerts, Vorfall-Zusammenfassung, Policy-Wirksamkeit, ESG-getaggte Entscheidungs-Aggregation), den manipulationssicheren Audit-Trail als Art-44-Prüf-Evidenz und ein ORSA-Narrativ-Arbeitsblatt; das ORSA-Urteil, der Risk-Appetite und die SCR/MCR-Kapitalberechnung bleiben bei Ihnen (nicht delegierbar, § 5 UWG).',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_scr_mcr', framework: 'solvency-ii', article: 'Säule 1 (Art. 100-131)',
      title: 'SCR/MCR-Kapital berechnen + vorhalten',
      whatYouMustDo: 'Sie berechnen und halten SCR (Art. 100-127) und MCR (Art. 128-131) vor — Standardformel oder (Teil-)internes Modell. Die Kapital-Berechnung selbst ist versicherungsmathematisch und nicht-delegierbar. Soweit KI-/ML-Komponenten in der Rechnung stecken (internes Modell, oder Rückstellungs-Schätzung Art. 76-86/82), gelten für diese die internen-Modell-Standards Art. 120/121(3)/124/125 (proportional, EIOPA-BoS-25-360 vom 06.08.2025) — als vier eigene Punkte unten geführt.',
      adjudonSupport: 'Die SCR/MCR-Berechnung liefert Adjudon NICHT (außerhalb des Scopes). Für die KI-/ML-Komponenten Ihrer Rechnung liefern wir die Nachweis-Schicht (Use-Test-Nutzung, Datenqualität, Validierungs-/Drift-/Backtesting-Evidenz, Dokumentation/Change-Log) über die fünf Säule-1-Arbeitsblätter.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_pillar1_ai_inventory', framework: 'solvency-ii', article: 'Art. 112-127 / 76-86',
      title: 'KI-Komponenten in der Säule-1-Rechnung inventarisieren',
      whatYouMustDo: 'Erfassen Sie, welche KI-/ML-Komponenten in Ihre Säule-1-Rechnung einfließen — als Baustein eines internen Modells (Art. 112-127), in der Best-Estimate-Schätzung (Art. 76-86) oder im Pricing/Underwriting, das das Risikoprofil speist. Je Komponente: Verortung, SCR-Pfad, Materialität und Art-115-Änderungsklasse. Nur Erfasstes lässt sich gegen Art. 120/121/124/125 nachweisen.',
      adjudonSupport: 'Wir liefern die Agenten-Registry (Modell-Metadaten) und das Decision-Trace-Volumen je Agent als Inventar-Basis; die Materialitäts- und SCR-Pfad-Einstufung (aktuariell) treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_pillar1_data_quality', framework: 'solvency-ii', article: 'Art. 82 / 121(3)',
      title: 'Datenqualität der KI-Komponente nachweisen (genau/vollständig/angemessen)',
      whatYouMustDo: 'Weisen Sie nach, dass die von der KI-Komponente genutzten Daten genau, vollständig und angemessen sind (Art. 82 für Rückstellungen; Art. 121(3) für interne Modelle; DelVO 2015/35 Art. 19 Datenverzeichnis, Art. 219 interne-Modell-Datenqualität). Führen Sie je Datensatz ein Datenverzeichnis, dokumentieren Sie die Qualitäts-Prüfungen und treffen Sie das finale Angemessenheits-Urteil.',
      adjudonSupport: 'Wir liefern die Data-Quality-Validierungs-Records (passed/failed je Prüfdimension, Aggregat-Score) und die Pass-Rate-Posture; das Datenverzeichnis und das Angemessenheits-Urteil verantworten Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_pillar1_use_test', framework: 'solvency-ii', article: 'Art. 120',
      title: 'Use-Test der KI-Komponente nachweisen',
      whatYouMustDo: 'Belegen Sie, dass die KI-Komponente breit genutzt wird und eine wichtige Rolle im Governance-/Risikomanagement-System, in Entscheidungen und der Kapitalallokation spielt (Verwendungstest, Art. 120). Beschreiben Sie den Verwendungszweck je Komponente, ziehen Sie die Nutzungs-Evidenz aus dem Audit-Trail und holen Sie das Vorstands-Attest „breit genutzt / wichtige Rolle".',
      adjudonSupport: 'Wir liefern das Entscheidungs-Volumen je Agent, die Anzahl eingesetzter Agenten und die menschliche-Aufsicht-Quote als Use-Test-Nutzungsnachweis; das Vorstands-Attest treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_pillar1_validation', framework: 'solvency-ii', article: 'Art. 124 (+ 122/123)',
      title: 'Validierungs-Zyklus für die KI-Komponente betreiben',
      whatYouMustDo: 'Betreiben Sie einen regelmäßigen Validierungs-Zyklus (Art. 124): Performance-Monitoring, laufende Angemessenheit, Test gegen die Erfahrung, Stabilitäts-/Sensitivitäts-Analyse. Legen Sie die Frequenz fest, betreiben Sie Backtesting gegen die Ist-Erfahrung, bewerten Sie offene Drift-Alerts und sichern Sie einen unabhängigen Sign-off. Die 99,5-%-VaR-Kalibrierung (Art. 122) bleibt aktuariell.',
      adjudonSupport: 'Wir liefern die Kalibrierungs-Map (ECE/Brier), Drift-Alerts (KS/ECE/Brier) und das Policy-Effectiveness-Backtesting (Precision/Recall/F1, Cohen-κ) als Validierungs-Evidenz; den unabhängigen Sign-off und die Kapital-Kalibrierung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_pillar1_documentation', framework: 'solvency-ii', article: 'Art. 125 / 115',
      title: 'KI-Komponente dokumentieren + Modelländerungs-Policy führen',
      whatYouMustDo: 'Dokumentieren Sie Design und Betrieb, die math.-empirische Grundlage, die Annahmen, den Nachweis der Erfüllung von Art. 120-124 und die Umstände, unter denen das Modell NICHT wirksam arbeitet (Art. 125). Führen Sie eine Policy für wesentliche/unwesentliche Modelländerungen (Art. 115) und dokumentieren Sie die Vorstands-Aufsicht (Art. 116).',
      adjudonSupport: 'Wir liefern die versionierte 9-Felder-Model-Card (immutable), das Annex-IV-Tech-Doc-Pack und den immutablen Policy-Version-Change-Log (Art-115-Historie); die Spezifikations-/Annahmen-Freigabe und die Vorstands-Aufsicht treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 12 * 86400000).toISOString(), attestedByEmail: 'aktuariat@demo.com', note: 'Model-Cards v2 + Art-115-Change-Log für 3 produktive KI-Systeme dokumentiert.', evidenceUrl: '',
    },
    {
      key: 'solvency_internal_model_validation', framework: 'solvency-ii', article: 'Art. 112–127 (Validierung: Art. 124)',
      title: 'Internes Modell unabhängig validieren + abzeichnen (sofern genutzt)',
      whatYouMustDo: 'Nur falls Sie ein (partielles) internes Modell für die SCR nutzen (Genehmigung Art. 112): Sie validieren das Modell unabhängig (Validierungsstandard Art. 124 — Validierungs-Team getrennt von der Modellentwicklung) und zeichnen es ab. Bei Nutzung der Standardformel nicht anwendbar — attestieren Sie diesen Status zur Bestätigung.',
      adjudonSupport: 'Wir liefern Calibration-/Bias-Backtesting-/Drift-Evidenz für die KI-Komponenten als Validierungs-Input; den unabhängigen Validierungs-Sign-off (Art. 124) treffen Sie. Die Modell-Validierung selbst ist nicht-delegierbar.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_governance_keyfunctions', framework: 'solvency-ii', article: 'Art. 41(1)/(2)',
      title: 'System der Governance einrichten + die vier Schlüsselfunktionen etablieren',
      whatYouMustDo: 'Richten Sie ein wirksames System der Governance mit klarer Struktur, sauberer Funktionstrennung und geregelten Informationswegen ein (Art. 41(1)/§ 23 VAG), das die Anforderungen der Art. 42-49 abdeckt, proportional gestaltet ist (Art. 41(2)), die vier Schlüsselfunktionen etabliert und für Kontinuität/Notfallpläne sorgt (Art. 41(4)). Betten Sie die KI-Entscheidungs-Schicht ein und holen Sie die AMSB-Freigabe.',
      adjudonSupport: 'Wir liefern den manipulationssicheren Audit-Trail als Prüfungsgegenstand der internen Revision, die laufzeit-verifizierte Evidenz (Confidence/Kalibrierung/Anomalie/Drift), die versionierten Policy- und Model-Card-Snapshots und eine Governance-System-Vorlage; das System, die Funktionstrennung, die Schlüsselfunktionen und die AMSB-Freigabe verantworten Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 35 * 86400000).toISOString(), attestedByEmail: 'vorstand@demo.com', note: 'Governance-System-Doku v1 inkl. KI-Schicht-Einbettung vom AMSB freigegeben.', evidenceUrl: '',
    },
    {
      key: 'solvency_art41_written_policies', framework: 'solvency-ii', article: 'Art. 41(3)',
      title: 'Schriftliche Leitlinien erstellen, jährlich überprüfen + vom Leitungsorgan freigeben',
      whatYouMustDo: 'Erstellen Sie die schriftlichen Pflicht-Leitlinien zu mindestens Risikomanagement, IKS, interner Revision und — soweit relevant — Ausgliederung (Art. 41(3); Adjudon als KI-Audit-Schicht ist eine solche Auslagerung), ergänzt um die weiteren EIOPA-/DelVO-erwarteten Leitlinien. Lassen Sie JEDE Leitlinie VOR Inkrafttreten vom AMSB genehmigen und mindestens jährlich überprüfen.',
      adjudonSupport: 'Wir halten die für die KI-Entscheidungen geltende Policy als unveränderliche, versionierte Snapshot-Kette mit Genehmigungs-Rollen (Autor/Prüfer/Freigeber) und append-only Transcript je Auswertung vor — der direkte Beweis, welche Leitlinie wann galt und wer sie freigab — und liefern ein Leitlinien-Register; das vollständige Leitlinien-Werk, die jährliche Überprüfung und die AMSB-Freigabe verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/policies', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 30 * 86400000).toISOString(), attestedByEmail: 'compliance@demo.com', note: 'KI-Policy als Teil des IKS-Leitlinienwerks v1 freigegeben; jährliche Review terminiert.', evidenceUrl: '',
    },
    {
      key: 'solvency_art41_key_functions', framework: 'solvency-ii', article: 'Art. 41(1) / Del.VO 268-272',
      title: 'Schlüsselfunktions-Chartas + KI-Aufsichts-Mapping (Risk/Compliance/Interne Revision/Aktuar)',
      whatYouMustDo: 'Etablieren Sie je Schlüsselfunktion eine Charta mit Mandat, Ressourcen und Berichtslinie an das AMSB (Art. 41(1) i.V.m. DelVO 268-272), sichern Sie Unabhängigkeit/Objektivität und Fit-&-Proper-Eignung und definieren Sie je Funktion, WIE sie ihre Aufsicht über die KI-Entscheidungs-Schicht wahrnimmt (entsprechend EIOPA-BoS-25-360 vom 06.08.2025).',
      adjudonSupport: 'Wir liefern je Funktion die KI-Schicht-Evidenz: Anomalie-/Drift-/Kalibrierungs-Signale (Risikomanagement), Policy-Durchsetzung + Transcript (Compliance), Hash-Chain-Audit-Trail + Auditor-Evidence-Pack (interne Revision), Bias-Backtesting + Kalibrierung (Aktuar) und die versionierte 9-Felder-Model-Card; Einrichtung, Unabhängigkeit, Besetzung und Berichtslinien verantworten Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_esg_classification', framework: 'solvency-ii', article: 'Art. 45 (RL 2025/2)',
      title: 'Nachhaltigkeitsrisiken klassifizieren',
      whatYouMustDo: 'Führen Sie die Wesentlichkeits-Vorprüfung der Klima-/Nachhaltigkeitsrisiken durch (im Rahmen der ORSA, verschärft durch RL (EU) 2025/2), klassifizieren Sie je Kategorie (7-teilige, an EIOPA angelehnte Taxonomie) Exposition und Wesentlichkeit über kurze/mittlere/lange Frist, operationalisieren Sie das SDK-Tagging (esgRiskTags/sustainabilityRelevant) und erstellen Sie den Sustainability-Risk-Management-Plan. Adjudon klassifiziert NICHT selbst.',
      adjudonSupport: 'Wir liefern die an EIOPA angelehnte ESG-Referenz-Taxonomie (7 Kategorien), die org-bezogene Aggregation der ESG-getaggten KI-Entscheidungen als ORSA-Input und ein ESG-Klassifizierungs-Arbeitsblatt; die Klassifizierung, das Wesentlichkeitsurteil und die Tag-Mapping-Regeln treffen Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_art45a_climate_scenario', framework: 'solvency-ii', article: 'Art. 45a (RL (EU) 2025/2)',
      title: 'Klimawandel-Szenarioanalyse in der ORSA durchführen',
      whatYouMustDo: 'Führen Sie die Wesentlichkeitsbewertung der Klimawandel-Exposition durch (physische + Transitionsrisiken, Art. 45a(1) — gilt für ALLE Unternehmen). Bei Wesentlichkeit: spezifizieren Sie mindestens zwei langfristige Klima-Szenarien und bewerten Sie deren Auswirkung auf Geschäft und Solvabilität (45a(2); kleine/nicht-komplexe Unternehmen sind von der Langfrist-Spezifikation befreit). Integrieren Sie das Ergebnis ins ORSA-Narrativ. Hinweis: Art. 45a ist erst ab 30.01.2027 anwendbar.',
      adjudonSupport: 'Wir liefern die Verteilungs-Evidenz der klima-getaggten KI-Entscheidungen (Tags climate-physical/climate-transition) als Input und ein Klima-Szenario-Arbeitsblatt; die Wesentlichkeitsbewertung, die Szenario-Auswahl/-Parametrisierung und die Auswirkungsbewertung sind aktuarielle/risikostrategische Urteile, die Sie verantworten (nicht delegierbar, § 5 UWG).',
      templateAvailable: false, templateRef: null, hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_art49_outsourcing_policy', framework: 'solvency-ii', article: 'Art. 49(1)/(2) + DelVO 274(1)',
      title: 'Auslagerungs-Leitlinie + Wesentlichkeitsbestimmung verantworten',
      whatYouMustDo: 'So gehen Sie vor: (1) Sie bleiben nach Art. 49(1) auch nach der Auslagerung an Adjudon voll verantwortlich. (2) Bestimmen Sie, ob die ausgelagerte Funktion kritisch/wichtig ist (löst Anzeige + verschärfte Pflichten aus). (3) Art-49(2)(a)-(d)-Selbstcheck: keine Beeinträchtigung von Governance-Qualität, operationellem Risiko, Aufsichts-Überwachung oder Service für Versicherungsnehmer. (4) Schriftliche Auslagerungs-Leitlinie (DelVO 274(1)). (5) Register-Zeile (EIOPA-BoS-20-002 Leitlinie 5 — nutzen Sie das DORA-Register). Vorlage herunterladen, abhaken.',
      adjudonSupport: 'Wir liefern die Adjudon-Selbstauskunft (EU-Residenz Frankfurt, Fail-Open, Performance-Verträge, Exit-Bundle), das DORA-Register als Register-Zeile, die Anomalie-/Audit-Evidenz für die laufende Überwachung + ein Leitlinien-/Wesentlichkeits-Arbeitsblatt; die Wesentlichkeitsbestimmung + das Governance-Urteil treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 21 * 86400000).toISOString(), attestedByEmail: 'compliance@demo.com', note: 'Adjudon als wichtige Auslagerung eingestuft; Leitlinie v1 freigegeben.', evidenceUrl: '',
    },
    {
      key: 'solvency_art49_due_diligence', framework: 'solvency-ii', article: 'DelVO 274(3)/(5)',
      title: 'Vorvertragliche Eignungs-/Risikoprüfung (Due Diligence) durchführen',
      whatYouMustDo: 'So gehen Sie vor (vor Vertragsschluss): (1) Auswahl-Sorgfalt DelVO 274(3)(a)-(f): Fähigkeit/Kapazität/Zulassung, Interessenkonflikte, schriftliche Vereinbarung, Organ-Genehmigung, Datenschutz, Sicherheit/Vertraulichkeit. (2) Bei kritisch/wichtig ZUSÄTZLICH 274(5)(a)-(d): RM/IKS des Anbieters, Berücksichtigung in IHREM RM/IKS, finanzielle Mittel + Personal, Notfallpläne + getestete Backups. (3) Nachweise (Zertifikate, Jahresabschluss, Restore-Test) bei Adjudon anfordern. (4) Eignungs-Urteil dokumentieren, abhaken.',
      adjudonSupport: 'Wir liefern die Due-Diligence-Selbstauskunft (GDPR-Art-28-DPA, PII-Scrubbing, EU-Residenz, TOM-Register, Fail-Open/Retry, SOC-2/ISO/C5-Ziele) + das Eignungs-Arbeitsblatt; die mit „anfordern" markierten Nachweise liefern wir auf Anforderung, das Eignungs-Urteil treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_art49_written_agreement', framework: 'solvency-ii', article: 'DelVO 274(4)',
      title: 'Schriftliche Auslagerungsvereinbarung mit Pflichtklauseln (274(4)(a)-(l)) abschließen',
      whatYouMustDo: 'So gehen Sie vor: (1) Alle Pflichtklauseln DelVO 274(4)(a)-(l) sicherstellen — insb. (h) Zugang + Vor-Ort-Prüfrecht für Sie/Prüfer/Aufsicht, (i) direkte Fragen der Aufsicht, (k)/(l) Unterauftrag, (d)/(e) Kündigung/Übergang. (2) Cloud: volle Zugangs-/Auditrechte (EIOPA Leitlinie 11) + getestete Ausstiegsstrategie (Leitlinie 15). (3) § 32 VAG: Steuerung durch Vorstand + Prüfrechte der Aufsicht nicht beeinträchtigt. (4) Klausel-Checkliste je Vertrag im DORA-Register pflegen. Vorlage herunterladen, anwaltlich finalisieren, abhaken.',
      adjudonSupport: 'Wir liefern die 274(4)(a)-(l)-Klausel-Checkliste mit Adjudon-Posture je Klausel, das Audit-/Zugangs-Mapping (Portable-Evidence-Bundle + Auditor-Evidence-Pack), den Exit-Mechanismus (Daten-Export) + die Verzahnung mit der DORA-Art-30-Checkliste; die rechtsverbindliche Endfassung verantworten Sie (Rechtsabteilung / Hogan Lovells).',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'solvency_art49_supervisory_notification', framework: 'solvency-ii', article: 'Art. 49(3) / § 47 Nr. 8 VAG',
      title: 'Auslagerung bei der Aufsicht (BaFin) anzeigen + Ausgliederungsbeauftragte/n benennen',
      whatYouMustDo: 'So gehen Sie vor: (1) Bei beabsichtigter Auslagerung einer kritischen/wichtigen Funktion rechtzeitig VORAB der BaFin anzeigen — mit Vertragsentwurf (Art. 49(3), § 47 Nr. 8 VAG). (2) Pflichtinhalte: Name + Anschrift des Dienstleisters, Umfang, Gründe, bei Schlüsselfunktion die verantwortliche Person beim Dienstleister. (3) Ausgliederungsbeauftragte/n benennen (§ 32 VAG) + der BaFin melden. (4) Elektronisch über die BaFin-MVP einreichen (VersAusgl-AnzV) — auch nachträgliche wesentliche Änderungen. Vorlage herunterladen, übermitteln, abhaken.',
      adjudonSupport: 'Wir liefern die Anbieter-Selbstauskunft (Name/Anschrift/Umfang/Sub-Prozessoren/EU-Residenz) als vorbefüllten Anzeige-Baustein + das Anzeige-Arbeitsblatt; die Einstufung, inhaltliche Vervollständigung + elektronische Übermittlung über die BaFin-MVP verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/dora-register', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_board_governance', framework: 'eiopa', article: 'Risikomanagementsystem',
      title: 'KI-Governance auf Vorstandsebene verantworten',
      whatYouMustDo: 'So gehen Sie vor: (1) Das Leitungsorgan (AMSB) übernimmt förmlich die Letztverantwortung für die KI-Governance — sie bleibt auch bei Drittanbieter-Systemen beim Unternehmen und ist nicht an den Vendor delegierbar (EIOPA-BoS-25-360; DORA Art. 5(2)). (2) Rollen + Verantwortlichkeiten der KI-Schicht klar zuweisen + trennen (drei Verteidigungslinien über die Schlüsselfunktionen Art. 44/46/47 / §§ 26/29/30 VAG). (3) KI-Governance in Ihre schriftlichen Leitlinien einbetten (Art. 41(3) / § 23(3) VAG; EIOPA fordert kein separates AI-Policy-Dokument). (4) KI-Kompetenz/AI Literacy für Leitungsorgan + Personal sicherstellen (§ 24 VAG, EU AI Act Art. 4). (5) Berichts- + Eskalationswege + Board-Reporting-Kadenz festlegen. (6) Einbettung in DORA-IKT-Governance + ORSA. Vorlage herunterladen, vom Vorstand freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die Board-Evidenz: manipulationssicherer SHA-256-Audit-Trail, Compliance-Posture + Compliance-Maturity-Register, Anomalie-/Drift-/Confidence-Risiko-Signale, versionierte 9-Felder-Model-Card, Policy-Engine + 3-Rollen-Approval-Transkripte und das signierte Auditor-Evidence-Pack — plus eine ausfüllbare Verantwortlichkeits-/Aufsichts-Vorlage; die Letztverantwortung, Rollenbesetzung, Policy-Inhalte + Freigabe verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 21 * 86400000).toISOString(), attestedByEmail: 'vorstand@demo.com', note: 'KI-Governance-Verantwortung per Vorstandsbeschluss v1 übernommen; in System of Governance verankert.', evidenceUrl: '',
    },
    {
      key: 'eiopa_proportionality', framework: 'eiopa', article: 'Risikobasierter Ansatz & Proportionalität',
      title: 'KI-Anwendungsfälle risikobasiert klassifizieren',
      whatYouMustDo: 'So gehen Sie vor: (1) KI-Anwendungsfälle inventarisieren (Adjudon-Agenten-Registry / IKT-Asset-Register als Input). (2) ZUERST das AI-Act-Gate: je Fall prüfen, ob verboten (Art. 5) oder hochriskant (Anhang III — u. a. Risikobewertung/Pricing Leben/Kranken); Hochrisiko folgt dem strengeren AI-Act-Regime und fällt aus der EIOPA-Opinion. (3) In-Scope-Fälle gegen die EIOPA-Kriterien bewerten (Datenvolumen/-sensibilität, Kundenbasis, Autonomiegrad/Anwendung, Grundrechte/finanzielle Inklusion, prudenzielle Implikationen) → Stufe hoch/mittel/niedrig (Ihre Schwellen). (4) Governance-Aufwand proportional zuordnen (Solvency II Art. 41(2)). (5) Materialität bewerten. (6) Klassifizierungen periodisch überprüfen. Vorlage herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern das KI-Anwendungsfall-Inventar (Agenten-Registry / IKT-Asset-Register), die Risiko-Signale (Confidence-Triangulation, Anomalie-z-Score, Kalibrierungs-Drift KS/ECE/Brier, Block-/Override-Rate, bias-stratifizierte ECE je Kohorte), das proportionale Plan-/Feature-Gating und eine ausfüllbare Klassifizierungs-Vorlage; das AI-Act-Gate, die Risiko-Klassifizierung, die Schwellen + die proportionale Governance-Zuordnung treffen Sie.',
      templateAvailable: true, templateRef: '/dashboard/anomalies', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_record_keeping', framework: 'eiopa', article: 'Dokumentation & Aufzeichnung',
      title: 'KI-Entscheidungen nachvollziehbar aufzeichnen',
      whatYouMustDo: 'So gehen Sie vor: (1) Umfang Ihrer KI-Aufzeichnungen bestimmen (welche Systeme/Anwendungsfälle, je System die verantwortliche Schlüsselfunktion). (2) Aufzeichnung im System of Governance verankern (Solvency II Art. 41(1) / Del. VO 258(1)(g); Hochrisiko zusätzlich EU-KI-VO Art. 12). (3) Aufbewahrungsfrist festlegen (§ 257 HGB / § 147 AO / BaFin ≥ 5 Jahre) + dataRetentionDays konfigurieren (Adjudon erzwingt; Boden 6 Monate, Art. 26(6)). (4) Regelmäßige Überprüfung bestimmen (interne Revision § 30 VAG). (5) Modell-Methodik + Trainingsdaten-Herkunft hinterlegen. (6) Vorlage herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die manipulationssichere Aufzeichnungs-Schicht: SHA-256-Hash-Chain je Entscheidung, 138-Aktions-Operations-Audit-Log, versionierte CalibrationMap + Model-Card, Erasure-Ledger, automatische Retention-Durchsetzung, Art-12-/Replay-Export + Portable-Bundle — plus ein Aufzeichnungs-Arbeitsblatt; Umfang, Frist, Kadenz + Methodik-/Trainingsdaten-Doku verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/audit-log', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 14 * 86400000).toISOString(), attestedByEmail: 'revision@demo.com', note: 'Aufbewahrung 1825 Tage (5 J.) gesetzt; jährliche Sichtung durch interne Revision verankert.', evidenceUrl: '',
    },
    {
      key: 'eiopa_transparency', framework: 'eiopa', article: 'Transparenz & Erklärbarkeit',
      title: 'KI-Entscheidungen transparent + erklärbar machen',
      whatYouMustDo: 'So gehen Sie vor: (1) Je Stakeholder (Aufsicht/Prüfer/DSB/Kunde) die Erklärungstiefe + den Kanal mappen. (2) Technische Erklärbarkeit für Aufsicht/Prüfer bereitstellen (Article-13-IFU + signierter Replay + Policy-Transcript). (3) Klar verständliche Kundenerklärung formulieren (IDD Art. 17; DSGVO Art. 22(3)) — Adjudon liefert die Faktoren, den Kundentext verantworten Sie. (4) Modell-Version-/Confidence-Offenlegung festlegen. (5) Wo volle Erklärbarkeit fehlt: Guardrails (Confidence-Routing) + menschliche Aufsicht. (6) Vorlage herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die Erklärbarkeits-Artefakte: Article-13-Instructions-for-Use, signierter Regulator-Replay je Entscheidung, Policy-Transcript + Kalibrierungs-Provenance, öffentlicher Verify-Endpoint + Guardrail-Mechanik — plus ein Transparenz-Arbeitsblatt; Stakeholder-Mapping, Kundenerklärung (IDD/DSGVO) + Offenlegungs-/Schwellen-Entscheidung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/calibration', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_human_oversight', framework: 'eiopa', article: 'Menschliche Aufsicht',
      title: 'Menschliche Aufsicht über KI sicherstellen',
      whatYouMustDo: 'So gehen Sie vor: (1) KI-Aufsicht in Ihr System of Governance einbetten — den vier Schlüsselfunktionen (Risikomanagement/Compliance/interne Revision/versicherungsmathematische Funktion) + DSB + Leitungsorgan zuordnen (drei Verteidigungslinien). (2) Operative Aufsicht konfigurieren (Confidence-Schwelle für Pflicht-Review, 3-Rollen-Freigabe LD-7, SLA + Eskalation). (3) Personenbenennung je Hochrisiko-System über den EU-KI-VO-Art-14-Nachweis. (4) Berichtslinie + Kadenz ans Leitungsorgan (Art. 41(1)) + ORSA. (5) Lebenszyklus abdecken (Model-Card-Freigabe → Monitoring → Vorfall). (6) Vorlage herunterladen, freigeben, abhaken.',
      adjudonSupport: 'Wir liefern die operative Aufsichts-Mechanik: Review-Queue (Uncertainty-Sortierung), 3-Rollen-Freigabe (LD-7) + Break-Glass, SLA-Tracking + Breach-Webhook, Drift-/Anomalie-Signale je Schlüsselfunktion, versionierte Model-Card + Compliance-Posture als Board-Beleg — plus ein Aufsichts-Arbeitsblatt; Funktions-Zuordnung, Personenbenennung, Schwellen + Berichtslinie verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/review-queue', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_model_approval', framework: 'eiopa', article: 'Risikomanagementsystem',
      title: 'KI-Modell vor Produktion freigeben',
      whatYouMustDo: 'Sie genehmigen jedes KI-Modell formal vor dem Produktiveinsatz und dokumentieren die Governance.',
      adjudonSupport: 'Wir liefern die versionierte 9-Felder-Model-Card (immutable, attestierbar) als Freigabe-Dokumentation; die Freigabe treffen Sie. (Model-Card-UI folgt auf der KI-System-Detailseite — Content-Phase.)',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_orsa_attestation', framework: 'eiopa', article: 'Risikomanagementsystem',
      title: 'KI-Governance attestieren + in ORSA integrieren',
      whatYouMustDo: 'Ihre Compliance-/Aktuar-Funktion attestiert die KI-Governance formal und integriert sie in die ORSA.',
      adjudonSupport: 'Wir liefern signierte Evidenz-Pakete (Auditor-Evidence-Pack); die Attestierung treffen Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_data_quality', framework: 'eiopa', article: 'Datengovernance',
      title: 'Daten-Qualitäts-Policy festlegen',
      whatYouMustDo: 'Sie definieren die Trainings-/Eingabedaten-Standards (Vollständigkeit, Genauigkeit, Angemessenheit, Repräsentativität).',
      adjudonSupport: 'Wir liefern Data-Quality- + Bias-Stratifikations-Signale; die Policy + Standards definieren Sie.',
      templateAvailable: false, templateRef: null, hasTemplate: false,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_fairness_monitoring', framework: 'eiopa', article: 'Fairness & Ethik',
      title: 'Fairness-Monitoring planen: geschützte Merkmale + Disparitäts-Schwellen festlegen',
      whatYouMustDo: 'So gehen Sie vor: (1) Die KI-Funktion(en) im Geltungsbereich bestimmen (inkl. ob DSGVO-Art.-22-Einzelentscheidungen betroffen sind). (2) Risikobasiert die getesteten geschützten Merkmale festlegen (AGG § 1) — wird zum requiredAttributes-Prüfumfang im Bias-Audit. (3) Disparitäts-Schwellen (ECE-Gap watch/breach) + Mindest-Stichprobe festlegen oder die Adjudon-Defaults (5 pp / 10 pp / 30) begründet bestätigen. (4) Kadenz + Eskalation festlegen und je Periode ein Bias-Audit erzeugen. EIOPA/BaFin benennen keine abschließende Merkmalsliste — Prüfumfang + Schwellen sind Ihre risikobasierte Governance-Entscheidung.',
      adjudonSupport: 'Wir liefern die Messung + das Vollständigkeits-Tracking: bias-stratifizierte Kalibrierung (Brier/ECE je Gruppe), den maximalen Zwischen-Gruppen-Abstand, die Fairness-Abdeckung und das Bias-Audit mit geprüften Merkmalen, Abdeckung in Prozent und Gesamt-Status (gesund/Beobachtung/Verstoß) — plus dieses Plan-Arbeitsblatt. Merkmalsauswahl, Schwellen + Kadenz verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/fairness-bias', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'eiopa_fairness_remediation', framework: 'eiopa', article: 'Fairness & Ethik',
      title: 'Fairness-Remediation entscheiden, umsetzen + dokumentieren',
      whatYouMustDo: 'Bei erkanntem breach: (1) Handlungsoptionen bewerten (monitor / manual_review / retrain / adjust_pricing / retire_model). (2) Maßnahme entscheiden (Aktuar + Compliance) + im System erfassen. (3) Umsetzen + Wirksamkeit per Nachkontroll-Audit prüfen (Regelkreis schließen). (4) Prüfen, ob das Fairness-Risiko wesentlich ist + in die ORSA (VAG § 26 / Solvency II Art. 45) bzw. eine BaFin-Information/Kundenredress einfließt. Die Remediations-Entscheidung ist nicht-delegierbar (Vorstand/Schlüsselfunktionen).',
      adjudonSupport: 'Wir liefern den Maßnahmen-Vorschlag (richtet sich nach der Größe der Fairness-Abweichung) + die unveränderliche Verlaufs-Dokumentation (vorgeschlagen → angenommen/abgelehnt → umgesetzt, inkl. Entscheider + Begründung + Zeitstempel) + dieses Entscheidungs-Protokoll. Bewertung, Entscheidung + Umsetzung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/fairness-bias', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_market_test', framework: 'vvg-idd', article: '§ 60 VVG',
      title: 'Ausgewogene Markt-/Produktauswahl durchführen',
      whatYouMustDo: 'So gehen Sie vor: (1) Beratungsgrundlage festlegen — ausgewogene Markt-Untersuchung (Makler, voller Markt) oder eingeschränkte Auswahl (§ 60 Abs. 1). (2) Bei eingeschränkter Auswahl: den VN VOR seiner Vertragserklärung ausdrücklich darauf hinweisen (Abs. 1 S. 2). (3) Die „hinreichende Zahl" der berücksichtigten Verträge + Versicherer fachlich bestimmen (Bedarf, Anbieterzahl, Marktanteil, Produktzahl, Merkmale — IDD Art. 20(3)) und den Produktvergleich erfassen + finalisieren. (4) Die Markt-/Informationsgrundlage + die Namen der zugrunde gelegten Versicherer mitteilen (Abs. 2; Vertreter zusätzlich: für welche Versicherer + ob ausschließlich). (5) Einen etwaigen Verzicht des VN als gesonderte schriftliche Erklärung ablegen (Abs. 3). (6) Vorlage herunterladen, Vergleich finalisieren, Item abhaken.',
      adjudonSupport: 'Wir liefern den § 60-Leitfaden als Arbeitsblatt (Download), das Produktvergleichs-Modul (empfohlenes Produkt + Alternativen + Marktumfang + Begründung) und beim Abschluss ein fälschungssicheres Siegel über die festgehaltenen Angaben (manipulationssichere Dokumentation). Die Marktuntersuchung, die „hinreichende Zahl", den Hinweis (Abs. 1 S. 2) + die Mitteilung (Abs. 2) verantworten Sie (§ 5 UWG; Haftung § 63 VVG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_conflict_disclosure', framework: 'vvg-idd', article: '§ 60 VVG',
      title: 'Interessenkonflikte + Vergütung offenlegen',
      whatYouMustDo: 'Sie informieren den Kunden aktiv über Interessenkonflikte + die Art Ihrer Vergütung.',
      adjudonSupport: 'Wir liefern das Vergütungs-Offenlegungs-Modul als Doku; die Offenlegung verantworten Sie.',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_liability_defense', framework: 'vvg-idd', article: '§ 63 VVG',
      title: 'Beratungshaftung absichern: Verteidigungs-Evidenz vorhalten',
      whatYouMustDo: 'So gehen Sie vor: (1) § 63 VVG verpflichtet Sie zum Schadensersatz, wenn dem Versicherungsnehmer durch eine Verletzung Ihrer Pflichten nach § 60 oder § 61 ein Schaden entsteht — es sei denn, Sie haben die Pflichtverletzung nicht zu vertreten. (2) Sichern Sie Ihre Verteidigung durch lückenlose Dokumentation: ohne hinreichende Dokumentation droht eine Beweiserleichterung bis zur Beweislastumkehr zugunsten des Kunden. (3) Stellen Sie je Beratung das Verteidigungs-Dossier zusammen (Wünsche/Bedürfnisse + Empfehlung + Begründung nach § 61, Markt-/Produktauswahl nach § 60, Geeignetheit nach IDD Art. 30) inkl. SHA-256-Integritäts-Anker + 10-Jahres-Aufbewahrung. (4) Bei Beratungsverzicht: Verzichts-Vorlage MIT dem Hinweis nach § 61 Abs. 2 VVG (sonst unwirksam). (5) Dossier herunterladen, bei der Schaden-/VSH-Akte hinterlegen, abhaken.',
      adjudonSupport: 'Wir liefern das Haftungsverteidigungs-Dossier (§ 63) als Arbeitsblatt + verknüpfen die Beweismittel: das finalisierte Beratungsprotokoll (§ 61/§ 62) mit SHA-256-Integritäts-Anker + 10-Jahres-Aufbewahrung, das Produktvergleichs-Modul (§ 60) und die serverseitig nachrechenbare Audit-Trail-Verifikation. Die Haftung, die Beratungsqualität und die Verzichts-Entscheidung verantworten Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/advisory', hasTemplate: true,
      attested: false, attestedAt: null, attestedByEmail: null, note: '', evidenceUrl: '',
    },
    {
      key: 'vvg_cpd_weiterbildung', framework: 'vvg-idd', article: '§ 34d Abs. 9 GewO / § 7 VersVermV',
      title: 'Weiterbildung nachweisen (15 Std./Kalenderjahr)',
      whatYouMustDo: 'So gehen Sie vor: (1) Bestimmen Sie den Kreis der Weiterbildungspflichtigen: Sie als Gewerbetreibende(r) sowie alle unmittelbar mitwirkenden Beschäftigten (§ 34d Abs. 9 S. 1 GewO) — inkl. der Personen, die die KI-gestützte Beratung bedienen/freigeben. (2) Stellen Sie je Person 15 Stunden Weiterbildung je Kalenderjahr sicher (§ 34d Abs. 9 S. 2 GewO; Inhalte/Qualität nach § 7 VersVermV; bei KI-Bedienung AI-Literacy, EU AI Act Art. 4). (3) Erfassen Sie je Maßnahme die drei Pflichtangaben nach § 7 Abs. 2 VersVermV. (4) Bewahren Sie die Nachweise fünf Jahre auf dauerhaftem Datenträger auf — Frist ab Kalenderjahresende. (5) Halten Sie eine IHK-Erklärung bereit (§ 7 Abs. 3 VersVermV). (6) Register herunterladen, je Person/Jahr pflegen, abhaken.',
      adjudonSupport: 'Wir liefern das Weiterbildungsnachweis-Register als strukturiertes Arbeitsblatt: je Person/Kalenderjahr ein Konto mit den drei Pflichtangaben (§ 7 Abs. 2 VersVermV), Soll/Ist gegen das 15-Stunden-Ziel, der Aufbewahrungs-Rechenregel (Kalenderjahresende + 5 Jahre) und einem IHK-Erklärungs-Block (§ 7 Abs. 3). Durchführung, Relevanz/Qualität der Inhalte und Personen-Eignung verantworten Sie (nicht delegierbar, § 5 UWG).',
      templateAvailable: true, templateRef: '/dashboard/evidence-exports', hasTemplate: true,
      attested: true, attestedAt: new Date(Date.now() - 33 * 86400000).toISOString(), attestedByEmail: 'vermittler@demo.com', note: '15 Std. je Person für das laufende Kalenderjahr nachgewiesen; Register + IHK-Erklärung hinterlegt.', evidenceUrl: '',
    },
  ];
  // ── Demo-Persona „Nordlicht Versicherung AG" (~9 Monate Nutzung, Start 15.09.2025)
  // Attestierungs-Verteilung pro Framework-Tag (SSOT: docs/DEMO_PERSONA.md). Werte-only-
  // Override der Inline-Seeds: attested/attestedAt/attestedByEmail/note/evidenceUrl. Die
  // Frische (current/due_soon/expired) wird unten DERIVED aus attestedAt + renewalMonths
  // (12, dora_tlpt 36) + 30-Tage-DUE_SOON — daysAgo ist so gewählt, dass die Ziel-Buckets
  // entstehen (current 34–222 d = Nov 2025–Mai 2026; due_soon 338–355 d / tlpt 1078 d;
  // expired 372–398 d = Mai 2025, erzählbar als Alt-Attestierung aus dem Pilotbetrieb).
  // Aggregat: 77 current · 10 due_soon · 5 expired · 35 never (≈60/8/4/28 %). Attestierer
  // rotieren @nordlicht.example; ~1/3 mit note, einige mit evidenceUrl. data-act fast never.
  const PERSONA_ATTEST: Record<string, { daysAgo: number; by: string; note: string; ev: string }> = {
    "dataact_access_journal": { daysAgo: 198, by: 'dsb@nordlicht.example', note: "Zugriffs-Journal als Art.-4-5-Nachweis geführt; JSON-LD-Export getestet.", ev: "" },
    "dataact_retention_policy": { daysAgo: 174, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vvg_advice_content": { daysAgo: 133, by: 'vertrieb@nordlicht.example', note: "Beratungsgrund-Struktur im Advisory-Modul produktiv; Stichprobe Q1 geprüft.", ev: "" },
    "vvg_information_form": { daysAgo: 51, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vvg_suitability": { daysAgo: 61, by: 'vermittler@nordlicht.example', note: "", ev: "" },
    "vvg_market_test": { daysAgo: 221, by: 'vertrieb@nordlicht.example', note: "", ev: "" },
    "vvg_conflict_disclosure": { daysAgo: 167, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vvg_cpd_weiterbildung": { daysAgo: 36, by: 'vermittler@nordlicht.example', note: "15 Std. je Vermittler für 2025 nachgewiesen; IHK-Erklärung hinterlegt.", ev: "" },
    "vvg_suitability_statement": { daysAgo: 338, by: 'vertrieb@nordlicht.example', note: "", ev: "" },
    "vvg_appropriateness": { daysAgo: 395, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vag_business_organisation": { daysAgo: 170, by: 'vorstand@nordlicht.example', note: "GO-Doku der KI-Schicht v2 vom Vorstand freigegeben; IKS-Mappe verlinkt.", ev: "" },
    "vag_internal_control_system": { daysAgo: 64, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vag_risk_appetite": { daysAgo: 97, by: 'revision@nordlicht.example', note: "", ev: "" },
    "vag_internal_audit": { daysAgo: 212, by: 'vorstand@nordlicht.example', note: "", ev: "" },
    "vag_outsourcing": { daysAgo: 172, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "vag_orsa": { daysAgo: 74, by: 'revision@nordlicht.example', note: "KI-Betriebs-/Modellrisiko in ORSA 2025 einbezogen; Aufsichtsbericht fristgerecht.", ev: "https://nordlicht.example/evidence/orsa-2025-ki-anhang.pdf" },
    "vag_fit_proper": { daysAgo: 352, by: 'vorstand@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art26_monitoring": { daysAgo: 98, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art26_11_affected_persons": { daysAgo: 146, by: 'cco@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art14_human_oversight": { daysAgo: 35, by: 'aktuariat@nordlicht.example', note: "Aufsichtspersonen je Hochrisiko-System benannt; Schulung Automation-Bias durchgeführt.", ev: "" },
    "eu_ai_act_art14_sla_escalation": { daysAgo: 120, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art27_fria_completion": { daysAgo: 184, by: 'cco@nordlicht.example', note: "FRIA für Fraud Detector + UW-Leben v2 genehmigt.", ev: "https://nordlicht.example/evidence/fria-v2.pdf" },
    "eu_ai_act_art15_accuracy_policy": { daysAgo: 206, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art15_bias_audits": { daysAgo: 101, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art11_technical_documentation": { daysAgo: 134, by: 'cco@nordlicht.example', note: "Annex-IV-Paket für UW-Leben-Hochrisiko v3 deklariert; Upstream-Testberichte angehängt.", ev: "" },
    "eu_ai_act_art12_retention_decision": { daysAgo: 34, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art13_instructions_use": { daysAgo: 155, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art73_incident_reporting": { daysAgo: 147, by: 'cco@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art15_remediation": { daysAgo: 341, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "eu_ai_act_art50_2_autowatermark_decision": { daysAgo: 372, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art13_14_privacy_notice": { daysAgo: 173, by: 'dsb@nordlicht.example', note: "Datenschutzerklärung v4 veröffentlicht; Art-13/14-Angaben aus ROPA übernommen.", ev: "" },
    "gdpr_art6_legal_basis": { daysAgo: 132, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art28_2_subprocessor_approval": { daysAgo: 65, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gdpr_art33_34_breach_decision": { daysAgo: 76, by: 'dsb@nordlicht.example', note: "", ev: "" },
    "gdpr_art12_dsar_fulfillment": { daysAgo: 138, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art17_erasure_decision": { daysAgo: 160, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gdpr_art22_human_review": { daysAgo: 87, by: 'dsb@nordlicht.example', note: "", ev: "" },
    "gdpr_art29_dpa_verification": { daysAgo: 94, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art44_49_transfer_assessment": { daysAgo: 179, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gdpr_art30_ropa_maintenance": { daysAgo: 76, by: 'dsb@nordlicht.example', note: "Gesamt-ROPA gepflegt; Adjudon-Baustein als Eintrag übernommen.", ev: "" },
    "gdpr_art32_security_risk_assessment": { daysAgo: 124, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art32_tom_acceptance": { daysAgo: 68, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gdpr_art28_3_dpa_conclusion": { daysAgo: 87, by: 'dsb@nordlicht.example', note: "AV-Vertrag v3 angenommen (elektronische Form, SHA-256-textgebunden).", ev: "" },
    "gdpr_art28_3a_documented_instructions": { daysAgo: 112, by: 'compliance@nordlicht.example', note: "Verarbeitungsweisungen dokumentiert; Retention + Zweckbindung als Weisung gesetzt.", ev: "" },
    "gdpr_art35_dpia": { daysAgo: 43, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gdpr_art32_effectiveness_testing": { daysAgo: 355, by: 'dsb@nordlicht.example', note: "", ev: "" },
    "gdpr_tdddg25_cookie_consent": { daysAgo: 349, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gdpr_art16_rectification_execution": { daysAgo: 379, by: 'legal@nordlicht.example', note: "", ev: "" },
    "gwg_risk_analysis": { daysAgo: 134, by: 'gwb@nordlicht.example', note: "Risiko-Analyse v2 erstellt; Leitungsebene-Zustimmung (§ 5 Abs. 3) eingeholt.", ev: "" },
    "gwg_customer_acceptance": { daysAgo: 171, by: 'compliance@nordlicht.example', note: "Sorgfaltspflichten-Prozess + KYCDecision-Modul aktiv; Kundenannahme-Workflow freigegeben.", ev: "" },
    "gwg_beneficial_owner": { daysAgo: 64, by: 'gwb@nordlicht.example', note: "", ev: "" },
    "gwg_screening_evaluation": { daysAgo: 211, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "gwg_edd_judgment": { daysAgo: 138, by: 'gwb@nordlicht.example', note: "", ev: "" },
    "gwg_control_plan": { daysAgo: 176, by: 'compliance@nordlicht.example', note: "Sicherungsmassnahmen-Konzept + Monitoring-Regelwerk schriftlich; KI-Governance dokumentiert.", ev: "" },
    "gwg_alert_sla_policy": { daysAgo: 180, by: 'gwb@nordlicht.example', note: "", ev: "" },
    "gwg_sar_decision": { daysAgo: 338, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "eiopa_robustness": { daysAgo: 139, by: 'vorstand@nordlicht.example', note: "", ev: "" },
    "eiopa_board_governance": { daysAgo: 87, by: 'aktuariat@nordlicht.example', note: "KI-Governance per Vorstandsbeschluss v1 übernommen; in System of Governance verankert.", ev: "" },
    "eiopa_proportionality": { daysAgo: 89, by: 'revision@nordlicht.example', note: "", ev: "" },
    "eiopa_record_keeping": { daysAgo: 211, by: 'vorstand@nordlicht.example', note: "Aufbewahrung 1825 Tage (5 J.) gesetzt; jährliche Sichtung durch interne Revision verankert.", ev: "" },
    "eiopa_transparency": { daysAgo: 220, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "eiopa_human_oversight": { daysAgo: 65, by: 'revision@nordlicht.example', note: "", ev: "" },
    "eiopa_model_approval": { daysAgo: 100, by: 'vorstand@nordlicht.example', note: "", ev: "" },
    "eiopa_fairness_monitoring": { daysAgo: 342, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "eiopa_orsa_attestation": { daysAgo: 342, by: 'revision@nordlicht.example', note: "", ev: "" },
    "dora_ict_risk_framework": { daysAgo: 146, by: 'ict-risk@nordlicht.example', note: "", ev: "" },
    "dora_art7_systems": { daysAgo: 219, by: 'ciso@nordlicht.example', note: "", ev: "" },
    "dora_art8_asset_identification": { daysAgo: 63, by: 'auslagerung@nordlicht.example', note: "", ev: "" },
    "dora_art9_10_protection_detection": { daysAgo: 101, by: 'ict-risk@nordlicht.example', note: "", ev: "" },
    "dora_art11_continuity_policy": { daysAgo: 174, by: 'ciso@nordlicht.example', note: "", ev: "" },
    "dora_art12_backup_recovery": { daysAgo: 181, by: 'auslagerung@nordlicht.example', note: "", ev: "" },
    "dora_art13_post_incident_review": { daysAgo: 207, by: 'ict-risk@nordlicht.example', note: "Post-Incident-Review zu Vorfall #3 (Fail-Open-Nacht) abgeschlossen.", ev: "" },
    "dora_art14_crisis_communication": { daysAgo: 118, by: 'ciso@nordlicht.example', note: "", ev: "" },
    "dora_art17_incident_management_process": { daysAgo: 137, by: 'auslagerung@nordlicht.example', note: "IKT-Vorfall-Management-Prozess v2 vom Leitungsorgan freigegeben; an Incident-Hub angebunden.", ev: "" },
    "dora_incident_classification": { daysAgo: 66, by: 'ict-risk@nordlicht.example', note: "", ev: "" },
    "dora_regulator_filing": { daysAgo: 48, by: 'ciso@nordlicht.example', note: "", ev: "" },
    "dora_art28_register_of_information": { daysAgo: 134, by: 'auslagerung@nordlicht.example', note: "Informationsregister vollständig; Adjudon-Selbstauskunft hinterlegt, CSV-Export getestet.", ev: "https://nordlicht.example/evidence/dora-informationsregister-2026.csv" },
    "dora_art30_contractual_provisions": { daysAgo: 99, by: 'ict-risk@nordlicht.example', note: "", ev: "" },
    "dora_tlpt": { daysAgo: 1078, by: 'ciso@nordlicht.example', note: "", ev: "" },
    "dora_art11_bcm_testing": { daysAgo: 398, by: 'auslagerung@nordlicht.example', note: "", ev: "" },
    "solvency_orsa_narrative": { daysAgo: 106, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "solvency_pillar1_data_quality": { daysAgo: 152, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "solvency_pillar1_use_test": { daysAgo: 191, by: 'vorstand@nordlicht.example', note: "", ev: "" },
    "solvency_pillar1_validation": { daysAgo: 68, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "solvency_pillar1_documentation": { daysAgo: 61, by: 'compliance@nordlicht.example', note: "Model-Cards v2 + Art-115-Change-Log für 5 produktive KI-Systeme dokumentiert.", ev: "https://nordlicht.example/evidence/model-cards-v2.pdf" },
    "solvency_governance_keyfunctions": { daysAgo: 161, by: 'vorstand@nordlicht.example', note: "Governance-System-Doku v1 inkl. KI-Schicht-Einbettung vom AMSB freigegeben.", ev: "" },
    "solvency_art41_written_policies": { daysAgo: 164, by: 'aktuariat@nordlicht.example', note: "KI-Policy als Teil des IKS-Leitlinienwerks v1 freigegeben; jährliche Review terminiert.", ev: "" },
    "solvency_art41_key_functions": { daysAgo: 196, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "solvency_art49_outsourcing_policy": { daysAgo: 143, by: 'vorstand@nordlicht.example', note: "Adjudon als wichtige Auslagerung eingestuft; Leitlinie v1 freigegeben.", ev: "" },
    "solvency_art49_due_diligence": { daysAgo: 222, by: 'aktuariat@nordlicht.example', note: "", ev: "" },
    "solvency_esg_classification": { daysAgo: 351, by: 'compliance@nordlicht.example', note: "", ev: "" },
    "solvency_internal_model_validation": { daysAgo: 379, by: 'vorstand@nordlicht.example', note: "", ev: "" },
  };
  // Immutable override: neue Objekte statt In-place-Mutation (coding-style).
  const allWithPersona = all.map((it) => {
    const o = PERSONA_ATTEST[it.key];
    return o
      ? { ...it, attested: true, attestedAt: new Date(Date.now() - o.daysAgo * 86400000).toISOString(), attestedByEmail: o.by as string | null, note: o.note, evidenceUrl: o.ev }
      : { ...it, attested: false, attestedAt: null as string | null, attestedByEmail: null as string | null, note: "", evidenceUrl: "" };
  });
  // Resolve hub SLUG → catalog framework TAG(s), mirroring the backend
  // (combined solvency-vag → both regimes; beratung→vvg-idd; aml-gwg→gwg; etc.).
  const SLUG_TO_TAGS: Record<string, string[]> = {
    'solvency-vag': ['solvency-ii', 'vag-mago'], beratung: ['vvg-idd'], 'aml-gwg': ['gwg'], 'data-act-fida': ['data-act'],
  };
  const tags = framework ? SLUG_TO_TAGS[framework] || [framework] : null;
  const items = tags ? allWithPersona.filter((i) => tags.includes(i.framework)) : allWithPersona;
  // Renewal freshness (mirrors the backend): an attestation is valid for a cadence
  // (annual default, TLPT 3y), then lapses. Demo = Prod — derive the same way so the
  // mock shows current / due-soon / expired states.
  const now = Date.now();
  const DUE_SOON_MS = 30 * 86400000;
  const renewalFor = (key: string): number => (key === 'dora_tlpt' ? 36 : 12);
  const enriched = items.map((i) => {
    const renewalMonths = renewalFor(i.key);
    let validUntil: string | null = null;
    let freshness: 'never' | 'current' | 'due_soon' | 'expired' = 'never';
    if (i.attested && i.attestedAt) {
      const exp = new Date(i.attestedAt);
      exp.setMonth(exp.getMonth() + renewalMonths);
      validUntil = exp.toISOString();
      const t = exp.getTime();
      freshness = now >= t ? 'expired' : now >= t - DUE_SOON_MS ? 'due_soon' : 'current';
    }
    return { ...i, renewalMonths, validUntil, freshness, pillar: MOCK_OBLIGATION_PILLAR[i.key] ?? null };
  });
  const attested = enriched.filter((i) => i.attested).length;
  const current = enriched.filter((i) => i.freshness === 'current').length;
  const dueSoon = enriched.filter((i) => i.freshness === 'due_soon').length;
  const expired = enriched.filter((i) => i.freshness === 'expired').length;
  const never = enriched.filter((i) => i.freshness === 'never').length;
  return {
    items: enriched,
    summary: { total: enriched.length, attested, outstanding: never + expired, current, dueSoon, expired, never },
  };
}

// Demo regulated entities ("Aufsichtsobjekte"). DORA gets a 2-entity GROUP so the
// roll-up + selector are exercised; other hubs run a single default entity.
const MOCK_ENTITIES: API.RegulatedEntity[] = [
  { id: 'ent-default', name: 'Muster Lebensversicherung AG', legalName: 'Muster Lebensversicherung AG', lei: '391200ABCDEF12345678', bafinId: '1234', legalForm: 'AG', supervisoryAuthority: 'BaFin', jurisdiction: 'DE', isDefault: true, status: 'active' },
  { id: 'ent-sach', name: 'Muster Sachversicherung AG', legalName: 'Muster Sachversicherung AG', lei: '391200ZYXW98765432AB', bafinId: '5678', legalForm: 'AG', supervisoryAuthority: 'BaFin', jurisdiction: 'DE', isDefault: false, status: 'active' },
];

function mockEntities(): API.RegulatedEntity[] {
  return MOCK_ENTITIES.map((e) => ({ ...e }));
}

type EnrichedObl = { freshness?: string; attested?: boolean; pillar?: string | null };
const freshOf = (i: EnrichedObl): string => i.freshness ?? (i.attested ? 'current' : 'never');

// Build one entity's readiness core from its obligation items.
function buildEntityCore(items: EnrichedObl[], framework: string): API.FrameworkReadiness {
  const total = items.length;
  const current = items.filter((i) => freshOf(i) === 'current').length;
  const dueSoon = items.filter((i) => freshOf(i) === 'due_soon').length;
  const expired = items.filter((i) => freshOf(i) === 'expired').length;
  const never = items.filter((i) => freshOf(i) === 'never').length;
  const valid = current + dueSoon;
  const pct = total ? Math.round((valid / total) * 100) : 0;
  const pillarMap = new Map<string, API.FrameworkReadinessPillar>();
  for (const it of items) {
    const label = it.pillar || 'Weitere';
    let p = pillarMap.get(label);
    if (!p) { p = { label, total: 0, valid: 0, expired: 0 }; pillarMap.set(label, p); }
    p.total += 1;
    if (freshOf(it) === 'current' || freshOf(it) === 'due_soon') p.valid += 1;
    if (freshOf(it) === 'expired') p.expired += 1;
  }
  const adjudon: API.FrameworkReadinessAdjudon =
    framework === 'dora'
      ? { overall: 'healthy', critical: 0, warning: 0, healthy: 1, total: 1 }
      : { overall: 'none', critical: 0, warning: 0, healthy: 0, total: 0 };
  const prevPct = Math.max(0, pct - 8);
  const now = new Date();
  const prevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const prevPeriod = `${prevMonth.getUTCFullYear()}-${String(prevMonth.getUTCMonth() + 1).padStart(2, '0')}`;
  return {
    framework,
    adjudon,
    attestations: { total, valid, current, dueSoon, expired, never, pct },
    pillars: Array.from(pillarMap.values()),
    trend: { previous: { pct: prevPct, period: prevPeriod, capturedAt: prevMonth.toISOString() }, deltaPct: pct - prevPct },
    generatedAt: new Date().toISOString(),
  };
}

// Degrade a copy of the items (flip up to n valid → never) for a 2nd demo entity.
function degradeItems<T extends EnrichedObl>(items: T[], n: number): T[] {
  let flipped = 0;
  return items.map((i) => {
    if (flipped < n && (freshOf(i) === 'current' || freshOf(i) === 'due_soon')) {
      flipped += 1;
      return { ...i, freshness: 'never', attested: false } as T;
    }
    return { ...i };
  });
}

// Consolidated group aggregate of N entity cores.
function aggregateCores(cores: API.FrameworkReadiness[], framework: string): API.FrameworkReadiness {
  let total = 0, valid = 0, current = 0, dueSoon = 0, expired = 0, never = 0;
  const pmap = new Map<string, API.FrameworkReadinessPillar>();
  for (const c of cores) {
    const a = c.attestations;
    total += a.total; valid += a.valid; current += a.current; dueSoon += a.dueSoon; expired += a.expired; never += a.never;
    for (const p of c.pillars) {
      let acc = pmap.get(p.label);
      if (!acc) { acc = { label: p.label, total: 0, valid: 0, expired: 0 }; pmap.set(p.label, acc); }
      acc.total += p.total; acc.valid += p.valid; acc.expired += p.expired;
    }
  }
  return {
    framework,
    adjudon: cores[0]?.adjudon || { overall: 'none', critical: 0, warning: 0, healthy: 0, total: 0 },
    attestations: { total, valid, current, dueSoon, expired, never, pct: total ? Math.round((valid / total) * 100) : 0 },
    pillars: Array.from(pmap.values()),
    trend: { previous: null, deltaPct: null },
    generatedAt: new Date().toISOString(),
  };
}

// Server-authoritative combined readiness — demo mirror of
// GET /api/compliance-overview/readiness/:framework[?entity=]. Multi-entity for
// DORA (group roll-up + per-entity breakdown); single default entity elsewhere.
async function mockFrameworkReadiness(framework: string, entityId: string | null = null): Promise<API.FrameworkReadiness | null> {
  const status = await mockControllerResponsibilities(framework);
  const baseItems = status.items as EnrichedObl[];
  const isGroup = framework === 'dora';
  const entityDefs = isGroup
    ? [
        { id: 'ent-default', name: 'Muster Lebensversicherung AG', isDefault: true, core: buildEntityCore(baseItems, framework) },
        { id: 'ent-sach', name: 'Muster Sachversicherung AG', isDefault: false, core: buildEntityCore(degradeItems(baseItems, 3), framework) },
      ]
    : [{ id: 'ent-default', name: 'Standard-Aufsichtsobjekt', isDefault: true, core: buildEntityCore(baseItems, framework) }];

  const entities: API.FrameworkReadinessEntitySummary[] = entityDefs.map((e) => ({
    id: e.id,
    name: e.name,
    isDefault: e.isDefault,
    pct: e.core.attestations.pct,
    valid: e.core.attestations.valid,
    total: e.core.attestations.total,
    expired: e.core.attestations.expired,
    never: e.core.attestations.never,
    adjudonOverall: e.core.adjudon.overall,
  }));

  let scope: API.FrameworkReadiness;
  let selectedEntityId: string | null;
  if (entityId) {
    const sel = entityDefs.find((e) => e.id === entityId);
    scope = sel ? sel.core : entityDefs[0].core;
    selectedEntityId = sel ? entityId : null;
  } else if (entityDefs.length > 1) {
    scope = aggregateCores(entityDefs.map((e) => e.core), framework);
    selectedEntityId = null;
  } else {
    scope = entityDefs[0].core;
    selectedEntityId = entityDefs[0].id;
  }

  return {
    ...scope,
    railCounts: framework === 'dora' ? { '/dashboard/dora-register': 12 } : {},
    entities,
    entityCount: entityDefs.length,
    selectedEntityId,
  };
}

// EU AI Act Art. 26(7)/(11) disclosure-notice template (demo).
function mockDisclosureNotice(params: { type: 'affected_persons' | 'workers'; locale?: 'de' | 'en'; systemName?: string; purpose?: string }): API.DeployerDisclosureNotice {
  const locale: 'de' | 'en' = params.locale === 'en' ? 'en' : 'de';
  const sys = params.systemName || (locale === 'de' ? '[BEZEICHNUNG DES KI-SYSTEMS]' : '[AI SYSTEM NAME]');
  const isWorkers = params.type === 'workers';
  const markdown = locale === 'de'
    ? (isWorkers
      ? `# Information der Beschäftigten und der Arbeitnehmervertretung\n_EU AI Act Art. 26 Abs. 7_\n\nVor Inbetriebnahme informiert Demo Versicherung AG die Arbeitnehmervertretung über „${sys}" (§§ 80, 87 Abs. 1 Nr. 6 und 90 BetrVG).\n\n_Anpassbare Vorlage; keine rechtsverbindliche Erklärung._`
      : `# Hinweis zur Nutzung eines KI-gestützten Systems\n_EU AI Act Art. 26 Abs. 11_\n\nDemo Versicherung AG setzt im Rahmen von „${sys}" ein hochriskantes KI-System ein, das Entscheidungen über Sie trifft oder unterstützt (Art. 22 Abs. 3 DSGVO).\n\n_Anpassbare Vorlage; keine rechtsverbindliche Erklärung._`)
    : (isWorkers
      ? `# Information of workers and workers’ representatives\n_EU AI Act Article 26(7)_\n\nBefore deployment, Demo Insurer informs the works council about "${sys}" (§§ 80, 87(1) no. 6, 90 BetrVG).\n\n_Customizable template; not a legally binding statement._`
      : `# Notice on the use of an AI-assisted system\n_EU AI Act Article 26(11)_\n\nDemo Insurer uses a high-risk AI system in "${sys}" that makes or assists decisions concerning you (Art. 22(3) GDPR).\n\n_Customizable template; not a legally binding statement._`);
  return {
    type: params.type,
    locale,
    title: markdown.split('\n', 1)[0].replace(/^#\s*/, ''),
    markdown,
    placeholders: params.purpose ? [] : [locale === 'de' ? '[ZWECK — VOM BETREIBER AUSZUFÜLLEN]' : '[PURPOSE — TO BE COMPLETED BY THE DEPLOYER]'],
    filled: { legalName: 'Demo Versicherung AG', systemName: params.systemName || null, purpose: params.purpose || null },
    generatedAt: new Date().toISOString(),
  };
}

function mockFRIA(id: string, status: 'draft' | 'review' | 'approved' | 'archived', completeness: number, payload?: any) {
  return {
    id,
    organizationId: 'org_demo',
    systemName: payload?.systemName ?? 'Customer Triage Agent',
    purpose: payload?.purpose ?? 'Route customer inquiries to appropriate human teams.',
    affectedPersons: payload?.affectedPersons ?? 'EU customers contacting support (~50k/month).',
    usageContext: payload?.usageContext ?? 'B2C support — first-line triage before human agent.',
    durationOfUse: payload?.durationOfUse ?? 'Continuous, per-request stateless.',
    humanOversightSummary: payload?.humanOversightSummary ?? 'All triage decisions reviewed weekly; low-confidence decisions routed to human in real time.',
    complaintsMechanism: payload?.complaintsMechanism ?? 'support@demo.com + dedicated EU representative.',
    risksIdentified: completeness >= 60 ? 'Risk of misrouting urgent cases.' : undefined,
    mitigations: completeness >= 80 ? 'Confidence threshold + human escalation on emotional language detection.' : undefined,
    linkedAgentIds: payload?.linkedAgentIds ?? (completeness >= 80 ? ['agent_uw_leben'] : []),
    status,
    completeness,
    createdBy: 'demo@adjudon.com',
    approvedBy: status === 'approved' ? 'compliance@demo' : null,
    approvedAt: status === 'approved' ? new Date(Date.now() - 7 * 86400000).toISOString() : null,
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// EU AI Act Art. 27 — mirrors backend insuranceFriaTemplate.js (Annex III §5(b)/(c)).
function mockInsuranceFriaTemplate(useCase?: string) {
  const purposeByUseCase: Record<string, string> = {
    underwriting: 'AI-assisted underwriting decision support: assessing applicant risk profile and eligibility',
    pricing: 'AI-assisted premium pricing: determining actuarially fair rates based on risk factors',
    claims: 'AI-assisted claims assessment: evaluating claim legitimacy and determining payout amounts',
  };
  return {
    purpose: purposeByUseCase[useCase ?? 'default'] ?? 'AI-assisted decision support for insurance operations',
    affectedPersons: 'Applicants, policyholders, claimants, and beneficiaries subject to AI-assisted underwriting, pricing, or claims decisions',
    usageContext: 'High-risk AI system per EU AI Act Annex III, used for consequential decisions in insurance contracts',
    durationOfUse: 'Ongoing, with periodic reassessment per EU AI Act Art. 25(2)',
    identifiedRisks: [
      { id: 'risk-001', title: 'Discrimination and Bias in Pricing', description: 'AI model may perpetuate or amplify historical biases in applicant data, resulting in discriminatory pricing for protected characteristics.', affectedRights: ['EU Charter Art. 21 (Non-Discrimination)', 'EU Charter Art. 23 (Equality)', 'GDPR Art. 9(1)'], likelihood: 'medium', severity: 'high', mitigation: 'Mandatory fairness audits; remove protected-attribute proxies; disparate-impact testing; appeal mechanism with human review.', residualRisk: 'medium' },
      { id: 'risk-002', title: 'Lack of Transparency in Rejection Decisions', description: 'Applicants rejected by AI receive insufficient explanation, preventing meaningful challenge.', affectedRights: ['GDPR Art. 13–15', 'EU AI Act Art. 13'], likelihood: 'high', severity: 'high', mitigation: 'Plain-language decision letters citing top decision factors; customer-accessible explanation dashboard; decision logs with model version.', residualRisk: 'low' },
      { id: 'risk-003', title: 'Data Quality and Model Degradation Over Time', description: 'Training data may be incomplete or outdated; model performance may degrade due to dataset shift.', affectedRights: ['EU Charter Art. 7', 'GDPR Art. 5(1)(a)', 'EU AI Act Art. 10'], likelihood: 'medium', severity: 'medium', mitigation: 'Continuous performance monitoring; decision-feedback loop; data lineage docs; retraining alerts on fairness drift.', residualRisk: 'medium' },
      { id: 'risk-004', title: 'Insufficient Human Oversight and Right to Human Review', description: 'Fully automated pipeline without meaningful human intervention for high-stakes decisions.', affectedRights: ['GDPR Art. 22', 'EU AI Act Art. 14'], likelihood: 'high', severity: 'high', mitigation: 'Tiered routing: auto-approve routine, specialist review on low confidence, mandatory human review on rejections; SLA for human review.', residualRisk: 'low' },
    ],
  };
}

function mockPatternFamily(id: string, name: string, domain: string, memberCount: number) {
  return {
    id,
    name,
    description: `Cross-org consortium for ${domain}-specific compliance pattern sharing.`,
    domain,
    coordinatorOrgId: 'org_coord',
    members: Array.from({ length: memberCount }).map((_, i) => ({
      organizationId: i === 0 ? 'org_demo' : `org_member_${i}`,
      joinedAt: new Date(Date.now() - i * 7 * 86400000).toISOString(),
      status: 'active' as const,
    })),
    defaultEpsilon: 1.0,
    defaultDelta: 1.0e-5,
    currentRound: 3,
    createdAt: new Date(Date.now() - 90 * 86400000).toISOString(),
  };
}

function mockFederatedRound(id: string, familyId: string, roundNumber: number, status: 'pending' | 'collecting' | 'aggregating' | 'completed' | 'failed', payload?: any) {
  return {
    id,
    familyId,
    roundNumber,
    status,
    initiatedBy: 'org_demo',
    participantCount: 8,
    epsilon: payload?.epsilon ?? 1.0,
    delta: payload?.delta ?? 1.0e-5,
    noiseMechanism: 'gaussian' as const,
    attestationRef: status === 'completed' ? 'tee_attest_xyz' : null,
    sigstoreBundleRef: status === 'completed' ? 'sigstore://mock-rekor/12345' : null,
    chainHash: status === 'completed' ? 'fl_chain'.padEnd(64, '0') : null,
    startedAt: new Date(Date.now() - roundNumber * 7 * 86400000).toISOString(),
    completedAt: status === 'completed' ? new Date(Date.now() - (roundNumber - 1) * 7 * 86400000).toISOString() : null,
    failureReason: null,
  };
}

function mockAdvisory(id: string, status: API.AdvisoryStatus, completeness: number, customerRef?: string, payload?: any): API.AdvisoryRecord {
  const isLimitedScope = payload?.consultationScope === 'limited';
  const now = Date.now();
  return {
    id,
    organizationId: 'org_demo',
    customerRef: customerRef ?? payload?.customerRef ?? `CUST_${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    customerDisplayName: payload?.customerDisplayName ?? 'Acme Insurance AG',
    advisorName: payload?.advisorName ?? 'Alice Schmidt (Makler)',
    advisorType: payload?.advisorType ?? 'human',
    linkedTraceId: payload?.linkedTraceId ?? null,
    linkedAgentId: payload?.linkedAgentId ?? null,
    consultationScope: payload?.consultationScope ?? 'full',
    limitedScopeReason: isLimitedScope ? payload?.limitedScopeReason ?? 'Kunde wünscht nur Beratung zu Krankenversicherung' : '',
    demandsAndNeeds: payload?.demandsAndNeeds ?? 'Umfassender Versicherungsschutz für Betrieb und Familie',
    recommendation: payload?.recommendation ?? 'Kombination aus Betriebshaftpflicht (€2M) + Erweiterter Krankenversicherung',
    recommendationReason: payload?.recommendationReason ?? 'Vollständige Abdeckung von Geschäftsrisiken + Familie',
    alternativesConsidered: payload?.alternativesConsidered ?? 'Minimale Versicherung (abgelehnt wegen unzureichend), Selbstversicherung (nicht tragbar)',
    productCategory: payload?.productCategory ?? 'liability',
    suitability: payload?.suitability ?? {
      applicable: completeness >= 70,
      knowledgeExperience: 'Versicherungsmakler seit 12 Jahren, spezialisiert auf KMU',
      financialSituation: 'Stabiler Betrieb, Jahresumsatz €1.2M',
      investmentObjectives: 'Maximale Risikoabwehr',
      riskTolerance: 'low' as const,
    },
    acknowledgment: payload?.acknowledgment ?? {
      acknowledged: status === 'acknowledged' || status === 'archived',
      acknowledgedAt: (status === 'acknowledged' || status === 'archived') ? new Date(now - 2 * 86400000).toISOString() : null,
      method: (status === 'acknowledged' || status === 'archived') ? 'text-form' : 'none',
      acknowledgedByRef: (status === 'acknowledged' || status === 'archived') ? 'CUST_REP_001' : null,
      statement: (status === 'acknowledged' || status === 'archived') ? 'Bestätigung per E-Mail erhalten und akzeptiert' : '',
    },
    deliveryContext: payload?.deliveryContext ?? {
      form: 'text-form',
      statusInfoBeforeDeclaration: status !== 'draft',
      providedBeforeConclusion: status !== 'draft',
      abs2Exception: { applies: false, reason: '', followUpTextFormAt: null, mandatoryInsuranceProvisionalCover: false },
    },
    advisedAt: status !== 'draft' ? new Date(now - 5 * 86400000).toISOString() : null,
    status,
    retentionUntil: status !== 'draft' ? new Date(now + 10 * 365 * 86400000).toISOString() : null,
    completeness,
    createdBy: 'demo@adjudon.com',
    createdByEmail: 'demo@adjudon.com',
    createdAt: new Date(now - 14 * 86400000).toISOString(),
    updatedAt: new Date().toISOString(),
  };
}